'use client';

import { worstFileCheck } from '@/lib/file-check/file-check-memory';
import { targetFromSizeValue } from '@/lib/file-check/print-file-check';
import { findQuantityNudge, quantityNudgeCopy } from '@/lib/conversion/quantity-nudge';
import { PurchaseReassurance } from '@/components/conversion/purchase-reassurance';
import { addToCartFileBlock, awaitsDesignerSize, configuratorFlowCopy, loadsDeliveryMatrix, resolveConfiguratorPrimaryAction, resolveVinylDesignerMode, uploadSlotsRuleOutPrintFile, visibleFileUploadSlots } from '@/lib/product/configurator-flow';
import { isSupplierBookkeepingOption } from '@/lib/catalog-option-visibility';
import { sizeLabelDimensionsCm } from '@/lib/products/size-label-dimensions';
import { buildSelectionProbe, checkSelectionQuote, optionSelectionKey } from '@/lib/printcom/selection-quote';
import { configurationHasForeignSupplierValue, isForeignSupplierSelection } from '@/lib/printcom/foreign-supplier-values';
import { verifiedWorkfileProfile } from '@/lib/printcom/verified-workfiles';
import { sizeUnitHintCopy, suggestCmFromMm } from '@/lib/product/size-unit-hint';
import { editorUnavailableReason, parseSizeDimensionsCm, resolveEditorBleedFinishing, resolveEditorSizeValue } from '@/lib/editor/editor-size-value';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { isBoogProduct } from '@/lib/studio/products';
import { boogFileMatchesMaterial } from '@/lib/studio/boog-cart';
import { resolveExcludedSelection } from '@/lib/pricing/option-conflict-resolution';
import { formatOptionPriceDelta } from '@/lib/products/option-price-delta';
import { OptionPriceDelta } from './option-price-delta';
import { sanitizeOptInAddonDefault } from '@/lib/pricing/opt-in-addon-defaults';
import { sameProviderOptionValue } from '@/lib/pricing/provider-option-value';
import { buildAutoChangeNotice, UNDO_LABELS } from '@/lib/pricing/auto-change-notice';
import { isExcludedByConfiguredOptionRules, maximumCopiesForConfiguration, minimumCopiesForConfiguration } from '@/lib/pricing/option-exclusions';
import { isMatrixQuantityExcluded } from '@/lib/pricing/matrix-quantity-exclusions';
import { existingMatrixQuantityFeedback } from '@/lib/product-options/matrix-custom-quantity-feedback';
import { isSinglePieceQuantityPolicy, singlePieceQuantityPolicy } from '@/lib/product-options/single-piece-quantity';
import { getVolumeDiscountRate, resolveVolumeDiscountTiers, volumeDiscountQuantities as resolveVolumeDiscountQuantities } from '@/lib/pricing/volume-discounts';
import { autoRetryDelaySeconds, fetchPricingBatch, isPriceRateLimitedError, MAX_AUTO_RETRY_SECONDS, throwIfRateLimited, type PriceRateLimitedError } from '@/lib/pricing/pricing-request-client';
import {
  HIDDEN_CONSUMER_OPTION_IDS,
  applyProductSpecificOptionFixes,
  applyProductSpecificSpecificationFixes,
  getCheapestDeliveryOptionValue,
  getForcedProductOptions,
  getPreferredPrintComPrintingMethod,
  getPrintComCatalogAllowedValueSet,
  isDeliverySpeedOption,
  isPrintedLetterheadsProduct,
  matchesShowIf,
  normalizeConsumerOptions,
  normalizeFullPricePropertyValues,
  orderPrintBeforeNone,
  stripMarkdownImages,
  applyBusinessCardSafeDefaultsTo,
  businessCardAutoRepairProtectedOptionIds,
  isRedundantTextileColorAlias,
  normalizeCustomShapeProviderValue,
  sortPrintfulSizeValues,
  type ProductOption,
} from '@/lib/product-options/configurator-rules';
import { recordEditorSessionEvent, getCurrentEditorSessionCode } from '@/lib/editor-session/client';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { CornerRadiusField } from '@/components/product/corner-radius-field';
import { CORNER_RADIUS_OPTION_ID, cornerRadiusCopy, cornerRadiusDisplayRow, designCornerRadiusMismatch, usesCustomCornerRadius, withCornerRadiusForShape } from '@/lib/product-options/corner-radius';
import { parseDimensions } from '@/lib/utils/dimensions';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Check, Info, ShoppingCart, Upload, Leaf, Sparkles, Wrench, Minus, Plus, PenTool, Truck, RotateCcw } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { VinylDesignerSettings } from '@/lib/vinyl/designer-product';
import { createSharedPriceFetch, stableConfigurationKey } from '@/lib/vinyl/shared-price-fetch';
import { createDesignerOrderGate, designerOrderError } from '@/lib/vinyl/designer-order-gate';
import { reportVstDesignerError } from '@/lib/vinyl/vst-monitoring';
import { hostMessages } from '@/lib/vinyl/vst-host-messages';
import type { VinylDesignResult } from '@/lib/vinyl/vst-host';
import { glassSideFor, withDesignChange, withDesignMaterial, withGlassSideDisplay, type VinylGlassSide } from '@/lib/vinyl/designer-configuration';

/**
 * De Vinyl Lettering Designer wordt lui geladen: deze configurator draait op
 * 1400+ producten die er niets aan hebben. De wrapper laadt de bundel zelf pas
 * bij het mounten (`/vst-designer/<versie>/`). `ssr: false` omdat de bundel
 * alleen in de browser draait.
 */
const VinylDesignerInline = dynamic(
  () => import('@/components/vinyl/vst-designer-mount').then((mod) => mod.VstDesignerMount),
  {
    ssr: false,
    // Alleen in de browser (ssr: false), dus de paginataal staat op <html lang>.
    loading: () => (
      <div role="status" className="flex h-40 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-sm text-gray-500">
        {hostMessages(typeof document === 'undefined' ? 'en' : document.documentElement.lang).loading}
      </div>
    ),
  }
);
import { OptionInfoButton } from './option-info-button';
import {
  buildDeliveryOverrideModel,
  configurationPatchForColumn,
  deliveryOverrideApproxText,
  findDeliveryOverrideColumn,
  selectedMatrixColumnId,
} from '@/lib/product-options/delivery-override-columns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileUploader } from './file-uploader';
import { TemplateDownloadButton } from './template-download-button';
import { templateDownloadPrompt, type ProductTemplateDownload } from '@/lib/product-template-downloads';
import { resolveProductionSheetProfile } from '@/lib/editor/production-sheet-selection';
import { useCartStore } from '@/store/cart-store';
import type { CartItem } from '@/store/cart-store';
import type { LinkedPrintRow } from './linked-sign-prints';
import { getLinkedSignPrint, getLinkedPrintCopy, matchesLinkedSignPrint } from '@/lib/products/linked-sign-prints';
const LinkedSignPrints = dynamic(() => import('./linked-sign-prints').then(mod => mod.LinkedSignPrints));
import { toast } from 'sonner';
import { GroupedSizeSelector } from './GroupedSizeSelector';
import { getOptionIcon, shouldShowIcons } from '@/lib/utils/option-icons';
import Image from 'next/image';
import { calculatePriceWithVAT, calculateStorefrontPriceWithVAT, formatPriceForLocale, getCountryFromLocale } from '@/lib/pricing';
import { printfulPlacementLabel, usedPrintfulPlacementsFromFiles } from '@/lib/printful/placement-pricing';
import { resolvePrintfulLineQuantity, syncQuantityDisplayRows } from '@/lib/printful/quantity';
import { translatePrintLabel } from '@/lib/utils/print-label-translations';
import { formatMetricSizeLabel } from '@/lib/utils/dimensions';
import { getPrintMaterialInfo } from '@/lib/printcom/material-info';
import { filterCompatiblePrintComAccessories } from '@/lib/printcom/accessory-compatibility';
import {
  getPrintComOptionCompatibilityIssue,
  hasIncompleteCustomSize,
  isNoPosterFinishValue,
  isPosterMaterialWithoutFinish,
} from '@/lib/printcom/option-compatibility';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { cartPath, localizedPath, productPath } from '@/lib/localized-paths';
import { sanitizeProductOptionValues, isUsableProductOptionValue } from '@/lib/product-options/sanitize';
import { PRICE_CRITICAL_OPTION_IDS } from '@/lib/pricing/quote-safety';
import { resolveLivePrintComExecution } from '@/lib/pricing/live-printcom-execution';
import { getClosedCustomQuantities, isAllowedClosedQuantity, customMatrixRowTotal } from '@/lib/product-options/closed-custom-quantity';
import { OPEN_QUANTITY_MAXIMUM, resolveCustomMatrixPrice, withOpenCustomQuantitySettings } from '@/lib/product-options/open-custom-quantity';
import { pendingDeliveryPresentation } from '@/lib/products/pending-delivery';
import { URGENCY_LEAD_TIMES, explicitSpecificationLeadTime, resolveUrgencyLeadTime } from '@/lib/products/urgency-lead-time';
import { deliveryRefusalKey, refusedDeliveryColumnIds, refusedDeliveryColumnsFor, rememberRefusedDeliveryColumns, type MatrixCellOutcome } from '@/lib/pricing/refused-delivery-columns';
import { usePrintComDeliveryAvailability, matrixDeliveryText, selectedQuantityDeliveryRequests, visibleDeliverySpeedIds, type MatrixDeliveryRequest } from './use-printcom-delivery-availability';
import { AdminProductOverlay } from '@/components/admin/AdminProductOverlay';
import { STANDARD_CUSTOM_SHAPE_PRESETS } from '@/lib/products/custom-shape-presets';
import { isPackagingProduct, isThreeDimensionalSizeValue, shouldOfferVirtualCustomSizePresets, type VirtualPresetProductContext } from '@/lib/product-options/virtual-custom-size-presets';
import { customSizeAreaHint, shouldRenderSeparateCustomSizePanel } from '@/lib/product-options/custom-size-rendering';
import { isForbiddenOptionValue, sampleOptionValueDescription, sampleOptionValueShortDescription } from '@/lib/product-options/forbidden-option-values';
import { optionValueDescription, optionValueDescriptionOverridesStoredDutch } from '@/lib/product-options/option-value-descriptions';
import { resolvePrintingMethodQuantityPolicy } from '@/lib/product-options/printing-method-quantity-policy';
import { VIRTUAL_SIZE_DERIVED_KEYS, getVirtualSizeConfigFromInitial } from '@/lib/product-options/virtual-size-keys';
import { computeCustomQuantitiesToRemove } from '@/lib/product-options/matrix-quantity-cleanup';
import { resolveInitialCopies, resolveInitialQuantity, resolveMatrixQuantityMaximum } from '@/lib/product-options/initial-quantity';
import { cartQuantityPolicyFromProduct } from '@/lib/cart/quantity-steps';
import { acceptsCustomQuantitySelection } from '@/lib/product-options/custom-quantity-selection';
import { isLockedOptionField } from '@/lib/product-options/locked-fields';
import { isColorOptionKey, shouldPresentAsColorOption } from '@/lib/product-options/color-option';
import { colorSearchText, hasResolvedColorSwatch, resolveColorSwatch } from '@/lib/product-options/color-swatch';
import { ColorOptionCombobox } from '@/components/product/color-option-combobox';
import { customSizeLimitsForConfiguration, normalizeCustomSizeLimits } from '@/lib/product-options/custom-size-limits';
import { CONTOUR_LINES_OPTION_ID, contourLinesForShape, isContourNoneValue, shapeUsesContourLines } from '@/lib/product-options/contour-lines';
import { useCountryShippingCost } from '@/hooks/use-country-shipping-cost';
import { evaluateDimensionConstraint, findMatchingDimensionRule, getDimensionRuleInputBounds } from '@/lib/products/dimension-constraint';
import { abortReason } from '@/lib/net/is-abort-error';
import { ARTWORK_ACCEPT_ATTRIBUTE } from '@/lib/uploads/artwork-formats';
import { artworkCopy } from '@/lib/artwork/artwork-copy';
import {
  accessoryCartFileFields,
  accessoryUploadBlocker,
  accessoryUploadCopy,
  uploadAccessoryArtwork,
  type AccessoryUploadState,
} from '@/lib/uploads/accessory-artwork-upload';


const debugLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production') console.log(...args);
};

/**
 * De server kiest de productiemethode zelf zodra `expandPrintingMethods` aan
 * staat, en repareert een onmogelijke oplage. Die twee mogen dus afwijken van
 * de aanvraag; elke andere prijsbepalende optie niet.
 */
const ECHOED_OPTION_DRIFT_IGNORED_KEYS = new Set(['printingmethod', 'copies']);

function hasVirtualSizeSelection(options: Record<string, any> | null | undefined) {
  const size = options?.size;
  if (typeof size !== 'string') return false;
  return size === 'custom' || size.startsWith('custom:') || size.startsWith('rotate:');
}

/**
 * Een prijs hoort bij de samenstelling die is opgevraagd. Antwoordt de server
 * met een ANDERE prijsbepalende optie dan gevraagd, dan is de prijs van iets
 * anders en moet de cel als niet beschikbaar gelden. `isPriceReady` toetst
 * alleen of er een prijs is, nooit of hij bij de keuze hoort.
 */
function findEchoedOptionDrift(
  requested: Record<string, any> | null | undefined,
  echoed: Record<string, any> | null | undefined,
) {
  if (!requested || !echoed) return null;

  // "Geen afwerking" heeft meerdere schrijfwijzen (geen, none, no_laminate, ...)
  // die de servernormalisatie onderling omzet. Zo'n omzetting is geen drift.
  const isNeutralValue = (value: unknown) => {
    const text = String(value ?? '').toLowerCase();
    if (text === '' || text === 'null' || text === '0' || text === 'false') return true;
    // group_none is de UI-groepering voor "geen"; de SKU-normalisatie zet hem om.
    if (text === 'group_none') return true;
    return isNoPosterFinishValue(text);
  };

  const virtualSize = hasVirtualSizeSelection(requested);

  for (const key of Object.keys(requested)) {
    if (ECHOED_OPTION_DRIFT_IGNORED_KEYS.has(key) || !PRICE_CRITICAL_OPTION_IDS.has(key)) continue;
    // `custom`, `custom:20:30` en `rotate:30_x_20_cm` zijn storefront-notaties
    // die de server hoort te vertalen naar een echte maat plus breedte en
    // hoogte; die vertaling is geen drift. Zelfde sleutels als de init-loop
    // hieronder gebruikt om een bestaande vrije maat uit `initialConfiguration`
    // te herstellen (zie `@/lib/product-options/virtual-size-keys`).
    if (virtualSize && VIRTUAL_SIZE_DERIVED_KEYS.has(key)) continue;

    const requestedValue = requested[key];
    if (requestedValue === undefined || requestedValue === null) continue;
    if (isNeutralValue(requestedValue)) continue;

    const echoedValue = echoed[key];
    // Een weggelaten sleutel is bestaand gedrag; alleen een andere waarde is drift.
    if (echoedValue === undefined) continue;
    // Een vormpreset (`custom_shape_simple|shape:round`) wordt als
    // `custom_shape_simple` geprijsd; dat is dezelfde samenstelling.
    if (sameProviderOptionValue(key, requestedValue, echoedValue)) continue;

    return { key, requested: requestedValue, echoed: echoedValue };
  }

  return null;
}

type DeliveryPriceCell = {
  id: string;
  label: string;
  description: string;
  businessDays: string;
  estimatedDate: string;
  subtotalExVat: number;
  totalInclVat: number;
  unitInclVat: number;
  quote?: any;
  productionOptions?: Record<string, string | number>;
  deliveryRequest?: MatrixDeliveryRequest;
  available: boolean;
};

type DeliveryPriceColumn = {
  id: string;
  label: string;
  businessDays: string;
  estimatedDate: string;
};

type DeliveryQuantityRow = {
  quantity: number;
  cells: Record<string, DeliveryPriceCell>;
  isCustom?: boolean;
};

type ProductOptionValue = NonNullable<ProductOption['values']>[number];

interface OptionVisual {
  samplePath?: string;
  optionKey: string;
  optionValue: string;
  productId?: string | null;
  svgContent?: string | null;
  imageUrl?: string | null;
  overviewImageUrl?: string | null;
  label?: string;
  description?: string | null;
}

// Fallback-copy voor gebruik buiten NextIntlClientProvider (admin-orderdialoog).
const PRODUCT_COPY_FALLBACK: Record<string, Record<string, string>> = {
  nl: { addToCart: 'Toevoegen aan winkelwagen', calculatingPrice: 'Prijs berekenen...', configuration: 'Product Configuratie', inclShippingVAT: 'Incl. BTW, excl. verzending', inclDeliveryVAT: 'Incl. BTW en standaardbezorging', perItem: 'per stuk', pricePerUnit: 'Prijs per stuk', quantity: 'Aantal', recommendedExtras: "Aanbevolen Extra's", stepConfigure: 'Configureer', stepOrder: 'Bestellen', total: 'Totaal' },
  de: { addToCart: 'In den Warenkorb', calculatingPrice: 'Preis wird berechnet...', configuration: 'Produktkonfiguration', inclShippingVAT: 'Inkl. MwSt., zzgl. Versand', inclDeliveryVAT: 'Inkl. MwSt. und Standardversand', perItem: 'pro Stück', pricePerUnit: 'Preis pro Stück', quantity: 'Anzahl', recommendedExtras: 'Empfohlene Extras', stepConfigure: 'Konfigurieren', stepOrder: 'Bestellen', total: 'Gesamt' },
  en: { addToCart: 'Add to cart', calculatingPrice: 'Calculating price...', configuration: 'Product Configuration', inclShippingVAT: 'Incl. VAT, excl. shipping', inclDeliveryVAT: 'Incl. VAT and standard delivery', perItem: 'per item', pricePerUnit: 'Price per unit', quantity: 'Quantity', recommendedExtras: 'Recommended Extras', stepConfigure: 'Configure', stepOrder: 'Order', total: 'Total' },
  fr: { addToCart: 'Ajouter au panier', calculatingPrice: 'Calcul du prix...', configuration: 'Configuration du produit', inclShippingVAT: 'TVA incluse, hors livraison', inclDeliveryVAT: 'TVA et livraison standard incluses', perItem: 'par pièce', pricePerUnit: 'Prix par pièce', quantity: 'Quantité', recommendedExtras: 'Extras recommandés', stepConfigure: 'Configurer', stepOrder: 'Commander', total: 'Total' },
};

export type ProductConfiguratorSaveData = Pick<CartItem,
  | 'configuration' | 'unitPrice' | 'totalPrice' | 'unitPriceExVat' | 'totalPriceExVat'
  | 'pricingQuote' | 'shippingIncluded' | 'supplierShippingExVat' | 'optionSupplierRoute' | 'printComQuoteMeta'
  | 'uploadedFile' | 'uploadedFiles' | 'editorSessionCode'
>;

interface ProductConfiguratorProps {
  /** Physical constraints for a matching insert; not editable by the customer. */
  lockedConfiguration?: Record<string, string | number>;
  draftScope?: string;
  productId: string;
  productName: string;
  productSlug: string;
  providerProductId?: string; // Print.com SKU (e.g. "self-adhesive-posters")
  providerType?: string; // PRINTCOM, GELATO, PRINTFUL, CUSTOM
  category: string;
  basePrice: number;
  fromUnitPriceInclVat?: number; // Per-stuk vanafprijs zoals op de catalogus-kaart
  options: ProductOption[];
  specifications?: any;
  isCanvasProduct?: boolean;
  isPrintProduct?: boolean;
  productThumbnail?: string; // Product thumbnail for cart
  initialConfiguration?: Record<string, string | number>; // Pre-fill configuration
  requestedSampleSelection?: { optionKey: string; optionValue: string };
  initialQuantity?: number;
  initialUploadedFiles?: CartItem['uploadedFiles'];
  initialFileUrl?: string; // Pre-loaded file URL
  initialDesignData?: any; // Pre-loaded canvas design data
  /**
   * Instellingen van de vinyl-ontwerptool, of undefined bij een product zonder.
   *
   * Bij een snijproduct komt de maat uit het ontwerp, dus `size_width` en
   * `size_height` zijn hier niet met de hand in te vullen en `customSizeReady`
   * (regel 5612) houdt de prijsaanvraag tegen. Dat is juist: een halve maat mag
   * nooit geprijsd worden. Daarom laadt de ontwerptool hier IN de configurator,
   * en niet op een aparte pagina: het ontwerp bepaalt de prijs mee, dus de klant
   * hoort tijdens het ontwerpen de prijs te zien bewegen.
   */
  vinylDesigner?: {
    settings: VinylDesignerSettings;
    productSlug: string;
    materials: Array<{ value: string; label: string }>;
    /** `default` van de materiaaloptie: startkleur van de designer (Ruling P11). */
    defaultMaterial?: string;
    /** Waarden van de copies-optie (aantallen in de designer). */
    allowedCopies?: number[];
    /** `getVinylContourLimit`: grens van het product, `null` = geen (Ruling P2). */
    contourLimit?: number | null;
    /** Heeft het product de contouroptie (`settings.contourOptionId`) echt? */
    hasContourOption?: boolean;
    /** Heeft het product een copies-optie? Vast per product (niet uit afgeleide state: een wissel zou de designer opnieuw starten). */
    hasCopiesOption?: boolean;
    /**
     * Er staat al een geregistreerd ontwerp klaar (`?generatedUploadId=` vanaf de
     * losse ontwerppagina). Dan geen designer: die zou maat en kleur van het
     * klaarstaande bestand overschrijven. De configurator bestelt dan zelf.
     */
    registeredDesign?: boolean;
    /** Glaskant van dat geregistreerde ontwerp (`Upload.providers.vinyl.design.glassSide`), voor de regel (P9). */
    registeredGlassSide?: VinylGlassSide | null;
  };
  locale?: string; // Override locale (for admin panel without intl context)
  designTemplateCount?: number;
  initialDesignTemplateSlug?: string;
  onSave?: (data: ProductConfiguratorSaveData) => void | Promise<void>;
  readOnly?: boolean; // Read-only mode for viewing (admin panel)
  aiImageToolsEnabled?: boolean; // AI-fotoknoppen (Replicate) tonen of verbergen
  /** Werkbestand/sjabloon dat de productpagina aanbiedt; ontbreekt buiten de winkel. */
  templateDownload?: {
    variants?: Pick<ProductTemplateDownload, 'id' | 'label' | 'labels' | 'fileName'>[];
  };
}

/** Zo lang wacht de bestelknop van de designer op bestand en prijs in de configurator. */
const DESIGNER_ADD_TO_CART_TIMEOUT_MS = 20_000;

const STICKER_CONTOUR_OPTION_IDS = new Set([
  'custom_shape',
]);

const BANNER_SIDE_FINISH_OPTION_IDS = new Set(['finish_left', 'finish_right', 'finish_top', 'finish_bottom']);

function isBannerSideFinishOption(optionId: string) {
  return BANNER_SIDE_FINISH_OPTION_IDS.has(optionId);
}

function getConsumerOptionLabel(option: ProductOption, configuration: Record<string, string | number>, locale: string) {
  const allSidesEqual = configuration.all_sides_equal_finish !== 'no';
  if (option.id === 'finish_left' && allSidesEqual) {
    return translatePrintLabel('Afwerking rondom', locale);
  }
  if (isStampInkColorOption(option)) {
    return translatePrintLabel('Inktkleur', locale);
  }
  return translatePrintLabel(option.label, locale);
}

function uniqueOptionValues<T extends { value: string | number; id?: string; label?: string }>(values: T[] = []): T[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const rawValue = String(value.value).trim().toLowerCase();
    const label = String(value.label || '').trim().toLowerCase();
    const isEmptyChoice =
      ['geen', 'none', 'group_none', 'null', 'no_finish'].includes(rawValue) &&
      !['nee', 'no'].includes(label);
    const key = isEmptyChoice ? '__empty_choice__' : String(value.value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isStampInkColorOption(option: Pick<ProductOption, 'id' | 'name' | 'label' | 'type'> & { values?: Array<{ value?: string | number; id?: string | number; label?: string }> }) {
  const key = `${option.id || ''} ${option.name || ''} ${option.label || ''}`.toLowerCase();
  return /\b(colou?r|kleur|farbe|couleur)\b/.test(key) && Boolean(option.values?.some((value) => /_ink_pad_stamp$/i.test(String(value.value || value.id || value.label || ''))));
}

/**
 * Staal voor een kleurwaarde als CSS `background` (kleur of verloop). De
 * resolutie (expliciete hex, PMS/RAL, kleurnamen) staat in
 * src/lib/product-options/color-swatch.ts.
 */
function colorSwatchForValue(value: any) {
  return resolveColorSwatch(value).background;
}
/**
 * Een vast rooster van vier kolommen liet lege cellen achter bij opties met
 * twee of drie waarden ("Frame dikte" beloofde vier plaatsen voor twee tegels).
 * Het aantal kolommen loopt daarom nooit voorbij het aantal tegels.
 */
function optionGridColumnsClass(count: number) {
  // De max-breedte houdt een tegel bij twee of drie waarden op ongeveer dezelfde
  // maat als in een vol raster; zonder die grens werd een keuze uit twee op
  // 1440px twee halve schermbreedtes.
  if (count <= 2) return 'grid-cols-2 lg:max-w-xl';
  if (count === 3) return 'grid-cols-2 sm:grid-cols-3 lg:max-w-3xl';
  return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
}

function visualMapKey(optionKey: string, optionValue: string | number) {
  return `${optionKey}:${String(optionValue)}`;
}

type MaterialValue = NonNullable<ProductOption['values']>[number];

function materialFamilyName(label: string) {
  return label
    .replace(/\s*\((meest gekozen|most chosen|am beliebtesten|le plus choisi)\)\s*/gi, ' ')
    .replace(/[®™]/g, '')
    .replace(/^\s*\d+(?:[,.]\d+)?\s*(?:g\/m(?:\u00b2|\u00c2\u00b2|2)?|gr(?:\s*\/?\s*m(?:\u00b2|\u00c2\u00b2|2)?)?)\s*/i, '')
    // Diktes horen bij de diktekiezer, niet bij de familienaam ("Polypropyleen 3,5 mm" -> "Polypropyleen").
    .replace(/\s*\b\d+(?:[,.]\d+)?\s*mm\b\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim() || label;
}

function materialWeightLabel(label: string, family: string) {
  const cleaned = label
    .replace(/\s*\((meest gekozen|most chosen|am beliebtesten|le plus choisi)\)\s*/gi, ' ')
    .replace(/[®™]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const escapedFamily = family
    .replace(/[®™]/g, '')
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const prefix = cleaned.replace(new RegExp(escapedFamily, 'i'), '').trim();
  return prefix || cleaned;
}

function materialThicknessLabel(count: number, locale: string) {
  const lang = locale.split('-')[0];
  if (lang === 'de') return `${count} Stärken`;
  if (lang === 'en') return `${count} thicknesses`;
  if (lang === 'fr') return `${count} epaisseurs`;
  return `${count} diktes`;
}

function isGeneratedLetterSvg(svg?: string | null) {
  const content = String(svg || '');
  return content.includes('viewBox="0 0 160 100"') && /<text\b/i.test(content);
}

function optionHasVisual(value: { image?: string; visualSvgContent?: string }) {
  return Boolean(value.image || (value.visualSvgContent && !isGeneratedLetterSvg(value.visualSvgContent)));
}

// Oude proefbestanden zijn verwijderd omdat ze geen correcte materiaalweergave
// waren. Sommige gesynchroniseerde optiegegevens bevatten nog zo'n pad; laat
// die nooit een kapotte <img> genereren maar val terug op de materiaalvisual.
function usableOptionImage(image?: string) {
  if (!image) return undefined;
  const removedMaterialImages = [
    'coated-silk-paper.png',
    'uncoated-natural-paper.png',
    'polyester-textile.png',
    'recycled-kraft-paper.png',
    'self-adhesive-vinyl.png',
  ];
  return removedMaterialImages.some((fileName) => image.includes(fileName)) ? undefined : image;
}

const MATERIAL_VISUAL_FRAME_RATIO = 16 / 9;

/**
 * Landschapsfoto's die ongeveer dezelfde verhouding als de tegel hebben mogen
 * het vlak vullen. Vierkante en staande bronbeelden blijven volledig zichtbaar;
 * een zachte achtergrondlaag vult dan de overblijvende ruimte.
 */
function shouldCoverMaterialVisual(width: number, height: number) {
  if (!width || !height) return true;
  const ratioDifference = Math.abs(width / height - MATERIAL_VISUAL_FRAME_RATIO);
  return ratioDifference <= 0.35;
}

function MaterialOptionImage({ src }: { src: string }) {
  const [fit, setFit] = useState<'cover' | 'contain'>('cover');

  const handleLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    setFit(shouldCoverMaterialVisual(image.naturalWidth, image.naturalHeight) ? 'cover' : 'contain');
  }, []);

  return (
    <>
      {fit === 'contain' && (
        <Image
          src={src}
          alt=""
          fill
          aria-hidden="true"
          sizes="(max-width: 640px) 50vw, 18rem"
          className="scale-110 object-cover opacity-30 blur-xl"
        />
      )}
      <Image
        src={src}
        alt=""
        fill
        sizes="(max-width: 640px) 50vw, 18rem"
        onLoad={handleLoad}
        className={fit === 'cover' ? 'object-cover' : 'object-contain'}
      />
    </>
  );
}

function configurationEquals(a: Record<string, string | number>, b: Record<string, string | number>) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => String(a[key] ?? '') === String(b[key] ?? ''));
}

function isHiddenRadioBubbleClick(event: React.MouseEvent<HTMLElement>) {
  const target = event.target as HTMLElement | null;
  return target?.tagName === 'INPUT';
}

function MaterialFamilySelector({
  values,
  selectedValue,
  onChange,
  onInfo,
  locale,
  isValueUnavailable = () => false,
  unavailableLabel,
}: {
  values: MaterialValue[];
  selectedValue?: string | number;
  onChange: (value: string | number) => void;
  onInfo?: (info: { title: string; description: string; image?: string; overviewImage?: string; visualSvgContent?: string }) => void;
  locale: string;
  /** Tijdelijk niet leverbaar: grijs tonen en niet kiesbaar maken. */
  isValueUnavailable?: (value: string | number) => boolean;
  unavailableLabel?: string;
}) {
  const groups = values.reduce((acc, value) => {
    const translatedLabel = translatePrintLabel(value.label, locale);
    const materialInfo = getPrintMaterialInfo({ value: value.value, label: translatedLabel, id: value.id }, locale);
    // Ook Print.com-namen ("3 mm Forex") door de familie-normalisatie halen,
    // anders wordt elke dikte een losse tegel.
    const family = materialFamilyName(locale.split('-')[0] === 'nl' ? (materialInfo?.name || translatedLabel) : translatedLabel);
    const existing = acc.find((group: any) => group.family === family);
    if (existing) {
      existing.values.push(value);
      if (!existing.description) existing.description = value.description || materialInfo?.description;
      if (!existing.image && value.image) existing.image = value.image;
      if (!existing.overviewImage && value.overviewImage) existing.overviewImage = value.overviewImage;
      if (!existing.visualSvgContent && value.visualSvgContent) existing.visualSvgContent = value.visualSvgContent;
      if (value.eco) existing.eco = true;
    } else {
      acc.push({
        family,
        values: [value],
        image: value.image,
        overviewImage: value.overviewImage,
        visualSvgContent: value.visualSvgContent,
        description: value.description || materialInfo?.description,
        eco: Boolean(value.eco),
      });
    }
    return acc;
  }, [] as Array<{ family: string; values: MaterialValue[]; image?: string; overviewImage?: string; visualSvgContent?: string; description?: string | null; eco?: boolean }>);

  // Within one tile (for example Kanaalplaat 3,5 and 8 mm) the chosen
  // thickness has its own text; the tile's first value is only the fallback,
  // the same way the image already follows the chosen thickness.
  const valueDescription = (value?: MaterialValue) => {
    if (!value) return undefined;
    if (value.description) return value.description;
    const translatedLabel = translatePrintLabel(value.label, locale);
    return getPrintMaterialInfo({ value: value.value, label: translatedLabel, id: value.id }, locale)?.description;
  };

  const selected = values.find((value) => String(value.value) === String(selectedValue)) || values[0];
  const selectedFamily = groups.find((group: any) => group.values.some((value: MaterialValue) => String(value.value) === String(selected?.value))) || groups[0];
  // Guard: if selectedValue isn't in the current family's values,
  // fall back to the family's first value to prevent Radix Select
  // from triggering onChange during render (causes infinite re-renders).
  const valueInFamily = selectedFamily?.values?.some(
    (v: MaterialValue) => String(v.value) === String(selected?.value)
  );
  const safeValue = valueInFamily ? selected?.value : selectedFamily?.values?.[0]?.value;

  if (!selected || !selectedFamily) return null;
  const selectedDescription = (valueInFamily && valueDescription(selected)) || selectedFamily.description;

  return (
    <div className="space-y-3">
      <div className="grid items-start gap-2" style={{ gridTemplateColumns: groups.length > 1 ? 'repeat(auto-fit, minmax(min(100%, 16rem), 1fr))' : 'minmax(0, 1fr)' }}>
        {groups.map((group) => {
          const isSelected = group.family === selectedFamily.family;
          const groupUnavailable = group.values.every((value) => isValueUnavailable(value.value));
          const firstValue = group.values.find((value) => !isValueUnavailable(value.value))?.value;
          const selectedGroupValue = isSelected
            ? group.values.find((value) => String(value.value) === String(selected?.value))
            : undefined;
          const groupImage = selectedGroupValue ? selectedGroupValue.image : group.image;
          const groupOverviewImage = selectedGroupValue ? selectedGroupValue.overviewImage : group.overviewImage;
          const groupVisualSvgContent = selectedGroupValue ? selectedGroupValue.visualSvgContent : group.visualSvgContent;
          const groupDescription = (selectedGroupValue && valueDescription(selectedGroupValue)) || group.description;
          const hasVisual = Boolean(groupImage || groupVisualSvgContent);
          return (
            <button
              key={group.family}
              type="button"
              aria-pressed={isSelected}
              aria-disabled={groupUnavailable && !isSelected}
              onClick={() => {
                if (!isSelected && firstValue !== undefined) onChange(firstValue);
              }}
              className={`relative flex min-h-20 min-w-0 items-center overflow-hidden rounded-lg border-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${
                groupUnavailable && !isSelected
                  ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-40'
                  : isSelected
                  ? 'border-blue-600 bg-blue-50 shadow-sm ring-1 ring-blue-600'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              {hasVisual && (
                <span className="relative ml-2 block h-14 w-14 shrink-0 overflow-hidden rounded-md bg-gray-100 sm:h-16 sm:w-16">
                  {groupVisualSvgContent ? (
                    <span className="block h-full w-full [&_svg]:h-full [&_svg]:w-full" dangerouslySetInnerHTML={{ __html: groupVisualSvgContent }} />
                  ) : (
                    <MaterialOptionImage key={groupImage} src={groupImage!} />
                  )}
                  {group.eco && (
                    <span className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-white shadow ring-2 ring-white">
                      <Leaf className="h-3.5 w-3.5" />
                    </span>
                  )}
                </span>
              )}
              <span className="flex min-h-14 w-full min-w-0 grow items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1">
                  <span className={`block break-words text-sm font-semibold leading-tight ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                    {group.family}
                  </span>
                  {group.values.length > 1 && (
                    <span className="block text-xs text-gray-500">{materialThicknessLabel(group.values.length, locale)}</span>
                  )}
                  {groupUnavailable && unavailableLabel && (
                    <span className="block text-xs font-semibold text-gray-500">{unavailableLabel}</span>
                  )}
                </span>
                {isSelected && (
                  <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-blue-600 shadow-sm">
                    <Check className="h-3.5 w-3.5 stroke-[3] text-white" />
                  </span>
                )}
                {groupDescription && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onInfo?.({
                        title: group.family,
                        description: groupDescription || '',
                        ...(groupImage ? { image: groupImage } : {}),
                        ...(groupOverviewImage ? { overviewImage: groupOverviewImage } : {}),
                        ...(groupVisualSvgContent ? { visualSvgContent: groupVisualSvgContent } : {}),
                      });
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      onInfo?.({
                        title: group.family,
                        description: groupDescription || '',
                        ...(groupImage ? { image: groupImage } : {}),
                        ...(groupOverviewImage ? { overviewImage: groupOverviewImage } : {}),
                        ...(groupVisualSvgContent ? { visualSvgContent: groupVisualSvgContent } : {}),
                      });
                    }}
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-gray-200 hover:bg-blue-50 hover:text-blue-700"
                    aria-label={`${group.family} — ${locale === 'de' ? 'Informationen' : locale === 'en' ? 'Information' : locale === 'fr' ? 'Informations' : 'informatie'}`}
                  >
                    <Info className="h-4 w-4" />
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {selectedDescription && (
        <p className="max-w-3xl rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm leading-relaxed text-blue-950">
          {selectedDescription}
        </p>
      )}

      {selectedFamily.values.length > 1 && (
        <Select value={String(safeValue)} onValueChange={onChange}>
          {/* !h-12: SelectTrigger zet zijn hoogte via een data-attribuutselector,
              die een gewone h-12 overstemt - de knop bleef daardoor 36px hoog. */}
          <SelectTrigger className="!h-12 w-full sm:max-w-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {selectedFamily.values.map((value) => {
              const label = translatePrintLabel(value.label, locale);
              return (
                <SelectItem key={String(value.value)} value={String(value.value)} disabled={isValueUnavailable(value.value)}>
                  {materialWeightLabel(label, selectedFamily.family)}
                  {isValueUnavailable(value.value) && unavailableLabel && (
                    <span className="ml-2 text-xs font-semibold text-gray-500">{unavailableLabel}</span>
                  )}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function formatCm(value: number) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1))).replace('.', ',');
}

function isSizeLikeOption(option?: Pick<ProductOption, 'id' | 'name' | 'label'> | null) {
  const text = `${option?.id || ''} ${option?.name || ''} ${option?.label || ''}`.toLowerCase();
  return (
    text.includes('size') ||
    text.includes('maat') ||
    text.includes('formaat') ||
    text.includes('breedte') ||
    text.includes('height') ||
    text.includes('width')
  );
}

function formatOptionDisplayLabel(option: Pick<ProductOption, 'id' | 'name' | 'label'> | undefined, label: unknown, locale: string) {
  const rawLabel = String(label || '');
  const normalizedLabel = isSizeLikeOption(option) ? formatMetricSizeLabel(rawLabel) : rawLabel;
  return translatePrintLabel(normalizedLabel, locale);
}

const STANDARD_CUSTOM_SIZE_PRESETS_CM = [
  { width: 20, height: 20 },
  { width: 30, height: 30 },
  { width: 40, height: 40 },
  { width: 50, height: 50 },
  { width: 60, height: 60 },
  { width: 80, height: 80 },
  { width: 100, height: 100 },
  { width: 30, height: 20 },
  { width: 40, height: 30 },
  { width: 50, height: 30 },
  { width: 60, height: 40 },
  { width: 80, height: 60 },
  { width: 100, height: 70 },
  { width: 120, height: 80 },
  { width: 150, height: 100 },
  { width: 200, height: 100 },
  { width: 300, height: 200 },
  { width: 20, height: 30 },
  { width: 30, height: 40 },
  { width: 30, height: 50 },
  { width: 40, height: 60 },
  { width: 60, height: 80 },
  { width: 70, height: 100 },
  { width: 80, height: 120 },
  { width: 100, height: 150 },
  { width: 100, height: 200 },
  { width: 200, height: 300 },
];

function formatCustomSizeValueNumber(value: number) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));
}

function customSizePresetKey(width: number, height: number) {
  return `${Math.round(width * 10)}x${Math.round(height * 10)}`;
}

function isWithinCustomSizeLimits(width: number, height: number, limits: ReturnType<typeof normalizeCustomSizeLimits>) {
  return (
    (!limits.minWidth || width >= limits.minWidth) &&
    (!limits.maxWidth || width <= limits.maxWidth) &&
    (!limits.minHeight || height >= limits.minHeight) &&
    (!limits.maxHeight || height <= limits.maxHeight)
  );
}

function makeVirtualCustomSizeValue(width: number, height: number, customOption: ProductOptionValue): ProductOptionValue {
  const widthValue = formatCustomSizeValueNumber(width);
  const heightValue = formatCustomSizeValueNumber(height);
  const value = `custom:${widthValue}:${heightValue}`;
  const orientation = width > height ? 'landscape' : height > width ? 'portrait' : 'square';
  const label = `${formatCm(width)} x ${formatCm(height)} cm`;

  return {
    ...customOption,
    id: value,
    label,
    value,
    width: width * 10,
    height: height * 10,
    priceModifier: 0,
    areaPriceModifier: 0,
    customSizes: undefined,
    metadata: {
      ...(customOption.metadata || {}),
      virtualCustomSize: true,
      orientation,
      width: width * 10,
      height: height * 10,
      editorWidthMm: width * 10,
      editorHeightMm: height * 10,
    },
  };
}

function withVirtualCustomSizePresets(
  option: ProductOption,
  values: ProductOptionValue[],
  providerType?: string,
  context?: VirtualPresetProductContext,
  selectedLimits?: ReturnType<typeof normalizeCustomSizeLimits>,
) {
  if (!shouldOfferVirtualCustomSizePresets(option, providerType, context)) return values;

  const customOption =
    values.find((value) => Boolean(value.customSizes)) ||
    values.find((value) => String(value.value) === 'custom');
  if (!customOption?.customSizes) return values;

  const limits = selectedLimits || normalizeCustomSizeLimits(customOption.customSizes);
  const existingDimensions = new Set<string>();
  const existingValues = new Set(values.map((value) => String(value.value)));

  values.forEach((value) => {
    const dimensions = parseSizeDimensionsCm(value);
    if (dimensions) existingDimensions.add(customSizePresetKey(dimensions.width, dimensions.height));
  });

  const virtualValues = STANDARD_CUSTOM_SIZE_PRESETS_CM
    .filter(({ width, height }) => isWithinCustomSizeLimits(width, height, limits))
    .filter(({ width, height }) => !existingDimensions.has(customSizePresetKey(width, height)))
    .map(({ width, height }) => makeVirtualCustomSizeValue(width, height, customOption))
    .filter((value) => !existingValues.has(String(value.value)));

  if (virtualValues.length === 0) return values;

  const nonCustomValues = values.filter((value) => String(value.value) !== 'custom' && !value.customSizes);
  const customValues = values.filter((value) => String(value.value) === 'custom' || value.customSizes);
  return [...nonCustomValues, ...virtualValues, ...customValues];
}

function withStandardCustomShapePresets(option: ProductOption, values: ProductOptionValue[]) {
  if (option.id !== 'custom_shape') return values;

  const simpleContour = values.find((value) => String(value.value) === 'custom_shape_simple');
  if (!simpleContour) return values;

  const existingValues = new Set(values.map((value) => String(value.value)));
  const presets = STANDARD_CUSTOM_SHAPE_PRESETS
    .filter((preset) => !existingValues.has(preset.value))
    .map((preset) => ({
      ...simpleContour,
      id: preset.value,
      label: preset.label,
      value: preset.value,
      description: preset.description,
      metadata: {
        ...(simpleContour.metadata || {}),
        standardShapePreset: true,
        providerValue: 'custom_shape_simple',
        shape: preset.shape,
      },
    }));

  return presets.length > 0 ? [...values, ...presets] : values;
}

function getSizeSelectorValues(option: ProductOption, values: ProductOptionValue[], productSlug: string, providerType?: string, productCategory?: string, selectedLimits?: ReturnType<typeof normalizeCustomSizeLimits>) {
  const catalogValues = withRotatedConsumerSizes(uniqueOptionValues(values), productSlug);
  return withVirtualCustomSizePresets(option, catalogValues, providerType, { productSlug, productCategory }, selectedLimits);
}

function getOrderAdjustedModifier(value: any) {
  const directModifier = Number(value?.metadata?.orderPriceModifier ?? value?.orderPriceModifier ?? 0);
  return Number.isFinite(directModifier) ? directModifier : 0;
}

function parseCustomDimensionInput(value: string) {
  const normalized = value.replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isAvailableSizeSelection(
  option: ProductOption,
  currentValue: string | number,
  configuration: Record<string, string | number>
) {
  if (option.id !== 'size') {
    return option.values?.some((value) => value.value === currentValue) || false;
  }

  const raw = String(currentValue);
  const customValue = option.values?.find((value) => value.value === 'custom' || value.customSizes);
  if (
    raw.startsWith('custom:') &&
    customValue &&
    matchesShowIf(customValue.showIf, configuration)
  ) {
    return true;
  }

  if (raw.startsWith('rotate:')) {
    const [, baseValue, widthCm, heightCm] = raw.split(':');
    const baseOptionValue = option.values?.find((value) => String(value.value) === baseValue);
    const width = Number(widthCm);
    const height = Number(heightCm);
    return Boolean(
      baseOptionValue &&
      matchesShowIf(baseOptionValue.showIf, configuration) &&
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      width > 0 &&
      height > 0
    );
  }

  if (
    (raw === 'custom' || (customValue && String(customValue.value) === raw)) &&
    customValue &&
    matchesShowIf(customValue.showIf, configuration) &&
    Number(configuration.size_width) > 0 &&
    Number(configuration.size_height) > 0
  ) {
    return true;
  }

  const selectedValue = option.values?.find((value) => value.value === currentValue);
  return Boolean(selectedValue && matchesShowIf(selectedValue.showIf, configuration));
}

function getSizeAreaM2FromConfiguration(configuration: Record<string, string | number>, options: ProductOption[]) {
  const directWidthCm = Number(configuration.width_cm);
  const directHeightCm = Number(configuration.height_cm);
  if (Number.isFinite(directWidthCm) && Number.isFinite(directHeightCm) && directWidthCm > 0 && directHeightCm > 0) {
    return (directWidthCm / 100) * (directHeightCm / 100);
  }

  if (typeof configuration.size === 'string' && configuration.size.startsWith('custom:')) {
    const [, width, height] = configuration.size.split(':');
    const widthCm = Number(width);
    const heightCm = Number(height);
    if (Number.isFinite(widthCm) && Number.isFinite(heightCm) && widthCm > 0 && heightCm > 0) {
      return (widthCm / 100) * (heightCm / 100);
    }
  }

  if (configuration.size === 'custom' && configuration.size_width && configuration.size_height) {
    const width = Number(configuration.size_width);
    const height = Number(configuration.size_height);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return (width / 100) * (height / 100);
    }
  }

  const sizeOption = options.find((option) => option.id === 'size');
  const selectedSize = sizeOption?.values?.find((value) => String(value.value) === String(configuration.size));
  const explicitAreaM2 = Number(selectedSize?.metadata?.printAreaM2 || selectedSize?.metadata?.areaM2);
  if (Number.isFinite(explicitAreaM2) && explicitAreaM2 > 0) return explicitAreaM2;
  const dimensions = selectedSize ? parseSizeDimensionsCm(selectedSize) : null;
  if (!dimensions) return null;

  return (dimensions.width / 100) * (dimensions.height / 100);
}

function getAreaAdjustedModifier(value: any, configuration: Record<string, string | number>, options: ProductOption[]) {
  const fixedModifier = Number(value?.priceModifier || 0);
  const areaModifier = Number(value?.areaPriceModifier || 0);
  const areaModifierPerM2 = Number(value?.metadata?.areaPriceModifierPerM2 || 0);
  const perimeterModifierPerM = Number(value?.metadata?.perimeterPriceModifierPerM || 0);
  const widthPriceModifiers = value?.metadata?.widthPriceModifiers;
  const widthOptionId = value?.metadata?.widthOptionId || 'width_cm';
  const widthModifier = widthPriceModifiers && typeof widthPriceModifiers === 'object'
    ? Number(widthPriceModifiers[String(configuration[widthOptionId])] || 0)
    : 0;

  const referenceAreaM2 = Number(value?.metadata?.referenceAreaM2 || 1);
  const widthCm = Number(configuration.width_cm);
  const heightCm = Number(configuration.height_cm);
  const customAreaM2 = Number.isFinite(widthCm) && Number.isFinite(heightCm) && widthCm > 0 && heightCm > 0
    ? (widthCm * heightCm) / 10000
    : null;
  const areaM2 = getSizeAreaM2FromConfiguration(configuration, options) || customAreaM2;
  const perimeterM = Number.isFinite(widthCm) && Number.isFinite(heightCm) && widthCm > 0 && heightCm > 0
    ? ((widthCm + heightCm) * 2) / 100
    : 0;

  if (!areaModifier && !areaModifierPerM2 && !perimeterModifierPerM) return fixedModifier + widthModifier;
  if (!areaM2 || !referenceAreaM2) return fixedModifier + widthModifier + areaModifier;

  return fixedModifier
    + widthModifier
    + areaModifier * (areaM2 / referenceAreaM2)
    + areaModifierPerM2 * areaM2
    + perimeterModifierPerM * perimeterM;
}

function getQuantityPolicyForConfiguration(
  productKey: string | undefined,
  specifications: any,
  configuration: Record<string, string | number>
) {
  // Een proefexemplaar bestaat alleen als 1 stuk; dat gaat voor alles.
  const singlePiecePolicy = singlePieceQuantityPolicy(configuration, specifications);
  if (singlePiecePolicy) return singlePiecePolicy;

  // Sluiten de excludes voor deze samenstelling een drukmethode uit (sleeves:
  // grote dozen alleen offset, vanaf 250), dan gelden alleen de aantallen van
  // de methodes die overblijven. Zie printing-method-quantity-policy.ts.
  // Handmatig ingestelde ladders per methode gaan voor.
  const excludeMethodPolicy = specifications?.quantityByPrintingMethod || specifications?.quantityRulesByPrintingMethod ? null : resolvePrintingMethodQuantityPolicy({
    rangesByMethod: specifications?.quantityRangesByPrintingMethod,
    excludes: specifications?.excludes,
    configuration,
    candidateQuantities: Array.isArray(specifications?.quantitySettings?.values) ? specifications.quantitySettings.values : [],
  });
  if (excludeMethodPolicy) return excludeMethodPolicy;

  if (specifications?.quantitySettings?.source === 'printcom-all-printing-methods') {
    return {
      minimum: Number(specifications.quantitySettings.minimum || 1),
      maximum: Number(specifications.quantitySettings.maximum || 100000),
      values: Array.isArray(specifications.quantitySettings.values)
        ? specifications.quantitySettings.values
        : [],
      exact: false,
    };
  }

  const policies = specifications?.quantityByPrintingMethod || specifications?.quantityRulesByPrintingMethod;
  const printingMethod = String(configuration.printingmethod || configuration.printing_method || configuration.printmethod || '');
  const methodPolicy = printingMethod && policies && typeof policies === 'object' ? policies[printingMethod] : null;

  if (methodPolicy) return methodPolicy;

  if (String(productKey || '').toLowerCase() === 'boxes-fefco-0201-digital') {
    if (printingMethod === 'offset') {
      return { minimum: 500, maximum: 2500, values: [500, 750, 1000, 1500, 2000, 2500] };
    }
    return { minimum: 50, maximum: 2000, values: [50, 100, 150, 250, 500] };
  }

  return null;
}

function getQuantityPolicyValues(policy: any) {
  return Array.isArray(policy?.values)
    ? policy.values.map((value: unknown) => Number(value)).filter((value: number) => Number.isFinite(value) && value > 0)
    : [];
}

function normalizeQuantityForPolicy(value: number, policy: any) {
  const fixedValues = getQuantityPolicyValues(policy);
  if ((policy?.type === 'fixed' || policy?.exact === true) && fixedValues.length > 0) {
    return fixedValues.includes(value) ? value : fixedValues[0];
  }
  const minimum = Number(policy?.minimum || policy?.min || 0);
  const maximum = Number(policy?.maximum || policy?.max || 0);
  let nextValue = value;
  if (Number.isFinite(minimum) && minimum > 0 && nextValue < minimum) nextValue = minimum;
  if (Number.isFinite(maximum) && maximum > 0 && nextValue > maximum) nextValue = maximum;

  const ranges = Array.isArray(policy?.ranges) ? policy.ranges : [];
  const matchingRange = ranges.find((range: any) => {
    const rangeMin = Number(range?.min ?? range?.minimum ?? 0);
    const rangeMax = Number(range?.max ?? range?.maximum ?? 0);
    return Number.isFinite(rangeMin) && Number.isFinite(rangeMax) && nextValue >= rangeMin && nextValue <= rangeMax;
  });
  if (matchingRange) {
    const rangeMin = Number(matchingRange.min ?? matchingRange.minimum ?? 0);
    const step = Number(matchingRange.step ?? matchingRange.steps ?? 0);
    if (Number.isFinite(rangeMin) && Number.isFinite(step) && step > 1) {
      nextValue = rangeMin + Math.round((nextValue - rangeMin) / step) * step;
      if (Number.isFinite(minimum) && minimum > 0 && nextValue < minimum) nextValue = minimum;
      if (Number.isFinite(maximum) && maximum > 0 && nextValue > maximum) nextValue = maximum;
    }
  }

  return nextValue;
}

// Dynamically derive valid option values from brezo_article showIf conditions.
// If a brezo_article value's showIf cannot be satisfied with the tested option value
// (given the rest of the current config), the value is unreachable and should be hidden.
// Only checks constraints for options that are currently set in config — unset options
// are skipped, so users see all possibilities until they start narrowing down.
function isValueReachableViaBrezo(
  optionId: string,
  value: string | number,
  config: Record<string, string | number>,
  allOptions: ProductOption[]
): boolean {
  const brezoOpt = allOptions.find(o => o.id === 'brezo_article' || o.name === 'brezo_article');
  if (!brezoOpt || !brezoOpt.values || brezoOpt.values.length === 0) return true;

  const hasShowIf = brezoOpt.values.some(v => v.showIf);
  if (!hasShowIf) return true; // no showIf data = skip filtering

  const testConfig = { ...config, [optionId]: value };
  // Een samenstelling met een waarde van een andere leverancier heeft geen
  // Print.com-artikelcode; die leverancier prijst en controleert zelf.
  if (configurationHasForeignSupplierValue(allOptions, testConfig)) return true;
  return brezoOpt.values.some(v => {
    if (!v.showIf) return true;
    const { optionId: siOptId, value: siVal, ...obj } = v.showIf;

    // Handle { optionId: 'x', value: 'y' } format
    if (siOptId && siVal !== undefined) {
      const selected = testConfig[siOptId];
      if (selected === undefined) return true; // constraining option not yet set
      return Array.isArray(siVal) ? siVal.includes(String(selected)) : selected === siVal;
    }

    // Handle { key1: 'val1', key2: 'val2' } multi-constraint format
    return Object.entries(obj).every(([key, expected]) => {
      const cur = testConfig[key];
      if (cur === undefined) return true; // not yet set, skip this constraint
      return Array.isArray(expected) ? expected.includes(String(cur)) : cur === expected;
    });
  });
}

function withRotatedConsumerSizes(values: any[], productSlug: string) {
  // Auto-rotation of sizes removed — caused too many issues with wrong dimensions
  return values;
}

const configuratorCopy = {
  nl: {
    locked: 'Standaard',
    checkingAvailability: 'Beschikbaarheid controleren...',
    selectionUnavailable: 'tijdelijk niet leverbaar in deze samenstelling',
    resetOptions: 'Standaard herstellen',
    stepUpload: 'Uploaden',
    choose: 'Kies',
    selectQuantity: 'Selecteer aantal',
    quantity: 'Aantal',
    pieces: 'stuks',
    enterQuantity: 'Voer aantal in',
    invalidCombination: 'Ongeldige combinatie',
    quantityMinimumText: 'Deze keuze kan vanaf {count} stuks. Kies {count} stuks of meer, of kies een andere bedrukking (bijvoorbeeld transfer) voor een kleiner aantal.',
    quantityMinimumAction: 'Kies {count} stuks',
    quantityMaximumAction: 'Kies {count}',
    quantityMaximumText: 'Deze keuze kan tot en met {count} stuk(s), bijvoorbeeld omdat het een proefexemplaar is. Kies {count}, of kies een andere uitvoering voor een groter aantal.',
    priceRateLimitedTitle: 'Prijs wordt zo opnieuw berekend',
    priceRateLimitedText: 'Je hebt in korte tijd veel opties vergeleken. We berekenen de prijs over ongeveer {seconds} seconden automatisch opnieuw.',
    priceRateLimitedLongText: 'Je hebt vandaag heel veel prijzen opgevraagd. We proberen het over een minuut automatisch opnieuw. Lukt het dan nog niet, vraag dan een offerte aan.',
    adjustSelection: 'Pas je selectie aan',
    proceedToUpload: 'Doorgaan naar upload',
    proceedToOverview: 'Doorgaan naar overzicht',
    addToCart: 'Toevoegen aan winkelwagen',
    uploadDesign: 'Upload je design',
    uploadIntro: 'Upload hier je drukklare bestand. We ondersteunen PDF, JPG en PNG.',
    selectAllOptions: 'Selecteer alle opties',
    priceOnRequest: 'Prijs op aanvraag: vraag hieronder een offerte aan voor jouw ontwerp.',
    designerRequiredText: 'Ontwerp hierboven eerst je tekst. Het formaat en het aantal snijlijnen komen daaruit, en die twee bepalen samen de prijs.',
    continueToOrder: 'Doorgaan naar bestellen',
    uploadAnotherFile: 'Ander bestand uploaden',
    backToConfiguration: 'Terug naar configuratie',
    backToUpload: 'Terug naar upload',
    saveChanges: 'Wijzigingen opslaan',
    uploadFirst: 'Upload eerst een bestand',
    saved: 'Wijzigingen opgeslagen!',
    saveError: 'Kon wijzigingen niet opslaan',
    addedToCart: 'Product toegevoegd aan winkelwagen!',
    priceUnavailable: 'Kon prijzen niet ophalen voor deze configuratie',
    priceRetry: 'Kon prijs niet ophalen. Probeer het opnieuw.',
    width: 'Breedte',
    height: 'Hoogte',
    min: 'Min',
    max: 'Max',
    eco: 'Duurzaam',
    priceAndDelivery: 'Prijs en levertijd',
    pricesLoading: 'Prijzen laden',
    optionsLoadingHint: 'Even geduld, bezig met laden...',
    customQuantity: 'Eigen aantal',
    custom: 'Eigen',
    add: 'Toevoegen',
    unavailable: 'Niet beschikbaar',
    temporarilyUnavailable: 'Tijdelijk niet leverbaar',
    removeQuantity: '{quantity} verwijderen',
    minMaxPieces: 'Min. {min}, max. {max} stuks.',
    minPieces: 'Minimaal {min} stuks.',
    enterWholeNumber: 'Vul een heel aantal in.',
    minimumPieces: 'Minimaal {min} stuks.',
    maximumPieces: 'Maximaal {max} stuks.',
    quantityAlreadyShown: 'Dit aantal staat al in de tabel.',

    quantityStillLoading: 'De prijzen voor dit aantal worden nog geladen.',

    quantityNotAvailable: 'Dit aantal is niet beschikbaar voor de huidige opties.',
    addQuantityHint: 'Druk op toevoegen of Enter om dit aantal als rij te plaatsen.',
    customRows: 'Eigen rijen: {quantities}.',
    enterCustomQuantityHint: 'Eigen aantal invoeren, min. {min} en max. {max} stuks.',
    deliveryPriceLoading: 'Levertijd en prijs worden berekend...',
    unitSuffix: 'p/st',
    payInParts: 'Betaal in 3 delen, 0% rente met Klarna.',
    fromPriceNote: 'Vanaf {price} {unit} in de voordeligste uitvoering',
    twoDesignsRequired: 'Upload voor deze bedrukking twee ontwerpen: voorkant en achterkant.',
    businessDays: 'werkdagen',
    needDesignHelp: 'Niet handig met ontwerpen?',
    designersFree: 'Onze designers maken gratis een printklaar ontwerp voor je.',
    requestDesign: 'Ontwerp aanvragen →',
    configurationImpossible: 'Deze combinatie van opties kan niet worden gedrukt. Kies een ander formaat of pas de overige opties aan.',
    configurationImpossibleDoubleSided: 'Dit formaat wordt alleen enkelzijdig geleverd. Zet dubbelzijdig uit, of kies een ander formaat.',
    combinationUnavailable: 'Deze combinatie is niet beschikbaar. Kies een andere materiaal-, formaat- of afwerkingsoptie.',
    pricingTemporarilyUnavailable: 'Deze combinatie kan tijdelijk niet worden geprijsd. Probeer het opnieuw of kies een andere samenstelling.',
    livePriceUnavailable: 'De actuele prijs is tijdelijk niet beschikbaar. Probeer het over enkele minuten opnieuw.',
    quoteCta: 'Liever een offerte aanvragen?',
    quoteUnavailableTitle: 'Prijs niet direct beschikbaar',
    quoteRequestTitle: 'Offerte aanvragen',
    quoteUnavailableDescription: 'Vraag een offerte aan voor deze samenstelling. We sturen de gekozen opties mee zodat dit snel opgepakt kan worden.',
    quoteRequestDescription: 'Voor een grote oplage, een afwijkende uitvoering of een gewenste leverdatum. We sturen de gekozen opties mee.',
    quoteSentNumber: 'Offerte {number} staat in je mail. Daar kun je hem bekijken en accepteren.',
    quoteReceivedManual: 'Offerteaanvraag ontvangen. Deze samenstelling vraagt om een handmatige prijs; je hoort snel van ons.',
    quoteName: 'Naam', quoteEmail: 'E-mailadres', quotePhone: 'Telefoon optioneel', quoteCompany: 'Bedrijfsnaam optioneel',
    quoteDeliveryDate: 'Gewenste leverdatum optioneel', quoteMessage: 'Opmerking optioneel', quoteSending: 'Versturen...', quoteSubmit: 'Vraag offerte aan',
    quoteRequired: 'Vul je naam en e-mailadres in', quotePriceFallback: 'Directe prijs niet beschikbaar', quoteSaveError: 'Offerteaanvraag kon niet worden opgeslagen',
    quoteSentToast: 'Offerte verstuurd naar je e-mail', quoteReceivedToast: 'Offerteaanvraag ontvangen', quoteFailed: 'Offerteaanvraag mislukt',
  },
  de: {
    locked: 'Standard',
    checkingAvailability: 'Verfügbarkeit wird geprüft...',
    selectionUnavailable: 'in dieser Zusammenstellung vorübergehend nicht lieferbar',
    resetOptions: 'Standard wiederherstellen',
    stepUpload: 'Hochladen',
    choose: 'Wählen',
    selectQuantity: 'Anzahl wählen',
    quantity: 'Anzahl',
    pieces: 'Stück',
    enterQuantity: 'Anzahl eingeben',
    invalidCombination: 'Ungültige Kombination',
    quantityMinimumText: 'Diese Auswahl ist ab {count} Stück möglich. Wählen Sie {count} Stück oder mehr, oder für kleinere Mengen eine andere Veredelung (zum Beispiel Transfer).',
    quantityMinimumAction: '{count} Stück wählen',
    quantityMaximumAction: '{count} wählen',
    quantityMaximumText: 'Diese Auswahl ist bis {count} Stück möglich, zum Beispiel weil es ein Muster ist. Wählen Sie {count} oder eine andere Ausführung für eine größere Menge.',
    priceRateLimitedTitle: 'Preis wird gleich neu berechnet',
    priceRateLimitedText: 'Sie haben in kurzer Zeit viele Optionen verglichen. Wir berechnen den Preis in etwa {seconds} Sekunden automatisch neu.',
    priceRateLimitedLongText: 'Sie haben heute sehr viele Preise abgefragt. Wir versuchen es in einer Minute automatisch erneut. Klappt es dann noch nicht, fordern Sie bitte ein Angebot an.',
    adjustSelection: 'Auswahl anpassen',
    proceedToUpload: 'Weiter zum Upload',
    proceedToOverview: 'Weiter zur Übersicht',
    addToCart: 'In den Warenkorb',
    uploadDesign: 'Design hochladen',
    uploadIntro: 'Laden Sie hier Ihre druckfertige Datei hoch. Wir unterstützen PDF, JPG und PNG.',
    selectAllOptions: 'Wählen Sie alle Optionen',
    priceOnRequest: 'Preis auf Anfrage: Fordern Sie unten ein Angebot für Ihr Design an.',
    designerRequiredText: 'Gestalten Sie oben zuerst Ihren Text. Format und Anzahl der Schnittlinien ergeben sich daraus, und beide bestimmen zusammen den Preis.',
    continueToOrder: 'Weiter zur Bestellung',
    uploadAnotherFile: 'Andere Datei hochladen',
    backToConfiguration: 'Zurück zur Konfiguration',
    backToUpload: 'Zurück zum Upload',
    saveChanges: 'Änderungen speichern',
    uploadFirst: 'Bitte laden Sie zuerst eine Datei hoch',
    saved: 'Änderungen gespeichert!',
    saveError: 'Änderungen konnten nicht gespeichert werden',
    addedToCart: 'Produkt wurde in den Warenkorb gelegt!',
    priceUnavailable: 'Für diese Konfiguration konnte kein Preis abgerufen werden',
    priceRetry: 'Preis konnte nicht abgerufen werden. Bitte versuchen Sie es erneut.',
    width: 'Breite',
    height: 'Höhe',
    min: 'Min.',
    max: 'Max.',
    eco: 'Nachhaltig',
    priceAndDelivery: 'Preis und Lieferzeit',
    pricesLoading: 'Preise werden geladen',
    optionsLoadingHint: 'Einen Moment, wird geladen...',
    customQuantity: 'Eigene Anzahl',
    custom: 'Eigene',
    add: 'Hinzufügen',
    unavailable: 'Nicht verfügbar',
    temporarilyUnavailable: 'Vorübergehend nicht lieferbar',
    removeQuantity: '{quantity} entfernen',
    minMaxPieces: 'Min. {min}, max. {max} Stück.',
    minPieces: 'Mindestens {min} Stück.',
    enterWholeNumber: 'Geben Sie eine ganze Zahl ein.',
    minimumPieces: 'Mindestens {min} Stück.',
    maximumPieces: 'Maximal {max} Stück.',
    quantityAlreadyShown: 'Diese Anzahl steht bereits in der Tabelle.',

    quantityStillLoading: 'Die Preise für diese Menge werden noch geladen.',

    quantityNotAvailable: 'Diese Menge ist für die gewählten Optionen nicht verfügbar.',
    addQuantityHint: 'Klicken Sie auf Hinzufügen oder Enter, um diese Anzahl als Zeile einzufügen.',
    customRows: 'Eigene Zeilen: {quantities}.',
    enterCustomQuantityHint: 'Eigene Anzahl eingeben, min. {min} und max. {max} Stück.',
    deliveryPriceLoading: 'Lieferzeit und Preis werden berechnet...',
    unitSuffix: 'pro Stück',
    payInParts: 'In 3 Raten zahlen, 0% Zinsen mit Klarna.',
    fromPriceNote: 'Ab {price} {unit} in der günstigsten Ausführung',
    twoDesignsRequired: 'Laden Sie für diesen Druck zwei Designs hoch: Vorderseite und Rückseite.',
    businessDays: 'Werktage',
    needDesignHelp: 'Nicht sicher beim Gestalten?',
    designersFree: 'Unsere Designer erstellen kostenlos ein druckfertiges Design.',
    requestDesign: 'Design anfragen →',
    configurationImpossible: 'Diese Kombination von Optionen kann nicht gedruckt werden. Wählen Sie ein anderes Format oder passen Sie die übrigen Optionen an.',
    configurationImpossibleDoubleSided: 'Dieses Format ist nur einseitig bedruckbar. Deaktivieren Sie den beidseitigen Druck, oder wählen Sie ein anderes Format.',
    combinationUnavailable: 'Diese Kombination ist nicht verfügbar. Wählen Sie eine andere Material-, Format- oder Veredelungsoption.',
    pricingTemporarilyUnavailable: 'Für diese Kombination kann vorübergehend kein Preis berechnet werden. Versuchen Sie es erneut oder wählen Sie eine andere Zusammenstellung.',
    livePriceUnavailable: 'Der aktuelle Preis ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in einigen Minuten erneut.',
    quoteCta: 'Möchten Sie lieber ein Angebot anfordern?',
    quoteUnavailableTitle: 'Preis nicht direkt verfügbar', quoteRequestTitle: 'Angebot anfordern',
    quoteUnavailableDescription: 'Fordern Sie für diese Zusammenstellung ein Angebot an. Die gewählten Optionen werden automatisch mitgesendet.',
    quoteRequestDescription: 'Für eine große Auflage, eine Sonderausführung oder einen gewünschten Liefertermin. Die gewählten Optionen werden mitgesendet.',
    quoteSentNumber: 'Angebot {number} wurde per E-Mail gesendet. Dort können Sie es ansehen und annehmen.',
    quoteReceivedManual: 'Anfrage erhalten. Diese Zusammenstellung wird manuell kalkuliert; wir melden uns schnellstmöglich.',
    quoteName: 'Name', quoteEmail: 'E-Mail-Adresse', quotePhone: 'Telefon (optional)', quoteCompany: 'Firmenname (optional)',
    quoteDeliveryDate: 'Gewünschter Liefertermin (optional)', quoteMessage: 'Anmerkung (optional)', quoteSending: 'Wird gesendet...', quoteSubmit: 'Angebot anfordern',
    quoteRequired: 'Bitte geben Sie Name und E-Mail-Adresse ein', quotePriceFallback: 'Direkter Preis nicht verfügbar', quoteSaveError: 'Anfrage konnte nicht gespeichert werden',
    quoteSentToast: 'Angebot wurde per E-Mail gesendet', quoteReceivedToast: 'Anfrage erhalten', quoteFailed: 'Anfrage fehlgeschlagen',
  },
  en: {
    locked: 'Standard',
    checkingAvailability: 'Checking availability...',
    selectionUnavailable: 'temporarily unavailable in this combination',
    resetOptions: 'Reset to default',
    stepUpload: 'Upload',
    choose: 'Choose',
    selectQuantity: 'Select quantity',
    quantity: 'Quantity',
    pieces: 'pcs',
    enterQuantity: 'Enter quantity',
    invalidCombination: 'Invalid combination',
    quantityMinimumText: 'This choice is available from {count} pieces. Choose {count} pieces or more, or pick another print method (for example transfer) for a smaller quantity.',
    quantityMinimumAction: 'Choose {count} pieces',
    quantityMaximumAction: 'Choose {count}',
    quantityMaximumText: 'This choice is available up to {count} piece(s), for example because it is a sample. Choose {count}, or pick another option for a larger quantity.',
    priceRateLimitedTitle: 'Price will update shortly',
    priceRateLimitedText: 'You compared a lot of options in a short time. We will recalculate the price automatically in about {seconds} seconds.',
    priceRateLimitedLongText: 'You have requested a lot of prices today. We will try again automatically in a minute. If it still does not work, please request a quote.',
    adjustSelection: 'Adjust your selection',
    proceedToUpload: 'Continue to upload',
    proceedToOverview: 'Continue to overview',
    addToCart: 'Add to cart',
    uploadDesign: 'Upload your design',
    uploadIntro: 'Upload your print-ready file here. We support PDF, JPG and PNG.',
    selectAllOptions: 'Select all options',
    priceOnRequest: 'Price on request: request a quote for your design below.',
    designerRequiredText: 'Design your text above first. The size and the number of cut lines follow from it, and together those two decide the price.',
    continueToOrder: 'Continue to order',
    uploadAnotherFile: 'Upload another file',
    backToConfiguration: 'Back to configuration',
    backToUpload: 'Back to upload',
    saveChanges: 'Save changes',
    uploadFirst: 'Upload a file first',
    saved: 'Changes saved!',
    saveError: 'Could not save changes',
    addedToCart: 'Product added to cart!',
    priceUnavailable: 'Could not retrieve prices for this configuration',
    priceRetry: 'Could not retrieve price. Please try again.',
    width: 'Width',
    height: 'Height',
    min: 'Min',
    max: 'Max',
    eco: 'Sustainable',
    priceAndDelivery: 'Price and delivery',
    pricesLoading: 'Loading prices',
    optionsLoadingHint: 'Please wait, loading...',
    customQuantity: 'Custom quantity',
    custom: 'Custom',
    add: 'Add',
    unavailable: 'Unavailable',
    temporarilyUnavailable: 'Temporarily unavailable',
    removeQuantity: 'Remove {quantity}',
    minMaxPieces: 'Min. {min}, max. {max} pieces.',
    minPieces: 'Minimum {min} pieces.',
    enterWholeNumber: 'Enter a whole number.',
    minimumPieces: 'Minimum {min} pieces.',
    maximumPieces: 'Maximum {max} pieces.',
    quantityAlreadyShown: 'This quantity is already shown in the table.',

    quantityStillLoading: 'Prices for this quantity are still loading.',

    quantityNotAvailable: 'This quantity is unavailable for the selected options.',
    addQuantityHint: 'Press add or Enter to add this quantity as a row.',
    customRows: 'Custom rows: {quantities}.',
    enterCustomQuantityHint: 'Enter a custom quantity, min. {min} and max. {max} pieces.',
    deliveryPriceLoading: 'Delivery time and price are being calculated...',
    unitSuffix: 'each',
    payInParts: 'Pay in 3 instalments, 0% interest with Klarna.',
    fromPriceNote: 'From {price} {unit} in the most economical configuration',
    twoDesignsRequired: 'Upload two designs for this print: front and back.',
    businessDays: 'business days',
    needDesignHelp: 'Not comfortable designing?',
    designersFree: 'Our designers create a print-ready design for free.',
    requestDesign: 'Request a design →',
    configurationImpossible: 'This combination of options cannot be printed. Choose a different size or adjust the other options.',
    configurationImpossibleDoubleSided: 'This size is only available single-sided. Turn off double-sided printing, or choose a different size.',
    combinationUnavailable: 'This combination is not available. Choose a different material, size or finish.',
    pricingTemporarilyUnavailable: 'This combination cannot be priced right now. Try again or choose a different configuration.',
    livePriceUnavailable: 'The current price is temporarily unavailable. Please try again in a few minutes.',
    quoteCta: 'Prefer to request a quote?',
    quoteUnavailableTitle: 'Price not immediately available', quoteRequestTitle: 'Request a quote',
    quoteUnavailableDescription: 'Request a quote for this configuration. Your selected options are included automatically so we can process it quickly.',
    quoteRequestDescription: 'For a large quantity, a special configuration or a preferred delivery date. Your selected options are included automatically.',
    quoteSentNumber: 'Quote {number} has been emailed to you. You can view and accept it there.',
    quoteReceivedManual: 'Quote request received. This configuration requires manual pricing; we will contact you shortly.',
    quoteName: 'Name', quoteEmail: 'Email address', quotePhone: 'Phone (optional)', quoteCompany: 'Company name (optional)',
    quoteDeliveryDate: 'Preferred delivery date (optional)', quoteMessage: 'Comment (optional)', quoteSending: 'Sending...', quoteSubmit: 'Request quote',
    quoteRequired: 'Enter your name and email address', quotePriceFallback: 'Direct price not available', quoteSaveError: 'The quote request could not be saved',
    quoteSentToast: 'Quote sent to your email', quoteReceivedToast: 'Quote request received', quoteFailed: 'Quote request failed',
  },
  fr: {
    locked: 'Standard',
    checkingAvailability: 'Vérification de la disponibilité...',
    selectionUnavailable: 'temporairement indisponible dans cette combinaison',
    resetOptions: 'Rétablir par défaut',
    stepUpload: 'Envoi',
    choose: 'Choisir',
    selectQuantity: 'Choisir la quantité',
    quantity: 'Quantité',
    pieces: 'pièces',
    enterQuantity: 'Saisir la quantité',
    invalidCombination: 'Combinaison non valide',
    quantityMinimumText: 'Ce choix est possible à partir de {count} pièces. Choisissez {count} pièces ou plus, ou une autre impression (par exemple transfert) pour une plus petite quantité.',
    quantityMinimumAction: 'Choisir {count} pièces',
    quantityMaximumAction: 'Choisir {count}',
    quantityMaximumText: "Ce choix est possible jusqu'à {count} pièce(s), par exemple parce qu'il s'agit d'un échantillon. Choisissez {count}, ou une autre option pour une plus grande quantité.",
    priceRateLimitedTitle: 'Le prix sera recalculé dans un instant',
    priceRateLimitedText: 'Vous avez comparé de nombreuses options en peu de temps. Nous recalculerons automatiquement le prix dans environ {seconds} secondes.',
    priceRateLimitedLongText: 'Vous avez demandé de très nombreux prix aujourd’hui. Nous réessaierons automatiquement dans une minute. Si cela ne fonctionne toujours pas, demandez un devis.',
    adjustSelection: 'Ajustez votre sélection',
    proceedToUpload: 'Continuer vers le téléchargement',
    proceedToOverview: 'Continuer vers l’aperçu',
    addToCart: 'Ajouter au panier',
    uploadDesign: 'Téléchargez votre design',
    uploadIntro: 'Téléchargez ici votre fichier prêt à imprimer. Nous acceptons PDF, JPG et PNG.',
    selectAllOptions: 'Sélectionnez toutes les options',
    priceOnRequest: 'Prix sur demande : demandez ci-dessous un devis pour votre design.',
    designerRequiredText: 'Composez d abord votre texte ci-dessus. Le format et le nombre de lignes de decoupe en decoulent, et ensemble ils determinent le prix.',
    continueToOrder: 'Continuer la commande',
    uploadAnotherFile: 'Télécharger un autre fichier',
    backToConfiguration: 'Retour à la configuration',
    backToUpload: 'Retour au téléchargement',
    saveChanges: 'Enregistrer les modifications',
    uploadFirst: 'Téléchargez d’abord un fichier',
    saved: 'Modifications enregistrées !',
    saveError: 'Impossible d’enregistrer les modifications',
    addedToCart: 'Produit ajouté au panier !',
    priceUnavailable: 'Impossible de récupérer les prix pour cette configuration',
    priceRetry: 'Impossible de récupérer le prix. Veuillez réessayer.',
    width: 'Largeur',
    height: 'Hauteur',
    min: 'Min.',
    max: 'Max.',
    eco: 'Durable',
    priceAndDelivery: 'Prix et délai',
    pricesLoading: 'Chargement des prix',
    optionsLoadingHint: 'Un instant, chargement en cours...',
    customQuantity: 'Quantité personnalisée',
    custom: 'Personnalisée',
    add: 'Ajouter',
    unavailable: 'Non disponible',
    temporarilyUnavailable: 'Temporairement indisponible',
    removeQuantity: 'Supprimer {quantity}',
    minMaxPieces: 'Min. {min}, max. {max} pièces.',
    minPieces: 'Minimum {min} pièces.',
    enterWholeNumber: 'Saisissez un nombre entier.',
    minimumPieces: 'Minimum {min} pièces.',
    maximumPieces: 'Maximum {max} pièces.',
    quantityAlreadyShown: 'Cette quantité est déjà dans le tableau.',

    quantityStillLoading: 'Les prix de cette quantité sont en cours de chargement.',

    quantityNotAvailable: 'Cette quantité est indisponible avec les options choisies.',
    addQuantityHint: 'Cliquez sur Ajouter ou appuyez sur Entrée pour ajouter cette quantité.',
    customRows: 'Lignes personnalisées : {quantities}.',
    enterCustomQuantityHint: 'Saisissez une quantité personnalisée, min. {min} et max. {max} pièces.',
    deliveryPriceLoading: 'Délai et prix en cours de calcul...',
    unitSuffix: 'pce',
    payInParts: 'Payez en 3 fois, 0% d’intérêt avec Klarna.',
    fromPriceNote: 'À partir de {price} {unit} dans la configuration la plus économique',
    twoDesignsRequired: 'Ajoutez deux designs pour cette impression : recto et verso.',
    businessDays: 'jours ouvrables',
    needDesignHelp: 'Pas à l’aise avec le design ?',
    designersFree: 'Nos designers créent gratuitement un fichier prêt à imprimer.',
    requestDesign: 'Demander un design →',
    configurationImpossible: 'Cette combinaison d’options ne peut pas être imprimée. Choisissez un autre format ou modifiez les autres options.',
    configurationImpossibleDoubleSided: 'Ce format est disponible uniquement en impression recto. Désactivez le recto-verso, ou choisissez un autre format.',
    combinationUnavailable: 'Cette combinaison n’est pas disponible. Choisissez une autre option de matériau, de format ou de finition.',
    pricingTemporarilyUnavailable: 'Cette combinaison ne peut pas être chiffrée pour le moment. Réessayez ou choisissez une autre configuration.',
    livePriceUnavailable: 'Le prix actuel est temporairement indisponible. Veuillez réessayer dans quelques minutes.',
    quoteCta: 'Vous préférez demander un devis ?',
    quoteUnavailableTitle: 'Prix non disponible immédiatement', quoteRequestTitle: 'Demander un devis',
    quoteUnavailableDescription: 'Demandez un devis pour cette configuration. Les options choisies sont jointes automatiquement afin de la traiter rapidement.',
    quoteRequestDescription: 'Pour une grande quantité, une configuration spéciale ou une date de livraison souhaitée. Les options choisies sont jointes automatiquement.',
    quoteSentNumber: 'Le devis {number} vous a été envoyé par e-mail. Vous pouvez le consulter et l’accepter depuis ce message.',
    quoteReceivedManual: 'Demande de devis reçue. Cette configuration nécessite un calcul manuel ; nous vous recontacterons rapidement.',
    quoteName: 'Nom', quoteEmail: 'Adresse e-mail', quotePhone: 'Téléphone (facultatif)', quoteCompany: 'Entreprise (facultatif)',
    quoteDeliveryDate: 'Date de livraison souhaitée (facultatif)', quoteMessage: 'Commentaire (facultatif)', quoteSending: 'Envoi...', quoteSubmit: 'Demander un devis',
    quoteRequired: 'Saisissez votre nom et votre adresse e-mail', quotePriceFallback: 'Prix direct non disponible', quoteSaveError: 'La demande de devis n’a pas pu être enregistrée',
    quoteSentToast: 'Devis envoyé par e-mail', quoteReceivedToast: 'Demande de devis reçue', quoteFailed: 'Échec de la demande de devis',
  },
};

function getOptionDescription(optionId: string, value: { value: string | number; label: string; description?: string }, locale = 'nl') {
  // Vaste uitleg die voor de opgeslagen (vaak lege of Engelse) tekst gaat.
  const fixedDescription = sampleOptionValueDescription(optionId, value.value, locale);
  if (fixedDescription) return fixedDescription;
  const translatedLabel = translatePrintLabel(value.label, locale);
  const lang = locale.split('-')[0];
  const valueKey = String(value.value);
  // Print.com sets the description equal to the name for some options, which
  // rendered every such value twice ("Rond Rond", "Hart Hart").
  const rawDescription = value.description && value.description.trim() === String(value.label).trim()
    ? undefined
    : value.description;
  const cleanDescription = stripMarkdownImages(rawDescription);
  // Central finishing copy (option-value-descriptions.ts). Precedence:
  // - en/de/fr: the module wins, because the stored text there is often the
  //   untranslated Dutch one. A stored localized text is still used below for
  //   values the module does not describe.
  // - nl and the other locales: a stored description wins (it can be
  //   product-specific or already translated), else the module (English
  //   fallback for locales without own copy).
  // - Exception for nl: values that used to come from the Dutch business card
  //   and folder tables keep winning over stored (often supplier) text.
  const moduleDescription = optionValueDescription(optionId, value.value, locale, value.label);
  if (moduleDescription) {
    const moduleWins = ['en', 'de', 'fr'].includes(lang) ||
      (lang === 'nl' && optionValueDescriptionOverridesStoredDutch(optionId, value.value));
    if (!moduleWins && cleanDescription && cleanDescription.trim() !== translatedLabel.trim()) {
      return lang === 'nl' ? translatePrintLabel(cleanDescription, locale) : cleanDescription;
    }
    return moduleDescription;
  }
  // Stored catalog text is localized before this component renders; static Dutch defaults must not overwrite it.
  if (lang !== 'nl' && cleanDescription) return cleanDescription;
  if (!['nl', 'de', 'en', 'fr'].includes(lang)) return '';

  if (optionId === 'color' && /_ink_pad_stamp$/i.test(valueKey)) {
    const descriptions: Record<string, Record<string, string>> = {
      nl: {
        black_ink_pad_stamp: 'Zwarte inkt voor de stempelafdruk. Meest gekozen voor kantoor, administratie en formulieren.',
        red_ink_pad_stamp: 'Rode inkt voor de stempelafdruk. Opvallend voor controles, waarschuwingen of goedkeuringen.',
        green_ink_pad_stamp: 'Groene inkt voor de stempelafdruk. Handig voor akkoord, verwerkt of gecontroleerd.',
        blue_ink_pad_stamp: 'Blauwe inkt voor de stempelafdruk. Zakelijk alternatief voor zwart en goed herkenbaar op documenten.',
      },
      de: {
        black_ink_pad_stamp: 'Schwarze Stempelfarbe für Büro, Verwaltung und Formulare.',
        red_ink_pad_stamp: 'Rote Stempelfarbe für Kontrollen, Warnhinweise oder Freigaben.',
        green_ink_pad_stamp: 'Grüne Stempelfarbe für Freigaben, verarbeitet oder kontrolliert.',
        blue_ink_pad_stamp: 'Blaue Stempelfarbe als geschaeftliche Alternative zu Schwarz.',
      },
      en: {
        black_ink_pad_stamp: 'Black ink for office use, administration and forms.',
        red_ink_pad_stamp: 'Red ink for checks, warnings or approvals.',
        green_ink_pad_stamp: 'Green ink for approved, processed or checked marks.',
        blue_ink_pad_stamp: 'Blue ink as a business alternative to black.',
      },
      fr: {
        black_ink_pad_stamp: 'Encre noire pour le bureau, l’administration et les formulaires.',
        red_ink_pad_stamp: 'Encre rouge pour les controles, avertissements ou validations.',
        green_ink_pad_stamp: 'Encre verte pour les mentions valide, traite ou contrôle.',
        blue_ink_pad_stamp: 'Encre bleue comme alternative professionnelle au noir.',
      },
    };
    const approximate: Record<string, string> = {
      nl: '* Kleuren zijn bij benadering',
      de: '* Farben sind Annäherungen',
      en: '* Colours are approximate',
      fr: '* Les couleurs sont approximatives',
    };
    const description = (descriptions[lang] || descriptions.nl)[valueKey];
    if (description) return `${description} ${approximate[lang] || approximate.nl}`;
  }

  if (optionId === 'accessories_frames') {
    const introByValue: Record<string, string> = {
      without_passe_partout: 'De lijst wordt zonder passe-partout geleverd.',
      passe_partout_black: 'Materiaal: karton\nKleur: zwart\nDikte: 1,25 mm',
      passe_partout_white: 'Materiaal: karton\nKleur: wit\nDikte: 1,25 mm',
      passe_partout_creme: 'Materiaal: karton\nKleur: creme\nDikte: 1,25 mm',
    };
    const table = [
      'Formaat lijst        Binnenmaat passe-partout',
      '20 x 30 cm          12 x 22 cm',
      '30 x 40 cm          20 x 30 cm',
      '40 x 60 cm          28 x 48 cm',
      '50 x 70 cm          36 x 56 cm',
      '60 x 80 cm          46 x 66 cm',
      '70 x 100 cm         54 x 84 cm',
    ].join('\n');
    const extra = 'Een passe-partout past zowel in een houten als een aluminium lijst. De ingelijste poster wordt altijd geprint op basis van het formaat van de lijst en zal daardoor niet schuiven in de lijst.';
    if (valueKey === 'without_passe_partout') return introByValue.without_passe_partout;
    if (introByValue[valueKey]) return `${introByValue[valueKey]}\n\n${table}\n\n${extra}`;
  }

  if (optionId === 'frame') {
    const descriptions: Record<string, Record<string, string>> = {
      nl: {
        '3mm_dibond': 'Premium 3 mm aluminium composietplaat met twee aluminium toplagen en een zwarte kunststof kern. Zeer vlak, vormvast en strak afgewerkt. Beste keuze voor luxe fotoprints, wanddecoratie, zakelijke displays en langduriger buitengebruik.',
        '3mm_dibond_budget': 'Voordelige 3 mm aluminium composietplaat voor scherpe foto- en displaypanelen. Lichter geprijsd dan premium Dibond, maar nog steeds stevig, vlak en geschikt voor binnen en buiten.',
        'photo-stretcher-frame': [
          'Het gepatenteerde aluminium ophangsysteem is ultralicht en eenvoudig te monteren. Door de slimme techniek wordt je print strak opgespannen.',
          'Met de meegeleverde muurbeugel hang je de print horizontaal of verticaal op. Het frame zweeft ca. 2 cm van de muur voor een strak, modern effect. Ben je uitgekeken op de print? Dan wissel je eenvoudig alleen de print in het herbruikbare aluminium ophangsysteem.',
          'Voordelen: licht van gewicht, veel formaten, eenvoudige montage en snel wisselen van de print.',
        ].join('\n\n'),
        without_frame: 'De print wordt geleverd zonder stretchframe. De print is wel voorzien van hoekbevestigingen op de achterzijde, zodat je hem direct kunt gebruiken of later met een passend systeem kunt combineren.',
      },
      de: {
        '3mm_dibond': 'Premium 3 mm Aluminium-Verbundplatte mit zwei Aluminiumschichten und schwarzem Kunststoffkern. Sehr plan, formstabil und hochwertig verarbeitet. Ideal für edle Fotodrucke, Wanddekoration und längeren Außeneinsatz.',
        '3mm_dibond_budget': 'Preisgünstige 3 mm Aluminium-Verbundplatte für scharfe Foto- und Displaypaneele. Günstiger als Premium-Dibond, aber dennoch stabil, plan und für innen und außen geeignet.',
        'photo-stretcher-frame': [
          'Das patentierte Aluminium-Aufhängesystem ist ultraleicht und einfach zu montieren. Durch die clevere Technik wird dein Print straff gespannt.',
          'Mit der mitgelieferten Wandhalterung kannst du den Print horizontal oder vertikal aufhängen. Der Rahmen schwebt ca. 2 cm von der Wand und sorgt für eine klare, moderne Wirkung. Wenn du ein neues Motiv willst, tauschst du einfach nur den Print im wiederverwendbaren Aluminiumsystem aus.',
          'Vorteile: leichtes Gewicht, viele Formate, einfache Montage und schneller Motivwechsel.',
        ].join('\n\n'),
        without_frame: 'Der Print wird ohne Spannrahmen geliefert. Auf der Rückseite sind jedoch Eckbefestigungen angebracht, sodass du den Print direkt verwenden oder später mit einem passenden System kombinieren kannst.',
      },
      en: {
        'photo-stretcher-frame': [
          'The patented aluminium hanging system is ultra-light and easy to install. Its smart construction keeps your print tightly stretched.',
          'The included wall bracket lets you hang the print horizontally or vertically. The frame floats approx. 2 cm off the wall for a clean, modern effect. Ready for a new image? Simply replace the print in the reusable aluminium system.',
          'Benefits: lightweight, many sizes, easy installation and quick print changes.',
        ].join('\n\n'),
        without_frame: 'The print is supplied without a stretch frame. It does include corner fixings on the back, so you can use it directly or combine it with a suitable system later.',
      },
      fr: {
        'photo-stretcher-frame': [
          'Le système de suspension en aluminium brevete est ultraleger et facile a monter. Sa technique intelligente maintient votre impression bien tendue.',
          'Le support mural fourni permet de suspendre l’impression horizontalement ou verticalement. Le cadre flotte a env. 2 cm du mur pour un effet net et moderne. Envie d’une nouvelle image ? Remplacez simplement l’impression dans le système aluminium reutilisable.',
          'Avantages : leger, nombreux formats, montage simple et changement rapide de l impression.',
        ].join('\n\n'),
        without_frame: 'L’impression est livrée sans cadre tendu. Elle est toutefois equipee de fixations d’angle au dos, afin de pouvoir être utilisee directement ou combinee plus tard avec un système adapte.',
      },
    };
    const frameDescription = (descriptions[lang] || descriptions.nl)[String(value.value)];
    if (frameDescription) return frameDescription;
  }

  if (['material', 'finish', 'laminate', 'coating'].includes(optionId)) {
    const materialInfo = getPrintMaterialInfo({ value: value.value, label: translatedLabel }, locale);
    if (materialInfo?.description) return materialInfo.description;

    if (cleanDescription && cleanDescription.trim() !== value.label.trim() && cleanDescription.trim() !== translatedLabel.trim()) {
      return translatePrintLabel(cleanDescription, locale);
    }

    const descriptions: Record<string, Record<string, string>> = {
      nl: {
        '135gr_gesatineerd_mc': 'Standaard posterpapier met een gladde halfmatte uitstraling. Goede keuze voor betaalbare posters binnen.',
        '250gr_gesatineerd_mc': 'Steviger posterpapier. Voelt dikker aan en blijft mooier vlak bij losse posters.',
        '170gr_silk_mc': 'Zijdeglans papier met iets luxere kleurweergave. Mooi voor foto posters met veel detail.',
        '250gr_silk_mc': 'Premium zijdeglans papier. Extra stevig met een verzorgde uitstraling voor cadeau of presentatie.',
        '160gr_ijm814_uncoated': 'Mat premium papier zonder glans. Rustige uitstraling, fijn voor illustraties en tekst.',
        '230gr_premium_matt': 'Dik premium mat papier. Luxe keuze voor posters met een zachte, niet-spiegelende afwerking.',
        '350gr_airtex': 'Sterk, mat en naadloos fotobehang voor een luxe wandafwerking. Geschikt voor woonkamers, slaapkamers en winkels.',
        '320gr_kavalan_gecko': 'PVC-vrij premium behang met een duurzame uitstraling. Goede keuze als je milieubewuster wilt bestellen.',
        '3mm_dibond_budget': 'Licht, stevig en strak voor foto’s aan de muur. De voordelige keuze voor thuis.',
        '3mm_dibond': 'Premium aluminium composietplaat met extra strakke afwerking en lange levensduur.',
        '4mm_dibond_budget': 'Steviger dan 3 mm en geschikt voor grotere wanddecoratie.',
        '6mm_dibond': 'Extra dikke plaat voor een robuuste premium uitstraling.',
        '3mm_acrylate_transparent': 'Heldere glans en dieptewerking. Mooi voor foto’s met sterke kleuren.',
        '5mm_acrylate_transparent': 'Dikker plexiglas met luxere dieptewerking.',
        '10mm_acrylate_transparent': 'Extra dik plexiglas voor een zeer premium presentatie.',
      },
      de: {
        '135gr_gesatineerd_mc': 'Standard-Posterpapier mit glatter, seidenmatter Wirkung. Gute Wahl für günstige Poster im Innenbereich.',
        '250gr_gesatineerd_mc': 'Stabileres Posterpapier. Fühlt sich dicker an und bleibt bei losen Postern schöner plan.',
        '170gr_silk_mc': 'Seidenglänzendes Papier mit etwas hochwertigerer Farbwiedergabe. Schön für Fotoposter mit vielen Details.',
        '250gr_silk_mc': 'Premium-Papier mit Seidenglanz. Extra stabil mit gepflegter Wirkung als Geschenk oder Präsentation.',
        '160gr_ijm814_uncoated': 'Mattes Premiumpapier ohne Glanz. Ruhige Wirkung, gut für Illustrationen und Text.',
        '230gr_premium_matt': 'Dickes Premium-Papier in matt. Luxuriöse Wahl für Poster mit weicher, nicht spiegelnder Oberfläche.',
        '350gr_airtex': 'Starke, matte und nahtlose Fototapete für eine hochwertige Wandgestaltung. Ideal für Wohnräume, Schlafzimmer und Geschäfte.',
        '320gr_kavalan_gecko': 'PVC-freie Premium-Tapete mit nachhaltiger Ausstrahlung. Gute Wahl für eine bewusstere Bestellung.',
        '3mm_dibond_budget': 'Leicht, stabil und sauber für Fotos an der Wand. Die preisbewusste Wahl für Zuhause.',
        '3mm_dibond': 'Premium-Aluminium-Verbundplatte mit besonders sauberer Oberfläche und langer Haltbarkeit.',
        '4mm_dibond_budget': 'Stabiler als 3 mm und geeignet für größere Wanddekoration.',
        '6mm_dibond': 'Extra dicke Platte für eine robuste Premium-Optik.',
        '3mm_acrylate_transparent': 'Klare Glanzwirkung und schöne Tiefe. Gut für Fotos mit kräftigen Farben.',
        '5mm_acrylate_transparent': 'Dickeres Acrylglas mit hochwertigerer Tiefenwirkung.',
        '10mm_acrylate_transparent': 'Extra dickes Acrylglas für eine sehr hochwertige Präsentation.',
      },
      en: {
        '135gr_gesatineerd_mc': 'Standard poster paper with a smooth semi-matt look. Good choice for affordable indoor posters.',
        '250gr_gesatineerd_mc': 'Sturdier poster paper. Feels thicker and stays flatter for loose posters.',
        '170gr_silk_mc': 'Silk paper with a slightly more premium colour reproduction. Good for detailed photo posters.',
        '250gr_silk_mc': 'Premium silk paper. Extra sturdy with a polished look for gifts or presentations.',
        '160gr_ijm814_uncoated': 'Matt premium paper without shine. Calm look, suitable for illustrations and text.',
        '230gr_premium_matt': 'Thick premium matt paper. A luxury choice for posters with a soft non-reflective finish.',
        '350gr_airtex': 'Strong matt seamless photo wallpaper for a premium wall finish. Suitable for living rooms, bedrooms and shops.',
        '320gr_kavalan_gecko': 'PVC-free premium wallpaper with a more sustainable look. Good choice for a more conscious order.',
        '3mm_dibond_budget': 'Light, sturdy and clean for photos on the wall. The budget-friendly choice for home use.',
        '3mm_dibond': 'Premium aluminium composite panel with an extra clean finish and long lifespan.',
        '4mm_dibond_budget': 'Sturdier than 3 mm and suitable for larger wall decoration.',
        '6mm_dibond': 'Extra thick panel for a robust premium look.',
        '2mm_cardboard': '2 mm display board with a matt white surface and grey recycled core. Lightweight and suitable for indoor displays.',
        '3.8mm_dispa': '3.8 mm Dispa is made from 100% paper. Flat, rigid and fully recyclable for indoor display work.',
        '10mm_reboard': '10 mm Re-board sandwich board. Lightweight but very strong, suitable for freestanding displays and indoor constructions.',
        '16mm_reboard': '16 mm Re-board sandwich board. Extra robust, lightweight and suitable for freestanding indoor displays.',
        '1.5mm_corrugated_cardboard': '1.5 mm E-flute corrugated cardboard. Lightweight, recyclable and suitable for indoor temporary displays.',
        '3mm_corrugated_cardboard': '3 mm EE-flute corrugated cardboard. Recyclable, lightweight and suitable for indoor temporary displays.',
        budget: 'Matt 3 mm aluminium composite panel with black core. Sharply priced and suitable for photo, art and signing.',
        premium: 'Premium 3 mm aluminium composite panel with black core and a clean finish.',
        butler: '3 mm aluminium composite with brushed metal look. The structure remains subtly visible through the print.',
        plexiglas: 'Clear 5 mm acrylic with print on the back and white layers. Gives extra depth and gloss.',
        kendal: 'Finely woven polyester with a calm matt look. Good for photos, illustrations and clean interior posters.',
        structex: 'Heavier textile with visible structure for a more tactile fabric look.',
        '230gr_premium_glossy': 'Gloss 230 g/m2 photo paper with vivid colour reproduction for framed posters.',
      },
      fr: {
        '135gr_gesatineerd_mc': 'Papier affiche standard avec aspect semi-mat lisse. Bon choix pour affiches interieures abordables.',
        '250gr_gesatineerd_mc': 'Papier affiche plus rigide. Plus epais et plus stable pour affiches libres.',
        '170gr_silk_mc': 'Papier satine avec rendu couleur plus premium. Idéal pour affiches photo detaillees.',
        '250gr_silk_mc': 'Papier satine premium. Extra rigide avec aspect soigne pour cadeau ou présentation.',
        '160gr_ijm814_uncoated': 'Papier premium mat sans brillance. Aspect sobre, adapte aux illustrations et textes.',
        '230gr_premium_matt': 'Papier premium mat épais. Choix luxe pour affiches avec finition douce non réfléchissante.',
        '350gr_airtex': 'Papier peint photo mat et solide pour une finition murale premium. Adapte aux salons, chambres et boutiques.',
        '320gr_kavalan_gecko': 'Papier peint premium sans PVC avec aspect plus durable. Bon choix pour une commande plus responsable.',
        '3mm_dibond_budget': 'Leger, rigide et net pour photos murales. Choix économique pour la maison.',
        '3mm_dibond': 'Panneau aluminium composite premium avec finition tres nette et longue duree de vie.',
        '4mm_dibond_budget': 'Plus rigide que 3 mm et adapte aux grandes décorations murales.',
        '6mm_dibond': 'Panneau extra epais pour un rendu premium robuste.',
        '2mm_cardboard': 'Carton display 2 mm avec surface blanche mate et noyau gris recycle. Leger et adapte aux displays intérieurs.',
        '3.8mm_dispa': 'Dispa 3,8 mm compose de 100% papier. Plat, rigide et recyclable pour displays interieurs.',
        '10mm_reboard': 'Re-board 10 mm en structure sandwich. Leger mais tres solide, adapte aux displays autoportants interieurs.',
        '16mm_reboard': 'Re-board 16 mm en structure sandwich. Extra robuste, leger et adapte aux displays interieurs.',
        '1.5mm_corrugated_cardboard': 'Carton ondule E 1,5 mm. Leger, recyclable et adapte aux displays temporaires interieurs.',
        '3mm_corrugated_cardboard': 'Carton ondule EE 3 mm. Recyclable, leger et adapte aux displays temporaires interieurs.',
        budget: 'Panneau composite aluminium mat 3 mm avec noyau noir. Prix serre, adapte aux photos, à l’art et à la signalétique.',
        premium: 'Panneau composite aluminium premium 3 mm avec noyau noir et finition nette.',
        butler: 'Composite aluminium 3 mm avec aspect metal brosse. La structure reste subtilement visible a travers l impression.',
        plexiglas: 'Plexiglas transparent 5 mm avec impression au dos et couches blanches. Donne plus de profondeur et de brillance.',
        kendal: 'Polyester finement tisse avec aspect mat sobre. Idéal pour photos, illustrations et affiches intérieur nettes.',
        structex: 'Textile plus lourd avec structure visible pour un rendu tissu plus tactile.',
        '230gr_premium_glossy': 'Papier photo brillant 230 g/m2 avec couleurs vives pour affiches encadrees.',
      },
    };
    const extraMaterialDescriptions: Record<string, Record<string, string>> = {
      nl: {
        '3mm_dibond_buttler_finish': "Geborsteld aluminium met een moderne metallic uitstraling. Mooi voor zwart-witfoto's en strakke interieurbeelden.",
        '1mm_forex': 'Lichtste Forex® plaat (0,68 kg/m²). Max 301×152 cm. Geschuimd PVC, waterafstotend en brandvertragend. Geschikt voor binnen en buiten.',
        '3mm_forex': 'Forex® 3 mm (1,73 kg/m²). Max 301×199 cm. Licht maar stevig, glad oppervlak, UV-bestendig. Indoor & outdoor tot 2 jaar.',
        '3mm_forex_re': 'Forex®re 3 mm — 30% gerecycled. Zelfde specs als 3 mm Forex® met milieubewustere uitstraling. Max 301×152 cm.',
        '5mm_forex': 'Forex® 5 mm (2,2 kg/m²). Max 301×199 cm. Stevigere plaat voor grotere formaten. Waterafstotend, brandvertragend EN-13501.',
        '5mm_forex_re': 'Forex®re 5 mm — 30% gerecycled. Zelfde stevigheid als 5 mm Forex® met duurzame uitstraling. Max 301×152 cm.',
        '10mm_forex': 'Forex® 10 mm (4,5 kg/m²). Max 301×152 cm. Extra stevig en robuust. Ideaal voor langdurige signing. Brandvertragend EN-13501.',
        '510gr_pvc': 'Stevig weerbestendig doek voor buiten. Beste keuze voor schutting, tuin, balkon en langduriger buitengebruik.',
        'pes_outdoor': 'Lichter PVC-vrij textieldoek voor buiten. Mooie duurzame keuze, maar niet met elke ringafwerking te combineren.',
      },
      de: {
        '3mm_dibond_buttler_finish': 'Gebürstetes Aluminium mit moderner Metallic-Optik. Schön für Schwarz-Weiß-Fotos und klare Interieurmotive.',
        '1mm_forex': 'Leichteste Forex® Platte (0,68 kg/m²). Max 301×152 cm. Geschaeumtes PVC, wasserabweisend und schwer entflammbar.',
        '3mm_forex': 'Forex® 3 mm (1,73 kg/m²). Max 301×199 cm. Leicht aber stabil, glatte Oberflaeche. Indoor & Outdoor bis 2 Jahre.',
        '3mm_forex_re': 'Forex®re 3 mm — 30% recycelt. Gleiche Eigenschaften wie 3 mm Forex® mit nachhaltigerer Optik. Max 301×152 cm.',
        '5mm_forex': 'Forex® 5 mm (2,2 kg/m²). Max 301×199 cm. Stabilere Platte für größere Formate. Wasserabweisend, EN-13501.',
        '5mm_forex_re': 'Forex®re 5 mm — 30% recycelt. Gleiche Stärke wie 5 mm Forex® mit nachhaltiger Ausstrahlung. Max 301×152 cm.',
        '10mm_forex': 'Forex® 10 mm (4,5 kg/m²). Max 301×152 cm. Extra stabil und robust. Ideal für dauerhafte Beschilderung.',
        '510gr_pvc': 'Stabiles wetterfestes Material für draußen. Beste Wahl für Zaun, Garten, Balkon und längere Nutzung im Außenbereich.',
        'pes_outdoor': 'Leichteres PVC-freies Textilgewebe für draußen. Nachhaltige Wahl, aber nicht mit jeder Ösenverarbeitung kombinierbar.',
      },
      en: {
        '3mm_forex': 'Lightweight 3 mm foamed PVC board with a matt white surface. A practical choice for affordable photo panels, displays and interior visuals. Slightly flexible, water-repellent and suitable for indoor and sheltered outdoor use.',
        '3mm_dibond': 'Premium 3 mm aluminium composite panel with two aluminium faces and a black plastic core. Very flat, rigid and neatly finished. Best for high-end photo prints, wall decoration, business displays and longer outdoor use.',
        '3mm_dibond_budget': 'Cost-effective 3 mm aluminium composite panel for sharp photo and display panels. More budget-friendly than premium Dibond, while still rigid, flat and suitable for indoor and outdoor use.',
        '3mm_dibond_buttler_finish': '3 mm aluminium composite with a brushed aluminium surface and horizontal brushing direction. White areas get a subtle metallic look. Strong for black-and-white photography, architecture and modern interiors.',
      },
      fr: {
        '3mm_forex': 'Panneau léger de 3 mm en PVC expanse avec surface blanche mate. Bon choix pour des panneaux photo abordables, displays et visuels d’intérieur. Legerement flexible, hydrofuge et adapte à l’intérieur comme à l’extérieur abrite.',
        '3mm_dibond': 'Panneau composite aluminium premium de 3 mm avec deux faces aluminium et noyau plastique noir. Tres plat, rigide et avec une finition nette. Idéal pour photos haut de gamme, décoration murale, displays professionnels et usage extérieur prolonge.',
        '3mm_dibond_budget': 'Panneau composite aluminium économique de 3 mm pour photos et displays nets. Plus abordable que le Dibond premium, tout en restant rigide, plat et adapte à l’intérieur comme à l’extérieur.',
        '3mm_dibond_buttler_finish': 'Composite aluminium 3 mm avec surface aluminium brossee et sens de brossage horizontal. Les zones blanches prennent un subtil effet metallique. Tres adapte aux photos noir et blanc, à l’architecture et aux intérieurs modernes.',
      },
    };
    const materialDescription =
      (descriptions[lang] || descriptions.nl)[String(value.value)] ||
      (extraMaterialDescriptions[lang] || extraMaterialDescriptions.nl)[String(value.value)];
    if (materialDescription) return materialDescription;

    // No generic fallback — materials without a real description stay undescribed
    return '';
  }

  if (optionId === 'printtype') {
    const descriptions: Record<string, Record<string, string>> = {
      nl: {
        '10_pms_black': 'Alleen zwarte PMS-bedrukking. Dit is de enige bedrukking die beschikbaar is op neonpapier.',
        '40': 'Full colour aan de voorkant. Niet beschikbaar op neonpapier; kies dan 1/0 PMS zwart.',
        '44': 'Full colour aan voor- en achterkant. Hiervoor kun je twee bestanden aanleveren. Niet beschikbaar op neonpapier.',
      },
      de: {
        '10_pms_black': 'Nur schwarzer PMS-Druck. Dies ist die einzige Druckart für Neonpapier.',
        '40': 'Vollfarbdruck auf der Vorderseite. Nicht für Neonpapier verfügbar; wähle dann 1/0 PMS Schwarz.',
        '44': 'Vollfarbdruck auf Vorder- und Rückseite. Dafür kannst du zwei Dateien hochladen. Nicht für Neonpapier verfügbar.',
      },
      en: {
        '10_pms_black': 'Black PMS print only. This is the only print option available for neon paper.',
        '40': 'Full colour on the front side. Not available on neon paper; choose 1/0 PMS black instead.',
        '44': 'Full colour on front and back. You can upload two files for this. Not available on neon paper.',
      },
      fr: {
        '10_pms_black': 'Impression PMS noire uniquement. C’est la seule impression disponible pour le papier neon.',
        '40': 'Couleur complete au recto. Non disponible sur papier neon; choisissez 1/0 PMS noir.',
        '44': 'Couleur complete recto verso. Vous pouvez fournir deux fichiers. Non disponible sur papier neon.',
      },
    };
    return (descriptions[lang] || descriptions.nl)[String(value.value)] || cleanDescription;
  }

  if (cleanDescription && cleanDescription.trim() !== value.label.trim() && cleanDescription.trim() !== translatedLabel.trim()) {
    return translatePrintLabel(cleanDescription, locale);
  }

  if (optionId === 'urgency') {
    const descriptions: Record<string, Record<string, string>> = {
      nl: {
        standard: 'Normale productietijd: meestal ongeveer 4-6 werkdagen voordat het product wordt verzonden.',
        moderate: 'Een dag eerder geproduceerd dan standaard: meestal ongeveer 3-5 werkdagen voordat het product wordt verzonden.',
        quick: 'Snellere productie: meestal ongeveer 2-4 werkdagen voordat het product wordt verzonden.',
        urgent: 'Spoedproductie: meestal ongeveer 1-3 werkdagen voordat het product wordt verzonden.',
      },
      de: {
        standard: 'Normale Produktionszeit: meist ca. 4-6 Werktage bis zum Versand.',
        moderate: 'Einen Tag früher als Standard: meist ca. 3-5 Werktage bis zum Versand.',
        quick: 'Schnellere Produktion: meist ca. 2-4 Werktage bis zum Versand.',
        urgent: 'Express-Produktion: meist ca. 1-3 Werktage bis zum Versand.',
      },
      en: {
        standard: 'Normal production. Best choice when your order is not urgent.',
        moderate: 'Produced slightly faster than standard.',
        quick: 'Faster production for urgent orders.',
        urgent: 'Fastest production option for rush orders.',
      },
      fr: {
        standard: 'Production normale. Meilleur choix si votre commande n’est pas urgente.',
        moderate: 'Production légèrement plus rapide que le standard.',
        quick: 'Production plus rapide pour les commandes urgentes.',
        urgent: 'Option de production la plus rapide.',
      },
    };

    const localeDescriptions = descriptions[lang] || descriptions.nl;
    const extraDescriptions: Record<string, Record<string, string>> = {
      nl: {
        fast: 'Snellere productie dan standaard. Handig als je het product eerder nodig hebt.',
        faster: 'Spoedproductie. Kies dit alleen als je bestelling echt haast heeft.',
        express: 'Snelste productie. Alleen nodig voor zeer urgente bestellingen.',
      },
      de: {
        fast: 'Schnellere Produktion als Standard. Praktisch, wenn Sie das Produkt früher benötigen.',
        faster: 'Express-Produktion. Nur wählen, wenn Ihre Bestellung wirklich eilig ist.',
        express: 'Schnellste Produktion. Nur für sehr dringende Bestellungen.',
      },
      en: {
        fast: 'Faster production than standard.',
        faster: 'Rush production for urgent orders.',
        express: 'Fastest production for very urgent orders.',
      },
      fr: {
        fast: 'Production plus rapide que le standard.',
        faster: 'Production express pour les commandes urgentes.',
        express: 'Production la plus rapide pour les commandes très urgentes.',
      },
    };
    return localeDescriptions[String(value.value)] ||
      (extraDescriptions[lang] || extraDescriptions.nl)[String(value.value)] ||
      localeDescriptions[value.label.toLowerCase()] ||
      '';
  }

  return '';
}

const urgencyLeadTimes = URGENCY_LEAD_TIMES;

function getUrgencyPresentation(
  urgencyId: string,
  urgencyOption: ProductOption | undefined,
  locale: string,
  fallbackDays = 5,
  explicitDays?: number,
) {
  const selectedValue = urgencyOption?.values?.find((value) => String(value.value) === String(urgencyId));
  const metadata = selectedValue?.metadata || {};
  const fallback = urgencyLeadTimes[urgencyId] || urgencyLeadTimes.standard;
  // Zonder spoedoptie wint specifications.deliveryDays van de vaste tabel.
  const leadTime = resolveUrgencyLeadTime({
    urgencyId,
    metadata,
    hasUrgencyOption: Boolean(urgencyOption),
    explicitDays,
    fallbackDays,
  });
  const localizedRange = leadTime.range;
  const days = leadTime.days;

  return {
    id: urgencyId,
    label: urgencyId === 'standard'
      ? (selectedValue?.label || fallback.label)
      : (selectedValue?.label || fallback.label),
    businessDays: localizedRange.includes('werkdagen') || localizedRange.includes('business')
      ? localizedRange
          .replace('werkdagen', configuratorCopy[locale.split('-')[0] as keyof typeof configuratorCopy]?.businessDays || configuratorCopy.nl.businessDays)
      : `${days} ${configuratorCopy[locale.split('-')[0] as keyof typeof configuratorCopy]?.businessDays || configuratorCopy.nl.businessDays}`,
    days,
    description: selectedValue?.description || metadata.description || fallback.description,
  };
}

function addBusinessDays(date: Date, businessDays: number) {
  const result = new Date(date);
  let added = 0;
  while (added < businessDays) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return result;
}

function formatDeliveryDate(date: Date, locale: string) {
  return new Intl.DateTimeFormat(locale || 'nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
}

function getStaticProviderLeadTime(providerType?: string, specifications?: any) {
  const explicitLeadTime = Number(
    specifications?.deliveryDays ??
    specifications?.leadTimeBusinessDays ??
    specifications?.productionDays
  );
  if (Number.isFinite(explicitLeadTime) && explicitLeadTime > 0) {
    return explicitLeadTime;
  }
  const source = String(specifications?.source || specifications?.provider || providerType || '').toLowerCase();
  const supplierMarker = `${specifications?.sourceMarketplace || ''} ${specifications?.sourceProductUrl || ''} ${specifications?.sourceUrl || ''}`.toLowerCase();
  if (supplierMarker.includes('vograce')) return 15;
  if (source.includes('prodigi') || source.includes('gelato') || source.includes('printful')) return 8;
  if (source.includes('probo')) return 5;
  if (providerType === 'CUSTOM') return 5;
  return 5;
}

/** True voor de "geen print"-waarde van een printtype-optie (bijv. Print.com printtype 00 "Geen"). */

export function ProductConfigurator({
  lockedConfiguration,
  draftScope,
  productId,
  productName,
  productSlug,
  providerProductId,
  providerType,
  category,
  basePrice,
  fromUnitPriceInclVat,
  options,
  specifications,
  isCanvasProduct = false,
  isPrintProduct = true,
  productThumbnail,
  initialConfiguration,
  requestedSampleSelection,
  initialQuantity,
  initialUploadedFiles,
  initialFileUrl,
  initialDesignData,
  vinylDesigner,
  locale = 'nl', // Default locale for admin panel
  designTemplateCount = 0,
  initialDesignTemplateSlug,
  onSave, // Custom save handler
  readOnly = false, // Read-only mode
  aiImageToolsEnabled = true,
  templateDownload,
}: ProductConfiguratorProps) {
  const router = useRouter();
  const sampleSelectionAttempted = useRef(false);
  const addItem = useCartStore((state) => state.addItem);
  const selectedCountryCode = useCartStore((state) => state.selectedCountryCode);
  // In de admin draait deze component buiten NextIntlClientProvider; daar
  // gooit useTranslations een exception. Val dan terug op eigen copy.
  //
  // Dit is een bewuste, geverifieerde uitzondering op Rules of Hooks, geen
  // gok: of er een provider is, ligt vast per route-tak (producten/[slug] en
  // winkelwagen zitten onder [locale] en hebben er altijd een, admin/orders
  // nooit) en de App Router remount de hele boom bij navigatie tussen die
  // takken - dezelfde gemonteerde component-instantie steekt dus nooit van
  // de ene naar de andere kant over. Zou dat wel gebeuren, dan verschilt het
  // aantal hook-slots tussen de twee paden (de succesvolle useTranslations
  // registreert intern meer dan de throw dat doet) en zou React's
  // hook-boekhouding echt corrupt raken. Splitsen in twee componenten is
  // hier niet realistisch (1800+ regels); zie ook feedback_auth_boundary_drops_context.
  let translate: (key: string) => string;
  let hasIntlContext = true;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    translate = useTranslations('product') as unknown as (key: string) => string;
  } catch {
    hasIntlContext = false;
    const dict = PRODUCT_COPY_FALLBACK[locale.split('-')[0]] || PRODUCT_COPY_FALLBACK.nl;
    translate = (key: string) => dict[key] ?? key;
  }
  const perItemLabel = PRODUCT_COPY_FALLBACK[locale.split('-')[0]]?.perItem || PRODUCT_COPY_FALLBACK.nl.perItem;
  const t = translate;
  const ui = configuratorCopy[locale.split('-')[0] as keyof typeof configuratorCopy] || configuratorCopy.nl;
  const flowCopy = configuratorFlowCopy(locale);
  const uiText = useCallback((key: keyof typeof ui, values: Record<string, string | number> = {}) => {
    let text = String(ui[key] || '');
    Object.entries(values).forEach(([name, value]) => {
      text = text.replaceAll(`{${name}}`, String(value));
    });
    return text;
  }, [ui]);
  const optionPlaceholder = (label: string) => {
    const translatedLabel = translatePrintLabel(label, locale);
    return locale.split('-')[0] === 'de'
      ? `${translatedLabel} wählen`
      : `${ui.choose} ${translatedLabel.toLowerCase()}`;
  };

  // Het officiële batchcontract berekent de all-in prijs voor `region`.
  // Gebruik daarom het werkelijk gekozen bezorgland; locale is alleen een
  // taalkeuze en mag de logistieke prijs niet bepalen.
  const countryCode = selectedCountryCode || getCountryFromLocale(locale);
  const vatCountryCode = countryCode;
  const deliveryCountryName = new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode) || countryCode;
  // Het landtarief is het minimum dat de klant aan verzending betaalt; toon het al hier
  // zodat de winkelwagen geen verrassing is. Zelfde databasetarief als de kassa.
  const countryShippingCost = useCountryShippingCost(countryCode);
  const shippingFromAmount = new Intl.NumberFormat(locale || 'nl-NL', { style: 'currency', currency: 'EUR' })
    .format(countryShippingCost);
  const shippingFromSuffix = ({ nl: 'vanaf', de: 'ab', en: 'from', fr: 'dès' } as Record<string, string>)[locale.split('-')[0]] || 'from';
  const exclShippingLabel = (label: string) => `${label} (${shippingFromSuffix} ${shippingFromAmount})`;
  const [configuration, setConfigurationState] = useState<Record<string, string | number>>({});
  const optionCheckController = useRef<AbortController | null>(null);
  const [rejectedSelections, setRejectedSelections] = useState<Set<string>>(() => new Set());
  const [selectionCheckPending, setSelectionCheckPending] = useState(false);
  const optionSelectionSequence = useRef(0);
  const latestSelectionContext = useRef('');
  latestSelectionContext.current = JSON.stringify([configuration, countryCode]);
  useEffect(() => () => optionCheckController.current?.abort(abortReason('Selection check cancelled: configurator unmounted')), []);

  const setConfiguration = useCallback((update: React.SetStateAction<Record<string, string | number>>) => {
    setConfigurationState(current => {
      const next = typeof update === 'function' ? update(current) : update;
      if (!lockedConfiguration) return next;
      const locked = { ...next, ...lockedConfiguration };
      return Object.keys(locked).length === Object.keys(current).length
        && Object.entries(locked).every(([key, value]) => current[key] === value) ? current : locked;
    });
  }, [lockedConfiguration]);
  // Vinyl Lettering Designer: de host prijst dezelfde configuratie als deze
  // configurator (getBaseConfiguration) en beide gaan door dezelfde gedeelde
  // prijsfetch. Zo is het één aanvraag per ontwerpwijziging en is de quote-hash
  // hier die van de designerprijs (Ruling P6; zie shared-price-fetch.ts).
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;
  const countryCodeRef = useRef(countryCode);
  countryCodeRef.current = countryCode;
  const sharedPriceFetch = useMemo(() => createSharedPriceFetch(), []);
  const [linkedPrintRows, setLinkedPrintRows] = useState<LinkedPrintRow[]>([]);
  const [quantity, setQuantity] = useState(() => resolveInitialQuantity(
    initialQuantity,
    options.some((option) => option.id === 'copies' || option.name === 'copies'),
    specifications?.quantitySettings,
  ));
  const [matrixCustomQuantity, setMatrixCustomQuantity] = useState('');
  const [customMatrixQuantities, setCustomMatrixQuantities] = useState<number[]>(() => {
    const copies = Number(initialConfiguration?.copies);
    if (getClosedCustomQuantities(providerType, specifications) !== null) return [];
    return lockedConfiguration && Number.isSafeInteger(copies) && copies > 0 ? [copies] : [];
  });
  const [deliveryColumns, setDeliveryColumns] = useState<DeliveryPriceColumn[]>([]);
  const [deliveryQuantityRows, setDeliveryQuantityRows] = useState<DeliveryQuantityRow[]>([]);
  const [matrixCustomQuantityError, setMatrixCustomQuantityError] = useState('');
  const [isDeliveryRowsLoading, setIsDeliveryRowsLoading] = useState(false);
  const [deliveryPromise, setDeliveryPromise] = useState<0 | 1 | 2>(0); // 0=standard, 1=insurance, 2=premium
  const [currentStep, setCurrentStep] = useState<'configure' | 'upload' | 'checkout'>('configure');
  // "Openen in editor" vanuit Mijn ontwerpen: ?savedDesign=<id>. Alleen de
  // ingelogde eigenaar krijgt het ontwerp; anders gebeurt er niets.
  const [accountDesignData, setAccountDesignData] = useState<any>(null);
  const accountDesignRequested = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const configureStepRef = useRef<HTMLDivElement | null>(null);
  const uploadStepRef = useRef<HTMLDivElement | null>(null);
  const checkoutStepRef = useRef<HTMLDivElement | null>(null);
  const matrixCustomQuantityInputRef = useRef<HTMLInputElement | null>(null);
  const priceRequestKeyRef = useRef('');
  const deliveryPriceRequestIdRef = useRef(0);
  const pricedProductionOptionsRef = useRef<Record<string, string | number>>({});
  const deliveryMatrixSnapshotRef = useRef<{
    columns: DeliveryPriceColumn[];
    rows: DeliveryQuantityRow[];
  } | null>(null);
  const optionValidationRunRef = useRef(0);
  const posterValidationRunRef = useRef(0);
  const posterValidationActiveRef = useRef(false);
  const priceFetchActiveRef = useRef(false);
  const hiddenConfigCacheRef = useRef<Map<string, Record<string, string | number>>>(new Map());
  const [localizedOptions, setLocalizedOptions] = useState<ProductOption[]>(() =>
    sanitizeProductOptionValues(applyProductSpecificOptionFixes(
      normalizeConsumerOptions(options, locale, specifications),
      providerProductId || productSlug,
      specifications
    ))
  );
  const [localizedSpecifications, setLocalizedSpecifications] = useState<any>(() =>
    withOpenCustomQuantitySettings(providerType, options,
      applyProductSpecificSpecificationFixes(specifications, providerProductId || productSlug))
  );
  const closedCustomQuantities = useMemo(
    () => getClosedCustomQuantities(providerType, localizedSpecifications),
    [providerType, localizedSpecifications],
  );
  const isClosedQuantitySelectionValid = isAllowedClosedQuantity(
    Number(localizedOptions.some(option => option.id === 'copies' || option.name === 'copies') ? configuration.copies : quantity),
    closedCustomQuantities,
  );
  useEffect(() => {
    if (closedCustomQuantities === null) return;
    setCustomMatrixQuantities(current => current.length ? [] : current);
    setMatrixCustomQuantity('');
  }, [closedCustomQuantities]);
  // Start "true": the localized-options fetch below always runs on mount, so
  // the component is never actually ready on the very first render. Starting
  // at false left a real window (SSR output, plus everything up to the first
  // effect pass) where the custom-quantity input/button rendered enabled
  // against the raw, unmerged initial options - a fast click there could be
  // silently rejected or lose what was typed before the localized data
  // arrived and replaced it.
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [optionVisuals, setOptionVisuals] = useState<Record<string, OptionVisual>>({});
  // De visuals-fetch heeft geen eigen laadvlag; zonder die vlag rendert een optie
  // eerst als dropdown en springt hij daarna om naar een tegelraster. Deze latch
  // gaat eenmalig aan zodra de eerste visuals-call is afgerond.
  const [optionVisualsSettled, setOptionVisualsSettled] = useState(false);
  const [printComGuideTemplateUrl, setPrintComGuideTemplateUrl] = useState<{ key: string; url: string } | null>(null);
  const [optionInfoModal, setOptionInfoModal] = useState<{
    title: string;
    description: string;
    image?: string;
    overviewImage?: string;
    visualSvgContent?: string;
  } | null>(null);
  const [optionHoverPreview, setOptionHoverPreview] = useState<{
    image: string;
    left: number;
    top: number;
  } | null>(null);

  const getPrintComValidationRequest = useCallback((configToValidate: Record<string, string | number>) => {
    const copiesOption = localizedOptions.find((option) => option.id === 'copies' || option.name === 'copies');
    const quantityPolicy = getQuantityPolicyForConfiguration(
      providerProductId || productSlug,
      localizedSpecifications,
      configToValidate
    );
    const normalizedQuantity = quantityPolicy
      ? normalizeQuantityForPolicy(
          Number(configToValidate[copiesOption?.id || 'copies'] || quantity || quantityPolicy.minimum || 1),
          quantityPolicy
        )
      : Math.max(1, Number(quantity || 1));

    const providerSafeConfig = {
      ...configToValidate,
      ...(configToValidate.custom_shape !== undefined
        ? { custom_shape: normalizeCustomShapeProviderValue(configToValidate.custom_shape) }
        : {}),
    };

    if (copiesOption) {
      return {
        quantity: 1,
        options: {
          ...providerSafeConfig,
          [copiesOption.id]: normalizedQuantity,
        },
      };
    }

    return {
      quantity: normalizedQuantity,
      options: providerSafeConfig,
    };
  }, [localizedOptions, localizedSpecifications, providerProductId, productSlug, quantity]);

  const showOptionHoverPreview = useCallback((event: React.MouseEvent, image?: string) => {
    if (!image || typeof window === 'undefined') return;
    const previewWidth = window.innerWidth >= 640 ? 224 : 176;
    const previewHeight = window.innerWidth >= 640 ? 128 : 112;
    const margin = 12;
    let left = event.clientX + 16;
    let top = event.clientY + 16;

    if (left + previewWidth + margin > window.innerWidth) {
      left = event.clientX - previewWidth - 16;
    }
    if (top + previewHeight + margin > window.innerHeight) {
      top = window.innerHeight - previewHeight - margin;
    }

    setOptionHoverPreview({
      image,
      left: Math.max(margin, left),
      top: Math.max(margin, top),
    });
  }, []);

  // Log country code for debugging
  useEffect(() => {
    debugLog(`Using selected delivery country ${countryCode} for the Print.com price calculation`);
    debugLog(`📍 User locale: ${locale} (VAT calculated for: ${vatCountryCode})`);
  }, [locale, vatCountryCode]);

  // 🌍 MULTI-LANGUAGE: Fetch localized options when locale changes
  useEffect(() => {
    const fetchLocalizedOptions = async () => {
      try {
        setIsLoadingOptions(true);
        debugLog(`🌍 Fetching localized options for product ${productId} in locale ${locale}`);

        const response = await fetch(`/api/products/${productId}/options?locale=${locale}`);

        if (!response.ok) {
          throw new Error('Failed to fetch localized options');
        }

        const data = await response.json();

        debugLog(`✅ Received localized options:`, {
          language: data.language,
          optionsCount: data.options?.length || 0,
          hasSpecs: !!data.specifications,
        });

        // Update localized options and specifications
        if (data.options) {
          setLocalizedOptions(
            sanitizeProductOptionValues(applyProductSpecificOptionFixes(
              normalizeConsumerOptions(data.options, locale, data.specifications || specifications),
              providerProductId || productSlug,
              data.specifications || specifications
            ))
          );
        }
        if (data.specifications) {
          setLocalizedSpecifications(
            withOpenCustomQuantitySettings(providerType, data.options || options,
              applyProductSpecificSpecificationFixes(data.specifications, providerProductId || productSlug))
          );
        }
      } catch (error) {
        console.error('❌ Error fetching localized options:', error);
        // Fallback to original options on error
        setLocalizedOptions(
          sanitizeProductOptionValues(applyProductSpecificOptionFixes(
            normalizeConsumerOptions(options, locale, specifications),
            providerProductId || productSlug,
            specifications
          ))
        );
        setLocalizedSpecifications(
          withOpenCustomQuantitySettings(providerType, options,
            applyProductSpecificSpecificationFixes(specifications, providerProductId || productSlug))
        );
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchLocalizedOptions();
  }, [productId, locale, options, specifications]);

  useEffect(() => {
    const optionKeys = Array.from(new Set(
      localizedOptions
        .filter((option) => option.values && option.values.length > 0)
        .map((option) => option.id || option.name)
        .filter(Boolean)
    ));

    if (optionKeys.length === 0) {
      setOptionVisuals({});
      setOptionVisualsSettled(true);
      return;
    }

    let cancelled = false;
    // Vangnet: als /api/option-visuals hangt mag de configurator niet eeuwig in
    // skeleton-stand blijven staan.
    const settleTimer = window.setTimeout(() => setOptionVisualsSettled(true), 2500);

    const fetchOptionVisuals = async () => {
      try {
        // Een gebundelde call voor alle optiesleutels; dit waren er 10+ los.
        const params = new URLSearchParams({ optionKeys: optionKeys.join(','), locale });
        if (productId) params.set('productId', productId);
        const response = await fetch(`/api/option-visuals?${params.toString()}`, { cache: 'no-store' });
        const data = response.ok ? await response.json() : { visuals: [] };
        const allVisuals: OptionVisual[] = Array.isArray(data.visuals) ? data.visuals : [];

        if (cancelled) return;

        const next: Record<string, OptionVisual> = {};
        allVisuals.forEach((visual: OptionVisual) => {
          if (!visual.optionKey || !visual.optionValue) return;
          next[visualMapKey(visual.optionKey, visual.optionValue)] = visual;
        });
        setOptionVisuals(next);
      } catch (error) {
        console.error('Could not load option visuals:', error);
      } finally {
        setOptionVisualsSettled(true);
      }
    };

    fetchOptionVisuals();

    return () => {
      cancelled = true;
      window.clearTimeout(settleTimer);
    };
  }, [localizedOptions, productId, locale]);

  // Skeleton zolang de gelokaliseerde opties of hun visuals nog onderweg zijn:
  // labels zonder inhoud en een dropdown die omklapt naar tegels lazen als een
  // sprong in de layout.
  const isOptionSkeletonVisible = isLoadingOptions || !optionVisualsSettled;

  const displayOptions = useMemo(() => {
    return localizedOptions.map((option) => {
      if (!option.values) return option;
      // "Geen contourlijnen" is geen klantkeuze: dat volgt uit recht afsnijden.
      const optionValues = option.id === CONTOUR_LINES_OPTION_ID
        ? option.values.filter((value) => !isContourNoneValue(value.value))
        : option.values;

      return {
        ...option,
        values: optionValues.map((value) => {
          const visual = optionVisuals[visualMapKey(option.id || option.name, value.value)];
          const existingSvg = isGeneratedLetterSvg(value.visualSvgContent) ? undefined : value.visualSvgContent;
          const existingImage = usableOptionImage(value.image);
          if (!visual) {
            return {
              ...value,
              description: stripMarkdownImages(value.description),
              image: existingImage,
              visualSvgContent: existingImage ? undefined : existingSvg,
            };
          }
          const visualSvg = isGeneratedLetterSvg(visual.svgContent) ? undefined : visual.svgContent || undefined;
          const image = (visual.samplePath ? visual.imageUrl : existingImage) || visual.imageUrl || undefined;

          return {
            ...value,
            image,
            overviewImage: visual.overviewImageUrl || undefined,
            visualSvgContent: visualSvg || (image ? undefined : existingSvg),
            description: stripMarkdownImages(value.description || visual.description || undefined),
          };
        }),
      };
    });
  }, [localizedOptions, optionVisuals]);

  // Eigen formaat = totale drukvlak; bij verpakkingen/3D-maten het uitgevouwen vel.
  const customSizeHintFor = (option: { values?: ProductOptionValue[] }) => customSizeAreaHint(
    locale,
    isPackagingProduct({ productSlug, productCategory: category }) || Boolean(option.values?.some(isThreeDimensionalSizeValue)),
  );

  const changeStep = (step: 'configure' | 'upload' | 'checkout') => {
    setCurrentStep(step);
    recordEditorSessionEvent('step_advanced', { step });
    if (step === 'checkout') {
      recordEditorSessionEvent('checkout_reached');
    }

    setTimeout(() => {
      const target = step === 'upload'
        ? uploadStepRef.current
        : step === 'checkout'
        ? checkoutStepRef.current
        : configureStepRef.current;

      if (!target) return;

      const headerOffset = 120;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 80);
  };
  // Initialize uploadedFile from initialFileUrl if provided (for admin panel)
  const [uploadedFile, setUploadedFile] = useState<any>(() => {
    if (initialUploadedFiles?.length) return { ...initialUploadedFiles[0] };
    if (initialFileUrl) {
      debugLog('📁 Initializing with file URL:', initialFileUrl);
      return {
        id: 'initial',
        url: initialFileUrl,
        fileName: initialFileUrl.split('/').pop() || 'uploaded-file',
        fileSize: 0,
        designData: initialDesignData,
      };
    }
    return null;
  });
  const [uploadedFiles, setUploadedFiles] = useState<any[]>(() => {
    if (initialUploadedFiles?.length) return initialUploadedFiles.map((file) => ({ ...file }));
    if (!initialFileUrl) return [];
    return [{
      id: 'initial',
      url: initialFileUrl,
      fileName: initialFileUrl.split('/').pop() || 'uploaded-file',
      fileSize: 0,
      designData: initialDesignData,
    }];
  });
  const [fileUploaderKey, setFileUploaderKey] = useState(0);
  // Gebruikte printplaatsen (rug, mouwen, ...) van het ontwerp: extra plaatsen
  // kosten een toeslag per stuk die de server in de prijs meeneemt.
  const printfulPlacementsKey = useMemo(() => (
    providerType === 'PRINTFUL' ? usedPrintfulPlacementsFromFiles([...uploadedFiles, uploadedFile]).join(',') : ''
  ), [providerType, uploadedFiles, uploadedFile]);
  const printfulPlacements = useMemo(() => (printfulPlacementsKey ? printfulPlacementsKey.split(',') : []), [printfulPlacementsKey]);
  const [placementSurcharges, setPlacementSurcharges] = useState<Array<{ placement: string; unitPrice: number }>>([]);

  // Skip to checkout step in admin/readOnly mode with existing file
  useEffect(() => {
    if ((readOnly || onSave) && (initialFileUrl || initialUploadedFiles?.length)) {
      debugLog('🔒 ReadOnly/Admin mode with file - skipping to checkout');
      setCurrentStep('checkout');
    }
  }, [readOnly, onSave, initialFileUrl, initialUploadedFiles]);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [quantityPrices, setQuantityPrices] = useState<Record<number, number>>({});
  const [shippingCost, setShippingCost] = useState<number>(0);
  const [shippingIncluded, setShippingIncluded] = useState(false);
  // Prijs kwam uit een handmatige leveranciersroute (calculation.supplierRoute).
  const [optionSupplierRoute, setOptionSupplierRoute] = useState(false);
  const [printComQuoteMeta, setPrintComQuoteMeta] = useState<{
    supplierId: string;
    supplierCountry?: string;
    pricedOptions: Record<string, string | number>;
    deliveryPromise: number;
  } | null>(null);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [isProviderMatrixLoading, setIsProviderMatrixLoading] = useState(false);
  const [latestPriceQuote, setLatestPriceQuote] = useState<{ hash: string; createdAt: string; expiresAt: string; countryCode?: string } | null>(null);
  // Configuratie (stabiele sleutel) waarvoor de laatste CUSTOM-quote is opgehaald. De bestelknop van de
  // Vinyl Lettering Designer voegt alleen toe als die gelijk is aan de huidige configuratie.
  const [latestPriceQuoteKey, setLatestPriceQuoteKey] = useState<string | null>(null);
  const [configurationError, setConfigurationError] = useState<string | null>(null);
  // Nederlandse uitleg als de server een uitgesloten samenstelling zelf heeft
  // gerepareerd (bijvoorbeeld druktype bij een materiaal dat alleen digitaal
  // in full colour gedrukt kan worden). Los van configurationError: dit is
  // geen fout, de prijs klopt gewoon voor de aangepaste samenstelling.
  const [configurationAdjustmentNote, setConfigurationAdjustmentNote] = useState<string | null>(null);
  const [isValidConfiguration, setIsValidConfiguration] = useState(true);
  // Een 429 van de prijsroute (limiet per IP, public-price-guard.ts) is
  // tijdelijk: een eigen melding en na Retry-After vanzelf opnieuw, in plaats
  // van "Ongeldige combinatie" met een offerteformulier.
  const [priceRateLimit, setPriceRateLimit] = useState<{ seconds: number; longWait: boolean } | null>(null);
  const [priceRetryNonce, setPriceRetryNonce] = useState(0);
  const priceRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlePriceRateLimited = useCallback((error: PriceRateLimitedError) => {
    const seconds = autoRetryDelaySeconds(error.retryAfterSeconds);
    setPriceRateLimit({ seconds, longWait: (error.retryAfterSeconds ?? 0) > MAX_AUTO_RETRY_SECONDS });
    setLatestPriceQuote(null);
    setIsValidConfiguration(false);
    setConfigurationError(null);
    setIsPriceLoading(false);
    setIsProviderMatrixLoading(false);
    if (priceRetryTimerRef.current) clearTimeout(priceRetryTimerRef.current);
    priceRetryTimerRef.current = setTimeout(() => {
      priceRetryTimerRef.current = null;
      setPriceRateLimit(null);
      setPriceRetryNonce((current) => current + 1);
    }, seconds * 1000 + 250);
  }, []);
  useEffect(() => () => {
    if (priceRetryTimerRef.current) clearTimeout(priceRetryTimerRef.current);
  }, []);
  // Een geslaagde prijs na een 429 (bv. de klant paste het ontwerp aan voordat de
  // wachttijd om was): de limiet is voorbij. Zonder dit bleef priceRateLimit staan
  // tot de timer afliep, en weigerde de bestel-gate van de designer elke bestelling
  // met de 429-melding (T6-review M1). De timer gaat ook weg, anders volgt er nog een
  // overbodige herhaalde prijsaanvraag.
  const clearPriceRateLimit = useCallback(() => {
    if (priceRetryTimerRef.current) {
      clearTimeout(priceRetryTimerRef.current);
      priceRetryTimerRef.current = null;
    }
    setPriceRateLimit(null);
  }, []);
  const [quoteRequestForm, setQuoteRequestForm] = useState({ name: '', email: '', phone: '', company: '', deliveryDate: '', message: '' });
  const [quoteRequestSubmitting, setQuoteRequestSubmitting] = useState(false);
  const [quoteRequestSent, setQuoteRequestSent] = useState(false);
  // Het offerteformulier staat dicht. Het hoort een zijdeur te zijn naast de
  // bestelknop, geen blok van zeven velden dat de halve kolom vult op elke
  // productpagina. Alleen als bestellen niet kan staat het meteen open, want
  // dan is het de enige weg.
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  // Kreeg de klant zijn offerte direct in de mail, of kijkt er nog iemand naar?
  // Dat verschil hoort hij te zien, anders wacht hij op iets wat er al is.
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);
  const [validatingOptions, setValidatingOptions] = useState<Set<string>>(new Set());
  const [invalidValues, setInvalidValues] = useState<Record<string, Set<string>>>({});

  // Canvas edge type - separate from configuration (not sent to Print.com)
  const [canvasEdgeType, setCanvasEdgeType] = useState<string>('wrapped');

  // Product accessories (ophangsystemen, standaards, etc.)
  interface AccessoryProduct {
    id: string;   // Unique Print.com accessory variant ID
    sku: string;
    slug?: string;
    name: string;
    description?: string;
    image?: string;
    basePrice?: number;
    priceByQuantity?: Record<string, {
      unitPriceExVat: number;
      totalPriceExVat: number;
      printcomConfiguration?: Record<string, unknown>;
    }>;
    printcomConfiguration?: Record<string, unknown>;
    isPrintable?: boolean;
  }
  const [accessories, setAccessories] = useState<AccessoryProduct[]>([]);
  const [selectedAccessories, setSelectedAccessories] = useState<Set<string>>(new Set());
  const [accessoryQuantities, setAccessoryQuantities] = useState<Record<string, number>>({});
  // Drukbestand per printbaar accessoire. Wordt direct bij het kiezen
  // geüpload; een browser-File overleeft de stap naar de winkelwagen niet.
  const [accessoryUploads, setAccessoryUploads] = useState<Record<string, AccessoryUploadState | undefined>>({});
  const accessoryUploadRequest = useRef(0);
  const accessoryCopy = accessoryUploadCopy(locale);
  const clearAccessoryUpload = useCallback((accessoryId: string) => {
    // Zonder staat is een nog lopende upload niet meer 'actueel' (zie isCurrent)
    // en overschrijft hij de lege keuze dus niet.
    setAccessoryUploads(p => { const n = { ...p }; delete n[accessoryId]; return n; });
  }, []);
  const selectAccessoryFile = useCallback(async (accessoryId: string, file: File) => {
    const requestId = ++accessoryUploadRequest.current;
    const isCurrent = (state: AccessoryUploadState | undefined) => state?.requestId === requestId;
    setAccessoryUploads(p => ({ ...p, [accessoryId]: { status: 'uploading', fileName: file.name, requestId } }));
    try {
      const uploaded = await uploadAccessoryArtwork(file, {
        productId: accessoryId,
        copy: { failed: accessoryCopy.failed, invalid: artworkCopy(locale).invalid, timedOut: accessoryCopy.timedOut },
      });
      setAccessoryUploads(p => isCurrent(p[accessoryId])
        ? { ...p, [accessoryId]: { status: 'done', fileName: uploaded.fileName, requestId, file: uploaded } }
        : p);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : accessoryCopy.failed;
      setAccessoryUploads(p => isCurrent(p[accessoryId])
        ? { ...p, [accessoryId]: { status: 'error', fileName: file.name, requestId, error: message } }
        : p);
    }
  }, [accessoryCopy, locale]);
  const [accessoriesLoading, setAccessoriesLoading] = useState(false);

  // Detect if product is printable by checking printing option
  const [isPrintable, setIsPrintable] = useState<boolean>(() => {
    // First check specifications.requiresPrintFile (most reliable)
    if (localizedSpecifications?.requiresPrintFile === false) {
      return false;
    }
    return isPrintProduct;
  });

  // Fetch product accessories from Print.com when product SKU changes
  useEffect(() => {
    // Exact/local price matrices intentionally hide the provider SKU from the
    // client. The accessory route also accepts our public product slug, so use
    // that as a safe lookup key instead of silently dropping linked products.
    const accessoryLookupKey = providerProductId || productSlug;
    if (!accessoryLookupKey) {
      setAccessories([]);
      setSelectedAccessories(new Set());
      return;
    }

    let cancelled = false;
    setAccessoriesLoading(true);

    const accessoryParams = new URLSearchParams({ locale: locale.split('-')[0] || 'nl' });
    if (configuration.urgency) accessoryParams.set('urgency', String(configuration.urgency));
    fetch(`/api/printcom/accessories/${encodeURIComponent(accessoryLookupKey)}?${accessoryParams.toString()}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && data.accessories && data.accessories.length > 0) {
          setAccessories(filterCompatiblePrintComAccessories(
            accessoryLookupKey,
            data.accessories,
          ));
        } else {
          setAccessories([]);
        }
      })
      .catch(() => {
        if (!cancelled) setAccessories([]);
      })
      .finally(() => {
        if (!cancelled) setAccessoriesLoading(false);
      });

    return () => { cancelled = true; };
  }, [providerProductId, productSlug, locale, configuration.urgency]);

  useEffect(() => {
    // De keuze van de klant gaat voor op de opgeslagen vlag.
    //
    // `requiresPrintFile: false` werd hier als laatste woord behandeld: geen
    // editor, geen uploadstap, klaar. Bij peesdoek stond die vlag op false
    // terwijl het product maar een drukoptie heeft, 4/0 full colour, en dus
    // altijd een bestand nodig heeft. Vijf producten hebben datzelfde probleem
    // en zijn zo niet te bestellen.
    //
    // Bij 78 andere producten is bedrukking wel echt optioneel (printtype 00 is
    // Geen). Ook daar klopt een vaste vlag niet: kiest de klant 4/0, dan moet
    // de uploadstap er alsnog zijn. Daarom telt hieronder de gekozen waarde, en
    // valt de vlag alleen terug als er niets te kiezen is.
    const printingOption = localizedOptions.find(opt =>
      opt.id === 'printing' ||
      // printtype is de Print.com-variant met codes: 00 is nul kleuren dus
      // onbedrukt, 40 is 4/0 full colour. Die stond hier niet bij, waardoor de
      // hele afweging bij dit soort producten werd overgeslagen.
      opt.id === 'printtype' ||
      opt.name?.toLowerCase().includes('bedrukking') ||
      opt.name?.toLowerCase().includes('printing')
    );

    if (localizedSpecifications?.requiresPrintFile === false && !printingOption) {
      debugLog('ℹ️ Product does not require print file (requiresPrintFile: false)');
      setIsPrintable(false);
      return;
    }

    // De uploadvakken van het product zeggen zelf welke keuzes een bestand
    // nodig hebben. Valt er bij deze keuze geen enkel vak over (paalbanner
    // "Alleen houder"), dan is er niets te drukken.
    if (uploadSlotsRuleOutPrintFile(localizedSpecifications?.fileUpload?.slots, configuration)) {
      setIsPrintable(false);
      return;
    }

    debugLog('🔍 Printing detection:', {
      found: !!printingOption,
      optionId: printingOption?.id,
      optionName: printingOption?.name,
      values: printingOption?.values?.map(v => ({ label: v.label, value: v.value })),
      selectedValue: printingOption ? configuration[printingOption.id] : undefined
    });

    if (printingOption && printingOption.values) {
      const selectedValue = configuration[printingOption.id] as string;
      const currentValue = printingOption.values.find(v => v.value === selectedValue);

      debugLog('🔍 Checking printing value:', {
        selectedValue,
        currentValue,
        label: currentValue?.label,
        isNo: currentValue?.value?.toLowerCase() === 'no',
        isNee: currentValue?.value?.toLowerCase() === 'nee',
        hasGeen: currentValue?.label?.toLowerCase().includes('geen'),
        hasNietMogelijk: currentValue?.label?.toLowerCase().includes('niet mogelijk')
      });

      // Check if printing is set to "no" or "nee"
      if (currentValue?.value?.toLowerCase() === 'no' ||
          currentValue?.value?.toLowerCase() === 'nee' ||
          // Print.com telt kleuren: 00 is nul kleuren voor en achter, dus
          // onbedrukt. Zonder deze regel gold 00 als een gewone drukkeuze.
          String(currentValue?.value ?? '') === '00' ||
          currentValue?.label?.toLowerCase().includes('geen') ||
          currentValue?.label?.toLowerCase().includes('niet mogelijk') ||
          currentValue?.label?.toLowerCase() === 'onbedrukt') {
        debugLog('✅ Setting isPrintable to FALSE');
        setIsPrintable(false);
      } else if (selectedValue) {
        debugLog('✅ Setting isPrintable to TRUE');
        setIsPrintable(true);
      }
    } else {
      // Priority 3: No printing option found, default to isPrintProduct value
      debugLog('ℹ️ No printing option found, using isPrintProduct:', isPrintProduct);
      setIsPrintable(isPrintProduct);
    }
  }, [configuration, localizedOptions, localizedSpecifications, isPrintProduct]);

  // Extract excludes from localized specifications
  const excludes = localizedSpecifications?.excludes || [];
  const selectedCustomSizeLimits = (option: ProductOption | undefined, selectedConfiguration = configuration) => {
    const custom = option?.values?.find((value) => value.customSizes || String(value.value) === 'custom');
    const effective = getConfigurationWithDefaults(selectedConfiguration);
    return customSizeLimitsForConfiguration(custom?.customSizes || custom?.metadata?.customSizes,
      effective, configurationHasForeignSupplierValue(localizedOptions, effective) ? [] : excludes);
  };
  const customSizeInputReady = (selectedConfiguration: Record<string, string | number>) => {
    const option = localizedOptions.find((item) => item.id === 'size');
    const size = String(selectedConfiguration.size || '');
    const selected = option?.values?.find((value) => String(value.value) === size);
    if (size !== 'custom' && !size.startsWith('custom:') && !selected?.customSizes) return true;
    const encoded = size.split(':');
    const width = Number(encoded.length === 3 ? encoded[1] : selectedConfiguration.size_width);
    const height = Number(encoded.length === 3 ? encoded[2] : selectedConfiguration.size_height);
    return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0 &&
      (configurationHasForeignSupplierValue(localizedOptions, selectedConfiguration) ||
        isWithinCustomSizeLimits(width, height, selectedCustomSizeLimits(option, selectedConfiguration)));
  };
  // Alle contourwaarden inclusief de "geen"-waarde: displayOptions laat die weg,
  // en zonder "geen" lijkt elk product altijd een aantal te vragen.
  const contourOptionValues = useMemo(() => (
    localizedOptions.find((option) => option.id === CONTOUR_LINES_OPTION_ID)?.values || []
  ).map((entry) => String(entry.value)), [localizedOptions]);
  const isStickerProduct = ['muursticker', 'vinylsticker', 'meubelsticker'].includes(productSlug);
  // Ooit kregen vier stickerslugs geforceerde dropdowns (zelfde noodverband
  // als het verwijderde offertepaneel). Iedereen gebruikt nu dezelfde UI,
  // inclusief de eigen-formaat-invoer in de formaatkiezer.
  const forceDropdownRadioOptions = false;
  const isFlyersProduct = productSlug === 'flyers' || providerProductId === 'flyers';
  const isFoldersProduct = productSlug === 'folder' || providerProductId === 'folders';
  const isPhotoPosterProduct =
    productSlug === 'foto-op-poster' ||
    providerProductId === 'photo-poster';
  const isPostersProduct =
    productSlug === 'posters' ||
    isPhotoPosterProduct ||
    providerProductId === 'posters' ||
    providerProductId === 'photo-poster';
  const isBusinessCardsProduct = productSlug === 'visitekaartjes' || providerProductId === 'businesscards';
  const applyBusinessCardSafeDefaults = (config: Record<string, string | number>) =>
    isBusinessCardsProduct ? applyBusinessCardSafeDefaultsTo(config) : config;
  const isPostcardsProduct = productSlug === 'ansichtkaarten' || providerProductId === 'postcards';
  const isBookmarksProduct = productSlug === 'boekenleggers' || providerProductId === 'bookmarks';
  const isBannerProduct = productSlug === 'spandoek' || providerProductId === 'banners';
  const isDisplayCubeProduct = productSlug === 'display-kubus' || providerProductId === 'display-cube';
  const isDisplayCardProduct = productSlug === 'displaykaart' || providerProductId === 'display-cards';
  const isFenceBannerProduct = ['dranghekdoeken', 'hekwerkbanners'].includes(productSlug) ||
    ['crowd-fence-banners', 'site-fence-banners'].includes(providerProductId || '');
  const shouldForceShowHiddenOption = (option: Pick<ProductOption, 'id'>): boolean =>
    (isDisplayCubeProduct && option.id === 'product_size') ||
    (isDisplayCardProduct && option.id === 'printtype');
  const carwrapIdentifier = `${productSlug || ''} ${providerProductId || ''} ${productName || ''}`.toLowerCase();
  const isCarwrapProduct = carwrapIdentifier.includes('carwrap') || carwrapIdentifier.includes('car wrap');
  const isPlexiglasProduct = ['acrylglas-print', 'plexiglas'].includes(String(productSlug || '')) ||
    ['acrylic-photo-prints', 'plexiglass'].includes(String(providerProductId || ''));
  const isHdMetalProduct = productSlug === 'hd-metal-print' || providerProductId === 'hd-metal-prints';
  const isPlateMaterialProduct = category === 'plaatmateriaal' || (
    localizedSpecifications?.source === 'printcom' &&
    localizedSpecifications?.requiresPrintFile === true
  );
  const isWayfindingSignProduct = ['bewegwijzeringsbord', 'wayfinding-sign', 'directional-sign', 'wegweiser-schild', 'panneau-de-signalisation', 'sportveldbord', 'sports-field-board', 'sportplatzschild', 'gevelbord', 'facade-sign', 'fassadenschild', 'reclamebord', 'advertising-sign', 'werbeschild'].includes(String(productSlug || '').toLowerCase());
  const isWoodTextileFrameProduct = ['houten-textielframe', 'wooden-textile-frame', 'textilrahmen-holz'].includes(String(productSlug || '').toLowerCase());
  const isTwoSidedTextileProduct = ['sierkussen', 'decorative-cushion', 'dekokissen', 'buitenkussen', 'outdoor-cushion', 'aussenkissen', 'zitzak', 'beanbag', 'sitzsack', 'stoepbord-met-watertank', 'pavement-sign-with-water-tank', 'kundenstopper-mit-wassertank', 'akoestische-roomdivider', 'acoustic-room-divider', 'akustik-raumteiler', 'akoestisch-bureauscherm', 'acoustic-desk-divider', 'akustischer-schreibtischteiler'].includes(String(productSlug || '').toLowerCase());
  const isBanierProduct = ['banier', 'banner-flag', 'bannerfahne'].includes(String(productSlug || '').toLowerCase());
  const [useSameDesignBothSides, setUseSameDesignBothSides] = useState(false);
  const selectedPrintType = String(configuration.printtype || '');
  const isDisplayCardDoubleSided = isDisplayCardProduct && selectedPrintType === '44';
  const isDoubleSidedFenceBanner = isFenceBannerProduct && String(configuration.size || '').includes('double_sided');
  const allFileUploadSlots = Array.isArray(localizedSpecifications?.fileUpload?.slots)
    ? localizedSpecifications.fileUpload.slots
    : [];
  // Sommige producten combineren bedrukte en onbedrukte onderdelen op één
  // pagina. Slotregels in de productdata houden de uploader dan in sync met
  // de gekozen samenstelling (bijv. één bestand voor enkelzijdig, twee voor
  // dubbelzijdig en geen bestand voor alleen een houder).
  const fileUploadSlots = visibleFileUploadSlots<any>(allFileUploadSlots, configuration);
  const isPageUploadProduct = localizedSpecifications?.fileUpload?.pageMode === true;
  const designSlotCount = fileUploadSlots.length > 0 ? fileUploadSlots.length : (
    isTwoSidedTextileProduct ||
    (isBanierProduct && String(configuration.application || '') === 'facade') ||
    (isWayfindingSignProduct && String(configuration.print_sides || '') === 'double') ||
    ((isFlyersProduct || isFoldersProduct || isPostersProduct || isBusinessCardsProduct || isPostcardsProduct) && ['44', '55_full_color_plus_white'].includes(selectedPrintType)) ||
    isDoubleSidedFenceBanner ||
    (isBannerProduct && selectedPrintType === '44')
  ) ? 2 : 1;
  const fileUploadIntro = localizedSpecifications?.fileUpload?.intro;
  const hasExactPrintComCatalogCoverage =
    providerType === 'PRINTCOM' &&
    localizedSpecifications?.printComPricingCoverage?.complete === true &&
    localizedSpecifications?.printComPricingCoverage?.configurationScope !== 'validated-on-demand';
  const requiredDesignCount = useSameDesignBothSides && designSlotCount === 2 ? 1 : designSlotCount;
  const uploadedDesignFiles = uploadedFiles.filter(file => file && (!isBoogProduct(productSlug) || boogFileMatchesMaterial(file, configuration.material))).slice(0, requiredDesignCount);
  const designsReady = uploadedDesignFiles.length >= requiredDesignCount;
  /**
   * EEN BESTELKNOP. Zolang de Vinyl Lettering Designer actief is, is zijn eigen
   * bestelknop de enige: die maakt het drukbestand, registreert het ontwerp en
   * laat daarna deze configurator het artikel toevoegen (onRequestAddToCart
   * hieronder). De configurator toont dan geen winkelwagenknop en ook geen
   * materiaal- en aantalkeuze (die staan in de designer; twee kiezers lopen uit
   * elkaar). Alleen bij een al geregistreerd ontwerp (vinylDesigner.registeredDesign)
   * is er geen designer en bestelt de configurator zelf, zoals bij de oude tool
   * (designMadeInline: knop zodra het bestand er is).
   */
  const [vinylDesignerLoadFailed, setVinylDesignerLoadFailed] = useState(false);
  // 'unavailable' (laadfout, of product zonder copies-optie: dan prijzen host en
  // configurator niet dezelfde aanvraag): geen designer, wel een melding met
  // "Probeer opnieuw" (laadfout) en een weg naar contact. De configuratorknop
  // blijft dan ook weg: zonder ontwerp is er geen maat en geen bestand.
  const vinylDesignerMode = resolveVinylDesignerMode({
    hasVinylDesigner: Boolean(vinylDesigner),
    registeredDesign: Boolean(vinylDesigner?.registeredDesign),
    readOnly: Boolean(readOnly),
    hasCopiesOption: vinylDesigner?.hasCopiesOption === true,
    loadFailed: vinylDesignerLoadFailed,
  });
  const designerOwnsOrder = vinylDesignerMode === 'designer' || vinylDesignerMode === 'unavailable';
  const primaryAction = resolveConfiguratorPrimaryAction({ step: currentStep, isPrintable, designsReady, designMadeInline: Boolean(vinylDesigner), designerOwnsOrder });
  // De oude stap 3 meldde 'checkout_reached' aan de editor-sessiefunnel. Die
  // stap is weg; hetzelfde moment is nu: ontwerp klaar in de ontwerpstap.
  const checkoutReachedRecorded = useRef(false);
  useEffect(() => {
    if (currentStep !== 'upload' || !designsReady || checkoutReachedRecorded.current) return;
    checkoutReachedRecorded.current = true;
    recordEditorSessionEvent('checkout_reached');
  }, [currentStep, designsReady]);
  // Na "Klaar" in de editor sluit die; breng de winkelwagenknop dan in beeld.
  const scrollToAddToCart = useCallback(() => {
    window.setTimeout(() => {
      document.querySelector('[data-add-to-cart-cta]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
  }, []);
  const orderDesignFiles = useSameDesignBothSides && designSlotCount === 2 && uploadedDesignFiles[0]
    ? [uploadedDesignFiles[0], uploadedDesignFiles[0]]
    : uploadedDesignFiles.slice(0, designSlotCount);
  const getDimensionValidation = (config: Record<string, string | number>) =>
    evaluateDimensionConstraint(localizedSpecifications?.dimensionConstraint, config);
  const dimensionValidation = getDimensionValidation(configuration);
  const getNumberOptionBounds = (option: ProductOption) => {
    const constraint = localizedSpecifications?.dimensionConstraint;
    const widthOptionId = String(constraint?.widthOptionId || 'width_cm');
    const heightOptionId = String(constraint?.heightOptionId || 'height_cm');
    const matchingRule = findMatchingDimensionRule(constraint, configuration);
    if (!matchingRule || ![widthOptionId, heightOptionId].includes(option.id)) {
      return { min: option.min, max: option.max };
    }
    const bounds = getDimensionRuleInputBounds(matchingRule, option.id === widthOptionId ? 'width' : 'height');
    return { min: bounds.min ?? option.min, max: bounds.max ?? option.max };
  };
  const customerVisibleStickerMaterials: Record<string, Set<string>> = {
    vinylsticker: new Set([
      'polymeer_white_gloss_orajet_3551g',
      'polymeer_transparent_orajet_3551g000',
    ]),
    muursticker: new Set([
      'dot_white_gloss_prime_quick_stick',
      'polymeer_white_matt_gf285_roughmark',
    ]),
    meubelsticker: new Set([
      'monomeric_white_removable_matt_biond',
    ]),
  };
  const customerVisibleStickerFinishes = new Set([
    'geen',
    'no_laminate',
    'polymeer_gloss_lamination_oraguard_215',
    'polymeer_matt_lamination_oraguard_215',
  ]);
  const customerHiddenStickerFinishValues = new Set([
    'floor_lamination_pvc_free_gf_213',
    'floor_lamination_monomeer_anti_skid_oraguard_250as_120_mic',
    'floor_lamination_monomeer_anti_skid_oraguard_255as_170_mic',
    'pavement_cast_mat_laminate_3m_3647',
    'luster_cast_mat_laminate_3m_3645',
    'whiteboard_lamination_prime',
  ]);
  const optionalNoneAliases: Record<string, Set<string>> = {
    finish: new Set(['none', 'geen', 'no_laminate', 'no_lamination', 'without_lamination']),
    drillholes: new Set(['none', 'geen', 'no_drillholes']),
    rounded_corners: new Set(['none', 'geen', 'no_rounded_corners']),
  };

  // Waarden die de server als tijdelijk niet leverbaar markeert (zie
  // src/lib/printcom/value-availability.ts). Rechtstreeks uit de options-prop,
  // zodat vertaling of lokale overrides de markering niet kunnen kwijtraken.
  const temporarilyUnavailableValues = useMemo(() => {
    const keys = new Set<string>();
    for (const source of [options, localizedOptions] as any[]) {
      if (!Array.isArray(source)) continue;
      for (const option of source) {
        for (const entry of Array.isArray(option?.values) ? option.values : []) {
          if (entry?.unavailable === true) keys.add(`${option.id}=${String(entry.value)}`);
        }
      }
    }
    return keys;
  }, [options, localizedOptions]);
  const isTemporarilyUnavailableValue = (optionId: string, value: unknown) =>
    temporarilyUnavailableValues.size > 0 && temporarilyUnavailableValues.has(`${optionId}=${String(value)}`);
  // De maatkiezer kent geen grijze stand; daar vallen gemarkeerde maten weg,
  // net als live ongeldige waarden.
  const invalidValuesWithTemporarilyUnavailable = (optionId: string) => {
    const prefix = `${optionId}=`;
    const marked = [...temporarilyUnavailableValues].filter((key) => key.startsWith(prefix)).map((key) => key.slice(prefix.length));
    if (!marked.length) return invalidValues[optionId];
    return new Set([...(invalidValues[optionId] || []), ...marked]);
  };

  const isConsumerHiddenValue = (optionId: string, value: any): boolean => {
    const optionValue = String(value?.value || '');
    const label = String(value?.label || '').toLowerCase();

    // In de admin of door de leverbaarheidscontrole verborgen waarden. De
    // materiaaltegels toonden PaperWise na herladen weer, omdat niets in de
    // storefront naar deze vlag keek.
    if (value?.hidden === true || value?.active === false) return true;
    // Nooit zichtbaar, bij geen enkel product (zie forbidden-option-values.ts).
    if (isForbiddenOptionValue(optionId, optionValue)) return true;

    if (isStickerProduct && optionId === 'material') {
      const visibleMaterials = customerVisibleStickerMaterials[productSlug];
      return Boolean(visibleMaterials && !visibleMaterials.has(optionValue));
    }

    // Klanten zien bij deze stickers maar een paar materialen (meubelsticker
    // alleen Biond). Een afwerking die met geen enkel zichtbaar materiaal kan,
    // zoals applicatiefolie op Biond, sprong na het kiezen terug of liet de
    // prijstabel falen. Die afwerking tonen we dan niet.
    if (isStickerProduct && optionId === 'custom_shape') {
      const visibleMaterials = customerVisibleStickerMaterials[productSlug];
      if (visibleMaterials && visibleMaterials.size > 0) {
        const shape = normalizeCustomShapeProviderValue(optionValue);
        const pairExcluded = (material: string) => (excludes || []).some((rule: any) =>
          Array.isArray(rule) && rule.length === 2
          && rule.some((part: any) => part?.property === 'custom_shape' && (part.options || []).map(String).includes(shape))
          && rule.some((part: any) => part?.property === 'material' && (part.options || []).map(String).includes(material)));
        if ([...visibleMaterials].every(pairExcluded)) return true;
      }
    }

    if (isStickerProduct && optionId === 'finish') {
      if (!customerVisibleStickerFinishes.has(optionValue)) return true;
      return (
        customerHiddenStickerFinishValues.has(optionValue) ||
        optionValue.includes('floor_lamination') ||
        optionValue.includes('pavement') ||
        optionValue.includes('anti_skid') ||
        label.includes('traffic graffic') ||
        label.includes('vloerlaminaat') ||
        label.includes('anti-skid') ||
        label.includes('whiteboard')
      );
    }

    return false;
  };

  // Helper to get a hidden option's value from specs (defaultOptions or autoSetOptions).
  // Used by exclude rules to evaluate conditions on hidden options correctly.
  const getSpecDefaultValue = (property: string) => {
    const specs = localizedSpecifications;
    const value = specs?.defaultOptions?.[property]
      ?? specs?.autoSetOptions?.[property]
      ?? (specifications as any)?.defaultOptions?.[property]
      ?? (specifications as any)?.autoSetOptions?.[property];
    return value !== undefined && value !== null ? String(value) : undefined;
  };

  const getCatalogDefaultValue = (property: string) => {
    const specs = localizedSpecifications;
    const value = specs?.defaultOptions?.[property]
      ?? (specifications as any)?.defaultOptions?.[property]
      ?? specs?.autoSetOptions?.[property]
      ?? (specifications as any)?.autoSetOptions?.[property];
    if (value === undefined || value === null || value === '') return undefined;
    return sanitizeOptInAddonDefault(property, value as string | number, localizedOptions as any);
  };

  const shouldSkipStaleHiddenPrintComDefault = (option: any) =>
    hasExactPrintComCatalogCoverage &&
    option?.hidden === true &&
    getCatalogDefaultValue(option.id) === undefined;

  // 🚫 Helper function to check if an option value is excluded based on current configuration
  const isValueExcluded = (optionId: string, value: string, currentConfig: Record<string, string | number> = {}, evaluateCustomShape = false): boolean => {
    if (rejectedSelections.has(optionSelectionKey(currentConfig, optionId, value, countryCode))) return true;
    // Een tijdelijk niet leverbare waarde telt overal als uitgesloten, zodat de
    // bestaande autocorrectie en de conflictoplosser hem nooit kiezen.
    if (isTemporarilyUnavailableValue(optionId, value)) return true;

    if (optionId === 'usage') return false;
    const providerValue = optionId === 'custom_shape' ? normalizeCustomShapeProviderValue(value) : value;
    const normalizedValue = String(providerValue).toLowerCase();
    // Synthetic optional defaults may be absent from catalog lists, but must
    // still obey supplier combination rules (e.g. printed HPL needs laminate).
    const isOptionalNone = optionalNoneAliases[optionId]?.has(normalizedValue) === true;
    // Another supplier's value is never in Print.com's priced/allowed lists;
    // only the stored excludes below the catalogue checks apply to it.
    const isForeignRouted = isForeignSupplierSelection(localizedOptions, optionId, value);
    const catalogAllowedValues = isForeignRouted ? null : getPrintComCatalogAllowedValueSet(localizedSpecifications, optionId);
    if (
      catalogAllowedValues &&
      !isOptionalNone &&
      optionId !== 'copies' &&
      !catalogAllowedValues.has(normalizedValue)
    ) {
      return true;
    }
    const fullPriceProperties = localizedSpecifications?.fullPriceProperties;
    if (
      localizedSpecifications?.source === 'printcom' &&
      !isOptionalNone &&
      !isForeignRouted &&
      localizedSpecifications?.printComPricingCoverage?.configurationScope !== 'validated-on-demand' &&
      optionId !== 'copies' &&
      fullPriceProperties &&
      Object.prototype.hasOwnProperty.call(fullPriceProperties, optionId)
    ) {
      const allowedValues = normalizeFullPricePropertyValues(fullPriceProperties[optionId]).map(String);
      if (allowedValues.length > 0 && !allowedValues.includes(normalizedValue)) {
        return true;
      }
    }
    // Materiaal is een primaire keuze, maar moet WEL symmetrisch door de
    // exclude-regels lopen. `resolveExcludedSelection` houdt een materiaal
    // zichtbaar zodra er ergens een geldige combinatie voor bestaat en laat
    // de botsende maat/afwerking vervolgens meebewegen. De vroegere generieke
    // `return false` voor material omzeilde die oplosser volledig: bij de
    // fleecedeken werkte maat -> materiaal daardoor wel, maar materiaal -> maat
    // niet en bleef een onprijsbare combinatie geselecteerd.
    if (
      isFenceBannerProduct &&
      optionId === 'material' &&
      String(currentConfig.size || configuration.size || '').includes('double_sided')
    ) {
      return providerValue !== 'pes_outdoor';
    }
    if (isPostersProduct && optionId === 'material') return false;
    if (isPostersProduct && !isOptionalNone) {
      const selectedMaterial = String(currentConfig.material || configuration.material || '');
      const selectedPrintType = String(currentConfig.printtype || configuration.printtype || '');
      const isNeonPaper = selectedMaterial.startsWith('95gr_neon_papier');

      if (optionId === 'printtype') {
        if (providerValue === '10_pms_black') return !isNeonPaper;
        if (isNeonPaper) return providerValue !== '10_pms_black';
      }

      if (optionId === 'finish' && (isNeonPaper || selectedPrintType === '10_pms_black')) {
        return providerValue !== 'geen';
      }

      if (
        optionId === 'finish' &&
        isPosterMaterialWithoutFinish(selectedMaterial) &&
        !isNoPosterFinishValue(providerValue)
      ) {
        return true;
      }
    }
    // Vormen blijven in de filter altijd zichtbaar. Bij een klik moet de
    // conflictoplosser de vormregels wel zien: anders bleef bij meubelsticker
    // het materiaal Biond staan na het kiezen van applicatiefolie (een
    // uitgesloten combinatie), en faalde de hele prijstabel.
    if (optionId === 'custom_shape' && !evaluateCustomShape) return false;
    if (!excludes || excludes.length === 0) return false;

    const getSelectedValue = (property: string) => {
      const configuredValue = currentConfig?.[property];
      if (configuredValue !== undefined && configuredValue !== '') return configuredValue;

      const option = localizedOptions.find((item) => item.id === property || item.name === property);
      if (option?.default !== undefined && option.default !== '') return option.default;

      const firstValue = option?.values?.find((item) => !item.showIf) || option?.values?.[0];
      return firstValue?.value;
    };

    const candidateConfig = { ...currentConfig, [optionId]: providerValue };
    return isExcludedByConfiguredOptionRules(excludes, (property) => {
      const opt = localizedOptions.find(o => o.id === property || o.name === property);
      return {
        hidden: opt?.hidden,
        value: candidateConfig[property] ?? (
          opt?.hidden ? getSpecDefaultValue(property) : getSelectedValue(property)
        ),
      };
    });
  };

  // 🚫 Symmetrisch met isValueReachableViaBrezo hieronder: printingmethod is
  // voor de klant een verborgen/automatisch gekozen eigenschap (zie
  // HIDDEN_CONSUMER_OPTION_IDS), maar Print.com sluit combinaties van maat en
  // printtype soms volledig uit via die eigenschap (bijvoorbeeld: elke
  // drukmethode is uitgesloten voor A0 + dubbelzijdig bij foto op poster).
  // Een losse exclude-regel die maat en printtype rechtstreeks combineert
  // bestaat niet; die onmogelijkheid volgt pas als je alle
  // drukmethode-waarden naloopt. Dat doen we hier door isValueExcluded te
  // hergebruiken voor elke bekende drukmethode-waarde, in plaats van een
  // tweede uitsluitingsmechanisme te verzinnen.
  const isValueReachableViaPrintingMethod = (
    optionId: string,
    value: string | number,
    currentConfig: Record<string, string | number>
  ): boolean => {
    if (optionId === 'printingmethod') return true;
    if (configurationHasForeignSupplierValue(localizedOptions, { ...currentConfig, [optionId]: value })) return true;
    const methodOption = localizedOptions.find(o => o.id === 'printingmethod' || o.name === 'printingmethod');
    const methodValues = (methodOption?.values || []).filter(v => !v.nullable && v.value !== undefined && v.value !== '');
    if (methodValues.length < 2) return true; // niets om via te bemiddelen -> fail open
    const testConfig = { ...currentConfig, [optionId]: value };
    return methodValues.some(mv => !isValueExcluded('printingmethod', String(mv.value), testConfig));
  };

  /*
      {

      // Check if the condition is met (i.e., the other property has a restricted value)
      const conditionProperty = condition.property;
      const conditionValues = condition.options || [];
      const currentValue = currentConfig[conditionProperty];

      // If the condition is met, check if our value is in the restricted list
      if (currentValue && conditionValues.includes(currentValue.toString())) {
        // Check if the value we're testing is excluded
        const excludedValues = restriction.options || [];
        if (excludedValues.includes(value)) {
          debugLog(`🚫 Value "${value}" excluded for ${optionId} because ${conditionProperty}=${currentValue}`);
          return true;
        }
      }
    }

    return false;
  };

  */

  // Filter options: separate single-value options from multi-value options
  const { visibleOptions, autoSetOptions } = useMemo(() => {
    const visible: ProductOption[] = [];
    const autoSet: Record<string, { label: string; value: string }> = {};

    displayOptions.forEach(option => {
      if (lockedConfiguration && Object.prototype.hasOwnProperty.call(lockedConfiguration, option.id)) return;
      if (option.hidden && !option.locked && !shouldForceShowHiddenOption(option)) {
        return;
      }

      if (option.type === 'number' || option.alwaysShow) {
        visible.push(option);
        return;
      }

      // Count real values. Optional yes/no choices can mark "no" as nullable,
      // but customers still need to see that choice in the UI.
      const usableValues = option.values?.filter((value) => isUsableProductOptionValue(option, value)) || [];
      const selectableValues = usableValues.filter(v => !v.nullable);

      // If only 1 real value exists, auto-set it and hide from UI
      if (usableValues.length === 1) {
        const valueToSet = selectableValues[0] || usableValues[0];
        autoSet[option.id] = {
          label: option.label,
          value: valueToSet.value
        };
        debugLog(`🔒 Auto-setting ${option.label}: ${valueToSet.label}`);
      } else if (usableValues.length > 1) {
        // ALWAYS show options with multiple values, even if marked as hidden
        // Users need to make a choice when there are 2+ options
        visible.push(option);
        debugLog(`👁️ Showing ${option.label}: ${usableValues.length} values`);
      }
      // If 0 selectable values, skip this option entirely
    });

    return { visibleOptions: visible, autoSetOptions: autoSet };
  }, [displayOptions, isDisplayCubeProduct, isDisplayCardProduct, lockedConfiguration]);

  // 🚫 Apply excludes to filter out incompatible option values (simple version)
  const filteredOptions = useMemo(() => {
    if (!visibleOptions || visibleOptions.length === 0) return visibleOptions;

    return visibleOptions.map(option => {
      // Skip options without values
      if (!option.values || option.values.length === 0) return option;

      const seenValues = new Set<string>();
      const filteredValues = option.values.filter(value => {
        const normalizedValue = String(value.value ?? '');
        if (!normalizedValue) return false;
        if (seenValues.has(normalizedValue)) return false;
        const isLetterheadPerforationOption =
          isPrintedLetterheadsProduct(providerProductId || productSlug) &&
          option.id === 'perforation';
        if (isConsumerHiddenValue(option.id, value)) return false;
        // Een waarde die een andere leverancier levert (zwart Forex 3 mm via
        // Probo) heeft geen Print.com-artikelcode of -drukmethode; de
        // bereikbaarheidscontroles hieronder verborgen hem daardoor altijd.
        if (isForeignSupplierSelection(localizedOptions, option.id, normalizedValue)) {
          seenValues.add(normalizedValue);
          return true;
        }
        // Tijdelijk niet leverbaar: zichtbaar houden (grijs, met uitleg) in
        // plaats van stil te laten verdwijnen. isValueExcluded houdt hem
        // buiten iedere automatische keuze.
        if (isTemporarilyUnavailableValue(option.id, normalizedValue)) {
          seenValues.add(normalizedValue);
          return true;
        }
        // Een uitgesloten waarde verdwijnt alleen als er geen enkele geldige
        // combinatie mee bestaat. Bestaat die wel, dan blijft hij kiesbaar en
        // beweegt de botsende optie mee zodra de klant hem aanklikt. Zonder
        // dit onderscheid ontstond een patstelling bij opties die elkaar
        // wederzijds uitsluiten, zoals bedrukken en laminaat op hpl-trespa:
        // wat je ook koos, de andere kant viel uit de lijst.
        if (
          option.id !== 'custom_shape'
          && isValueExcluded(option.id, normalizedValue, configuration)
          && resolveExcludedSelection({
            optionId: option.id,
            value: normalizedValue,
            configuration,
            // Bewust de ONGEFILTERDE lijst: visibleOptions is hier al door
            // eerdere filters gegaan, en dan ontbreekt juist de waarde waarmee
            // de botsing op te lossen valt. Met de gefilterde lijst viel
            // "geen bedrukking" alsnog weg zodra er laminaat gekozen was.
            options: localizedOptions as any,
            isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
          }) === null
        ) return false;
        // Keep optional perforation choices for letterheads visible. The
        // brezo_article reachability filter is meant for variant combinations,
        // not for hiding a valid "none" choice on an optional finishing option.
        if (!isLetterheadPerforationOption && !isValueReachableViaBrezo(option.id, normalizedValue, configuration, localizedOptions)) return false;
        // Ook deze bereikbaarheidscontrole moet de botsingsoplosser kennen, maar
        // alleen wanneer een uitsluitregel de oorzaak is. Hij test of er nog een
        // printmethode mogelijk is met de HUIDIGE samenstelling, en erft daarmee
        // een botsing die bij een andere optie ligt. Op `canvas` sluiten
        // materiaal en gebruik elkaar over en weer uit: canvasstof kan niet naar
        // buiten, pvc niet naar binnen. Met canvasstof geselecteerd was elke
        // printmethode uitgesloten voor "Buiten", waarna deze regel die keuze
        // weghaalde. De klant zag daardoor alleen "Binnen", terwijl het product
        // buiten gewoon leverbaar is en de configurator het materiaal bij die
        // keuze zelf omzet.
        //
        // Blijft de waarde onbereikbaar zonder dat een regel hem uitsluit, dan
        // is er echt geen drukmethode voor (A0 dubbelzijdig is het bekende
        // geval) en hoort hij te verdwijnen. De oplosser zou daar een lege patch
        // teruggeven en de controle stilzwijgend uitschakelen; daarom staat de
        // uitsluitvraag ervoor. En de gevonden patch moet de bereikbaarheid ook
        // echt herstellen, anders ruilen we een verborgen keuze in voor een
        // doodlopende die niet te prijzen is.
        if (option.id !== 'custom_shape' && !isValueReachableViaPrintingMethod(option.id, normalizedValue, configuration)) {
          const botsingPatch = resolveExcludedSelection({
            optionId: option.id,
            value: normalizedValue,
            configuration,
            options: localizedOptions as any,
            // Geblokkeerd betekent hier allebei: door een uitsluitregel geraakt,
            // of geen drukmethode meer over. Alleen de eerste vraag stellen zou
            // de oplosser een lege patch laten teruggeven zodra geen regel de
            // waarde raakt, en dan viel deze controle stilzwijgend weg.
            isExcluded: (id, waarde, config) =>
              isValueExcluded(id, waarde, config as Record<string, string | number>)
              || !isValueReachableViaPrintingMethod(id, waarde, config as Record<string, string | number>),
          });
          if (!botsingPatch || Object.keys(botsingPatch).length === 0) return false;
        }
        seenValues.add(normalizedValue);
        return true;
      });

      return {
        ...option,
        values: filteredValues
      };
    });
  }, [visibleOptions, configuration, excludes, localizedOptions]);

  const getFirstCompatibleOptionValue = (
    option: ProductOption,
    config: Record<string, string | number>
  ) => {
    if (!option.values || option.values.length === 0) return null;

    // Een printproduct start altijd met print: "Geen" bij het printtype komt pas als laatste
    // aan de beurt, alleen als geen enkele printvariant met de rest van de keuze kan.
    return orderPrintBeforeNone(option).find((value) => {
      const normalizedValue = String(value.value ?? '');
      if (!normalizedValue || normalizedValue === 'null') return false;
      if (isConsumerHiddenValue(option.id, value)) return false;
      if (!matchesShowIf(value.showIf, config)) return false;
      if (option.id !== 'custom_shape' && isValueExcluded(option.id, normalizedValue, config)) return false;
      if (option.id !== 'custom_shape' && !isValueReachableViaPrintingMethod(option.id, normalizedValue, config)) return false;
      return true;
    }) || null;
  };

  // 🚫 Auto-correct configuration if selected values become excluded
  useEffect(() => {
    if (!filteredOptions || filteredOptions.length === 0) return;

    const corrections: Record<string, string> = {};

    filteredOptions.forEach(option => {
      const currentValue = configuration[option.id];

      // Skip hidden options (they're auto-set elsewhere)
      if (option.hidden) return;
      // Copies can be a free custom quantity even when Print.com also exposes
      // common preset values. Do not force a custom number back to the first preset.
      if (option.id === 'copies' || option.name === 'copies') return;
      if (isPrintedLetterheadsProduct(providerProductId || productSlug) && option.id === 'perforation') return;

      // Case 1: Current value is excluded, select first available
      if (currentValue) {
        const isExcluded = option.id === 'custom_shape' ? false : isValueExcluded(option.id, String(currentValue), configuration);
        // Shape presets encode as "<providerValue>|shape:<vorm>" and only exist at
        // render time, not in option.values. Without recognising the base value
        // here, clicking Hart or Ster was instantly reverted to the first shape.
        const presetBase = String(currentValue).split('|shape:')[0];
        const isAvailable = option.id === 'size'
          ? isAvailableSizeSelection(option, currentValue, configuration)
          : option.values?.some(v => v.value === currentValue || String(v.value) === presetBase);

        if ((!isAvailable || isExcluded) && option.values && option.values.length > 0) {
          const firstAvailable = getFirstCompatibleOptionValue(option, configuration) || option.values[0];
          if (String(firstAvailable.value) === String(currentValue)) return;
          corrections[option.id] = firstAvailable.value;
          debugLog(`🔄 Auto-correcting ${option.id} from ${currentValue} to ${firstAvailable.value} (excluded by other selection)`);
        }
      }
    });

    if (Object.keys(corrections).length > 0) {
      setConfiguration(prev => ({ ...prev, ...corrections }));
    }
  }, [filteredOptions, configuration]);

  // Keep hidden required provider options compatible with the visible
  // configuration. Print.com still needs values like printingmethod for
  // pricing, even though customers should not have to choose them.
  useEffect(() => {
    if (!isInitialized || !localizedOptions || localizedOptions.length === 0) return;

    const corrections: Record<string, string> = {};

    localizedOptions.forEach((option) => {
      if (!option.hidden || !option.values || option.values.length === 0) return;
      if (option.type === 'specification') return;
      if (!matchesShowIf(option.showIf, configuration)) return;

      const currentValue = configuration[option.id];
      const currentOptionValue = option.values.find((value) => String(value.value) === String(currentValue));
      // Niet-verplichte verborgen opties alleen corrigeren als hun bekende waarde
      // door een zichtbare keuze is uitgesloten. Zo bleef bij meubelsticker het
      // verborgen standaardmateriaal Biond staan na het kiezen van
      // applicatiefolie, en faalde de hele prijstabel op die combinatie.
      if (!option.required && !(currentOptionValue && isValueExcluded(option.id, String(currentOptionValue.value), configuration))) return;
      if (acceptsCustomQuantitySelection(option, currentValue, localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, configuration))) return;
      const currentValueValid = currentOptionValue
        ? matchesShowIf(currentOptionValue.showIf, configuration) &&
          !isValueExcluded(option.id, String(currentOptionValue.value), configuration)
        : false;

      if (currentValueValid) return;

      const preferredPrintingMethod = option.id === 'printingmethod'
        ? getPreferredPrintComPrintingMethod(localizedSpecifications, localizedOptions, configuration)
        : null;
      const orderedValues = preferredPrintingMethod
        ? [
            ...option.values.filter((value) => String(value.value) === preferredPrintingMethod),
            ...option.values.filter((value) => String(value.value) !== preferredPrintingMethod),
          ]
        : option.values;

      const firstCompatible = orderedValues.find((value) =>
        matchesShowIf(value.showIf, configuration) &&
        !isValueExcluded(option.id, String(value.value), configuration)
      );

      if (firstCompatible && String(firstCompatible.value) !== String(currentValue)) {
        corrections[option.id] = firstCompatible.value;
      }
    });

    if (Object.keys(corrections).length > 0) {
      setConfiguration(prev => ({ ...prev, ...corrections }));
    }
  }, [isInitialized, localizedOptions, configuration, excludes, localizedSpecifications, providerProductId, productSlug]);

  // 🎯 Auto-select options with only 1 value after excludes are applied
  useEffect(() => {
    if (!isInitialized || !filteredOptions || filteredOptions.length === 0) return;

    setConfiguration(prev => {
      const corrections: Record<string, string> = {};

      filteredOptions.forEach(option => {
        // Skip hidden options (they're auto-set elsewhere)
        if (option.hidden) return;
        if (acceptsCustomQuantitySelection(option, prev[option.id], localizedSpecifications?.quantitySettings,
          getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, prev))) return;

        // If only 1 option available after excludes → ensure it's selected
        if (option.values && option.values.length === 1) {
          const onlyAvailable = option.values[0];
          const currentValue = prev[option.id];

          if (currentValue !== onlyAvailable.value) {
            corrections[option.id] = onlyAvailable.value;
            debugLog(`🎯 Auto-selecting ${option.id}: ${onlyAvailable.label} (only option available after excludes)`);
          }
        }
      });

      if (Object.keys(corrections).length > 0) {
        return { ...prev, ...corrections };
      }
      return prev; // Return same object to avoid unnecessary re-render
    });
  }, [isInitialized, filteredOptions, localizedSpecifications, providerProductId, productSlug]);

  // Auto-set single-value options on mount
  useEffect(() => {
    const updates: Record<string, string> = {};
    Object.entries(autoSetOptions).forEach(([optionId, { value }]) => {
      if (!configuration[optionId]) {
        updates[optionId] = value;
      }
    });

    if (Object.keys(updates).length > 0) {
      setConfiguration(prev => ({ ...prev, ...updates }));
    }
  }, [autoSetOptions]);

  // Log excludes for debugging
  useEffect(() => {
    if (excludes.length > 0) {
      debugLog(`🚫 Loaded ${excludes.length} exclude rules for ${productName}`);
      debugLog('Exclude rules:', excludes);
    } else {
      debugLog(`✅ No exclude rules for ${productName}`);
    }
  }, [excludes, productName]);

  // Initialize default values on component mount and merge with initialConfiguration
  useEffect(() => {
    if (isInitialized) return; // Only run once

    const defaultConfig: Record<string, string | number> = {};

    // Auto-set hidden options when flexiblePrintingMethod is false
    if (localizedSpecifications?.autoSetOptions) {
      debugLog('🔧 Auto-setting hidden options:', localizedSpecifications.autoSetOptions);
      Object.assign(defaultConfig, localizedSpecifications.autoSetOptions);
    }

    Object.assign(
      defaultConfig,
      getForcedProductOptions(providerProductId || productSlug, localizedSpecifications, localizedOptions, defaultConfig)
    );

    // Set default values for all options (including hidden ones for API calls)
    localizedOptions.forEach((option) => {
      // Skip if initialConfiguration already has a value for this option
      if (initialConfiguration && initialConfiguration[option.id] !== undefined) {
        debugLog(`ℹ️ Using initialConfiguration for ${option.label}: ${initialConfiguration[option.id]}`);
        defaultConfig[option.id] = initialConfiguration[option.id];
        return;
      }

      // Leverancierscatalogi zetten geregeld `quick` als technische default.
      // Dat is een duurdere spoedroute, geen goede klantstandaard. Een klant
      // die zelf een levertijd kiest wordt hierboven vanzelf met rust gelaten.
      const cheapestDelivery = getCheapestDeliveryOptionValue(option);
      if (cheapestDelivery !== undefined) {
        defaultConfig[option.id] = cheapestDelivery;
        return;
      }

      const catalogDefault = getCatalogDefaultValue(option.id);
      if (hasExactPrintComCatalogCoverage && catalogDefault !== undefined) {
        defaultConfig[option.id] = catalogDefault;
        if (!option.hidden) {
          debugLog(`Catalog default for ${option.label}: ${catalogDefault}`);
        }
        return;
      }

      if (shouldSkipStaleHiddenPrintComDefault(option)) {
        return;
      }

      // Use explicit default if provided
      if (option.default !== undefined) {
        defaultConfig[option.id] = option.default;
        if (!option.hidden) {
          debugLog(`✅ Default for ${option.label}: ${option.default}`);
        }
        return;
      }

      // Optional finishing options are opt-in. Supplier catalogues sometimes
      // list only paid variants (for example perforation or drill holes), so
      // choosing the first value would silently add an operation the customer
      // never requested. Without an explicit default, absence means "none".
      if (!option.required) return;

      // For options with values, use first AVAILABLE value as default
      if (option.values && option.values.length > 0) {
        // Filter values to only include those that don't have showIf conditions
        // (at mount, we can't evaluate showIf, so we only use unconditional values)
        const rawAvailableValues = orderPrintBeforeNone(option).filter(v => !v.showIf);
        const availableValues = providerType === 'PRINTFUL' && option.id === 'size'
          ? sortPrintfulSizeValues(rawAvailableValues)
          : rawAvailableValues;
        if (availableValues.length > 0) {
          const firstValue = availableValues[0];
          defaultConfig[option.id] = firstValue.value;
          if (!option.hidden) {
            debugLog(`✅ Default for ${option.label}: ${firstValue.label} (${firstValue.value})`);
          }
        } else {
          // If all values have showIf, use the first one and hope it's valid
          const firstValue = option.values[0];
          defaultConfig[option.id] = firstValue.value;
          if (!option.hidden) {
            debugLog(`⚠️ Default for ${option.label}: ${firstValue.label} (${firstValue.value}) - conditional`);
          }
        }
        return;
      }

      // For number options, use min or 1 as default
      if (option.type === 'number') {
        defaultConfig[option.id] = option.min || 1;
        if (!option.hidden) {
          debugLog(`✅ Default for ${option.label}: ${option.min || 1}`);
        }
      }
    });

    // De lus hierboven doorloopt uitsluitend `localizedOptions` (het
    // productschema), dus virtuele maatvelden zoals size_width/size_height
    // - die pas ontstaan zodra een klant zelf een vrije maat intypt en nooit
    // als losse optie in het schema staan - worden overgeslagen. Bij het
    // heropenen van een bestaande order (bijv. de "Open in editor"-knop in de
    // admin) staan ze wel in initialConfiguration; zonder deze aanvulling
    // verliest de editor de vrije maat en valt hij terug op de kale string
    // "custom" (zie formatSizeForEditor).
    const virtualSizeAdditions = getVirtualSizeConfigFromInitial(defaultConfig, initialConfiguration);
    if (Object.keys(virtualSizeAdditions).length > 0) {
      debugLog('📐 Herstel vrije maat uit initialConfiguration:', virtualSizeAdditions);
      Object.assign(defaultConfig, virtualSizeAdditions);
    }

    // Apply material-dependent defaults on initialization
    if (localizedSpecifications?.materialDefaults && defaultConfig.material) {
      const materialDefaults = localizedSpecifications.materialDefaults[defaultConfig.material as string];
      if (materialDefaults) {
        debugLog(`🎨 Applying material-dependent defaults for ${defaultConfig.material}:`, materialDefaults);
        Object.assign(defaultConfig, materialDefaults);
      }
    }

    // Auto-update hidden required options with showIf conditions
    // This handles cases like article_number that depends on size+color
    localizedOptions.forEach((option) => {
      // Only process hidden required options
      if (!option.hidden || !option.required || !option.values) return;

      // Find the matching value based on current configuration
      const matchingValue = option.values.find((val) => {
        if (!val.showIf) return false;

        // Check if ALL showIf conditions match the default configuration
        return Object.entries(val.showIf).every(
          ([key, requiredValue]) => defaultConfig[key] === requiredValue
        );
      });

      if (matchingValue) {
        debugLog(`🔄 Auto-setting ${option.label} on init: ${matchingValue.value}`);
        defaultConfig[option.id] = matchingValue.value;
      }
    });

    for (let pass = 0; pass < 3; pass += 1) {
      let changed = false;

      localizedOptions.forEach((option) => {
        if (!option.values || option.values.length === 0 || option.type === 'number') return;
        if (option.id === 'copies' || option.name === 'copies') return;

        const currentValue = defaultConfig[option.id];
        if (currentValue === undefined || currentValue === '') return;
        const catalogDefault = getCatalogDefaultValue(option.id);
        if (
          hasExactPrintComCatalogCoverage &&
          option.hidden === true &&
          catalogDefault !== undefined &&
          String(currentValue) === String(catalogDefault)
        ) {
          return;
        }

        const currentValueValid = option.id === 'custom_shape'
          ? true
          : option.values.some((value) =>
              String(value.value) === String(currentValue) &&
              matchesShowIf(value.showIf, defaultConfig) &&
              !isConsumerHiddenValue(option.id, value) &&
              !isValueExcluded(option.id, String(value.value), defaultConfig)
            );

        if (currentValueValid) return;

        // Botst deze standaardwaarde met een andere optie, laat dan die andere
        // optie meebewegen in plaats van de klantkeuze weg te gooien. Op
        // hpl-trespa staat de catalogusstandaard op bedrukt (40_sticker) met
        // beschermlaminaat, maar het laminaat kreeg eerst zijn eigen eerste
        // waarde "geen". Bedrukt leek daardoor onmogelijk en werd hier
        // teruggezet naar "geen bedrukking", waarna beide opties tot een
        // enkele waarde inklapten: de klant kon alleen onbedrukt bestellen,
        // terwijl bedrukt gewoon leverbaar is.
        const conflictPatch = resolveExcludedSelection({
          optionId: option.id,
          value: String(currentValue),
          configuration: defaultConfig,
          options: localizedOptions as any,
          isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
        });
        if (conflictPatch && Object.keys(conflictPatch).length > 0) {
          Object.assign(defaultConfig, conflictPatch);
          changed = true;
          debugLog(`🔗 Botsende optie meegenomen voor ${option.id}=${currentValue}: ${JSON.stringify(conflictPatch)}`);
          return;
        }

        const firstCompatible = getFirstCompatibleOptionValue(option, defaultConfig);
        if (!firstCompatible || String(firstCompatible.value) === String(currentValue)) return;

        defaultConfig[option.id] = firstCompatible.value;
        changed = true;
        debugLog(`🔄 Correcting incompatible default ${option.id}: ${currentValue} -> ${firstCompatible.value}`);
      });

      if (!changed) break;
    }

    Object.assign(defaultConfig, applyBusinessCardSafeDefaults(defaultConfig));

    const copiesOption = localizedOptions.find(opt => (opt.id === 'copies' || opt.name === 'copies'));
    const quantitySettings = localizedSpecifications?.quantitySettings;
    const quantityPolicy = getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, defaultConfig);
    // Set initial copies to a valid value based on quantitySettings
    if (copiesOption && quantitySettings) {
      const currentCopies = Number(defaultConfig[copiesOption.id] ?? defaultConfig.copies ?? 0);
      defaultConfig[copiesOption.id] = resolveInitialCopies(currentCopies, quantitySettings);
    }
    if (copiesOption && quantityPolicy) {
      const currentCopies = Number(defaultConfig[copiesOption.id] ?? defaultConfig.copies ?? 0);
      defaultConfig[copiesOption.id] = normalizeQuantityForPolicy(currentCopies, quantityPolicy);
    }

    if (Object.keys(defaultConfig).length > 0) {
      const safeDefaultConfig = { ...applyBusinessCardSafeDefaults(defaultConfig), ...lockedConfiguration };
      debugLog('🎯 Setting merged configuration (defaults + initialConfiguration):', safeDefaultConfig);
      setConfiguration(safeDefaultConfig);
      setIsInitialized(true);

      setQuantity(resolveInitialQuantity(initialQuantity, Boolean(copiesOption), quantitySettings));
    }
  }, [localizedOptions, localizedSpecifications, initialConfiguration, initialQuantity, isInitialized, providerProductId, productSlug, lockedConfiguration]);

  useEffect(() => {
    if (!isInitialized || accountDesignRequested.current || typeof window === 'undefined') return;
    const savedDesignId = new URLSearchParams(window.location.search).get('savedDesign');
    if (!savedDesignId) return;
    accountDesignRequested.current = true;
    fetch(`/api/account/designs/${encodeURIComponent(savedDesignId)}`, { credentials: 'same-origin', cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const design = data?.design;
        if (!design?.designData || typeof design.designData !== 'object') return;
        const savedOptions = design.configuration?.options;
        if (savedOptions && typeof savedOptions === 'object' && !Array.isArray(savedOptions)) {
          setConfiguration((previous) => ({ ...previous, ...savedOptions }));
        }
        setAccountDesignData(design.designData);
        setCurrentStep('upload');
      })
      .catch(() => undefined);
  }, [isInitialized]);

  // Keep quantity at 1 for copies products
  useEffect(() => {
    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    if (copiesOption && configuration.copies !== undefined) {
      // For products with copies option (SELECT or NUMBER), quantity should always be 1
      if (quantity !== 1) {
        debugLog(`🔢 Copies product: forcing quantity to 1 (was ${quantity})`);
        setQuantity(1);
      }
    }
  }, [configuration.copies, localizedOptions, quantity]);

  useEffect(() => {
    if (!isInitialized) return;
    const forcedOptions = getForcedProductOptions(
      providerProductId || productSlug,
      localizedSpecifications,
      localizedOptions,
      configuration
    );
    const corrections = Object.entries(forcedOptions).reduce<Record<string, string | number>>((next, [key, value]) => {
      if (String(configuration[key] ?? '') !== String(value)) {
        next[key] = value;
      }
      return next;
    }, {});

    if (Object.keys(corrections).length > 0) {
      setConfiguration((previous) => ({ ...previous, ...corrections }));
    }
  }, [configuration, isInitialized, providerProductId, productSlug]);

  // Aantal van voor een proefexemplaar (1 stuk), terug te zetten zodra de
  // klant weer een gewone uitvoering kiest.
  const copiesBeforeSinglePieceRef = useRef<number | string | null>(null);
  useEffect(() => {
    if (!isInitialized) return;
    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    if (!copiesOption) {
      // Colsjaal en magneetdoos hebben geen copies-optie: daar telt `quantity`.
      if (readOnly || lockedConfiguration) return;
      const singlePiece = Boolean(singlePieceQuantityPolicy(getConfigurationWithDefaults(configuration), localizedSpecifications));
      if (singlePiece && quantity !== 1) {
        if (copiesBeforeSinglePieceRef.current === null) copiesBeforeSinglePieceRef.current = quantity;
        setQuantity(1);
      } else if (!singlePiece && copiesBeforeSinglePieceRef.current !== null) {
        const restore = Number(copiesBeforeSinglePieceRef.current);
        copiesBeforeSinglePieceRef.current = null;
        if (quantity === 1 && restore > 1) setQuantity(restore);
      }
      return;
    }
    const quantityPolicy = getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, configuration);
    const currentCopies = Number(configuration[copiesOption.id] || 0);
    if (isSinglePieceQuantityPolicy(quantityPolicy)) {
      if (currentCopies !== 1 && copiesBeforeSinglePieceRef.current === null) {
        copiesBeforeSinglePieceRef.current = configuration[copiesOption.id] ?? null;
      }
    } else if (copiesBeforeSinglePieceRef.current !== null) {
      const restore = copiesBeforeSinglePieceRef.current;
      copiesBeforeSinglePieceRef.current = null;
      if (currentCopies === 1 && Number(restore) > 1) {
        setConfiguration((previous) => ({ ...previous, [copiesOption.id]: restore }));
        return;
      }
    }
    if (!quantityPolicy) return;

    const nextCopies = normalizeQuantityForPolicy(currentCopies, quantityPolicy);
    if (nextCopies !== currentCopies) {
      setConfiguration((previous) => ({ ...previous, [copiesOption.id]: nextCopies }));
    }
  }, [configuration, isInitialized, localizedOptions, localizedSpecifications, providerProductId, productSlug, quantity, readOnly, lockedConfiguration]);

  // Auto-set options that have only 1 TOTAL value (not after showIf filtering)
  useEffect(() => {
    if (!isInitialized || !localizedOptions) return;

    const updates: Record<string, string | number> = {};

    localizedOptions.forEach(option => {
      if (!option.values || option.values.length === 0) return;
      if (option.type === 'specification') return; // Skip specification types
      if (acceptsCustomQuantitySelection(option, configuration[option.id], localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, configuration))) return;

      // Only auto-set if the option has exactly 1 value IN TOTAL
      // (not after showIf filtering - those should still be shown)
      if (option.values.length === 1 && configuration[option.id] !== option.values[0].value) {
        updates[option.id] = option.values[0].value;
        debugLog(`🔄 Auto-setting ${option.label} to ${option.values[0].label} (only 1 total value)`);
      }
    });

    if (Object.keys(updates).length > 0) {
      setConfiguration(prev => ({ ...prev, ...updates }));
    }
  }, [configuration, localizedOptions, isInitialized, localizedSpecifications, providerProductId, productSlug]);

  useEffect(() => {
    if (!isInitialized || configuration.all_sides_equal_finish === 'no') return;
    const finishValue = configuration.finish_left || configuration.finish_top || configuration.finish_right || configuration.finish_bottom;
    if (!finishValue) return;

    const needsSync =
      configuration.finish_left !== finishValue ||
      configuration.finish_right !== finishValue ||
      configuration.finish_top !== finishValue ||
      configuration.finish_bottom !== finishValue;

    if (needsSync) {
      setConfiguration(prev => ({
        ...prev,
        finish_left: finishValue,
        finish_right: finishValue,
        finish_top: finishValue,
        finish_bottom: finishValue,
      }));
    }
  }, [
    isInitialized,
    configuration.all_sides_equal_finish,
    configuration.finish_left,
    configuration.finish_right,
    configuration.finish_top,
    configuration.finish_bottom,
  ]);

  const getConfigurationWithDefaults = (baseConfiguration: Record<string, string | number>) => {
    const merged: Record<string, string | number> = { ...baseConfiguration };
    Object.assign(
      merged,
      getForcedProductOptions(providerProductId || productSlug, localizedSpecifications, localizedOptions, merged)
    );
    localizedOptions.forEach((option) => {
      if (merged[option.id] !== undefined && merged[option.id] !== '') return;
      if (isPrintedLetterheadsProduct(providerProductId || productSlug) && option.id === 'perforation') {
        merged[option.id] = 'none';
        return;
      }
      const cheapestDelivery = getCheapestDeliveryOptionValue(option);
      if (cheapestDelivery !== undefined) {
        merged[option.id] = cheapestDelivery;
        return;
      }
      const catalogDefault = getCatalogDefaultValue(option.id);
      if (hasExactPrintComCatalogCoverage && catalogDefault !== undefined) {
        merged[option.id] = catalogDefault;
        return;
      }
      if (shouldSkipStaleHiddenPrintComDefault(option)) {
        return;
      }
      if (option.default !== undefined && option.default !== '') {
        merged[option.id] = option.default;
        return;
      }
      // Do not synthesize an add-on choice for optional properties. Omission
      // is the provider's canonical representation of "none" when the
      // catalogue has no explicit none-value.
      if (!option.required) return;
      const firstValue = option.values?.find((value) => !value.showIf) || option.values?.[0];
      if (firstValue?.value !== undefined && firstValue.value !== '') {
        merged[option.id] = firstValue.value;
      }
      // Fallback: use provider defaultOptions (e.g. copies for Print.com number-type options)
      if ((merged[option.id] === undefined || merged[option.id] === '') && specifications?.defaultOptions?.[option.id] !== undefined) {
        merged[option.id] = specifications.defaultOptions[option.id];
      }
    });
    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    if (copiesOption) {
      const quantityPolicy = getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, merged);
      if (quantityPolicy) {
        const currentCopies = Number(merged[copiesOption.id] ?? 0);
        merged[copiesOption.id] = normalizeQuantityForPolicy(currentCopies, quantityPolicy);
      }
    }
    if (
      String(productSlug || '').toLowerCase() === 'a-borden' &&
      merged.size &&
      String(merged.size) !== 'a1' &&
      String(merged.color || '') === 'black'
    ) {
      merged.color = 'grey';
    }
    Object.assign(merged, applyBusinessCardSafeDefaults(merged));

    localizedOptions.forEach((option) => {
      if (!option.required || !option.values || option.values.length === 0) return;
      if (!matchesShowIf(option.showIf, merged)) return;

      const currentValue = merged[option.id];
      if (acceptsCustomQuantitySelection(option, currentValue, localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, merged))) return;
      const catalogDefault = getCatalogDefaultValue(option.id);
      if (
        hasExactPrintComCatalogCoverage &&
        option.hidden === true &&
        catalogDefault !== undefined &&
        String(currentValue) === String(catalogDefault)
      ) {
        return;
      }
      const currentOptionValue = option.values.find((value) => String(value.value) === String(currentValue));
      const currentValueValid = option.id === 'size'
        ? isAvailableSizeSelection(option, currentValue, merged)
        : option.id === 'custom_shape' && String(currentValue || '').includes('|shape:')
        ? option.values.some((value) => String(value.value) === normalizeCustomShapeProviderValue(currentValue))
        : currentOptionValue
        ? matchesShowIf(currentOptionValue.showIf, merged)
        : false;
      if (currentValueValid) return;

      if (isBusinessCardsProduct && businessCardAutoRepairProtectedOptionIds.has(option.id)) return;

      const firstCompatible = option.values.find((value) => matchesShowIf(value.showIf, merged));
      if (firstCompatible?.value !== undefined && firstCompatible.value !== '') {
        merged[option.id] = firstCompatible.value;
      }
    });

    // Een aangevulde standaardwaarde mag niet botsen met wat de klant koos.
    // Meubelsticker vulde het verborgen materiaal aan met de catalogusstandaard
    // Biond, ook na het kiezen van applicatiefolie; de prijstabel vroeg dan
    // een combinatie aan die Print.com niet levert. Alleen aangevulde waarden
    // worden vervangen, nooit een keuze van de klant. Twee rondes, omdat een
    // ander materiaal ook een andere drukmethode kan vragen.
    for (let round = 0; round < 2; round += 1) {
      localizedOptions.forEach((option) => {
        if (option.id === 'custom_shape' || option.id === 'copies' || !option.values?.length) return;
        if (baseConfiguration[option.id] !== undefined && baseConfiguration[option.id] !== '') return;
        const filledValue = merged[option.id];
        if (filledValue === undefined || filledValue === '') return;
        if (!isValueExcluded(option.id, String(filledValue), merged)) return;
        const compatible = getFirstCompatibleOptionValue(option, merged);
        if (compatible?.value !== undefined && compatible.value !== '') merged[option.id] = compatible.value;
      });
    }
    return applyBusinessCardSafeDefaults(merged);
  };

  const getHiddenRequiredOptionCandidates = (baseConfiguration: Record<string, string | number>) => {
    if (hasExactPrintComCatalogCoverage) return [];

    const candidates = localizedOptions.filter((option) =>
      option.hidden &&
      option.required &&
      option.type !== 'specification' &&
      option.values &&
      option.values.length > 1 &&
      matchesShowIf(option.showIf, baseConfiguration)
    );

    return candidates.map((option) => ({
      option,
      // For hidden provider options, don't apply isValueExcluded —
      // exclude rules are consumer-facing and can incorrectly filter out
      // valid hidden values. Only filter by showIf and skip null values.
      values: (option.values || []).filter((value) =>
        matchesShowIf(value.showIf, baseConfiguration) &&
        (isBusinessCardsProduct || !value.nullable) &&
        value.value !== 'null' &&
        value.value !== ''
      ).sort((a, b) => {
        if (!isBusinessCardsProduct) return 0;
        return Number(Boolean(b.nullable)) - Number(Boolean(a.nullable));
      }).slice(0, 6),
    })).filter((candidate) => candidate.values.length > 0);
  };

  const findCompatibleHiddenConfiguration = async (baseConfiguration: Record<string, string | number>) => {
    // Check cache first: key is sorted visible options that differ from defaults
    const visibleKeys = localizedOptions
      .filter(o => !o.hidden && o.values && o.values.length > 1 && baseConfiguration[o.id] !== undefined)
      .map(o => `${o.id}:${baseConfiguration[o.id]}`)
      .sort();
    const cacheKey = visibleKeys.join('|');
    const cached = hiddenConfigCacheRef.current.get(cacheKey);
    if (cached) {
      // Apply cached hidden values
      const merged = { ...baseConfiguration, ...cached };
      return merged;
    }

    const hiddenCandidates = getHiddenRequiredOptionCandidates(baseConfiguration);

    // Also include a few visible options that commonly cause incompatibility
    const visibleFixCandidates = localizedOptions.filter((option) =>
      !option.hidden && option.values && option.values.length > 1 &&
      matchesShowIf(option.showIf, baseConfiguration) &&
      option.id !== 'copies' && option.name !== 'copies' &&
      !(isBusinessCardsProduct && businessCardAutoRepairProtectedOptionIds.has(option.id)) &&
      !(isPrintedLetterheadsProduct(providerProductId || productSlug) && option.id === 'perforation') &&
      option.type !== 'specification' && option.type !== 'number'
    ).slice(0, 3).map((option) => ({
      option,
      values: (option.values || []).filter(v =>
        String(v.value) !== String(baseConfiguration[option.id] || '') &&
        !v.nullable && v.value !== 'null' && v.value !== ''
      ).slice(0, 2),
    })).filter(c => c.values.length > 0);

    // Try visible alternatives FIRST (they're more likely to fix the issue),
    // then hidden candidates
    const allCandidates = [...visibleFixCandidates, ...hiddenCandidates];

    const configurations: Record<string, string | number>[] = [];
    const build = (index: number, options: Array<{ option: ProductOption; values: any[] }>, current: Record<string, string | number>) => {
      if (configurations.length >= 80) return;
      if (index >= options.length) {
        configurations.push(current);
        return;
      }

      const candidate = options[index];
      const currentValue = current[candidate.option.id];
      const orderedValues = [...candidate.values].sort((a, b) => {
        if (String(a.value) === String(currentValue)) return -1;
        if (String(b.value) === String(currentValue)) return 1;
        return 0;
      });

      orderedValues.forEach((value) => {
        build(index + 1, options, {
          ...current,
          [candidate.option.id]: value.value,
        });
      });
    };

    if (allCandidates.length > 0) {
      build(0, allCandidates, baseConfiguration);
    } else {
      configurations.push(baseConfiguration);
    }

    // Filter out same-as-current and limit to 40 configs.
    // If the filter would remove everything, fall back to unfiltered so we
    // always try at least one config — otherwise the batch call is never made.
    const filtered = allCandidates.length > 0
      ? configurations.filter(c =>
          !allCandidates.every(({ option }) => String(c[option.id]) === String(baseConfiguration[option.id])))
      : configurations;
    const configsToTry = (filtered.length > 0 ? filtered : configurations).slice(0, 40);

    if (configsToTry.length === 0) return null;

    try {
      // Use batch pricing via our proxy — all configs in one call
      const response = await fetch('/api/printcom/calculate-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch: configsToTry.map(cfg => ({
            sku: providerProductId || productSlug,
            options: cfg,
            countryCode,
            deliveryPromise,
          })),
        }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      const results = data?.results || [];

      // Find first successful result
      for (let i = 0; i < results.length; i++) {
        const item = results[i];
        if (item && !item.error) {
          const winningConfig = configsToTry[i];
          // Cache the diff
          const diff: Record<string, string | number> = {};
          allCandidates.forEach(({ option }) => {
            if (String(winningConfig[option.id]) !== String(baseConfiguration[option.id])) {
              diff[option.id] = winningConfig[option.id];
            }
          });
          if (Object.keys(diff).length > 0) {
            hiddenConfigCacheRef.current.set(cacheKey, diff);
            if (hiddenConfigCacheRef.current.size > 50) {
              const firstKey = hiddenConfigCacheRef.current.keys().next().value;
              if (firstKey) hiddenConfigCacheRef.current.delete(firstKey);
            }
          }
          return winningConfig;
        }
      }
    } catch {
      // Batch call failed — return null to show the error
    }

    return null;
  };

  // Fetch prices for multiple quantities from Print.com API when configuration changes
  const fetchPrintComPrice = async (signal?: AbortSignal) => {
    if (!isClosedQuantitySelectionValid) {
      setQuantityPrices({});
      setLatestPriceQuote(null);
      setIsValidConfiguration(false);
      setIsPriceLoading(false);
      return;
    }
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    const priceRequestKey = JSON.stringify({
      configuration: effectiveConfiguration,
      deliveryPromise,
      quantity,
      countryCode,
      locale,
      providerType,
      productSlug,
      providerProductId,
      printfulPlacementsKey,
    });
    priceRequestKeyRef.current = priceRequestKey;
    const isCurrentPriceRequest = () => priceRequestKeyRef.current === priceRequestKey;
    // Helper to check if option should be shown based on showIf condition
    const shouldShowOption = (option: ProductOption) => {
      return matchesShowIf(option.showIf, effectiveConfiguration);
    };

    // Check if all VISIBLE required options are configured
    const visibleRequiredOptions = localizedOptions.filter(opt =>
      opt.required && !opt.hidden && shouldShowOption(opt)
    );
    const missingOptions = visibleRequiredOptions.filter(opt =>
      effectiveConfiguration[opt.id] === undefined || effectiveConfiguration[opt.id] === ''
    );

    // Additional check: if a custom size option is selected, ensure custom dimensions are provided
    let customSizeMissing = false;
    const sizeOption = localizedOptions.find(opt => opt.id === 'size');
    if (sizeOption) {
      const selectedSizeValue = sizeOption.values?.find(v => v.value === effectiveConfiguration.size);
      if (selectedSizeValue?.customSizes) {
        const sizeWidth = effectiveConfiguration.size_width;
        const sizeHeight = effectiveConfiguration.size_height;
        if (!sizeWidth || !sizeHeight || sizeWidth === 0 || sizeHeight === 0) {
          customSizeMissing = true;
          debugLog('⚠️ Custom size selected but dimensions not provided:', { sizeWidth, sizeHeight });
        }
      }
    }

    const dimensionCheck = getDimensionValidation(effectiveConfiguration);
    const allRequired = missingOptions.length === 0 && !customSizeMissing && dimensionCheck.valid;

    debugLog('🔍 Price fetch check:', {
      allRequired,
      productSlug,
      configuration: effectiveConfiguration,
      quantity,
      deliveryPromise,
      allRequiredOptions: localizedOptions.filter(o => o.required).map(o => o.id),
      visibleRequiredOptions: visibleRequiredOptions.map(o => o.id),
      missingOptions: missingOptions.map(o => o.id)
    });

    if (!allRequired || !productSlug) {
      debugLog('⚠️ Missing required options:', missingOptions.map(o => `${o.label} (${o.id})`));
      if (!isCurrentPriceRequest()) return;
      setCalculatedPrice(null);
      if (customSizeMissing) {
        setQuantityPrices({});
        setLatestPriceQuote(null);
        setIsValidConfiguration(false);
        setConfigurationError(
          locale === 'de'
            ? 'Geben Sie Breite und Höhe für das eigene Format ein.'
            : locale === 'en'
            ? 'Enter width and height for the custom size.'
            : locale === 'fr'
            ? 'Saisissez la largeur et la hauteur du format personnalise.'
            : 'Vul breedte en hoogte in voor het eigen formaat.'
        );
      } else if (!dimensionCheck.valid) {
        setQuantityPrices({});
        setIsValidConfiguration(false);
        setConfigurationError(dimensionCheck.message);
      }
      // Don't reset quantityPrices - keep old prices during configuration changes
      return;
    }

    const compatibilityIssue = hasExactPrintComCatalogCoverage ? null : getPrintComOptionCompatibilityIssue({
      sku: providerProductId || productSlug,
      productSlug,
      providerProductId,
      options: effectiveConfiguration,
    });
    if (compatibilityIssue) {
      if (!isCurrentPriceRequest()) return;
      setCalculatedPrice(null);
      setQuantityPrices({});
      setShippingCost(0);
      setLatestPriceQuote(null);
      setIsValidConfiguration(false);
      setConfigurationError(ui.unavailable);
      setIsPriceLoading(false);
      return;
    }

    // FOR GELATO PRODUCTS: Use Gelato pricing API
    if (providerType === 'GELATO') {
      debugLog(`💰 Fetching Gelato price (catalogUid from specs)...`);
      setIsPriceLoading(true);

      try {
        const catalogUid = specifications?.catalogUid;
        if (!catalogUid) {
          throw new Error('No catalogUid in specifications');
        }

        const response = await fetch('/api/gelato/calculate-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            catalogUid,
            options: configuration,
            quantity,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.calculation) {
            const totalPrice = data.calculation.subtotal || 0;
            const shippingCost = data.calculation.shippingCost || 0;
            setQuantityPrices({ [quantity]: totalPrice });
            setShippingCost(shippingCost);
            setIsValidConfiguration(true);
            setConfigurationError(null);
            debugLog(`✅ Gelato price: €${totalPrice.toFixed(2)} (incl. shipping €${shippingCost.toFixed(2)})`);
          } else {
            // Fallback to basePrice if no match
            const fallbackTotal = basePrice * quantity;
            setQuantityPrices({ [quantity]: fallbackTotal });
            setShippingCost(5.95);
            setIsValidConfiguration(true);
            setConfigurationError(null);
            debugLog(`⚠️ Gelato price fallback: €${fallbackTotal.toFixed(2)}`);
          }
        } else {
          // Fallback to basePrice
          const fallbackTotal = basePrice * quantity;
          setQuantityPrices({ [quantity]: fallbackTotal });
          setShippingCost(5.95);
          setIsValidConfiguration(true);
          setConfigurationError(null);
        }
      } catch (error) {
        console.error('❌ Error fetching Gelato price:', error);
        // Fallback to basePrice
        const fallbackTotal = basePrice * quantity;
        setQuantityPrices({ [quantity]: fallbackTotal });
        setShippingCost(5.95);
        setIsValidConfiguration(true);
      } finally {
        setIsPriceLoading(false);
      }
      return;
    }

    // FOR PRODIGI PRODUCTS: Use generic provider pricing API.
    if (providerType === 'PRODIGI') {
      setIsPriceLoading(true);

      try {
        const selectedCopies = Math.max(1, Number(configuration.copies || quantity || 1));
        const prodigiPriceItem = (rowQuantity: number) => ({
          productId,
          configuration: {
            productId: providerProductId || productSlug,
            options: { ...configuration, copies: rowQuantity, countryCode },
            quantity: rowQuantity,
          },
          quantity: rowQuantity,
          countryCode,
          locale,
        });
        const prodigiRow = (rowQuantity: number, data: any) => ({
          quantity: rowQuantity,
          totalPrice: Number(data?.pricing?.subtotal || data?.calculation?.subtotal || 0),
          shippingCost: Number(data?.pricing?.shippingCost || data?.calculation?.shippingCost || 0),
        });
        const fetchProdigiQuantity = async (rowQuantity: number) => {
          const response = await fetch('/api/pricing/calculate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(prodigiPriceItem(rowQuantity)),
          });

          throwIfRateLimited(response);
          if (!response.ok) return { quantity: rowQuantity, totalPrice: 0, shippingCost: 0 };
          return prodigiRow(rowQuantity, await response.json());
        };

        const selectedRow = await fetchProdigiQuantity(selectedCopies);
        if (!isCurrentPriceRequest()) return;
        if (selectedRow.totalPrice <= 0) throw new Error('Prodigi gaf geen geldige prijs terug');

        setQuantityPrices((current) => ({ ...current, [selectedCopies]: selectedRow.totalPrice }));
        setShippingCost(selectedRow.shippingCost);
        setIsValidConfiguration(true);
        setConfigurationError(null);
        setIsPriceLoading(false);

        const quantities = Array.from(new Set([
          selectedCopies,
          ...defaultMatrixQuantities,
          ...customMatrixQuantities,
        ]))
          .filter((rowQuantity) => Number.isFinite(rowQuantity) && rowQuantity > 0)
          .sort((a, b) => a - b);
        // De overige rijen in een aanvraag (telt als een eenheid per 6 rijen).
        const otherQuantities = quantities.filter((rowQuantity) => rowQuantity !== selectedCopies);
        fetchPricingBatch(otherQuantities.map(prodigiPriceItem))
          .then((results) => {
            if (!isCurrentPriceRequest()) return;
            const rows = results
              .map((result, index) => (result.ok ? prodigiRow(otherQuantities[index], result.data) : null))
              .filter((row): row is { quantity: number; totalPrice: number; shippingCost: number } => Boolean(row && row.totalPrice > 0));
            if (rows.length > 0) setQuantityPrices((current) => ({ ...current, ...Object.fromEntries(rows.map((row) => [row.quantity, row.totalPrice])) }));
          })
          .catch((error) => {
            if (isPriceRateLimitedError(error) && isCurrentPriceRequest()) handlePriceRateLimited(error);
          });
      } catch (error) {
        if (isPriceRateLimitedError(error)) {
          if (isCurrentPriceRequest()) handlePriceRateLimited(error);
          return;
        }
        console.error('Error fetching Prodigi price:', error);
        setCalculatedPrice(null);
        setQuantityPrices({});
        setShippingCost(0);
        setIsValidConfiguration(false);
        setConfigurationError('Kon prijs niet ophalen. Probeer het opnieuw.');
      }
      return;
    }

    // FOR CUSTOM PRODUCTS: Calculate price locally, including printable custom provider products.
    if (providerType === 'CUSTOM' || localizedSpecifications?.priceMatrix?.enabled || (!isPrintProduct && providerType !== 'PRINTCOM')) {
      // Designer bestelt maar gaf nog geen maat: niets te prijzen (de designer meldt hem zo).
      if (awaitsDesignerSize(vinylDesignerMode, configuration)) {
        setIsPriceLoading(false);
        return;
      }
      debugLog(`💰 Calculating custom product price locally...`);
      setIsPriceLoading(true);

      try {
        const livePrintComExecution = resolveLivePrintComExecution(
          localizedSpecifications,
          configuration,
          quantity,
        );
        let price = basePrice;
        let orderLevelModifier = 0;
        let priceIsOrderTotal = false;
        // Oppervlak van één stuk, voor staffelkortingen per uitvoering en maat
        // (zelfde regel als de server, src/lib/pricing/volume-discounts.ts).
        let pieceAreaM2: number | null = null;
        const copiesOption = localizedOptions.find(opt => (opt.id === 'copies' || opt.name === 'copies'));
        const hasCopiesOption = copiesOption !== undefined;
        const actualQuantity = hasCopiesOption && configuration.copies
          ? parseInt(configuration.copies.toString())
          : quantity;

        const areaPricing = localizedSpecifications?.areaPricing;
        if (areaPricing?.enabled) {
          const widthOptionId = areaPricing.widthOptionId || 'width_cm';
          const heightOptionId = areaPricing.heightOptionId || 'height_cm';
          let width = Number(configuration[widthOptionId]);
          const height = Number(configuration[heightOptionId]);
          if ((!Number.isFinite(width) || width <= 0) && areaPricing.widthByOption) {
            width = Number(areaPricing.widthByOption[String(configuration[widthOptionId])]);
          }
          let resolvedHeight = height;
          if ((!Number.isFinite(resolvedHeight) || resolvedHeight <= 0) && areaPricing.heightByOption) {
            resolvedHeight = Number(areaPricing.heightByOption[String(configuration[heightOptionId])]);
          }
          const matchingVariant = Array.isArray(areaPricing.variants)
            ? areaPricing.variants.find((variant: any) => {
                const conditions = variant?.conditions || {};
                return Object.entries(conditions).every(([key, expected]) => {
                  const selected = configuration[key];
                  return Array.isArray(expected)
                    ? expected.map(String).includes(String(selected))
                    : String(selected) === String(expected);
                });
              })
            : null;
          const priceSource = matchingVariant || areaPricing;
          const pricePerM2 = Number(priceSource.pricePerM2 || 0);
          const perimeterPricePerM = Number(priceSource.perimeterPricePerM || 0);
          const setupPrice = Number(priceSource.setupPrice || 0);
          const minPrice = Number(priceSource.minPrice || areaPricing.minPrice || basePrice || 0);

          if (Number.isFinite(width) && Number.isFinite(resolvedHeight) && width > 0 && resolvedHeight > 0 && (pricePerM2 > 0 || perimeterPricePerM !== 0 || Array.isArray(priceSource.priceTiers))) {
            pieceAreaM2 = (width * resolvedHeight) / 10000;
            const areaQuantity = configuration.copies ? Math.max(1, Number(configuration.copies) || 1) : Math.max(1, quantity);
            const areaM2 = Math.max(
              Number(areaPricing.minAreaM2 || 0),
              ((width * resolvedHeight) / 10000) * (areaPricing.useTotalArea === true ? areaQuantity : 1),
            );
            const perimeterM = ((width + resolvedHeight) * 2) / 100;
            const tier = Array.isArray(priceSource.priceTiers)
              ? priceSource.priceTiers.find((candidate: any) => {
                  const hasExplicitMinimum = candidate?.minAreaM2 !== undefined && candidate?.minAreaM2 !== null;
                  const minAreaM2 = Number(candidate?.minAreaM2 || 0);
                  const maxAreaM2 = Number(candidate?.maxAreaM2 || 0);
                  return hasExplicitMinimum
                    ? (!minAreaM2 || areaM2 >= minAreaM2) && (!maxAreaM2 || areaM2 < maxAreaM2)
                    : (!maxAreaM2 || areaM2 <= maxAreaM2);
                })
              : null;
            const tierPricePerM2 = Number(tier?.pricePerM2 ?? pricePerM2);
            const tierPerimeterPricePerM = Number(tier?.perimeterPricePerM ?? perimeterPricePerM);
            const tierSetupPrice = Number(tier?.setupPrice ?? setupPrice);
            const tierMinPrice = Number(tier?.minPrice ?? minPrice);
            const minPricePerItem = Number(tier?.minPricePerItem ?? priceSource.minPricePerItem ?? 0);
            const perItemPrice = Number(tier?.perItemPrice ?? priceSource.perItemPrice ?? 0);
            const variablePrice = areaM2 * tierPricePerM2 + perItemPrice * areaQuantity + perimeterM * tierPerimeterPricePerM;
            price = priceSource.setupAppliesToMinimum === true
              ? tierSetupPrice + Math.max(minPricePerItem * areaQuantity, variablePrice)
              : Math.max(tierMinPrice, minPricePerItem * areaQuantity, tierSetupPrice + variablePrice);
            if (areaPricing.priceIsOrderTotal === true || areaPricing.useTotalArea === true) priceIsOrderTotal = true;
          }
        }

        const customSizePricing = localizedSpecifications?.customSizePricing;
        if (
          customSizePricing?.type === 'perimeter' &&
          configuration.size === 'custom' &&
          configuration.size_width &&
          configuration.size_height
        ) {
          const width = Number(configuration.size_width);
          const height = Number(configuration.size_height);
          if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
            const perimeter = (width + height) * 2;
            const basePerimeter = Number(customSizePricing.basePerimeterCm || 0);
            const extraPerimeter = Math.max(0, perimeter - basePerimeter);
            const pricePerExtraCm = Number(customSizePricing.pricePerExtraCm || 0);
            price = basePrice + extraPerimeter * pricePerExtraCm;
          }
        }

        const priceMatrix = localizedSpecifications?.priceMatrix;
        if (priceMatrix?.enabled && priceMatrix?.prices && typeof priceMatrix.prices === 'object') {
          const keyFields = Array.isArray(priceMatrix.keyFields) ? priceMatrix.keyFields : [];
          const matrixPrice = Number(resolveCustomMatrixPrice(priceMatrix, countryCode, configuration));
          if (Number.isFinite(matrixPrice) && matrixPrice > 0) {
            price = matrixPrice;
            if (priceMatrix.priceIsOrderTotal === true) priceIsOrderTotal = true;
          }
        }

        localizedOptions.forEach(option => {
          const selectedValue = configuration[option.id];
          if (selectedValue && option.values) {
            const selectedOption = option.values.find(v => v.value === selectedValue);
            if (selectedOption) {
              price += getAreaAdjustedModifier(selectedOption, configuration, localizedOptions) * (priceIsOrderTotal ? actualQuantity : 1);
              orderLevelModifier += getOrderAdjustedModifier(selectedOption);
            }
          }
        });

        if (pieceAreaM2 === null) {
          const sizeAreaM2 = getSizeAreaM2FromConfiguration(configuration, localizedOptions);
          if (sizeAreaM2 && sizeAreaM2 > 0) pieceAreaM2 = sizeAreaM2;
        }
        const discountTiers = resolveVolumeDiscountTiers(localizedSpecifications, configuration, pieceAreaM2);
        const discountRate = getVolumeDiscountRate(discountTiers, actualQuantity);
        const discountedUnitPrice = price * (1 - discountRate);
        const localTotalPrice = (priceIsOrderTotal ? discountedUnitPrice : discountedUnitPrice * actualQuantity) + orderLevelModifier;
        let totalPrice = localTotalPrice;
        let localQuote = null;
        if (!priceMatrix?.enabled || providerType === 'CUSTOM') {
          // Met de designer: gedeelde fetch, de designer-host heeft deze aanvraag meestal al gedaan.
          const response = await (designerOwnsOrder ? sharedPriceFetch : fetch)('/api/pricing/calculate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              productId,
              configuration,
              quantity: hasCopiesOption ? 1 : quantity,
              countryCode,
              locale,
            }),
          });
          throwIfRateLimited(response);
          const data = await response.json();
          // Eindreview M2: intussen een nieuwere aanvraag (ander ontwerp): dit antwoord negeren,
          // anders zet het prijs en quote-hash van het vorige ontwerp neer.
          if (!isCurrentPriceRequest()) return;
          if (!response.ok || !data.success) {
            // Een gecodeerde 422 (offerteproduct, uitgesloten combinatie,
            // aantal buiten de staffel) draagt een melding die de klant
            // direct helpt; alleen een echte storing krijgt de generieke tekst.
            const pricingError = new Error(data.message || data.error || 'Custom price failed') as Error & { code?: string };
            if (typeof data.code === 'string') pricingError.code = data.code;
            throw pricingError;
          }
          totalPrice = Number(data.calculation?.subtotal || localTotalPrice);
          localQuote = data.quote || null;
          setShippingIncluded(data.calculation?.shippingIncluded === true);
        }
        if (!Number.isFinite(totalPrice) || totalPrice <= 0) throw new Error('Invalid custom price response');
        const lookupQuantity = hasCopiesOption ? actualQuantity : quantity;
        const fixedCopyQuantities = copiesOption?.values
          ? uniqueOptionValues(copiesOption.values)
            .map((value) => Number(value.value))
            .filter((value) => Number.isFinite(value) && value > 0)
          : [];
        const quantitySettings = localizedSpecifications?.quantitySettings;
        const fixedQuantityValues = quantitySettings?.type === 'fixed' && Array.isArray(quantitySettings.values)
          ? quantitySettings.values
            .map((value: unknown) => Number(value))
            .filter((value: number) => Number.isFinite(value) && value > 0)
          : [];
        const volumeDiscountQuantities = resolveVolumeDiscountQuantities(localizedSpecifications, configuration, pieceAreaM2);
        const hasOpenQuantityOption = Boolean(
          copiesOption &&
          fixedCopyQuantities.length === 0 &&
          (
            copiesOption.type === 'number' ||
            copiesOption.values?.some((value: any) => value.type === 'digital' || String(value.value || '') === '')
          )
        );
        const openQuantityPresets = hasOpenQuantityOption ? [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000] : [];
        const customPriceQuantities = livePrintComExecution
          ? [lookupQuantity]
          : Array.from(new Set([
              lookupQuantity,
              1,
              ...fixedCopyQuantities,
              ...fixedQuantityValues,
              ...volumeDiscountQuantities,
              ...openQuantityPresets,
              ...customMatrixQuantities,
            ]))
              .filter((value) => value > 0)
              .sort((a, b) => a - b)
              .slice(0, 12);
        const nextQuantityPrices = Object.fromEntries(customPriceQuantities
          .filter(value => isAllowedClosedQuantity(value, closedCustomQuantities))
          .flatMap((rowQuantity) => {
          const rowDiscountRate = getVolumeDiscountRate(discountTiers, rowQuantity);
          let rowUnitPrice = price;
          if (priceMatrix?.enabled && priceMatrix?.prices && typeof priceMatrix.prices === 'object') {
            const rowConfiguration = hasCopiesOption
              ? { ...configuration, copies: String(rowQuantity) }
              : configuration;
            const matrixRowPrice = Number(resolveCustomMatrixPrice(priceMatrix, countryCode, rowConfiguration));
            if ((closedCustomQuantities !== null || priceIsOrderTotal) && (!Number.isFinite(matrixRowPrice) || matrixRowPrice <= 0)) return [];
            if (Number.isFinite(matrixRowPrice) && matrixRowPrice > 0) rowUnitPrice = matrixRowPrice;
          }
          const rowTotal = rowQuantity === lookupQuantity ? totalPrice
            : closedCustomQuantities !== null || priceIsOrderTotal
              ? customMatrixRowTotal(rowUnitPrice, rowQuantity, priceIsOrderTotal, rowDiscountRate, orderLevelModifier)
              : (rowUnitPrice * (1 - rowDiscountRate) * rowQuantity) + orderLevelModifier;
          return rowTotal === null ? [] : [[rowQuantity, rowTotal]];
        }));
        setQuantityPrices(nextQuantityPrices);
        setShippingCost(0);
        setLatestPriceQuote(localQuote);
        setLatestPriceQuoteKey(localQuote ? stableConfigurationKey(configuration) : null);
        setIsValidConfiguration(true);
        setConfigurationError(null);
        clearPriceRateLimit();
      } catch (error) {
        if (isPriceRateLimitedError(error)) {
          if (isCurrentPriceRequest()) handlePriceRateLimited(error);
          return;
        }
        // Een fout van een verouderde aanvraag hoort niet bij het huidige ontwerp (M2): via
        // designerOrderError zou hij een wachtende designerbestelling laten weigeren.
        if (!isCurrentPriceRequest()) return;
        console.error('❌ Error calculating custom product price:', error);
        setCalculatedPrice(null);
        setQuantityPrices({});
        setLatestPriceQuote(null);
        setIsProviderMatrixLoading(false);
        setIsValidConfiguration(false);
        const pricingCode = (error as { code?: string } | null)?.code;
        if (pricingCode === 'QUOTE_ONLY') {
          setConfigurationError(ui.quotePriceFallback);
        } else if (pricingCode && error instanceof Error && error.message) {
          setConfigurationError(error.message);
        } else {
          // In de taal van de klant (eindreview I2): deze tekst komt via de bestel-gate ook in de designer.
          setConfigurationError(hostMessages(locale).priceFailed);
        }
      } finally {
        if (isCurrentPriceRequest()) setIsPriceLoading(false);
      }
      return;
    }

    // For non-Print.com products (Printful, Gelato, etc.), use generic pricing API
    if (providerType && providerType !== 'PRINTCOM' && providerType !== 'CUSTOM') {
      debugLog('💰 Fetching price from generic pricing API for ' + providerType + '...');
      setIsPriceLoading(true);
      try {
        const copiesOption = localizedOptions.find(opt => (opt.id === 'copies' || opt.name === 'copies'));
        const providerQuantityOption = providerType === 'PRINTFUL'
          ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
          : null;
        const productQuantityOption = providerQuantityOption || copiesOption;
        const productQuantityOptionId = productQuantityOption?.id;
        const usesProductQuantityOption = !!productQuantityOptionId;
        const selectedProviderQuantity = Math.max(1, Number(usesProductQuantityOption ? (effectiveConfiguration[productQuantityOptionId] || quantity || 1) : quantity));
        const providerPriceItem = (rowQuantity: number) => ({
          productId,
          configuration: usesProductQuantityOption && productQuantityOptionId
            ? { ...effectiveConfiguration, [productQuantityOptionId]: rowQuantity }
            : effectiveConfiguration,
          quantity: rowQuantity,
          countryCode,
          locale,
          ...(providerType === 'PRINTFUL' ? { printfulPlacements } : {}),
        });
        const providerRow = (rowQuantity: number, data: any) => {
          const subtotal = Number(data?.pricing?.subtotal || data?.calculation?.subtotal || 0);
          if (subtotal <= 0) return null;
          return {
            quantity: rowQuantity,
            subtotal,
            placementSurcharges: Array.isArray(data.calculation?.placementSurcharges) ? data.calculation.placementSurcharges : [],
            unitPrice: Number(data.pricing?.unitPrice || data.calculation?.unitPrice || subtotal / rowQuantity),
            shippingCost: Number(data.pricing?.shippingCost || data.calculation?.shippingCost || 0),
            quote: data.quote || null,
          };
        };
        const fetchProviderQuantity = async (rowQuantity: number) => {
          const response = await fetch('/api/pricing/calculate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(providerPriceItem(rowQuantity)),
          });
          throwIfRateLimited(response);
          if (!response.ok) return null;
          return providerRow(rowQuantity, await response.json());
        };

        const selectedRow = await fetchProviderQuantity(selectedProviderQuantity);
        if (!isCurrentPriceRequest()) return;

        if (selectedRow) {
          const providerMatrixQuantities = (providerType === 'PRINTFUL' || providerType === 'PROBO')
            ? Array.from(new Set([
                selectedProviderQuantity,
                ...defaultMatrixQuantities,
                ...customMatrixQuantities,
                ...(providerType === 'PRINTFUL' ? [1, 5, 10, 25, 50, 100] : []),
              ]))
                .sort((a, b) => a - b)
                .filter((rowQuantity) => rowQuantity !== selectedProviderQuantity)
            : [];

          if (providerMatrixQuantities.length > 0) {
            setIsProviderMatrixLoading(true);
          } else {
            setIsProviderMatrixLoading(false);
          }

          setQuantityPrices((current) => ({ ...current, [selectedProviderQuantity]: selectedRow.subtotal }));
          setPlacementSurcharges(selectedRow.placementSurcharges);
          setCalculatedPrice(selectedRow.unitPrice);
          setShippingCost(selectedRow.shippingCost);
          setLatestPriceQuote(selectedRow.quote);
          setIsValidConfiguration(true);
          setConfigurationError('');
          setIsPriceLoading(false);

          if (providerMatrixQuantities.length > 0) {
              // Alle tabelrijen in een aanvraag in plaats van een per rij.
              fetchPricingBatch(providerMatrixQuantities.map(providerPriceItem))
                .then((results) => {
                  if (!isCurrentPriceRequest()) return;
                  const rows = results
                    .map((result, index) => (result.ok ? providerRow(providerMatrixQuantities[index], result.data) : null))
                    .filter((row): row is NonNullable<ReturnType<typeof providerRow>> => Boolean(row));
                  if (rows.length > 0) setQuantityPrices((current) => ({ ...current, ...Object.fromEntries(rows.map((row) => [row.quantity, row.subtotal])) }));
                })
                .catch((error) => {
                  if (isPriceRateLimitedError(error) && isCurrentPriceRequest()) handlePriceRateLimited(error);
                })
                .finally(() => {
                  if (isCurrentPriceRequest()) setIsProviderMatrixLoading(false);
                });
          }
          return;
        }

        const response = await fetch('/api/pricing/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: productId,
            configuration: effectiveConfiguration,
            quantity,
            countryCode,
            locale,
            ...(providerType === 'PRINTFUL' ? { printfulPlacements } : {}),
          }),
        });
        throwIfRateLimited(response);
        if (response.ok) {
          const data = await response.json();
          if (data.pricing && data.pricing.unitPrice) {
            const unitPrice = data.pricing.unitPrice;
            const qp: Record<number, number> = {};
            [1, 25, 50, 100, 250, 500].forEach(q => {
              // Apply volume discount tiers
              let discount = 0;
              if (q >= 500) discount = unitPrice * q * 0.15;
              else if (q >= 250) discount = unitPrice * q * 0.10;
              else if (q >= 100) discount = unitPrice * q * 0.05;
              qp[q] = unitPrice * q - discount;
            });
            qp[quantity] = data.pricing.subtotal;
            setQuantityPrices(qp);
            setCalculatedPrice(data.pricing.unitPrice);
            setShippingCost(data.pricing.shippingCost || 0);
            setLatestPriceQuote(data.quote || null);
            setIsValidConfiguration(true);
            setConfigurationError('');
            setIsPriceLoading(false);
            return;
          }
        }
        setConfigurationError('Deze combinatie kan niet worden geprijsd.');
        setIsValidConfiguration(false);
      } catch (error) {
        if (isPriceRateLimitedError(error)) {
          if (isCurrentPriceRequest()) handlePriceRateLimited(error);
          return;
        }
        setConfigurationError('Kon prijs niet ophalen.');
        setIsProviderMatrixLoading(false);
        setIsValidConfiguration(false);
      }
      setIsPriceLoading(false);
      return;
    }

    // FOR PRINT.COM PRODUCTS: Fetch price from API
    debugLog('💰 Fetching prices for all quantities from Print.com API...');
    setIsPriceLoading(true);

    try {
      // Check if this product has a 'copies' option (select OR number)
      // If so, we don't use volume pricing - the copies value IS the quantity
      const copiesOption = localizedOptions.find(opt => (opt.id === 'copies' || opt.name === 'copies'));
      const hasCopiesOption = copiesOption !== undefined;
      const hasCopiesSelect = copiesOption && copiesOption.type === 'select' && copiesOption.values && copiesOption.values.length > 0;

      // For products with copies option (select OR number), we need to fetch price for the specific copies value
      // For other products, fetch the current quantity + volume pricing tiers
      let quantities;

      if (hasCopiesOption && effectiveConfiguration.copies) {
        // Fetch price only for the specific copies value (API quantity is always 1)
        const copiesValue = parseInt(effectiveConfiguration.copies.toString());
        quantities = [1]; // API quantity is always 1, copies value is in options
        debugLog(`📦 Copies product: fetching price for ${copiesValue} copies (API qty=1)`);
      } else {
        // The delivery/quantity matrix is loaded through a separate batch call.
        // Keep the primary price request focused on the selected quantity so
        // Print.com products don't wait on duplicate volume-tier requests.
        quantities = [Math.max(1, quantity || 1)];
      }
      const failedPriceMessages: Array<{ message: string; code?: string; reasonHint?: string }> = [];
      const pricePromises = quantities.map(async (qty) => {
        const requestBody = {
          sku: providerProductId || productSlug, // Use Print.com SKU if available, fallback to productSlug
          quantity: qty,
          options: effectiveConfiguration,
          deliveryPromise, // 0=standard, 1=insurance, 2=premium
          countryCode, // Country for shipping cost calculation
          locale, // 🌍 Language for multi-language support
        };

        const response = await fetch('/api/printcom/calculate-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pricingMode: 'api',
            expandPrintingMethods: true,
            batch: [requestBody],
          }),
          signal,
        });

        if (response.ok) {
          const payload = await response.json();
          const data = Array.isArray(payload?.results) ? payload.results[0] : payload;
          if (data?.success === false) {
            failedPriceMessages.push({
              message: data.error || data.message || 'Deze samenstelling vraagt om een offerte.',
              code: data.code,
              reasonHint: data.reasonHint,
            });
            return { qty, totalPrice: null, shippingCost: null, shippingIncluded: false, subtotal: null, quote: null };
          }
          const totalPrice = data.calculation?.subtotal || 0;
          const shippingCost = data.calculation?.shippingCost || 0;
          const subtotal = data.calculation?.subtotal || 0;
          return {
            qty,
            totalPrice,
            shippingCost,
            shippingIncluded: data.calculation?.shippingIncluded === true,
            optionSupplierRoute: Boolean(data.calculation?.supplierRoute),
            supplierId: data.calculation?.supplierId,
            supplierCountry: data.calculation?.supplierCountry,
            subtotal,
            quote: data.quote,
            adjustedOptions: data.calculation?.options,
            adjustedKeys: data.adjustedKeys,
            adjustmentMessage: typeof data.adjustmentMessage === 'string' ? data.adjustmentMessage : null,
          };
        }
        const errorData = await response.json().catch(() => null);
        failedPriceMessages.push({
          message: errorData?.error || errorData?.message || 'Deze combinatie kan niet worden geprijsd.',
          code: errorData?.code,
          reasonHint: errorData?.reasonHint,
        });
        return { qty, totalPrice: null, shippingCost: null, shippingIncluded: false, subtotal: null, quote: null };
      });

      const results = await Promise.all(pricePromises);
      if (!isCurrentPriceRequest()) return;

      // Build price map
      const priceMap: Record<number, number> = {};
      let fetchedShippingCost = 0;
      let fetchedShippingIncluded = false;
      let fetchedOptionSupplierRoute = false;
      let fetchedPrintComQuoteMeta: typeof printComQuoteMeta = null;
      let fetchedQuote: typeof latestPriceQuote = null;
      let fetchedAdjustedOptions: Record<string, any> | null = null;
      let fetchedAdjustedKeys: string[] = [];
      let fetchedAdjustmentMessage: string | null = null;
      const lookupQuantity = hasCopiesOption ? 1 : quantity;
      results.forEach(({ qty, totalPrice, shippingCost, shippingIncluded: resultShippingIncluded, optionSupplierRoute: resultOptionSupplierRoute, supplierId, supplierCountry, quote, adjustedOptions, adjustedKeys, adjustmentMessage }: any) => {
        if (totalPrice !== null) {
          priceMap[qty] = totalPrice;
          if (shippingCost) fetchedShippingCost = shippingCost;
          if (qty === lookupQuantity) fetchedShippingIncluded = resultShippingIncluded === true;
          if (qty === lookupQuantity) fetchedOptionSupplierRoute = resultOptionSupplierRoute === true;
          if (qty === lookupQuantity && supplierId) {
            fetchedPrintComQuoteMeta = {
              supplierId: String(supplierId),
              supplierCountry: supplierCountry ? String(supplierCountry) : undefined,
              pricedOptions: adjustedOptions || effectiveConfiguration,
              deliveryPromise,
            };
          }
          if (qty === lookupQuantity && quote?.hash && quote?.expiresAt) fetchedQuote = quote;
          if (qty === lookupQuantity && adjustedOptions && Array.isArray(adjustedKeys) && adjustedKeys.length > 0) {
            fetchedAdjustedOptions = adjustedOptions;
            fetchedAdjustedKeys = adjustedKeys;
          }
          if (qty === lookupQuantity && typeof adjustmentMessage === 'string' && adjustmentMessage) {
            fetchedAdjustmentMessage = adjustmentMessage;
          }
        }
      });

      debugLog('✅ Quantity prices fetched:', priceMap);
      debugLog('📦 Shipping cost:', fetchedShippingCost);

      // Set the current quantity price as calculated price
      const currentPrice = priceMap[lookupQuantity];
      if (currentPrice !== undefined) {
        if (fetchedAdjustedOptions && fetchedAdjustedKeys.length > 0) {
          // Include the hidden winning printingmethod, but exclude derived
          // metadata such as width, grammage and color.rgb.
          const knownOptionIds = new Set(localizedOptions.map(o => o.id));
          const adjustedPatch = Object.fromEntries(
            fetchedAdjustedKeys
              .filter((key) =>
                fetchedAdjustedOptions?.[key] !== undefined &&
                knownOptionIds.has(key)
              )
              .map((key) => [key, fetchedAdjustedOptions?.[key]])
          );
          if (Object.keys(adjustedPatch).length > 0) {
            setConfiguration((previous) => {
              const changedPatch = Object.fromEntries(
                Object.entries(adjustedPatch).filter(
                  ([key, value]) => String(previous[key] ?? '') !== String(value ?? '')
                )
              );
              return Object.keys(changedPatch).length > 0
                ? { ...previous, ...changedPatch }
                : previous;
            });
          }
        }
        // Don't set calculatedPrice - we'll use quantityPrices directly in calculatePrice()
        setQuantityPrices(priceMap);
      setShippingCost(fetchedShippingCost);
      setShippingIncluded(fetchedShippingIncluded);
      setOptionSupplierRoute(fetchedOptionSupplierRoute);
      setPrintComQuoteMeta(fetchedPrintComQuoteMeta);
        setLatestPriceQuote(fetchedQuote);
        setIsValidConfiguration(true);
        setConfigurationError(null);
        setConfigurationAdjustmentNote(fetchedAdjustmentMessage);
        debugLog(`💰 Price set - quantity: ${quantity}, copies: ${effectiveConfiguration.copies}, hasCopiesSelect: ${hasCopiesSelect}`);

      } else {
        // A material can make an otherwise valid fold unavailable even when
        // Print.com's catalog excludes do not describe that combination. Keep
        // valid folds, but verify a neutral fold before showing an error.
        const foldOption = localizedOptions.find((option) => option.id === 'fold');
        const neutralFold = foldOption?.values?.find((value) => String(value.value) === 'no_fold');
        if (neutralFold && String(effectiveConfiguration.fold || '') !== String(neutralFold.value)) {
          const repairedConfiguration: Record<string, string | number> = {
            ...effectiveConfiguration,
            fold: neutralFold.value,
          };
          const neutralDependentValues: Record<string, string> = {
            folded: 'no',
            creasing: 'no',
            variable_creasing_line: 'none',
            plano_delivery: 'no',
          };
          Object.entries(neutralDependentValues).forEach(([optionId, neutralValue]) => {
            const option = localizedOptions.find((candidate) => candidate.id === optionId);
            if (option?.values?.some((candidate) => String(candidate.value) === neutralValue)) {
              repairedConfiguration[optionId] = neutralValue;
            }
          });

          try {
            const repairResponse = await fetch('/api/printcom/calculate-price', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sku: providerProductId || productSlug,
                quantity: hasCopiesOption ? 1 : quantity,
                options: repairedConfiguration,
                deliveryPromise,
                countryCode,
                locale,
              }),
              signal,
            });
            const repairData = await repairResponse.json().catch(() => null);
            if (
              repairResponse.ok &&
              repairData?.success !== false &&
              Number(repairData?.calculation?.subtotal || 0) > 0 &&
              isCurrentPriceRequest()
            ) {
              const repairKeys = ['fold', ...Object.keys(neutralDependentValues)];
              setConfiguration((previous) => {
                const patch = Object.fromEntries(
                  repairKeys
                    .filter((key) => repairedConfiguration[key] !== undefined)
                    .filter((key) => String(previous[key] ?? '') !== String(repairedConfiguration[key] ?? ''))
                    .map((key) => [key, repairedConfiguration[key]])
                );
                return Object.keys(patch).length > 0 ? { ...previous, ...patch } : previous;
              });
              setConfigurationError(null);
              setLatestPriceQuote(null);
              return;
            }
          } catch {
            // Continue with the existing compatibility repair and error path.
          }
        }

        const compatibleHiddenConfiguration = await findCompatibleHiddenConfiguration(effectiveConfiguration);
        if (compatibleHiddenConfiguration && isCurrentPriceRequest()) {
          const hiddenFixes: Record<string, string | number> = {};
          localizedOptions.forEach((option) => {
            if (!option.hidden) return;
            if (String(compatibleHiddenConfiguration[option.id] || '') !== String(effectiveConfiguration[option.id] || '')) {
              hiddenFixes[option.id] = compatibleHiddenConfiguration[option.id];
            }
          });

          if (Object.keys(hiddenFixes).length > 0) {
            setConfiguration((previous) => ({ ...previous, ...hiddenFixes }));
            return;
          }
        }

        setCalculatedPrice(null);
        setQuantityPrices({});
        setShippingCost(0);
        setLatestPriceQuote(null);
        setIsValidConfiguration(false);
        setConfigurationAdjustmentNote(null);
        const firstFailure = failedPriceMessages[0];
        const firstFailureText = firstFailure?.message || 'Kon prijzen niet ophalen voor deze configuratie';
        if (firstFailure?.code === 'SUPPLIER_CONFIGURATION_UNAVAILABLE') {
          setConfigurationError(ui.combinationUnavailable);
        } else if (firstFailure?.code === 'CONFIGURATION_IMPOSSIBLE') {
          // Niet tijdelijk: de opgeslagen excludes sluiten elke drukmethode
          // uit voor deze samenstelling (bijvoorbeeld A0 dubbelzijdig). Zeg
          // dat expliciet in plaats van een generieke foutmelding te tonen.
          setConfigurationError(
            firstFailure.reasonHint === 'double_sided_unavailable'
              ? ui.configurationImpossibleDoubleSided
              : ui.configurationImpossible
          );
        } else {
          const isExcludedCombination = /excluded configuration|exclude group|ongeldige lokale optie/i.test(firstFailureText);
          setConfigurationError(isExcludedCombination ? ui.combinationUnavailable : firstFailureText);
        }
      }

    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') return;
      console.error('❌ Error fetching prices:', error);
      setCalculatedPrice(null);
      setQuantityPrices({});
      setLatestPriceQuote(null);
      setIsValidConfiguration(false);
      setConfigurationAdjustmentNote(null);
      setConfigurationError('Kon prijs niet ophalen. Probeer het opnieuw.');
    } finally {
      if (isCurrentPriceRequest()) setIsPriceLoading(false);
    }
  };

  // Calculate price based on configuration and quantity
  const pricing = useMemo(() => {
    let unitPriceExclVAT = 0;
    let totalExclVAT = 0;

    // Check if this product has a product-level quantity option (select OR number)
    const copiesOption = localizedOptions.find(opt => (opt.id === 'copies' || opt.name === 'copies'));
    const providerQuantityOption = providerType === 'PRINTFUL'
      ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
      : null;
    const quantityOptionId = providerQuantityOption?.id || copiesOption?.id;
    const hasCopiesOption = Boolean(quantityOptionId);
    const actualQuantity = quantityOptionId && configuration[quantityOptionId]
      ? parseInt(configuration[quantityOptionId].toString())
      : quantity;

    // ALWAYS use quantityPrices - this is the only reliable source
    // For products with copies option, lookup by quantity=1 (since API qty is always 1)
    const lookupQuantity = hasCopiesOption
      ? (providerType === 'PRODIGI' || providerType === 'PRINTFUL' || providerType === 'CUSTOM' ? actualQuantity : 1)
      : quantity;

    if (quantityPrices[lookupQuantity] !== undefined) {
      totalExclVAT = quantityPrices[lookupQuantity];
      // For copies products, the total is for all copies, so divide by copies value to get unit price
      unitPriceExclVAT = totalExclVAT / actualQuantity;

      // Add selected accessory prices to the total
      for (const accId of selectedAccessories) {
        const accessory = accessories.find(a => a.id === accId);
        if (accessory?.basePrice) {
          const qty = accessoryQuantities[accId] || 1;
          const quotedAccessory = accessory.priceByQuantity?.[String(qty)];
          const accessoryTotalExVat = Number(
            quotedAccessory?.totalPriceExVat ?? Number(accessory.basePrice) * qty
          );
          totalExclVAT += accessoryTotalExVat;
          unitPriceExclVAT += accessoryTotalExVat / actualQuantity;
        }
      }

      debugLog(`💰 Price calculation: total=${totalExclVAT.toFixed(2)}, actualQuantity=${actualQuantity}, unitPrice=${unitPriceExclVAT.toFixed(2)}, lookupQty=${lookupQuantity}`);
    }
    // If no price available yet, return 0 (loading state)
    else {
      debugLog(`⏳ No price available yet for lookupQty=${lookupQuantity}, configuration:`, configuration);
      unitPriceExclVAT = 0;
      totalExclVAT = 0;
    }

    // Apply VAT to all prices
    const unitPriceInclVAT = calculatePriceWithVAT(unitPriceExclVAT, vatCountryCode);
    const totalInclVAT = calculatePriceWithVAT(totalExclVAT, vatCountryCode);

    return {
      unitPrice: unitPriceInclVAT,
      // Prijs exclusief btw: verandert niet bij een landwissel na het
      // toevoegen aan de winkelwagen, in tegenstelling tot unitPrice/total
      // hierboven. Wordt meegegeven aan de winkelwagen (zie handleAddToCart)
      // zodat de weergave daar het tarief van het actuele land kan volgen
      // in plaats van te bevriezen op vatCountryCode van dit moment.
      // Onafgerond: de winkelwagen rekent er btw over en rondt dan pas af
      // (grossFromExVat), net als deze pagina en de server. Eerst afronden
      // maakte 10 flyers in de kassa een cent goedkoper dan hier en in Stripe.
      unitPriceExVat: unitPriceExclVAT,
      subtotal: totalInclVAT,
      totalExVat: totalExclVAT,
      discount: 0,
      discountAmount: 0,
      total: totalInclVAT,
    };
  }, [quantityPrices, quantity, configuration, localizedOptions, vatCountryCode, providerType]);

  const matrixQuantityBounds = useMemo(() => {
    if (closedCustomQuantities !== null) return {
      minimum: closedCustomQuantities[0] || 1,
      maximum: closedCustomQuantities.at(-1) || 1,
      step: 1,
      fixedValues: closedCustomQuantities,
    };
    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    const providerQuantityOption = providerType === 'PRINTFUL'
      ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
      : null;
    const productQuantityOption = providerQuantityOption || copiesOption;
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    const quantityPolicy = productQuantityOption
      ? getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, effectiveConfiguration)
      : null;
    const policyValues = getQuantityPolicyValues(quantityPolicy);
    const fixedCopyValues = productQuantityOption?.values
      ? uniqueOptionValues(productQuantityOption.values)
        .map((value) => Number(value.value))
        .filter((value) => Number.isFinite(value) && value > 0)
      : [];
    const quantitySettings = localizedSpecifications?.quantitySettings;
    const fixedQuantityValues = quantitySettings && Array.isArray(quantitySettings.values)
      ? quantitySettings.values.map((value: unknown) => Number(value)).filter((value: number) => Number.isFinite(value) && value > 0)
      : [];
    const fixedValues = policyValues.length > 0
      ? policyValues
      : productQuantityOption
      ? [...fixedCopyValues, ...fixedQuantityValues]
      : fixedQuantityValues;
    const minimum = Number(quantityPolicy?.minimum || quantityPolicy?.min || 0)
      || productQuantityOption?.min
      || (quantitySettings?.type === 'minimum' ? Number(quantitySettings.minimum) : undefined)
      || (fixedValues.length ? Math.min(...fixedValues) : 1);
    // Print.com supports far higher quantities than the preset values.
    // The copies option presets (1,2,3,4,5) are convenience buttons, not hard limits.
    // Verified: Foto op Forex accepts copies=100 via Print.com API directly.
    const maximum = resolveMatrixQuantityMaximum(
      quantityPolicy?.maximum ?? quantityPolicy?.max,
      quantitySettings?.maximum ?? quantitySettings?.max,
      productQuantityOption?.max,
      fixedValues,
    );
    const step = Number(quantitySettings?.step);
    return {
      minimum: Number.isFinite(minimum) && minimum > 0 ? minimum : 1,
      maximum: Number.isFinite(maximum) && maximum > 0 ? maximum : 100000,
      step: Number.isSafeInteger(step) && step > 1 ? step : 1,
      fixedValues,
    };
  }, [configuration, localizedOptions, localizedSpecifications, providerProductId, productSlug, providerType, closedCustomQuantities]);

  const defaultMatrixQuantities = useMemo(() => {
    if (closedCustomQuantities !== null) return closedCustomQuantities;
    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    const providerQuantityOption = providerType === 'PRINTFUL'
      ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
      : null;
    const productQuantityOption = providerQuantityOption || copiesOption;
    const productQuantityOptionId = productQuantityOption?.id;
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    const quantityPolicy = productQuantityOption
      ? getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, effectiveConfiguration)
      : null;
    const policyValues = getQuantityPolicyValues(quantityPolicy);
    const fixedCopyValues = productQuantityOption?.values
      ? uniqueOptionValues(productQuantityOption.values)
        .map((value) => Number(value.value))
        .filter((value) => Number.isFinite(value) && value > 0)
      : [];
    const quantitySettings = localizedSpecifications?.quantitySettings;
    const fixedQuantityValues = quantitySettings && Array.isArray(quantitySettings.values)
      ? quantitySettings.values.map((value: unknown) => Number(value)).filter((value: number) => Number.isFinite(value) && value > 0)
      : [];
    const volumeDiscountQuantities = Array.isArray(localizedSpecifications?.volumeDiscounts)
      ? localizedSpecifications.volumeDiscounts
        .map((tier: any) => Number(tier?.minQuantity || tier?.quantity || 0))
        .filter((value: number) => Number.isFinite(value) && value > 0)
      : [];
    const hasOpenQuantityOption = Boolean(
      productQuantityOption &&
      (
        productQuantityOption.type === 'number' ||
        productQuantityOption.values?.some((value: any) => value.type === 'digital' || String(value.value || '') === '')
      )
    );
    const actualQuantity = productQuantityOptionId && effectiveConfiguration[productQuantityOptionId]
      ? Math.max(1, parseInt(String(effectiveConfiguration[productQuantityOptionId]), 10) || 1)
      : Math.max(1, quantity || 1);
    const normalizedActualQuantity = quantityPolicy ? normalizeQuantityForPolicy(actualQuantity, quantityPolicy) : actualQuantity;
    const quantityRows = policyValues.length > 0
      ? [
          normalizedActualQuantity,
          ...policyValues,
          ...volumeDiscountQuantities,
        ]
      : [
          normalizedActualQuantity,
          ...volumeDiscountQuantities,
          ...(productQuantityOption ? [...fixedCopyValues, ...fixedQuantityValues] : fixedQuantityValues),
        ];
    return Array.from(new Set([
      ...quantityRows,
      // Only use limited presets if NO values from Print.com at all
      ...(hasOpenQuantityOption && !quantityPolicy && fixedQuantityValues.length === 0 && fixedCopyValues.length === 0 ? [1, 5, 10, 25, 50, 100, 250, 500] : []),
      ...(providerType === 'PRODIGI' || providerType === 'PRINTFUL' ? [1, 5, 10, 25, 50, 100] : []),
      ...(productQuantityOption || fixedQuantityValues.length ? [] : [1, 25, 50, 100, 250]),
    ]))
      .filter((value) => value > 0)
      .filter((value) => !quantityPolicy || normalizeQuantityForPolicy(value, quantityPolicy) === value)
      .sort((a, b) => a - b);
  }, [configuration, localizedOptions, localizedSpecifications, providerProductId, productSlug, quantity, providerType, closedCustomQuantities]);

  // Auto-remove custom quantities only for providers where a completed matrix
  // request is authoritative. Printful/Probo fill custom rows asynchronously.
  // See computeCustomQuantitiesToRemove() for why PRINTCOM and CUSTOM need
  // different "did this quantity get priced?" checks here: PRINTCOM's
  // quantityPrices only ever holds the single currently-selected quantity,
  // not a price per matrix row, so checking it directly used to strip a
  // freshly typed custom quantity back out before the debounced matrix fetch
  // that would price it ever ran - the quantity briefly appeared to be added,
  // then silently vanished with no error.
  useEffect(() => {
    if (isPriceLoading || isDeliveryRowsLoading) return;
    const toRemove = computeCustomQuantitiesToRemove({
      providerType,
      customMatrixQuantities,
      quantityPrices,
      deliveryQuantityRows,
    });
    if (toRemove.length > 0) {
      setCustomMatrixQuantities(prev => prev.filter(q => !toRemove.includes(q)));
    }
  }, [customMatrixQuantities, deliveryQuantityRows, isDeliveryRowsLoading, isPriceLoading, providerType, quantityPrices]);

  const pendingMatrixQuantityNumber = useMemo(() => {
    if (closedCustomQuantities !== null) return null;
    const value = Number(matrixCustomQuantity);
    if (!Number.isInteger(value) || value <= 0) return null;
    if (value < matrixQuantityBounds.minimum || value > matrixQuantityBounds.maximum) return null;
    return value;
  }, [matrixCustomQuantity, matrixQuantityBounds.maximum, matrixQuantityBounds.minimum, closedCustomQuantities]);

  useEffect(() => {
    if (!matrixCustomQuantity) {
      setMatrixCustomQuantityError('');
      return;
    }
    const value = Number(matrixCustomQuantity);
    if (!Number.isInteger(value) || value <= 0) {
      setMatrixCustomQuantityError(uiText('enterWholeNumber'));
    } else if (value < matrixQuantityBounds.minimum) {
      setMatrixCustomQuantityError(uiText('minimumPieces', { min: matrixQuantityBounds.minimum }));
    } else if (value > matrixQuantityBounds.maximum) {
      setMatrixCustomQuantityError(uiText('maximumPieces', { max: matrixQuantityBounds.maximum }));
    } else if (defaultMatrixQuantities.includes(value) || customMatrixQuantities.includes(value)) {
      setMatrixCustomQuantityError(uiText(existingMatrixQuantityFeedback({
        quantity: value, presetQuantities: defaultMatrixQuantities, customQuantities: customMatrixQuantities,
        rows: deliveryQuantityRows, loading: isDeliveryRowsLoading,
      }) ?? 'quantityAlreadyShown'));
    } else {
      setMatrixCustomQuantityError('');
    }
  }, [customMatrixQuantities, defaultMatrixQuantities, deliveryQuantityRows, isDeliveryRowsLoading, matrixCustomQuantity, matrixQuantityBounds.maximum, matrixQuantityBounds.minimum, uiText]);

  const hasPendingLocalConfigurationCorrection = useMemo(() => {
    if (!isInitialized) return true;
    if (!filteredOptions || filteredOptions.length === 0) return false;

    return filteredOptions.some((option) => {
      const currentValue = configuration[option.id];
      if (acceptsCustomQuantitySelection(option, currentValue, localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, configuration))) return false;
      if (
        currentValue === undefined ||
        currentValue === null ||
        currentValue === '' ||
        !option.values ||
        option.values.length === 0
      ) {
        return false;
      }

      const currentValueKey = String(currentValue);
      const currentBaseValue = currentValueKey.startsWith('rotate:')
        ? currentValueKey.split(':')[1]
        : currentValueKey;
      const ignoreLiveInvalidValue =
        option.id === 'material' || option.id === 'cover_material';
      const isCurrentVisible = option.id === 'size'
        ? isAvailableSizeSelection(option, currentValue, configuration) &&
          (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(currentValueKey)) &&
          (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(currentBaseValue)) &&
          !isValueExcluded(option.id, currentBaseValue, configuration)
        : option.id === 'custom_shape' && currentValueKey.includes('|shape:')
        ? option.values.some((value) =>
          String(value.value) === normalizeCustomShapeProviderValue(currentValueKey) &&
          matchesShowIf(value.showIf, configuration)
        )
        : option.values.some((value) =>
          String(value.value) === currentValueKey &&
          matchesShowIf(value.showIf, configuration) &&
          (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(String(value.value))) &&
          !isValueExcluded(option.id, String(value.value), configuration)
        );

      if (isCurrentVisible) return false;

      return option.values.some((value) =>
        matchesShowIf(value.showIf, configuration) &&
        (
          option.id === 'custom_shape' ||
          (
            (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(String(value.value))) &&
            !isValueExcluded(option.id, String(value.value), configuration)
          )
        )
      );
    });
  }, [configuration, filteredOptions, invalidValues, isInitialized, localizedSpecifications, providerProductId, productSlug]);

  const addMatrixCustomQuantity = useCallback((valueOverride?: string) => {
    if (closedCustomQuantities !== null) return;
    const rawValue = valueOverride || matrixCustomQuantityInputRef.current?.value || matrixCustomQuantity;
    const typedValue = Number(rawValue);
    // Producten die per set worden verkocht (bijv. per 3) krijgen het
    // eerstvolgende geldige aantal; de rij toont dat aantal met zijn prijs.
    const value = Number.isInteger(typedValue) && typedValue > 0 && matrixQuantityBounds.step > 1
      ? Math.ceil(typedValue / matrixQuantityBounds.step) * matrixQuantityBounds.step
      : typedValue;
    if (!Number.isInteger(value) || value <= 0) {
      setMatrixCustomQuantityError(uiText('enterWholeNumber'));
      return;
    }
    if (value < matrixQuantityBounds.minimum) {
      setMatrixCustomQuantityError(uiText('minimumPieces', { min: matrixQuantityBounds.minimum }));
      return;
    }
    if (value > matrixQuantityBounds.maximum) {
      setMatrixCustomQuantityError(uiText('maximumPieces', { max: matrixQuantityBounds.maximum }));
      return;
    }
    if (defaultMatrixQuantities.includes(value) || customMatrixQuantities.includes(value)) {
      setMatrixCustomQuantityError(uiText(existingMatrixQuantityFeedback({
        quantity: value, presetQuantities: defaultMatrixQuantities, customQuantities: customMatrixQuantities,
        rows: deliveryQuantityRows, loading: isDeliveryRowsLoading,
      }) ?? 'quantityAlreadyShown'));
      return;
    }
    setCustomMatrixQuantities((current) => [...current, value].sort((a, b) => a - b));
    setMatrixCustomQuantity('');
    if (matrixCustomQuantityInputRef.current) matrixCustomQuantityInputRef.current.value = '';
    setMatrixCustomQuantityError('');
  }, [customMatrixQuantities, defaultMatrixQuantities, deliveryQuantityRows, isDeliveryRowsLoading, matrixCustomQuantity, matrixQuantityBounds.maximum, matrixQuantityBounds.minimum, matrixQuantityBounds.step, uiText, closedCustomQuantities]);

  // Een optie met `metadata.deliveryOverride`-waarden (levering=snel bij
  // dekbedovertrekken) staat als extra kolom in de prijsmatrix, niet als
  // tegelgroep. Alleen Print.com: calculate-price prijst die kolom via de
  // leveranciersroute, net als bij afrekenen (option-supplier-route.ts).
  const deliveryOverrideModel = useMemo(
    () => providerType === 'PRINTCOM' ? buildDeliveryOverrideModel(localizedOptions) : null,
    [providerType, localizedOptions],
  );

  useEffect(() => {
    let cancelled = false;

    // Designer bestelt: de tabel is verborgen en de designer prijst zelf, één
    // aanvraag per ontwerpwijziging (Ruling P6). Geen batch van tien erbij.
    if (!loadsDeliveryMatrix(vinylDesignerMode)) {
      setDeliveryQuantityRows((vorige) => (vorige.length ? [] : vorige));
      setIsDeliveryRowsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // Several local effects repair dependent defaults after the localized
    // catalogue arrives. Do not spend a supplier call on an intermediate
    // configuration that is already known to be replaced in the same render
    // cycle (for example die_cut=yes -> no, then finish/printingmethod).
    if (hasPendingLocalConfigurationCorrection) {
      if (providerType === 'PRINTCOM') {
        setIsPriceLoading(true);
        setLatestPriceQuote(null);
      }
      return () => {
        cancelled = true;
      };
    }

    // Wait until the locale-specific option set is complete. Previously the
    // temporary initial options triggered one matrix, followed immediately by
    // a second larger matrix when the localized options arrived.
    if (isLoadingOptions) {
      if (providerType === 'PRINTCOM') {
        setIsPriceLoading(true);
        setLatestPriceQuote(null);
      }
      return () => {
        cancelled = true;
      };
    }

    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    const providerQuantityOption = providerType === 'PRINTFUL'
      ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
      : null;
    const productQuantityOption = providerQuantityOption || copiesOption;
    const productQuantityOptionId = productQuantityOption?.id;
    const hasCopiesOption = Boolean(copiesOption);
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    const actualQuantity = productQuantityOptionId && effectiveConfiguration[productQuantityOptionId]
      ? Math.max(1, parseInt(String(effectiveConfiguration[productQuantityOptionId]), 10) || 1)
      : Math.max(1, quantity || 1);
    const sizeOption = localizedOptions.find(opt => opt.id === 'size');
    const selectedSizeValue = sizeOption?.values?.find(v => String(v.value) === String(effectiveConfiguration.size));
    const customSizeSelected = Boolean(
      selectedSizeValue?.customSizes ||
      String(effectiveConfiguration.size || '') === 'custom' || String(effectiveConfiguration.size || '').startsWith('custom:')
    );
    const customSizeReady = !customSizeSelected || (providerType === 'PRINTCOM' ? customSizeInputReady(effectiveConfiguration) : (
      Number(effectiveConfiguration.size_width) > 0 && Number(effectiveConfiguration.size_height) > 0
    ));
    // Een proefexemplaar is 1 stuk: alleen die rij opvragen (zie single-piece-quantity.ts).
    const singlePieceOnly = Boolean(singlePieceQuantityPolicy(effectiveConfiguration, localizedSpecifications, copiesOption?.id));
    const quantityCandidates = singlePieceOnly ? [1] : Array.from(new Set([
      ...defaultMatrixQuantities,
      ...customMatrixQuantities,
    ]))
      .filter(v => v >= matrixQuantityBounds.minimum) // Don't show quantities below minimum
      .filter(v => isAllowedClosedQuantity(v, closedCustomQuantities))
      .sort((a, b) => a - b);

    const urgencyOption = localizedOptions.find(opt => opt.id === 'urgency');
    const toColumn = (urgencyId: string): DeliveryPriceColumn => {
      const meta = getUrgencyPresentation(urgencyId, urgencyOption, locale, getStaticProviderLeadTime(providerType, localizedSpecifications), explicitSpecificationLeadTime(localizedSpecifications));
      return {
        id: urgencyId,
        label: urgencyId === 'standard' && providerType === 'PRINTCOM' ? ui.locked : meta.label,
        businessDays: providerType === 'PRINTCOM' ? meta.label : meta.businessDays,
        estimatedDate: providerType === 'PRINTCOM' ? '' : formatDeliveryDate(addBusinessDays(new Date(), meta.days), locale),
        ...pendingDeliveryPresentation(providerType, localizedSpecifications, locale),
      };
    };

    if (providerType === 'CUSTOM') {
      const urgencyValues = uniqueOptionValues(urgencyOption?.values || [])
        .map((value) => String(value.value))
        .filter(Boolean);
      const columns = (urgencyValues.length ? urgencyValues : ['standard']).map(toColumn);
      const unavailableCell = (column: DeliveryPriceColumn): DeliveryPriceCell => ({
        ...column,
        description: column.label,
        subtotalExVat: 0,
        totalInclVat: 0,
        unitInclVat: 0,
        available: false,
      });
      const initialRows = quantityCandidates.map((rowQuantity) => ({
        quantity: rowQuantity,
        cells: Object.fromEntries(columns.map((column) => [column.id, unavailableCell(column)])) as Record<string, DeliveryPriceCell>,
        isCustom: customMatrixQuantities.includes(rowQuantity),
      }));

      setDeliveryColumns(columns);
      setDeliveryQuantityRows(initialRows);
      setIsDeliveryRowsLoading(true);

      const controller = new AbortController();
      const timer = setTimeout(async () => {
        try {
          // De hele tabel (aantallen x levertijden) in een aanvraag. Voorheen
          // was elke cel een eigen aanvraag en stond een klant die opties
          // vergeleek na een paar klikken op de limiet per IP.
          const cellRequests = quantityCandidates.flatMap((rowQuantity) => columns.map((column) => ({
            key: `${rowQuantity}:${column.id}`,
            item: {
              productId,
              configuration: {
                ...effectiveConfiguration,
                ...(hasCopiesOption ? { [copiesOption!.id]: rowQuantity } : {}),
                urgency: column.id,
              },
              quantity: hasCopiesOption ? 1 : rowQuantity,
              countryCode,
              locale,
            },
          })));
          const cellResults = await fetchPricingBatch(cellRequests.map((cell) => cell.item), { signal: controller.signal });
          const cellResultAt = new Map(cellRequests.map((cell, index) => [cell.key, cellResults[index]]));
          const rows = quantityCandidates.map((rowQuantity) => {
            const entries = columns.map((column) => {
              const result = cellResultAt.get(`${rowQuantity}:${column.id}`);
              if (!result?.ok || !result.data) return [column.id, unavailableCell(column)] as const;
              const data = result.data;
              const subtotalExVat = Number(data.calculation?.subtotal || 0);
              const totalInclVat = calculatePriceWithVAT(subtotalExVat, vatCountryCode);
              return [column.id, {
                ...column,
                description: pendingDeliveryPresentation(providerType, localizedSpecifications, locale)?.businessDays
                  || getUrgencyPresentation(column.id, urgencyOption, locale, getStaticProviderLeadTime(providerType, localizedSpecifications), explicitSpecificationLeadTime(localizedSpecifications)).description,
                subtotalExVat,
                totalInclVat,
                unitInclVat: rowQuantity > 0 ? totalInclVat / rowQuantity : totalInclVat,
                quote: data.quote,
                available: subtotalExVat > 0,
              } satisfies DeliveryPriceCell] as const;
            });

            return {
              quantity: rowQuantity,
              cells: Object.fromEntries(entries) as Record<string, DeliveryPriceCell>,
              isCustom: customMatrixQuantities.includes(rowQuantity),
            };
          });

          if (!cancelled) {
            const visibleRows = rows.filter((row) =>
              row.isCustom || Object.values(row.cells).some((cell) => cell?.available)
            );
            const availableColumns = columns.filter((column) => visibleRows.some((row) => row.cells[column.id]?.available));
            setDeliveryColumns(availableColumns.length > 0 ? availableColumns : columns);
            setDeliveryQuantityRows(visibleRows.length > 0 ? visibleRows : rows);
          }
        } catch (error) {
          if (cancelled || (error as { name?: string })?.name === 'AbortError') return;
          if (isPriceRateLimitedError(error)) {
            setDeliveryColumns(columns);
            setDeliveryQuantityRows(initialRows);
            handlePriceRateLimited(error);
            return;
          }
          console.error('Kon custom levertijd/prijstabel niet ophalen:', error);
          if (!cancelled) {
            setDeliveryColumns(columns);
            setDeliveryQuantityRows(initialRows);
          }
        } finally {
          if (!cancelled) setIsDeliveryRowsLoading(false);
        }
      }, 200);

      return () => {
        cancelled = true;
        clearTimeout(timer);
        controller.abort();
      };
    }

    if (providerType !== 'PRINTCOM') {
      const column = toColumn('standard');
      const rows = providerType === 'PRODIGI' || providerType === 'PRINTFUL' || providerType === 'PROBO'
        ? quantityCandidates.map((rowQuantity) => {
            const totalExVat = Number(quantityPrices[rowQuantity] || 0);
            const totalInclVat = calculatePriceWithVAT(totalExVat, vatCountryCode);
            return {
              quantity: rowQuantity,
              cells: {
                standard: {
                  ...column,
                  description: getUrgencyPresentation('standard', urgencyOption, locale, getStaticProviderLeadTime(providerType, localizedSpecifications), explicitSpecificationLeadTime(localizedSpecifications)).description,
                  subtotalExVat: totalExVat,
                  totalInclVat,
                  unitInclVat: rowQuantity > 0 ? totalInclVat / rowQuantity : totalInclVat,
                  available: totalInclVat > 0,
                },
              },
              isCustom: customMatrixQuantities.includes(rowQuantity),
            };
          })
        : null;
      const totalInclVat = pricing.total;
      setDeliveryColumns([column]);
      setDeliveryQuantityRows(rows || [{
        quantity: actualQuantity,
        cells: {
          standard: {
            ...column,
            description: getUrgencyPresentation('standard', urgencyOption, locale, getStaticProviderLeadTime(providerType, localizedSpecifications), explicitSpecificationLeadTime(localizedSpecifications)).description,
            subtotalExVat: pricing.subtotal > 0 ? pricing.subtotal / 1.21 : 0,
            totalInclVat,
            unitInclVat: actualQuantity > 0 ? totalInclVat / actualQuantity : totalInclVat,
            available: totalInclVat > 0,
          },
        },
      }]);
      setIsDeliveryRowsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // This is the sole live-price path for Print.com products. Invalidate the
    // previous quote immediately so a stale configuration can never be ordered
    // while the debounced replacement batch is still loading.
    pricedProductionOptionsRef.current = {};
    setIsPriceLoading(true);
    setLatestPriceQuote(null);

    if (!customSizeReady) {
      const column = toColumn('standard');
      setDeliveryColumns([column]);
      setDeliveryQuantityRows(quantityCandidates.map((rowQuantity) => ({
        quantity: rowQuantity,
        cells: {
          standard: {
            ...column,
            description: column.label,
            subtotalExVat: 0,
            totalInclVat: 0,
            unitInclVat: 0,
            available: false,
          },
        },
        isCustom: customMatrixQuantities.includes(rowQuantity),
      })));
      setIsDeliveryRowsLoading(false);
      setQuantityPrices({});
      setIsValidConfiguration(false);
      setIsPriceLoading(false);
      setLatestPriceQuote(null);
      setConfigurationError(Number(effectiveConfiguration.size_width) > 0 && Number(effectiveConfiguration.size_height) > 0
        ? ui.configurationImpossible : null);
      return () => {
        cancelled = true;
      };
    }

    // A Print.com product can carry option values made by another linked
    // supplier (value.metadata.supplierRoute). Print.com cannot price or judge
    // those, so this matrix uses the central pricing route instead, which
    // prices them from that supplier exactly like order creation does
    // (resolveSupplierOnlyPricing). Only the standard delivery column applies.
    if (configurationHasForeignSupplierValue(localizedOptions, effectiveConfiguration)) {
      const column = toColumn('standard');
      const unavailable = (): DeliveryPriceCell => ({
        ...column, description: column.label, subtotalExVat: 0, totalInclVat: 0, unitInclVat: 0, available: false,
      });
      if (!deliveryMatrixSnapshotRef.current) {
        setDeliveryColumns([column]);
        setDeliveryQuantityRows(quantityCandidates.map((rowQuantity) => ({
          quantity: rowQuantity, cells: { standard: unavailable() }, isCustom: customMatrixQuantities.includes(rowQuantity),
        })));
      }
      setIsDeliveryRowsLoading(true);
      const controller = new AbortController();
      const timer = setTimeout(async () => {
        const deliveryRequestId = deliveryPriceRequestIdRef.current + 1;
        deliveryPriceRequestIdRef.current = deliveryRequestId;
        const isCurrent = () => !cancelled && deliveryPriceRequestIdRef.current === deliveryRequestId;
        try {
          // Alle rijen in een aanvraag (zie fetchPricingBatch).
          const rowResults = await fetchPricingBatch(quantityCandidates.map((rowQuantity) => ({
            productId,
            configuration: {
              ...effectiveConfiguration,
              ...(hasCopiesOption ? { [copiesOption!.id]: rowQuantity } : {}),
              ...(urgencyOption ? { urgency: 'standard' } : {}),
            },
            quantity: hasCopiesOption ? 1 : rowQuantity,
            countryCode,
            locale,
          })), { signal: controller.signal });
          const rows = quantityCandidates.map((rowQuantity, rowIndex) => {
            const data = rowResults[rowIndex]?.ok ? rowResults[rowIndex].data : null;
            const subtotalExVat = Number(data?.calculation?.subtotal || 0);
            const totalInclVat = calculatePriceWithVAT(subtotalExVat, vatCountryCode);
            const cell: DeliveryPriceCell = data?.success !== false && subtotalExVat > 0 && data?.quote?.hash
              ? { ...column, description: column.label, subtotalExVat, totalInclVat,
                  unitInclVat: rowQuantity > 0 ? totalInclVat / rowQuantity : totalInclVat, quote: data.quote, available: true }
              : unavailable();
            return { quantity: rowQuantity, cells: { standard: cell }, isCustom: customMatrixQuantities.includes(rowQuantity) };
          });
          if (!isCurrent()) return;
          const availableRows = rows.filter((row) => row.isCustom || row.cells.standard.available);
          setDeliveryColumns([column]);
          setDeliveryQuantityRows(availableRows.length > 0 ? availableRows : rows);
          const selectedCell = rows.find((row) => row.quantity === actualQuantity)?.cells.standard;
          if (selectedCell?.available && selectedCell.quote?.hash) {
            setQuantityPrices(hasCopiesOption ? { 1: selectedCell.subtotalExVat } : { [actualQuantity]: selectedCell.subtotalExVat });
            setShippingCost(0);
            setLatestPriceQuote(selectedCell.quote);
            pricedProductionOptionsRef.current = {};
            setIsValidConfiguration(true);
            setConfigurationError(null);
          } else {
            setQuantityPrices({});
            setLatestPriceQuote(null);
            pricedProductionOptionsRef.current = {};
            setIsValidConfiguration(false);
            setConfigurationError(ui.combinationUnavailable);
          }
        } catch (error) {
          if ((error as any)?.name === 'AbortError' || !isCurrent()) return;
          if (isPriceRateLimitedError(error)) {
            handlePriceRateLimited(error);
            return;
          }
          setQuantityPrices({});
          setLatestPriceQuote(null);
          setIsValidConfiguration(false);
          setConfigurationError(ui.livePriceUnavailable);
        } finally {
          if (isCurrent()) {
            setIsDeliveryRowsLoading(false);
            setIsPriceLoading(false);
          }
        }
      }, 250);
      return () => {
        cancelled = true;
        clearTimeout(timer);
        controller.abort();
      };
    }

    // Alleen levertijden die bij deze configuratie horen (showIf), zodat spoed
    // niet als kolom verschijnt voor uitvoeringen die de leverancier niet met spoed maakt.
    // Levertijden die Print.com voor precies deze samenstelling al weigerde
    // (bijv. spoed bij boekenleggers) niet opnieuw opvragen. De gekozen
    // levertijd blijft staan, zodat de terugval naar standaard hieronder werkt.
    const deliveryRefusalSku = providerProductId || productSlug;
    const refusalKey = deliveryRefusalKey(
      deliveryRefusalSku,
      effectiveConfiguration,
      [copiesOption?.id, deliveryOverrideModel?.optionId].filter((key): key is string => Boolean(key)),
    );
    const refusedUrgencies = refusedDeliveryColumnsFor(refusalKey);
    const urgencyValues = uniqueOptionValues(urgencyOption?.values || [])
      .filter((value) => matchesShowIf(value.showIf, effectiveConfiguration))
      .map((value) => String(value.value))
      .filter(Boolean)
      .filter((value) => !refusedUrgencies.has(value) || value === String(effectiveConfiguration.urgency || ''));
    const urgencyColumns = (urgencyValues.length ? urgencyValues : ['standard'])
      .sort((a, b) => (urgencyLeadTimes[b]?.days || 0) - (urgencyLeadTimes[a]?.days || 0))
      .map(toColumn)
      .map((column) => deliveryOverrideModel && column.id === 'standard'
        // Kop "Standaard" uit de leveringsoptie, zodat hij naast "Snel" klopt.
        ? { ...column, businessDays: deliveryOverrideModel.baseLabel || column.businessDays }
        : column);
    // Levertijden van een andere leverancier (levering=snel) achter de
    // Print.com-kolommen. Geen datumaanvraag bij Print.com voor deze kolom.
    const overrideColumns: DeliveryPriceColumn[] = (deliveryOverrideModel?.columns || []).map((column) => ({
      id: column.id,
      label: column.label,
      businessDays: column.label,
      estimatedDate: '',
    }));
    const columns = [...urgencyColumns, ...overrideColumns];

    const unavailableCell = (column: DeliveryPriceColumn): DeliveryPriceCell => ({
      ...column,
      description: column.label,
      subtotalExVat: 0,
      totalInclVat: 0,
      unitInclVat: 0,
      available: false,
    });

    const initialRows = quantityCandidates.map((rowQuantity) => ({
      quantity: rowQuantity,
      cells: Object.fromEntries(columns.map((column) => [column.id, unavailableCell(column)])) as Record<string, DeliveryPriceCell>,
      isCustom: customMatrixQuantities.includes(rowQuantity),
    }));

    if (!deliveryMatrixSnapshotRef.current) {
      setDeliveryColumns(columns);
      setDeliveryQuantityRows(initialRows);
    }
    setIsDeliveryRowsLoading(true);

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsDeliveryRowsLoading(true);
      const deliveryRequestId = deliveryPriceRequestIdRef.current + 1;
      deliveryPriceRequestIdRef.current = deliveryRequestId;
      const isCurrentDeliveryPriceRequest = () => deliveryPriceRequestIdRef.current === deliveryRequestId;
      try {
        const fetchColumnPrices = async (requestedColumns: DeliveryPriceColumn[]) => {
          const batchRequests: Array<{
            sku: string;
            quantity: number;
            options: Record<string, any>;
            deliveryPromise: number;
            countryCode: string;
            locale: string;
          }> = [];
          const cellIndexMap: Array<{ rowQuantity: number; columnId: string }> = [];
          let selectedQuantityExcluded = false;

          for (const rowQuantity of quantityCandidates) {
            for (const column of requestedColumns) {
              const isOverrideColumn = Boolean(findDeliveryOverrideColumn(deliveryOverrideModel, column.id));
              const optionsForRow = {
                ...effectiveConfiguration,
                ...(hasCopiesOption ? { [copiesOption!.id]: rowQuantity } : {}),
                // urgency plus (bij een leveringsoptie) de bijbehorende waarde:
                // de snel-kolom vraagt levering=snel, zodat calculate-price hem
                // prijst zoals de checkout hem afrekent.
                ...configurationPatchForColumn(deliveryOverrideModel, column.id),
              };
              // A number field may have holes depending on material/size (e.g.
              // tape 96 or giftboxes 50). Do not ask the server to price known
              // forbidden cells. Evaluate the actual delivery column, and leave
              // technique-specific conflicts to expandPrintingMethods below.
              if (!isOverrideColumn && hasCopiesOption && isMatrixQuantityExcluded(excludes, optionsForRow, rowQuantity)) {
                if (rowQuantity === actualQuantity && column.id === selectedMatrixColumnId(effectiveConfiguration, deliveryOverrideModel)) {
                  selectedQuantityExcluded = true;
                }
                continue;
              }
              const compatibilityIssue = hasExactPrintComCatalogCoverage || isOverrideColumn ? null : getPrintComOptionCompatibilityIssue({
                sku: providerProductId || productSlug,
                productSlug,
                providerProductId,
                options: optionsForRow,
              });
              if (compatibilityIssue) continue;
              batchRequests.push({
                sku: providerProductId || productSlug,
                quantity: hasCopiesOption ? 1 : rowQuantity,
                options: optionsForRow,
                deliveryPromise,
                countryCode,
                locale,
              });
              cellIndexMap.push({ rowQuantity, columnId: column.id });
            }
          }

          const cellResults = new Map<string, DeliveryPriceCell>();
          const cellOutcomes: MatrixCellOutcome[] = [];
          // Als de opgeslagen excludes elke drukmethode voor deze samenstelling
          // uitsluiten (bijvoorbeeld A0 dubbelzijdig), is dat blijvend zo, niet
          // "tijdelijk". Onthouden welke reden de server meegaf zodat we hierna
          // eerlijk kunnen zijn in plaats van standaard om een nieuwe poging te vragen.
          let impossibleReasonHint: 'double_sided_unavailable' | 'generic' | 'supplier_unavailable' | null = selectedQuantityExcluded ? 'generic' : null;
          if (batchRequests.length === 0) return { cellResults, impossibleReasonHint };

          const batchResponse = await fetch('/api/printcom/calculate-price', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              // The matrix always uses current supplier prices. Hybrid mode
              // first walks the local tier catalog and then calls this same
              // official batch endpoint, which adds latency and can be stale.
              pricingMode: 'api',
              expandPrintingMethods: true,
              batch: batchRequests,
            }),
            signal: controller.signal,
          });
          throwIfRateLimited(batchResponse);
          const batchData = await batchResponse.json().catch(() => null);
          const results = Array.isArray(batchData?.results) ? batchData.results : [];
          if (!batchResponse.ok && results.length === 0) {
            console.warn(`Print.com prijsmatrix niet beschikbaar (${batchResponse.status}); cellen worden als niet beschikbaar getoond.`);
          }

          for (let i = 0; i < results.length && i < cellIndexMap.length; i++) {
            const { rowQuantity, columnId } = cellIndexMap[i];
            const result = results[i];
            const key = `${rowQuantity}:${columnId}`;
            const columnMeta = columns.find((column) => column.id === columnId) || toColumn(columnId);
            if (!findDeliveryOverrideColumn(deliveryOverrideModel, columnId)) {
              cellOutcomes.push({ columnId, success: Boolean(result?.success && !result?.error), code: result?.code });
            }
            if (result?.error || !result?.success) {
              if (result?.code === 'SUPPLIER_CONFIGURATION_UNAVAILABLE' || result?.code === 'SUPPLIER_ROUTE_PRICE_UNAVAILABLE') impossibleReasonHint = 'supplier_unavailable';
              if (result?.code === 'CONFIGURATION_IMPOSSIBLE') {
                impossibleReasonHint = result.reasonHint === 'double_sided_unavailable'
                  ? 'double_sided_unavailable'
                  : (impossibleReasonHint || 'generic');
              }
              cellResults.set(key, unavailableCell(columnMeta));
              continue;
            }

            // De server kan een prijs teruggeven voor een andere samenstelling
            // dan gevraagd. Dan is de cel niet beschikbaar, hoe geldig de prijs
            // en de quote-hash er ook uitzien.
            const optionDrift = findEchoedOptionDrift(
              batchRequests[i]?.options,
              result.calculation?.options
            );
            if (optionDrift) {
              console.warn(
                `Prijs hoort niet bij de gevraagde samenstelling (${optionDrift.key}): ` +
                `gevraagd=${String(optionDrift.requested)} geprijsd=${String(optionDrift.echoed)}`,
                { requested: batchRequests[i]?.options, echoed: result.calculation?.options }
              );
              cellResults.set(key, unavailableCell(columnMeta));
              continue;
            }

            const subtotalExVat = Number(result.calculation?.subtotal || 0);
            const totalInclVat = calculatePriceWithVAT(subtotalExVat, vatCountryCode);
            const overrideColumn = findDeliveryOverrideColumn(deliveryOverrideModel, columnId);
            cellResults.set(key, {
              ...columnMeta,
              description: overrideColumn?.description || urgencyLeadTimes[columnId]?.description || columnMeta.label,
              subtotalExVat,
              totalInclVat,
              unitInclVat: rowQuantity > 0 ? totalInclVat / rowQuantity : totalInclVat,
              quote: result.quote,
              productionOptions: result.calculation?.options?.printingmethod
                ? { printingmethod: result.calculation.options.printingmethod }
                : undefined,
              // Een overridekolom wordt niet door Print.com gemaakt; daar
              // vragen we dus ook geen leverdatum voor op.
              ...(overrideColumn ? {} : {
                deliveryRequest: {
                  id: key,
                  sku: batchRequests[i].sku,
                  quantity: batchRequests[i].quantity,
                  options: { ...batchRequests[i].options, ...result.calculation?.options },
                  deliveryPromise: batchRequests[i].deliveryPromise,
                },
              }),
              available: subtotalExVat > 0,
            });
          }
          rememberRefusedDeliveryColumns(refusalKey, refusedDeliveryColumnIds(cellOutcomes));
          return { cellResults, impossibleReasonHint };
        };

        const publishRows = (
          cellResults: Map<string, DeliveryPriceCell>,
          impossibleReasonHint: 'double_sided_unavailable' | 'generic' | 'supplier_unavailable' | null
        ) => {
          if (cancelled || !isCurrentDeliveryPriceRequest()) return;
          const rows = quantityCandidates.map((rowQuantity) => ({
            quantity: rowQuantity,
            cells: Object.fromEntries(columns.map((column) => [
              column.id,
              cellResults.get(`${rowQuantity}:${column.id}`) || unavailableCell(column),
            ])) as Record<string, DeliveryPriceCell>,
            isCustom: customMatrixQuantities.includes(rowQuantity),
          }));
          const availableRows = rows.filter((row) =>
            row.isCustom || Object.values(row.cells).some((cell) => cell?.available)
          );
          setDeliveryColumns(columns);
          setDeliveryQuantityRows(availableRows.length > 0 ? availableRows : rows);

          const selectedRow = rows.find((row) => row.quantity === actualQuantity);
          // Bij levering=snel hoort de prijs uit de snel-kolom; nooit terugvallen
          // op een Print.com-cel, want dan klopt het getoonde bedrag niet met
          // wat de checkout voor snel rekent.
          const selectedColumnId = effectiveConfiguration.urgency || deliveryOverrideModel
            ? selectedMatrixColumnId(effectiveConfiguration, deliveryOverrideModel)
            : String(columns[0]?.id || 'standard');
          const selectedIsOverride = Boolean(findDeliveryOverrideColumn(deliveryOverrideModel, selectedColumnId));
          const selectedCell = selectedRow?.cells[selectedColumnId] || (selectedIsOverride ? undefined :
            Object.values(selectedRow?.cells || {}).find((cell) => cell.available && !findDeliveryOverrideColumn(deliveryOverrideModel, cell.id)));

          if (selectedCell?.available && selectedCell.subtotalExVat > 0 && selectedCell.quote?.hash) {
            setQuantityPrices(hasCopiesOption
              ? { 1: selectedCell.subtotalExVat }
              : { [actualQuantity]: selectedCell.subtotalExVat });
            setShippingCost(0);
            setLatestPriceQuote(selectedCell.quote);
            pricedProductionOptionsRef.current = selectedCell.productionOptions || {};
            setIsValidConfiguration(true);
            setConfigurationError(null);
          } else {
            setQuantityPrices({});
            setLatestPriceQuote(null);
            pricedProductionOptionsRef.current = {};
            setIsValidConfiguration(false);
            setConfigurationError(
              impossibleReasonHint === 'supplier_unavailable' ? ui.combinationUnavailable : impossibleReasonHint === 'double_sided_unavailable'
                ? ui.configurationImpossibleDoubleSided
                : impossibleReasonHint === 'generic'
                ? ui.configurationImpossible
                : ui.pricingTemporarilyUnavailable
            );
          }
        };

        // One supplier batch contains every quantity and delivery column.
        // Splitting this doubled the Print.com request count after each change.
        const { cellResults, impossibleReasonHint } = await fetchColumnPrices(columns);
        publishRows(cellResults, impossibleReasonHint);
      } catch (error) {
        if ((error as any)?.name === 'AbortError') return;
        if (isPriceRateLimitedError(error)) {
          if (!cancelled && isCurrentDeliveryPriceRequest()) {
            pricedProductionOptionsRef.current = {};
            handlePriceRateLimited(error);
          }
          return;
        }
        console.error('Kon levertijd/prijstabel niet ophalen:', error);
        if (!cancelled && isCurrentDeliveryPriceRequest()) {
          if (!deliveryMatrixSnapshotRef.current) {
            setDeliveryColumns(columns);
            setDeliveryQuantityRows(initialRows);
          }
          setQuantityPrices({});
          setLatestPriceQuote(null);
          pricedProductionOptionsRef.current = {};
          setIsValidConfiguration(false);
          setConfigurationError(ui.livePriceUnavailable);
        }
      } finally {
        if (!cancelled && isCurrentDeliveryPriceRequest()) {
          setIsDeliveryRowsLoading(false);
          setIsPriceLoading(false);
        }
      }
    }, 700);

    return () => {
      cancelled = true;
      deliveryPriceRequestIdRef.current += 1;
      controller.abort(abortReason('Delivery price matrix superseded by a configuration change'));
      clearTimeout(timer);
    };
  }, [
    configuration,
    quantity,
    customMatrixQuantities,
    defaultMatrixQuantities,
    hasPendingLocalConfigurationCorrection,
    isInitialized,
    // CUSTOM haalt de tabel zelf op en leest quantityPrices niet; anders liep
    // de hele tabel na elke hoofdprijs nog een keer (13 aanvragen per klik).
    providerType === 'PRINTCOM' || providerType === 'CUSTOM' ? null : quantityPrices,
    isProviderMatrixLoading,
    priceRetryNonce,
    isLoadingOptions,
    localizedOptions,
    localizedSpecifications,
    providerType,
    providerProductId,
    productSlug,
    deliveryPromise,
    countryCode,
    locale,
    vatCountryCode,
    vinylDesignerMode,
    deliveryOverrideModel,
  ]);

  const isPriceReady = useMemo(() => {
    if (!isClosedQuantitySelectionValid) return false;
    if (readOnly) return true;
    // Een prijs die bij de vorige materiaal-/optiecombinatie hoorde is tijdens
    // een nieuwe aanvraag nooit bestelbaar, ook al is het bedrag nog positief.
    if (isPriceLoading) return false;
    if (!isValidConfiguration || configurationError) return false;
    if (!Number.isFinite(pricing.total) || pricing.total <= 0) return false;
    if ((providerType === 'PRINTCOM' || providerType === 'CUSTOM') && !latestPriceQuote?.hash) return false;
    return true;
  }, [configurationError, isPriceLoading, isValidConfiguration, latestPriceQuote, pricing.total, providerType, readOnly, isClosedQuantitySelectionValid]);

  const printComQuantityOption = localizedOptions.find((option) => option.id === 'copies' || option.name === 'copies');
  const selectedDeliveryQuantity = Number((printComQuantityOption && configuration[printComQuantityOption.id]) || quantity || 1);
  const selectedDeliveryUrgency = String(configuration.urgency || 'standard');
  const selectedDeliveryRow = useMemo(() => deliveryQuantityRows.find((row) => row.quantity === selectedDeliveryQuantity)
    || deliveryQuantityRows.find((row) => Object.values(row.cells).some((cell) => cell.available)), [deliveryQuantityRows, selectedDeliveryQuantity]);
  const deliveryDateQuantity = selectedDeliveryRow?.quantity || selectedDeliveryQuantity;
  // Een eigen optie als `levering` kent Print.com niet; met die sleutel erin
  // bleef de leverdatum op "wordt gecontroleerd" staan (16-10-2026).
  const deliveryAvailabilityRequests = useMemo(() => {
    const requests = selectedQuantityDeliveryRequests(selectedDeliveryRow?.cells || {}, deliveryDateQuantity);
    const localKey = deliveryOverrideModel?.optionId;
    if (!localKey) return requests;
    return requests.map((request) => {
      if (!Object.prototype.hasOwnProperty.call(request.options, localKey)) return request;
      const { [localKey]: _local, ...options } = request.options;
      return { ...request, options };
    });
  }, [selectedDeliveryRow, deliveryDateQuantity, deliveryOverrideModel]);
  // Een optiewaarde met `metadata.deliveryOverride` (bijv. levering=snel bij
  // dekbedovertrekken) laat de regel door een andere leverancier maken. De
  // Print.com-leverdatum geldt dan niet en wordt ook niet opgevraagd; de
  // levertijd komt uit de metadata van die waarde, zoals bij CUSTOM-producten.
  const selectedDeliveryOverride = useMemo(() => {
    for (const option of localizedOptions) {
      const selected = configuration[option.id];
      if (selected === undefined || selected === null || selected === '') continue;
      const value = option.values?.find((candidate) => String(candidate.value) === String(selected));
      const metadata = (value?.metadata || {}) as Record<string, unknown>;
      if (metadata.deliveryOverride !== true) continue;
      const days = Number(metadata.deliveryDays);
      return {
        days: Number.isFinite(days) && days > 0 ? days : 5,
        range: typeof metadata.businessDaysRange === 'string' ? metadata.businessDaysRange : '',
      };
    }
    return null;
  }, [localizedOptions, configuration]);
  const deliveryOverrideText = useMemo(() => {
    if (!selectedDeliveryOverride) return '';
    const language = locale.split('-')[0];
    const approx = selectedDeliveryOverride.range || (({
      nl: `ca. ${selectedDeliveryOverride.days} werkdagen`,
      en: `about ${selectedDeliveryOverride.days} working days`,
      de: `ca. ${selectedDeliveryOverride.days} Werktage`,
      fr: `environ ${selectedDeliveryOverride.days} jours ouvrés`,
    } as Record<string, string>)[language] || `about ${selectedDeliveryOverride.days} working days`);
    return `${approx} (${formatDeliveryDate(addBusinessDays(new Date(), selectedDeliveryOverride.days), locale)})`;
  }, [selectedDeliveryOverride, locale]);
  // De Print.com-kolommen houden hun eigen leverdatum, ook als de klant de
  // snel-kolom koos; de overridecellen hebben geen deliveryRequest en komen
  // dus nooit in deze aanvraag.
  const deliveryAvailability = usePrintComDeliveryAvailability(deliveryAvailabilityRequests, countryCode, providerType === 'PRINTCOM' && !isDeliveryRowsLoading);
  const selectedMatrixColumn = selectedMatrixColumnId(configuration, deliveryOverrideModel);
  // Datumregel onder een kolomkop: een overridekolom rekent zelf vanaf vandaag
  // (zelfde berekening als de bezorgregel), de rest komt van Print.com.
  const deliveryTextFor = (columnId: string) => {
    const override = findDeliveryOverrideColumn(deliveryOverrideModel, columnId);
    if (override) {
      return `${deliveryOverrideApproxText(override, locale)} (${formatDeliveryDate(addBusinessDays(new Date(), override.days), locale)})`;
    }
    return matrixDeliveryText(deliveryAvailability.get(`${deliveryDateQuantity}:${columnId}`), locale);
  };
  const visibleDeliveryColumns = useMemo(() => {
    if (providerType !== 'PRINTCOM') return deliveryColumns;
    const printComIds = visibleDeliverySpeedIds(
      deliveryColumns.filter((item) => !findDeliveryOverrideColumn(deliveryOverrideModel, item.id)).map((item) => item.id),
      deliveryDateQuantity,
      deliveryAvailability,
    );
    // Een overridekolom heeft geen Print.com-datum en valt dus niet onder
    // visibleDeliverySpeedIds; hij blijft staan zolang er een prijs in zit.
    return deliveryColumns.filter((column) => findDeliveryOverrideColumn(deliveryOverrideModel, column.id)
      ? deliveryQuantityRows.some((row) => row.cells[column.id]?.available) || isDeliveryRowsLoading || selectedMatrixColumn === column.id
      : printComIds.includes(column.id));
  }, [providerType, deliveryColumns, deliveryDateQuantity, deliveryAvailability, deliveryOverrideModel, deliveryQuantityRows, isDeliveryRowsLoading, selectedMatrixColumn]);
  const isDeliverySpeedOrderable = providerType !== 'PRINTCOM' || selectedDeliveryUrgency === 'standard'
    || visibleDeliveryColumns.some(column => column.id === selectedDeliveryUrgency);
  const selectedDeliveryKey = `${deliveryDateQuantity}:${selectedDeliveryUrgency}`;
  const selectedDeliveryResult = deliveryAvailability.get(selectedDeliveryKey);
  // Blijft de leverdatum van de leverancier uit, toon dan na een paar seconden de
  // gewone werkdagenrange in plaats van een melding die blijft hangen.
  const [deliveryWaitExpiredKey, setDeliveryWaitExpiredKey] = useState<string | null>(null);
  useEffect(() => {
    if (providerType !== 'PRINTCOM' || !isPriceReady || selectedDeliveryOverride || selectedDeliveryResult?.status === 'confirmed') return;
    const timer = window.setTimeout(() => setDeliveryWaitExpiredKey(selectedDeliveryKey), 6000);
    return () => window.clearTimeout(timer);
  }, [providerType, isPriceReady, selectedDeliveryKey, selectedDeliveryResult?.status, selectedDeliveryOverride]);
  const staticDeliveryRange = deliveryColumns.find((column) => column.id === selectedDeliveryUrgency)?.businessDays;
  const selectedProductionDays = isPriceReady
    ? providerType === 'PRINTCOM'
      ? deliveryOverrideText || (selectedDeliveryResult?.status !== 'confirmed' && selectedDeliveryResult?.status !== 'unavailable'
        && deliveryWaitExpiredKey === selectedDeliveryKey && staticDeliveryRange)
        || matrixDeliveryText(selectedDeliveryResult, locale)
      : staticDeliveryRange
    : undefined;
  const productionTimeLabel = providerType === 'PRINTCOM'
    ? ({ nl: 'Verwachte bezorging', de: 'Voraussichtliche Lieferung', en: 'Expected delivery', fr: 'Livraison prévue' } as Record<string, string>)[locale.split('-')[0]] || 'Expected delivery'
    : ({ nl: 'Productietijd', de: 'Produktionszeit', en: 'Production time', fr: 'Délai de production' } as Record<string, string>)[locale.split('-')[0]] || 'Productietijd';

  // De aantal-optie op productniveau (copies, of quantity bij Printful). Stond
  // eerder per tabelcel opnieuw uitgerekend; nu een keer, zodat de mobiele
  // kaartweergave en de tabel exact dezelfde selectie-logica delen.
  const matrixQuantityOption = useMemo(() => {
    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
    const providerQuantityOption = providerType === 'PRINTFUL'
      ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
      : null;
    return providerQuantityOption || copiesOption;
  }, [localizedOptions, providerType]);

  // Aantal zoals de klant het ziet: een product met een eigen aantal-optie
  // telt zwaarder dan de orderregel-hoeveelheid.
  const displayQuantity = useMemo(() => {
    const raw = matrixQuantityOption ? configuration[matrixQuantityOption.id] : undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : (Number(quantity) || 1);
  }, [configuration, matrixQuantityOption, quantity]);

  const linkedSignPrint = useMemo(() => !onSave
    ? getLinkedSignPrint(providerProductId || productSlug, configuration.size) || getLinkedSignPrint(productSlug, configuration.size)
    : null, [onSave, providerProductId, productSlug, configuration.size]);
  const linkedPrintContext = JSON.stringify([productId, configuration.size, displayQuantity, vatCountryCode]);
  const linkedPrintsReady = linkedPrintRows.every(row => row.context === linkedPrintContext
    && linkedSignPrint && matchesLinkedSignPrint(linkedSignPrint, row.item.configuration.options));

  // De sticky mobiele bestelbalk staat buiten deze component en toont anders de
  // statische vanafprijs terwijl de klant al een duurdere uitvoering koos. We
  // zenden de berekende prijs uit als event; de balk luistert er vrijblijvend op.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ready = isPriceReady && linkedPrintsReady && !readOnly
      && !configurationError
      && !isPriceLoading
      && Number.isFinite(pricing.total)
      && pricing.total > 0
      && pricing.unitPrice > 0;

    const detail = {
      productId,
      ready,
      total: pricing.total + linkedPrintRows.reduce((sum, row) => sum + row.item.totalPrice, 0),
      unitPrice: pricing.unitPrice,
      quantity: displayQuantity,
      // Gekozen formaat, voor de DPI-check-link onder de configurator.
      size: typeof configuration.size === 'string' ? configuration.size : null,
      // Gekozen uitvoering, voor de herkomstmelding (VK/VS) op de productpagina.
      variantSku: typeof configuration.variantSku === 'string' ? configuration.variantSku : null,
      currencyLocale: locale,
      // Vooraf opgemaakt: de balk kent geen locale en mag geen eigen
      // valuta-notatie of vertaalde tekst gaan verzinnen.
      totalLabel: ready ? formatPriceForLocale(pricing.total + linkedPrintRows.reduce((sum, row) => sum + row.item.totalPrice, 0), locale) : null,
      subLabel: ready ? `${displayQuantity}x · ${shippingIncluded && linkedPrintRows.every(row => row.item.shippingIncluded) ? t('inclDeliveryVAT') : exclShippingLabel(t('inclShippingVAT'))}` : null,
    };

    const emit = () => window.dispatchEvent(new CustomEvent('printen:pricing', { detail }));
    emit();
    // Herhaling in de volgende tick: de balk kan zijn listener later koppelen
    // dan dit effect vuurt, en zou de eerste prijs dan missen.
    const replay = window.setTimeout(emit, 0);
    return () => window.clearTimeout(replay);
  }, [configurationError, displayQuantity, isPriceLoading, isPriceReady, locale, pricing.total, pricing.unitPrice, productId, readOnly, shippingIncluded, t, linkedPrintsReady, linkedPrintRows, configuration.size, configuration.variantSku]);

  /**
   * Verplichte opties waar de klant niets aan kan doen.
   *
   * Een optie die verplicht is, zichtbaar hoort te zijn, geen enkele waarde
   * heeft om uit te kiezen en ook geen standaard: die raakt nooit ingevuld. Op
   * softcover-boek is dat `spinewidth`, de rugdikte, die Print.com zelf afleidt
   * uit het aantal pagina's en de papierdikte. Veertien producten hebben zo'n
   * optie, en ze waren geen van alle te bestellen: de knop stond uit met het
   * label "pas je selectie aan", terwijl er niets aan te passen viel.
   */
  const unsatisfiableRequiredOptions = useMemo(() => {
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    return localizedOptions.filter((option) =>
      option.required &&
      !option.hidden &&
      // Een optie die de klant sowieso nooit te zien krijgt is geen keuze die
      // hij kan maken. `designs` staat op die lijst en heeft bij 130 producten
      // geen waarden, want het aantal ontwerpen volgt uit de uploadstap. Zonder
      // deze regel zou elk van die producten alleen nog via een offerte kunnen,
      // terwijl er niets aan de hand is.
      !HIDDEN_CONSUMER_OPTION_IDS.has(option.id) &&
      // Het aantal staat in de aantalkiezer, niet tussen de opties.
      option.id !== 'copies' && option.name !== 'copies' &&
      matchesShowIf(option.showIf, effectiveConfiguration) &&
      // Een getal- of tekstveld hoort geen waardenlijst te hebben: de klant
      // typt daar zelf een maat. Alleen een keuzelijst zonder keuzes is stuk.
      !['number', 'text', 'textarea'].includes(String(option.type || '')) &&
      (option.values || []).length === 0 &&
      (effectiveConfiguration[option.id] === undefined || effectiveConfiguration[option.id] === '')
    );
  }, [configuration, localizedOptions]);

  // Aantalregels van de leverancier ("zeefdruk vanaf 10 stuks"): het aantal
  // is een getalveld, dus de conflictoplosser kan het niet laten meebewegen.
  // Noem het minimum en bied het aan in plaats van alleen "kan niet".
  const copiesOptionForMinimum = localizedOptions.find((option) => option.id === 'copies' || option.name === 'copies');
  const quantityMinimum = copiesOptionForMinimum && !readOnly && !lockedConfiguration
    ? minimumCopiesForConfiguration(excludes, (property) => {
      const option = localizedOptions.find((item) => item.id === property || item.name === property);
      const configured = configuration[option?.id || property];
      if (configured !== undefined && configured !== '') return { value: configured, hidden: option?.hidden };
      return { value: option?.hidden ? getSpecDefaultValue(property) : option?.default, hidden: option?.hidden };
    }, { copiesProperty: copiesOptionForMinimum.id })
    : null;
  // Omgekeerd: een keuze die maar tot een aantal kan (sleeves-proefexemplaar: 1 stuk).
  const quantityMaximum = copiesOptionForMinimum && !quantityMinimum && !readOnly && !lockedConfiguration
    ? maximumCopiesForConfiguration(excludes, (property) => {
      const option = localizedOptions.find((item) => item.id === property || item.name === property);
      const configured = configuration[option?.id || property];
      if (configured !== undefined && configured !== '') return { value: configured, hidden: option?.hidden };
      return { value: option?.hidden ? getSpecDefaultValue(property) : option?.default, hidden: option?.hidden };
    }, { copiesProperty: copiesOptionForMinimum.id })
    : null;
  const quantityLimit = quantityMinimum ?? quantityMaximum;

  const isTemporaryPricingError = Boolean(
    configurationError &&
    /tijdelijk|actuele prijs|temporar|vorübergehend|prix actuel|temporairement/i.test(configurationError)
  );
  // Twee verschillende vragen die eerder een waren.
  //
  // De eerste: is een offerte de ENIGE weg, omdat deze samenstelling niet te
  // prijzen is? Dan verdwijnt de bestelknop en komt het formulier ervoor in de
  // plaats. Dat is het geval waar dit blok ooit voor gebouwd is.
  //
  // De tweede: mag de klant een offerte vragen? Dat mag altijd. Zolang die twee
  // dezelfde vraag waren, kon je op een product met een gewone prijs geen
  // offerte aanvragen, en juist daar zit het zakelijke verkeer: grote oplages,
  // een gewenste leverdatum, een vraag over de uitvoering.
  const quoteIsOnlyOption = Boolean(
    (
      (configurationError && !isTemporaryPricingError && !isPriceLoading) ||
      // Ontbreekt er een verplichte waarde die de klant niet kan kiezen, dan is
      // bestellen niet mogelijk maar een offerte wel: daar vult iemand die
      // waarde met de hand in.
      unsatisfiableRequiredOptions.length > 0
    ) &&
    !readOnly &&
    !onSave
  );
  const canRequestQuote = Boolean(!readOnly && !onSave);

  const hasVisibleDeliveryPrices = useMemo(() => (
    deliveryQuantityRows.some((row) =>
      Object.values(row.cells || {}).some((cell) => cell?.available)
    )
  ), [deliveryQuantityRows]);

  useEffect(() => {
    if (!hasVisibleDeliveryPrices) return;
    deliveryMatrixSnapshotRef.current = {
      columns: deliveryColumns,
      rows: deliveryQuantityRows,
    };
  }, [deliveryColumns, deliveryQuantityRows, hasVisibleDeliveryPrices]);

  // Staffelhint uit de al opgehaalde prijzen in de gekozen levertijdkolom
  // (src/lib/conversion/quantity-nudge.ts). Geen eigen verzoeken, geen schatting.
  const matrixSelectedQuantity = Number((matrixQuantityOption && configuration[matrixQuantityOption.id]) || quantity || 1);
  const quantityNudge = useMemo(() => {
    if (readOnly || isDeliveryRowsLoading || !deliveryQuantityRows.length) return null;
    return findQuantityNudge(deliveryQuantityRows, matrixSelectedQuantity, selectedMatrixColumn);
  }, [readOnly, isDeliveryRowsLoading, deliveryQuantityRows, matrixSelectedQuantity, selectedMatrixColumn]);

  const selectDeliveryPrice = useCallback((
    rowQuantity: number,
    columnId: string,
    cell: DeliveryPriceCell,
    quantityOptionId?: string,
  ) => {
    if (!isAllowedClosedQuantity(rowQuantity, closedCustomQuantities)) return;
    if (providerType === 'PRINTCOM' && columnId !== 'standard' && !visibleDeliveryColumns.some(column => column.id === columnId)) return;
    if (!cell?.available || cell.subtotalExVat <= 0) return;

    pricedProductionOptionsRef.current = cell.productionOptions || {};
    setQuantityPrices(quantityOptionId
      ? { 1: cell.subtotalExVat }
      : { [rowQuantity]: cell.subtotalExVat });
    setLatestPriceQuote(cell.quote || null);
    setIsValidConfiguration(true);
    setConfigurationError(null);

    // Een snel-kolom zet urgency=standard plus levering=snel; een Print.com-
    // kolom zet die urgency en zet de levering terug op standaard.
    const columnPatch = configurationPatchForColumn(deliveryOverrideModel, columnId);
    if (quantityOptionId) {
      setConfiguration((previous) => ({
        ...previous,
        [quantityOptionId]: rowQuantity,
        ...columnPatch,
      }));
      setQuantity(1);
      return;
    }

    setQuantity(rowQuantity);
    setConfiguration((previous) => ({
      ...previous,
      ...columnPatch,
    }));
  }, [closedCustomQuantities, providerType, visibleDeliveryColumns, deliveryOverrideModel]);

  // Staffelhint onder de aantallen (rooster en tabel delen hem).
  const renderQuantityNudge = () => {
    if (!quantityNudge) return null;
    const nudgeCopy = quantityNudgeCopy(locale, quantityNudge, formatPriceForLocale(quantityNudge.unitInclVat, locale), formatPriceForLocale(quantityNudge.extraInclVat, locale));
    return (
      <div className="mt-2 flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-950 sm:flex-row sm:items-center sm:justify-between">
        <span>{nudgeCopy.text}</span>
        <button
          type="button"
          className="min-h-9 shrink-0 rounded-md bg-white px-3 py-1.5 font-semibold text-emerald-800 ring-1 ring-emerald-300 transition hover:bg-emerald-100"
          onClick={() => {
            const nudgeCell = deliveryQuantityRows.find((row) => row.quantity === quantityNudge.quantity)?.cells[selectedMatrixColumn];
            if (!nudgeCell?.available) return;
            window.printenTrack?.('quantity_nudge_click', {
              product_slug: productSlug,
              from_quantity: matrixSelectedQuantity,
              to_quantity: quantityNudge.quantity,
              saving_percent: quantityNudge.savingPercent,
            });
            selectDeliveryPrice(quantityNudge.quantity, selectedMatrixColumn, nudgeCell, matrixQuantityOption?.id);
          }}
        >
          {nudgeCopy.cta}
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (providerType !== 'PRINTCOM' || selectedDeliveryUrgency === 'standard' || isDeliverySpeedOrderable) return;
    const result = deliveryAvailability.get(`${deliveryDateQuantity}:${selectedDeliveryUrgency}`);
    if (!result || result.status === 'pending') return;
    const standardCell = selectedDeliveryRow?.cells.standard;
    if (standardCell?.available) selectDeliveryPrice(deliveryDateQuantity, 'standard', standardCell, printComQuantityOption?.id);
  }, [providerType, selectedDeliveryUrgency, isDeliverySpeedOrderable, deliveryAvailability, deliveryDateQuantity, selectedDeliveryRow, selectDeliveryPrice, printComQuantityOption?.id]);

  // Auto-fetch prices when configuration or quantity changes
  useEffect(() => {
    // Print.com is priced by the single debounced matrix batch below. Running
    // this selected-price request as well used to double supplier traffic for
    // every option change.
    if (providerType === 'PRINTCOM') return;

    priceFetchActiveRef.current = true;
    const controller = new AbortController();

    setCalculatedPrice(null);
    setIsProviderMatrixLoading(false);
    setIsPriceLoading(true);

    // Debounce to avoid too many API calls
    const timer = setTimeout(() => {
      fetchPrintComPrice(controller.signal).finally(() => {
        priceFetchActiveRef.current = false;
      });
    }, 180);

    return () => {
      clearTimeout(timer);
      controller.abort();
      priceFetchActiveRef.current = false;
    };
  }, [configuration, deliveryPromise, quantity, customMatrixQuantities, providerType, closedCustomQuantities, printfulPlacementsKey, priceRetryNonce]);

  const validateNextOptions = async (newConfiguration: Record<string, string | number>) => {
    const runId = ++optionValidationRunRef.current;
    await new Promise((resolve) => setTimeout(resolve, 250));
    if (runId !== optionValidationRunRef.current) return;

    // Find options that are not yet configured
    const unconfiguredOptions = localizedOptions.filter(opt =>
      !opt.hidden &&
      opt.required &&
      !newConfiguration[opt.id]
    );

    if (unconfiguredOptions.length === 0) return;

    // Validate the first unconfigured option
    const optionToValidate = unconfiguredOptions[0];
    if (!optionToValidate.values) return;

    debugLog(`🔍 Validating values for option: ${optionToValidate.label}`);
    setValidatingOptions(prev => new Set(prev).add(optionToValidate.id));

    const invalidValuesForOption = new Set<string>();
    const candidates: Array<{
      value: string;
      request: {
        sku: string;
        quantity: number;
        options: Record<string, any>;
        deliveryPromise: number;
        countryCode: string;
        locale: string;
      };
    }> = [];

    for (const val of optionToValidate.values) {
      if (isValueExcluded(optionToValidate.id, val.value, newConfiguration)) {
        invalidValuesForOption.add(String(val.value));
        continue;
      }

      const effectiveTestConfig = getConfigurationWithDefaults({
        ...newConfiguration,
        [optionToValidate.id]: val.value,
      });
      // Print.com cannot judge a combination with another supplier's value;
      // its stored excludes (isValueExcluded above) already apply.
      if (configurationHasForeignSupplierValue(localizedOptions, effectiveTestConfig)) continue;
      // Eigen formaat zonder maten: Print.com kan niets beoordelen, en een
      // mislukte proef zou hier elke waarde ten onrechte uitgrijzen.
      if (hasIncompleteCustomSize(effectiveTestConfig)) continue;
      const validationRequest = getPrintComValidationRequest(effectiveTestConfig);
      candidates.push({
        value: String(val.value),
        request: {
          sku: providerProductId || productSlug,
          quantity: validationRequest.quantity,
          options: validationRequest.options,
          deliveryPromise: 0,
          countryCode,
          locale,
        },
      });
    }

    try {
      if (candidates.length > 0) {
        const response = await fetch('/api/printcom/calculate-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pricingMode: 'api',
            expandPrintingMethods: candidates.length <= 40,
            batch: candidates.map((candidate) => candidate.request),
          }),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(data?.results)) return;

        candidates.forEach((candidate, index) => {
          const result = data.results[index];
          if (!result?.success || result?.error) {
            invalidValuesForOption.add(candidate.value);
          }
        });
      }

      if (runId !== optionValidationRunRef.current) return;
      setInvalidValues(prev => ({
        ...prev,
        [optionToValidate.id]: invalidValuesForOption,
      }));
    } catch (error) {
      console.error(`Error validating ${optionToValidate.label}:`, error);
    } finally {
      setValidatingOptions(prev => {
        const next = new Set(prev);
        next.delete(optionToValidate.id);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!isInitialized || providerType !== 'PRINTCOM') return;
    // Per-option supplier probes are disabled in production. Availability is
    // derived from local rules and the one debounced live-price matrix batch.
    if (process.env.NEXT_PUBLIC_PRINTCOM_REMOTE_OPTION_VALIDATION !== 'enabled') return;
    const needsDynamicAvailability = productSlug === 'poster';
    if (!needsDynamicAvailability) return;

    const optionIdsToValidate = ['finish', 'size'];
    const optionsToValidate = optionIdsToValidate
      .map((optionId) => localizedOptions.find((option) => option.id === optionId && option.values && option.values.length > 0))
      .filter((option): option is ProductOption => Boolean(option));

    if (optionsToValidate.length === 0) return;

    if (posterValidationActiveRef.current) return;
    posterValidationActiveRef.current = true;

    const runId = ++posterValidationRunRef.current;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const nextInvalidValues: Record<string, Set<string>> = {};
      const candidates: Array<{
        optionId: string;
        rawValue: string;
        request: {
          sku: string;
          quantity: number;
          options: Record<string, any>;
          deliveryPromise: number;
          countryCode: string;
          locale: string;
        };
      }> = [];

      for (const option of optionsToValidate) {
        const optionValues = option.id === 'size'
          ? withRotatedConsumerSizes(option.values || [], productSlug)
          : option.values || [];
        const uniqueValues = Array.from(
          new Map(optionValues.map((value: any) => [String(value.value), value])).values()
        );
        const invalidForOption = new Set<string>();

        for (const value of uniqueValues as any[]) {
          const rawValue = String(value.value);

          if (option.id === 'size' && (rawValue === 'custom' || rawValue.startsWith('custom:') || value.customSizes)) {
            continue;
          }

          if (
            isConsumerHiddenValue(option.id, value) ||
            !matchesShowIf(value.showIf, configuration) ||
            isValueExcluded(option.id, rawValue, configuration)
          ) {
            invalidForOption.add(rawValue);
            continue;
          }

          const testConfig = getConfigurationWithDefaults({
            ...configuration,
            [option.id]: value.value,
          });
          if (configurationHasForeignSupplierValue(localizedOptions, testConfig)) continue;
          if (hasIncompleteCustomSize(testConfig)) continue;
          const validationRequest = getPrintComValidationRequest(testConfig);
          candidates.push({
            optionId: option.id,
            rawValue,
            request: {
              sku: providerProductId || productSlug,
              quantity: validationRequest.quantity,
              options: validationRequest.options,
              deliveryPromise: 0,
              countryCode,
              locale,
            },
          });
        }

        nextInvalidValues[option.id] = invalidForOption;
      }

      try {
        if (candidates.length > 0) {
          const response = await fetch('/api/printcom/calculate-price', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pricingMode: 'api',
              expandPrintingMethods: candidates.length <= 40,
              batch: candidates.map((candidate) => candidate.request),
            }),
            signal: controller.signal,
          });
          const data = await response.json().catch(() => null);
          if (!response.ok || !Array.isArray(data?.results)) return;

          candidates.forEach((candidate, index) => {
            const result = data.results[index];
            if (!result?.success || result?.error) {
              nextInvalidValues[candidate.optionId]?.add(candidate.rawValue);
            }
          });
        }

        if (runId !== posterValidationRunRef.current) return;
        setInvalidValues((previous) => ({
          ...previous,
          ...nextInvalidValues,
        }));
      } catch (error) {
        if ((error as { name?: string })?.name !== 'AbortError') {
          console.error('Posteropties valideren mislukt:', error);
        }
      } finally {
        if (runId === posterValidationRunRef.current) {
          posterValidationActiveRef.current = false;
        }
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
      posterValidationActiveRef.current = false;
    };
  }, [
    isInitialized,
    providerType,
    localizedOptions,
    productSlug,
    providerProductId,
    configuration,
    configuration.material,
    configuration.printtype,
    configuration.finish,
    configuration.size,
    excludes,
  ]);

  useEffect(() => {
    if (!isInitialized || !filteredOptions || filteredOptions.length === 0) return;

    const corrections: Record<string, string> = {};

    filteredOptions.forEach((option) => {
      const currentValue = configuration[option.id];
      if (acceptsCustomQuantitySelection(option, currentValue, localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, configuration))) return;
      if (!currentValue || !option.values || option.values.length === 0) return;

      const currentValueKey = String(currentValue);
      const currentBaseValue = currentValueKey.startsWith('rotate:')
        ? currentValueKey.split(':')[1]
        : currentValueKey;
      const ignoreLiveInvalidValue = option.id === 'material' || option.id === 'cover_material';
      const isCurrentVisible = option.id === 'size'
        ? isAvailableSizeSelection(option, currentValue, configuration) &&
          (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(currentValueKey)) &&
          (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(currentBaseValue)) &&
          !isValueExcluded(option.id, currentBaseValue, configuration)
        : option.id === 'custom_shape' && currentValueKey.includes('|shape:')
        ? option.values.some((value) =>
          String(value.value) === normalizeCustomShapeProviderValue(currentValueKey) &&
          matchesShowIf(value.showIf, configuration)
        )
        : option.values.some((value) =>
          String(value.value) === currentValueKey &&
          matchesShowIf(value.showIf, configuration) &&
          (ignoreLiveInvalidValue || !invalidValues[option.id]?.has(String(value.value))) &&
          !isValueExcluded(option.id, String(value.value), configuration)
        );

      if (isCurrentVisible) return;

      const firstVisible = option.values.find((value) =>
        matchesShowIf(value.showIf, configuration) &&
        (option.id === 'custom_shape' ||
          ((ignoreLiveInvalidValue || !invalidValues[option.id]?.has(String(value.value))) &&
          !isValueExcluded(option.id, String(value.value), configuration)))
      );

      if (firstVisible) corrections[option.id] = firstVisible.value;
    });

    if (Object.keys(corrections).length > 0) {
      setConfiguration(prev => ({ ...prev, ...corrections }));
    }
  }, [isInitialized, filteredOptions, invalidValues, configuration, localizedSpecifications, providerProductId, productSlug]);

  const getMatrixUnitPrice = (config: Record<string, string | number>) => {
    const priceMatrix = localizedSpecifications?.priceMatrix;
    if (!priceMatrix?.enabled || !priceMatrix?.prices || typeof priceMatrix.prices !== 'object') return null;

    const keyFields = Array.isArray(priceMatrix.keyFields) ? priceMatrix.keyFields : [];
    if (keyFields.length === 0) return null;

    const price = Number(resolveCustomMatrixPrice(priceMatrix, countryCode, config));
    return Number.isFinite(price) && price > 0 ? price : null;
  };

  const syncHiddenRequiredOptionValues = (config: Record<string, string | number>) => {
    localizedOptions.forEach((option) => {
      if (!option.hidden || !option.required || !option.values?.length) return;
      const matchingValue = option.values.find((value) => value.showIf && matchesShowIf(value.showIf, config));
      if (matchingValue) config[option.id] = matchingValue.value;
    });
    return config;
  };

  const getOptionDisplayModifier = (option: ProductOption, value: any) => {
    const areaModifier = getAreaAdjustedModifier(value, configuration, localizedOptions);
    if (option.id === 'copies' || option.name === 'copies') return areaModifier;

    const currentMatrixPrice = getMatrixUnitPrice(configuration);
    const nextConfig = { ...configuration, [option.id]: value.value };

    localizedOptions.forEach((candidate) => {
      if (candidate.hidden || !candidate.required || !candidate.values || candidate.id === option.id) return;
      if (!matchesShowIf(candidate.showIf, nextConfig)) return;

      const currentValue = nextConfig[candidate.id];
      if (acceptsCustomQuantitySelection(candidate, currentValue, localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, nextConfig))) return;
      const currentOptionValue = candidate.values.find((item) => String(item.value) === String(currentValue));
      const currentValueStillVisible = currentOptionValue ? matchesShowIf(currentOptionValue.showIf, nextConfig) : false;
      if (currentValueStillVisible) return;

      const firstVisibleValue = candidate.values.find((item) => matchesShowIf(item.showIf, nextConfig));
      if (firstVisibleValue) nextConfig[candidate.id] = firstVisibleValue.value;
    });

    syncHiddenRequiredOptionValues(nextConfig);

    const nextMatrixPrice = getMatrixUnitPrice(nextConfig);
    if (currentMatrixPrice !== null && nextMatrixPrice !== null) {
      return Number((nextMatrixPrice - currentMatrixPrice).toFixed(2));
    }

    return areaModifier;
  };

  // Terug naar de standaardsamenstelling van het product. Na veel wisselen
  // (en automatisch meebewegende opties) is dit de snelste weg terug naar een
  // leverbare, geprijsde basis. De initialisatie hieronder draait dan opnieuw.
  const resetToDefaultOptions = () => {
    optionCheckController.current?.abort(abortReason('Selection check cancelled: options reset to defaults'));
    optionCheckController.current = null;
    optionSelectionSequence.current += 1;
    setSelectionCheckPending(false);
    setRejectedSelections(new Set());
    setConfigurationAdjustmentNote(null);
    setLatestPriceQuote(null);
    setIsValidConfiguration(false);
    setIsInitialized(false);
  };

  const handleOptionChange = async (optionId: string, value: string | number) => {
    sampleSelectionAttempted.current = true;
    if (lockedConfiguration && Object.prototype.hasOwnProperty.call(lockedConfiguration, optionId)) return;
    if (isLockedOptionField(localizedOptions, optionId)) return;
    // Tijdelijk niet leverbaar (Print.com prijst de waarde nergens): ook een
    // voorselectie via link of staal mag hem niet kiezen.
    if (isTemporarilyUnavailableValue(optionId, value)) return;
    if (String(configuration[optionId] ?? '') === String(value ?? '')) {
      // Controlled selects may echo the old value while an async choice is
      // pending. That is a no-op, not a cancellation of the requested choice.
      return;
    }

    optionCheckController.current?.abort(abortReason('Selection check superseded by a newer option choice'));
    optionCheckController.current = null;

    // Botst deze keuze met een andere optie, dan beweegt die mee in plaats van
    // dat de keuze geweigerd wordt. Levert dat niets op, dan blijft de
    // samenstelling zoals hij was en vangt de bestaande autocorrectie het af.
    //
    // Dezelfde vraag als in filteredOptions, met dezelfde definitie van
    // geblokkeerd: een uitsluitregel of geen drukmethode meer over. Zo kan de
    // filter geen waarde tonen die deze klik daarna niet kan oplossen; dat zou
    // een zichtbare keuze zijn die de samenstelling onprijsbaar maakt.
    // Alleen waarden die de klant kan zien. Anders koos de oplosser bij
    // muursticker een verborgen materiaal (Orajet), zette de autocorrectie dat
    // terug naar Quick Stick en sprong kisscut na de prijscontrole terug.
    const customerSelectableOptions = localizedOptions.map((option) => (
      Array.isArray(option.values)
        ? { ...option, values: option.values.filter((entry) => !isConsumerHiddenValue(option.id, entry)) }
        : option
    ));
    // De contourlijnen volgen de vorm (zie verderop). Rekent de oplosser nog
    // met de oude waarde, dan botst kisscut met "geen contourlijnen" en vond
    // hij bij muursticker nooit Roughmark + uvgel.
    const resolutionConfiguration = (() => {
      if (optionId !== 'custom_shape') return configuration;
      const contourOption = localizedOptions.find((option) => option.id === CONTOUR_LINES_OPTION_ID);
      if (!contourOption?.values?.length) return configuration;
      const contourLines = contourLinesForShape(value, configuration[CONTOUR_LINES_OPTION_ID],
        contourOption.values.map((entry) => String(entry.value)), excludes);
      return contourLines === undefined ? configuration : { ...configuration, [CONTOUR_LINES_OPTION_ID]: contourLines };
    })();
    const resolvedConflictPatch = resolveExcludedSelection({
      optionId,
      value: String(value ?? ''),
      configuration: resolutionConfiguration,
      options: customerSelectableOptions as any,
      pairVisibleWithDerived: true,
      isExcluded: (id, waarde, config) =>
        isValueExcluded(id, waarde, config as Record<string, string | number>, id === 'custom_shape')
        || !isValueReachableViaPrintingMethod(id, waarde, config as Record<string, string | number>),
    });

    // Een zichtbare waarde mag nooit worden toegepast wanneer er geen geldige
    // combinatie voor bestaat. Voorheen veranderde de UI de selectie toch,
    // waarna alle prijscellen op "-" eindigden en bestellen onmogelijk werd.
    // Vormen werden altijd toegepast; vindt de oplosser voor een vorm geen
    // passende combinatie, dan blijft dat oude gedrag gelden.
    const conflictPatch = resolvedConflictPatch === null && optionId === 'custom_shape' ? {} : resolvedConflictPatch;
    if (conflictPatch === null) {
      setIsPriceLoading(false);
      return;
    }

    const automaticChanges = Object.entries(conflictPatch).filter(([id, nextValue]) => String(configuration[id] ?? '') !== String(nextValue ?? ''));
    // Geen browservraag meer: de botsende optie beweegt mee. Alleen zichtbare
    // klantkeuzes krijgen achteraf een melding met "Ongedaan maken".
    const configurationBeforeChange = configuration;
    const autoChangeNotice = automaticChanges.length > 0
      ? buildAutoChangeNotice({
          changes: automaticChanges,
          previous: configuration,
          options: localizedOptions as any,
          locale,
          isHiddenForCustomer: (id) => isSupplierBookkeepingOption(id) || HIDDEN_CONSUMER_OPTION_IDS.has(id),
        })
      : null;

    const newConfig = {
      ...configuration,
      [optionId]: value,
      ...conflictPatch,
    };

    const syncBannerFinishes = (finishValue: string | number) => {
      newConfig.finish_left = finishValue;
      newConfig.finish_right = finishValue;
      newConfig.finish_top = finishValue;
      newConfig.finish_bottom = finishValue;
    };

    if (optionId === 'all_sides_equal_finish' && value === 'yes') {
      syncBannerFinishes(newConfig.finish_left || newConfig.finish_top || 'trimmed');
    }

    if (optionId === 'finish_left' && newConfig.all_sides_equal_finish !== 'no') {
      syncBannerFinishes(value);
    }

    if (optionId === 'size' && value !== 'custom') {
      delete newConfig.size_width;
      delete newConfig.size_height;
    }

    if (optionId === 'custom_shape') {
      const contourOption = localizedOptions.find((option) => option.id === CONTOUR_LINES_OPTION_ID);
      if (contourOption?.values?.length) {
        const contourLines = contourLinesForShape(value, newConfig[CONTOUR_LINES_OPTION_ID],
          contourOption.values.map((entry) => String(entry.value)), excludes);
        if (contourLines !== undefined) newConfig[CONTOUR_LINES_OPTION_ID] = contourLines;
      }
      // Afgeronde hoeken (vormpreset): eigen hoekradius, standaard 10 mm. Bij
      // een andere vorm verdwijnt de radius uit de configuratie.
      const shaped = withCornerRadiusForShape(newConfig, cornerRadiusSizeMm(newConfig));
      delete newConfig[CORNER_RADIUS_OPTION_ID];
      Object.assign(newConfig, shaped);
    }

    if (isBookmarksProduct && optionId === 'shape') {
      newConfig.die_cut = String(value) === 'rectangle' ? 'clean_cut' : 'laser';
      newConfig.clean_cut = 'yes';
      if (String(value) === 'ribbon_bookmark') {
        newConfig.size = '50_x_180_mm';
        delete newConfig.size_width;
        delete newConfig.size_height;
      }
    }

    if (isDisplayCubeProduct && optionId === 'product_size') {
      const displayCubeSizeMap: Record<string, string> = {
        kubus_20x20x20_cm: 'kubus_20_x_20_x_20_cm',
        kubus_30x30x30cm: 'kubus_30_x_30_x_30_cm',
        kubus_40x40x40cm: 'kubus_38_x_38_x_38_cm',
      };
      const mappedSize = displayCubeSizeMap[String(value)];
      if (mappedSize) newConfig.size = mappedSize;
    }

    if (isPlexiglasProduct && optionId === 'printtype') {
      newConfig.white_print = value === '40' ? 'none' : 'full_white';
    }

    if (isPlexiglasProduct && optionId === 'white_print') {
      newConfig.printtype = value === 'none' ? '40' : '50_200';
    }

    if (isPlexiglasProduct && optionId === 'custom_shape' && String(value) !== 'clean_cut') {
      newConfig.drillholes = 'none';
    }

    if (isPostersProduct && optionId === 'material') {
      const selectedMaterial = String(value);
      const isNeonPaper = selectedMaterial.startsWith('95gr_neon_papier');
      const uvGelOnlyPosterMaterials = new Set([
        '160gr_ijm814_uncoated',
        '230gr_premium_matt',
      ]);
      if (isNeonPaper) {
        newConfig.printtype = '10_pms_black';
        newConfig.printingmethod = 'lfp_uvgel';
        newConfig.finish = 'geen';
      } else if (uvGelOnlyPosterMaterials.has(selectedMaterial)) {
        newConfig.printingmethod = 'lfp_uvgel';
        newConfig.finish = 'geen';
      } else if (newConfig.printtype === '10_pms_black') {
        newConfig.printtype = '40';
      }

      if (isPosterMaterialWithoutFinish(selectedMaterial)) {
        newConfig.finish = 'geen';
      }
    }

    // Material/Usage dependency logic
    // Canvas (300gr_canvas) only works with Inside (canvas_inside)
    // Frontlit (510gr_pvc) only works with Outside (canvas_outside)
    if (optionId === 'material') {
      if (value === '300gr_canvas') {
        // Canvas chosen -> set usage to inside
        newConfig['usage'] = 'canvas_inside';
      } else if (value === '510gr_pvc') {
        // Frontlit chosen -> set usage to outside
        newConfig['usage'] = 'canvas_outside';
      }

      // Material-dependent defaults for tuinposter/banners
      // Set printingmethod and packaging based on material choice
      const materialDefaults = specifications?.materialDefaults?.[value as string];
      if (materialDefaults) {
        debugLog(`🎨 Setting material-dependent defaults for ${value}:`, materialDefaults);
        if (materialDefaults.printingmethod) {
          newConfig['printingmethod'] = materialDefaults.printingmethod;
        }
        if (materialDefaults.packaging_extras) {
          newConfig['packaging_extras'] = materialDefaults.packaging_extras;
        }
      }
    } else if (optionId === 'usage') {
      if (value === 'canvas_inside') {
        // Inside chosen -> set material to canvas
        newConfig['material'] = '300gr_canvas';
      } else if (value === 'canvas_outside') {
        // Outside chosen -> set material to frontlit
        newConfig['material'] = '510gr_pvc';
      }
    }

    // Auto-update hidden required options with showIf conditions
    // This handles cases like article_number that depends on size+color
    localizedOptions.forEach((option) => {
      // Only process hidden required options
      if (!option.hidden || !option.required || !option.values) return;

      // Find the matching value based on current configuration
      const matchingValue = option.values.find((val) => {
        if (!val.showIf) return false;

        // Check if ALL showIf conditions match the new configuration
        return Object.entries(val.showIf).every(
          ([key, requiredValue]) => newConfig[key] === requiredValue
        );
      });

      if (matchingValue) {
        debugLog(`🔄 Auto-updating ${option.label}: ${matchingValue.value}`);
        newConfig[option.id] = matchingValue.value;
      }
    });

    localizedOptions.forEach((option) => {
      if (option.hidden || !option.required || !option.values || option.values.length === 0) return;
      if (!matchesShowIf(option.showIf, newConfig)) return;

      const currentValue = newConfig[option.id];
      if (acceptsCustomQuantitySelection(option, currentValue, localizedSpecifications?.quantitySettings,
        getQuantityPolicyForConfiguration(providerProductId || productSlug, localizedSpecifications, newConfig))) return;
      const currentOptionValue = option.values.find((candidate) => String(candidate.value) === String(currentValue));
      const currentValueStillVisible = option.id === 'size'
        ? isAvailableSizeSelection(option, currentValue, newConfig)
        : option.id === 'custom_shape' && String(currentValue || '').includes('|shape:')
        ? option.values.some((candidate) => String(candidate.value) === normalizeCustomShapeProviderValue(currentValue))
        : currentOptionValue ? matchesShowIf(currentOptionValue.showIf, newConfig) : false;
      if (currentValueStillVisible) return;

      const firstVisibleValue = option.values.find((candidate) => matchesShowIf(candidate.showIf, newConfig));
      if (firstVisibleValue) {
        newConfig[option.id] = firstVisibleValue.value;
      }
    });

    // Keep dependent visible options compatible after a primary choice
    // changes. Several Print.com constraints depend on each other (for
    // example material -> printing method -> fold), so resolve to a fixed
    // point instead of doing only one order-dependent pass.
    for (let pass = 0; pass < 4; pass += 1) {
      let changed = false;
      const hiddenBefore = localizedOptions
        .filter((option) => option.hidden && option.required)
        .map((option) => `${option.id}:${String(newConfig[option.id] ?? '')}`)
        .join('|');
      syncHiddenRequiredOptionValues(newConfig);
      const hiddenAfter = localizedOptions
        .filter((option) => option.hidden && option.required)
        .map((option) => `${option.id}:${String(newConfig[option.id] ?? '')}`)
        .join('|');
      if (hiddenBefore !== hiddenAfter) changed = true;
      localizedOptions.forEach((option) => {
        if (option.hidden || !option.values || option.values.length === 0) return;
        if (option.id === optionId || option.id === 'copies' || option.name === 'copies') return;
        if (!matchesShowIf(option.showIf, newConfig)) return;

        const currentValue = newConfig[option.id];
        const currentOptionValue = option.values.find((candidate) => String(candidate.value) === String(currentValue));
        const currentValueValid = option.id === 'size'
          ? isAvailableSizeSelection(option, currentValue, newConfig)
          : option.id === 'custom_shape' && String(currentValue || '').includes('|shape:')
          ? option.values.some((candidate) => String(candidate.value) === normalizeCustomShapeProviderValue(currentValue))
          : currentOptionValue
          ? matchesShowIf(currentOptionValue.showIf, newConfig) &&
            (option.id === 'custom_shape' || !isValueExcluded(option.id, String(currentOptionValue.value), newConfig))
          : false;
        if (currentValueValid) return;

        const firstCompatible = option.values.find((candidate) =>
          matchesShowIf(candidate.showIf, newConfig) &&
          (option.id === 'custom_shape' || !isValueExcluded(option.id, String(candidate.value), newConfig))
        );
        if (firstCompatible && String(newConfig[option.id] ?? '') !== String(firstCompatible.value)) {
          newConfig[option.id] = firstCompatible.value;
          changed = true;
        }
      });
      if (!changed) break;
    }

    // A visible parent option may have corrected another dependent value in
    // the loop above (for example material series -> available thickness).
    // Recalculate hidden exact variants afterwards so pricing and supplier
    // routing always receive the final valid combination.
    syncHiddenRequiredOptionValues(newConfig);

    const changedOption = localizedOptions.find(option => option.id === optionId);
    // Geen voorwaarde meer op een geldige huidige samenstelling: juist vanuit
    // een ongeldige stand glipte een onleverbare keuze anders ongecontroleerd
    // door (eerst ander materiaal, dan PaperWise: foutmelding).
    // Never let Print.com judge a selection that contains a value another
    // linked supplier makes: its rejection would bounce or grey out a valid
    // choice. The regular price request prices those via that supplier.
    const stageSelection = providerType === 'PRINTCOM' && providerProductId
      && changedOption?.values?.some(candidate => String(candidate.value) === String(value))
      && optionId !== 'copies' && String(value) !== 'custom'
      && customSizeInputReady(getConfigurationWithDefaults(newConfig))
      && !configurationHasForeignSupplierValue(localizedOptions, getConfigurationWithDefaults(newConfig));

    // De keuze staat meteen; de leverbaarheid wordt op de achtergrond
    // gecontroleerd. Wachten op Print.com voor elke klik maakte wisselen
    // stroef, en een afgewezen keuze deed daarna zichtbaar niets.
    const selectionSequence = ++optionSelectionSequence.current;
    if (!stageSelection) setSelectionCheckPending(false);
    setConfigurationAdjustmentNote(null);
    setIsPriceLoading(true);
    setLatestPriceQuote(null);
    setIsValidConfiguration(false);
    setConfiguration((previous) => (configurationEquals(previous, newConfig) ? previous : newConfig));
    if (autoChangeNotice) {
      toast(autoChangeNotice.title, {
        description: <div className="space-y-0.5">{autoChangeNotice.lines.map((line) => <div key={line}>{line}</div>)}</div>,
        duration: 8000,
        action: {
          label: UNDO_LABELS[String(locale || 'nl').slice(0, 2).toLowerCase()] || UNDO_LABELS.nl,
          onClick: () => setConfiguration(configurationBeforeChange),
        },
      });
    }

    if (stageSelection) {
      const controller = new AbortController();
      optionCheckController.current = controller;
      setSelectionCheckPending(true);
      try {
        const effectiveNewConfig = getConfigurationWithDefaults(newConfig);
        const probe = buildSelectionProbe(optionId, value, effectiveNewConfig, getConfigurationWithDefaults(configurationBeforeChange)[optionId]);
        const availability = await checkSelectionQuote({
          sku: providerProductId, quantity: localizedOptions.some(option => option.id === 'copies' || option.name === 'copies') ? 1 : quantity, options: effectiveNewConfig,
          countryCode, locale, deliveryPromise, strictOptions: true,
          ...(probe ? { probe } : {}),
        }, controller.signal);
        if (controller.signal.aborted) return;
        if (availability === 'unavailable') {
          setRejectedSelections(previous => new Set(previous).add(optionSelectionKey(configurationBeforeChange, optionId, value, countryCode)));
          // Alleen terugzetten als de klant intussen niets anders koos.
          if (optionSelectionSequence.current === selectionSequence) {
            setConfiguration(configurationBeforeChange);
            const label = changedOption?.values?.find(candidate => String(candidate.value) === String(value))?.label || String(value);
            toast(`${label}: ${ui.selectionUnavailable}`, { duration: 6000 });
          }
        }
      } catch {
        // Een storing bij de controle blokkeert de keuze niet; de gewone
        // prijsberekening meldt dan zelf of er een prijs is.
      } finally {
        if (optionCheckController.current === controller) optionCheckController.current = null;
        if (optionSelectionSequence.current === selectionSequence) setSelectionCheckPending(false);
      }
    }

  };

  // A library link is a requested choice, not a bypass of price validation.
  // Establish the regular working configuration, then use the same guarded
  // selection path as a click. An intervening customer choice takes priority.
  useEffect(() => {
    if (!requestedSampleSelection || sampleSelectionAttempted.current || readOnly || isLoadingOptions || isPriceLoading || !isValidConfiguration) return;
    if (!(Number(calculatedPrice) > 0 || Object.values(quantityPrices).some(price => price > 0))) return;
    sampleSelectionAttempted.current = true;
    const option = localizedOptions.find(o => o.id === requestedSampleSelection.optionKey);
    if (!option?.values?.some(v => String(v.value) === requestedSampleSelection.optionValue)) return;
    void handleOptionChange(requestedSampleSelection.optionKey, requestedSampleSelection.optionValue);
  }, [requestedSampleSelection, readOnly, isLoadingOptions, isPriceLoading, isValidConfiguration, calculatedPrice, quantityPrices, localizedOptions, handleOptionChange]);

  const missingRequiredOptions = useMemo(() => {
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    return localizedOptions
      .filter((opt) => opt.required && !opt.hidden && matchesShowIf(opt.showIf, effectiveConfiguration))
      .filter((opt) => (opt.values || []).length > 0 || ['number', 'text', 'textarea'].includes(String(opt.type || '')))
      .filter((opt) => effectiveConfiguration[opt.id] === undefined || effectiveConfiguration[opt.id] === '')
      .map((opt) => getConsumerOptionLabel(opt, effectiveConfiguration, locale) || opt.name || opt.id);
  }, [configuration, localizedOptions, locale]);

  const isConfigurationComplete = () => {
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    if (providerType === 'PRINTCOM' && !customSizeInputReady(effectiveConfiguration)) return false;
    const shouldShowOption = (option: ProductOption) => {
      return matchesShowIf(option.showIf, effectiveConfiguration);
    };

    return localizedOptions
      .filter(opt => opt.required && !opt.hidden && shouldShowOption(opt))
      // Een keuzelijst zonder keuzes is geen keuze. Hem meetellen betekent een
      // pagina die nooit verder komt en de klant niet vertelt waarom. Een getal-
      // of tekstveld heeft van nature geen lijst en blijft dus wel verplicht.
      .filter(opt => (opt.values || []).length > 0 || ['number', 'text', 'textarea'].includes(String(opt.type || '')))
      .every(opt => effectiveConfiguration[opt.id] !== undefined && effectiveConfiguration[opt.id] !== '');
  };

  // Het laatst geregistreerde designerontwerp (uploadId + glaskant); onDesignReady zet het.
  const designerRegisteredRef = useRef<{ uploadId: string; glassSide: VinylGlassSide | null } | null>(null);
  const buildCartDisplayOptions = () => {
    const effectiveConfiguration = getConfigurationWithDefaults(configuration);
    const displayRows: Array<{ key: string; label: string; value: string }> = [];

    localizedOptions.forEach((option) => {
      if (option.hidden || isSupplierBookkeepingOption(option.id) || HIDDEN_CONSUMER_OPTION_IDS.has(option.id)) return;
      if (!matchesShowIf(option.showIf, effectiveConfiguration)) return;

      const rawValue = effectiveConfiguration[option.id];
      if (rawValue === undefined || rawValue === null || rawValue === '') return;
      if (option.id === CONTOUR_LINES_OPTION_ID && isContourNoneValue(rawValue)) return;

      const optionValue = option.values?.find((value) => String(value.value) === String(rawValue));
      const label = getConsumerOptionLabel(option, effectiveConfiguration, locale);
      const value = optionValue
        ? formatOptionDisplayLabel(option, optionValue.label, locale)
        : translatePrintLabel(String(rawValue).replace(/_/g, ' '), locale);

      if (!label || !value) return;
      displayRows.push({
        key: option.id,
        label,
        value,
      });
    });

    // Printful: de optie "Aantal" viel terug op zijn standaard (1 stuks)
    // terwijl de regel het gekozen aantal bestelde. Zelfde getal als de regel.
    if (providerType === 'PRINTFUL') {
      const lineQuantity = resolvePrintfulLineQuantity(configuration, quantity);
      displayRows.splice(0, displayRows.length, ...(syncQuantityDisplayRows(displayRows, lineQuantity) as typeof displayRows));
    }

    const cornerRadiusRow = cornerRadiusDisplayRow(effectiveConfiguration, locale);
    if (cornerRadiusRow) displayRows.push(cornerRadiusRow);

    if (isCanvasProduct) {
      const canvasEdgeLabels: Record<string, string> = {
        wrapped: translatePrintLabel('Doorlopende zijkanten', locale),
        white: translatePrintLabel('Witte zijkanten', locale),
        black: translatePrintLabel('Zwarte zijkanten', locale),
        stretched: translatePrintLabel('Uitgetrokken zijkanten', locale),
        mirrored: translatePrintLabel('Gespiegelde zijkanten', locale),
      };
      displayRows.push({
        key: 'canvasEdgeType',
        label: translatePrintLabel('Randafwerking', locale),
        value: canvasEdgeLabels[canvasEdgeType] || canvasEdgeType,
      });
    }

    // Ruling P9: binnenkant glas. Alleen weergave; de leveranciersoptie mirror blijft normal (P8).
    if (vinylDesigner) {
      const glassSide = vinylDesigner.registeredDesign
        ? vinylDesigner.registeredGlassSide ?? null
        : glassSideFor(designerRegisteredRef.current, uploadedFile?.id);
      return withGlassSideDisplay(displayRows, glassSide, locale);
    }

    return displayRows;
  };

  const submitProductQuoteRequest = async () => {
    if (!quoteRequestForm.name.trim() || !quoteRequestForm.email.trim()) {
      toast.error(ui.quoteRequired);
      return;
    }

    setQuoteRequestSubmitting(true);
    try {
      const effectiveConfiguration = getConfigurationWithDefaults(configuration);
      const response = await fetch('/api/product-quote-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          productSlug,
          productName,
          providerProductId,
          providerType,
          locale,
          countryCode,
          quantity,
          options: effectiveConfiguration,
          pricingError: configurationError || ui.quotePriceFallback,
          customerName: quoteRequestForm.name,
          customerEmail: quoteRequestForm.email,
          customerPhone: quoteRequestForm.phone,
          companyName: quoteRequestForm.company,
          desiredDeliveryDate: quoteRequestForm.deliveryDate,
          message: quoteRequestForm.message,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || ui.quoteSaveError);
      }

      setQuoteRequestSent(true);
      setQuoteNumber(typeof data.quoteNumber === 'string' ? data.quoteNumber : null);
      toast.success(data.quoted ? ui.quoteSentToast : ui.quoteReceivedToast);
    } catch (error: any) {
      toast.error(error.message || ui.quoteFailed);
    } finally {
      setQuoteRequestSubmitting(false);
    }
  };

  // Zelfde opties als de winkelwagen (handleAddToCart), voor "Opslaan als wederverkoopproduct" in de editor.
  const getResellerCartOptions = () => {
    const options: Record<string, unknown> = {
      ...configuration,
      ...pricedProductionOptionsRef.current,
      ...(isCanvasProduct && { canvasEdgeType }),
    };
    return Object.fromEntries(Object.entries(options).filter(([key, value]) => !key.startsWith('__')
      && ((typeof value === 'string' && value !== '') || (typeof value === 'number' && Number.isFinite(value))))) as Record<string, string | number>;
  };

  /**
   * Toevoegen aan de winkelwagen. Geeft `true` als het artikel is toegevoegd (in
   * beheer: opgeslagen), anders de reden voor de klant ('' = geen eigen tekst).
   * De knop toont die reden als toast; de Vinyl Lettering Designer
   * (onRequestAddToCart) laat zijn belofte ermee weigeren en toont hem zelf.
   */
  const addToCartCore = async (): Promise<true | string> => {
    if (!isConfigurationComplete() || !isPriceReady || !isDeliverySpeedOrderable) return '';
    if (!linkedPrintsReady || linkedPrintRows.some(row => !row.item.pricingQuote?.hash
      || !(Date.parse(row.item.pricingQuote.expiresAt) > Date.now()))) {
      return getLinkedPrintCopy(locale).stale;
    }
    if (!dimensionValidation.valid) {
      return dimensionValidation.message;
    }
    const accessoryBlock = accessoryUploadBlocker(
      selectedAccessories,
      (id) => Boolean(accessories.find(a => a.id === id)?.isPrintable),
      accessoryUploads,
    );
    if (accessoryBlock) {
      return accessoryBlock.reason === 'uploading' ? accessoryCopy.waitForUpload : accessoryCopy.fixUpload;
    }

    // Only require file upload for printable products. Een lage resolutie
    // houdt het toevoegen niet meer tegen.
    const fileBlock = addToCartFileBlock({
      isPrintable,
      designCount: uploadedDesignFiles.length,
      requiredDesignCount,
    });
    if (fileBlock === 'upload-first') {
      return ui.uploadFirst;
    }
    // Afgeronde hoeken: een editorontwerp dat met een andere hoekradius is
    // opgeslagen, heeft een andere snijlijn in het bestand dan besteld.
    if (uploadedDesignFiles.some((file) => designCornerRadiusMismatch(file?.designData, configuration, cornerRadiusSizeMm(configuration)))) {
      return cornerRadiusCopy(locale).redesign;
    }

    const configData: ProductConfiguratorSaveData = {
      configuration: {
        options: {
          ...configuration,
          ...pricedProductionOptionsRef.current,
          // Add canvasEdgeType to options for display purposes (not sent to Print.com API)
          ...(isCanvasProduct && { canvasEdgeType }),
          // Afgeronde hoeken: de gekozen (of standaard) hoekradius reist mee naar
          // de order; de editor zette dezelfde radius als snijlijn in het bestand.
          ...(usesCustomCornerRadius(configuration)
            ? { [CORNER_RADIUS_OPTION_ID]: withCornerRadiusForShape(configuration, cornerRadiusSizeMm(configuration))[CORNER_RADIUS_OPTION_ID] }
            : {}),
        },
        // Printful: het aantal zit in de optie `quantity`; de regel krijgt
        // hetzelfde aantal zodat de kassa niet 1 stuk afrekent.
        quantity: providerType === 'PRINTFUL' ? resolvePrintfulLineQuantity(configuration, quantity) : quantity,
        displayOptions: buildCartDisplayOptions(),
      },
      unitPrice: pricing.unitPrice,
      totalPrice: pricing.total,
      unitPriceExVat: pricing.unitPriceExVat,
      totalPriceExVat: pricing.totalExVat,
      pricingQuote: latestPriceQuote || undefined,
      shippingIncluded,
      supplierShippingExVat: shippingIncluded ? Number(shippingCost.toFixed(2)) : undefined,
      optionSupplierRoute,
      printComQuoteMeta: printComQuoteMeta || undefined,
      editorSessionCode: getCurrentEditorSessionCode({ productId, productSlug }) ?? undefined,
      // Only include uploaded file if product is printable
      uploadedFile: uploadedFile ? {
        id: uploadedFile.id,
        fileName: uploadedFile.fileName,
        fileSize: uploadedFile.fileSize,
        url: uploadedFile.url,
        designData: uploadedFile.designData,
        providerFileId: uploadedFile.providerFileId,
      } : undefined,
      uploadedFiles: orderDesignFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileSize: file.fileSize,
        url: file.url,
        designData: file.designData,
        providerFileId: file.providerFileId,
        role: file.role,
        printPages: file.printPages,
      })),
    };

    // If custom save handler is provided (admin mode), use that
    if (onSave) {
      try {
        await onSave(configData);
        toast.success(ui.saved);
        return true;
      } catch (error) {
        console.error('Save error:', error);
        return ui.saveError;
      }
    }

    // Commit the parent and genuinely configured print products together.
    const mainCartItem: Omit<CartItem, 'id' | 'addedAt'> = {
      productId,
      productName,
      productSlug,
      providerType,
      category,
      productThumbnail,
      ...configData,
      // Met product erbij: de sessiecache overleeft navigatie tussen
      // productpagina's, dus zonder deze controle kreeg deze regel de code van
      // het vorige product zodra hier zelf niets is geupload of ontworpen.
      editorSessionCode: getCurrentEditorSessionCode({ productId, productSlug }) ?? undefined,
      quantityPolicy: cartQuantityPolicyFromProduct(providerType, localizedOptions, localizedSpecifications) ?? undefined,
    };
    if (linkedPrintRows.length) useCartStore.getState().addItems([mainCartItem, ...linkedPrintRows.map(row => row.item)]);
    else addItem(mainCartItem);

    // Add selected accessories as separate cart items
    for (const accId of selectedAccessories) {
      const accessory = accessories.find(a => a.id === accId);
      if (!accessory) continue;

      const qty = accessoryQuantities[accId] || 1;
      const quotedAccessory = accessory.priceByQuantity?.[String(qty)];
      const accessoryUnitPriceExVat = Number(quotedAccessory?.unitPriceExVat ?? accessory.basePrice ?? 0);
      const accessoryTotalPriceExVat = Number(quotedAccessory?.totalPriceExVat ?? accessoryUnitPriceExVat * qty);
      const accessoryUnitPrice = calculateStorefrontPriceWithVAT(accessoryUnitPriceExVat, vatCountryCode);
      const accessoryTotalPrice = calculateStorefrontPriceWithVAT(accessoryTotalPriceExVat, vatCountryCode);
      const printcomConfiguration = quotedAccessory?.printcomConfiguration
        || accessory.printcomConfiguration
        || { copies: qty };

      const accFile = accessory.isPrintable ? accessoryCartFileFields(accessoryUploads[accId]) : { options: {} };

      addItem({
        productId: accessory.id,
        productName: accessory.name,
        productSlug: accessory.slug || accessory.sku,
        providerType: 'PRINTCOM',
        category: 'accessoires',
        productThumbnail: accessory.image,
        configuration: {
          options: {
            ...printcomConfiguration,
            parentProduct: providerProductId || productSlug,
            printcomAccessorySku: accessory.sku,
            ...accFile.options,
          },
          quantity: qty,
        },
        unitPrice: accessoryUnitPrice,
        totalPrice: accessoryTotalPrice,
        // accessory.basePrice is al exclusief btw (calculateStorefrontPriceWithVAT
        // hierboven telt de btw er pas overheen), dus rechtstreeks bruikbaar
        // als ex-btw-basis voor de winkelwagenweergave bij een landwissel.
        // Onafgerond, zie grossFromExVat.
        unitPriceExVat: accessoryUnitPriceExVat,
        totalPriceExVat: accessoryTotalPriceExVat,
        // Het drukbestand is al bij het kiezen geüpload (selectAccessoryFile),
        // zodat de bestelregel van het accessoire een fileUrl krijgt.
        ...(accFile.uploadedFile ? { uploadedFile: accFile.uploadedFile } : {}),
      });
    }

    // Uitkomst van de bestandscheck mee, zodat conversie per stoplicht te
    // meten is. 'none' = drukproduct zonder (gecontroleerd) bestand.
    const fileCheckAtAdd = isPrintable
      ? worstFileCheck(uploadedFiles.length ? uploadedFiles.map((file) => file?.url) : [uploadedFile?.url])
      : null;
    window.printenTrack?.('add_to_cart', {
      productId,
      productSku: providerProductId || productSlug,
      productName,
      value: pricing.total,
      currency: 'EUR',
      contentIds: [productId],
      contents: [{ id: productId, quantity, item_price: pricing.unitPrice }],
      ...(isPrintable ? { file_check_status: fileCheckAtAdd?.level || 'none' } : {}),
    });

    toast.success(ui.addedToCart);

    // Navigate to cart
    setTimeout(() => {
      router.push(cartPath(locale));
    }, 500);
    return true;
  };

  const handleAddToCart = async () => {
    const uitkomst = await addToCartCore();
    if (uitkomst !== true && uitkomst) toast.error(uitkomst);
  };

  /**
   * Bestellen met de knop van de Vinyl Lettering Designer (designer-order-gate.ts).
   *
   * De host roept eerst onDesignReady (bestand -> setUploadedFile) en meteen
   * daarna onRequestAddToCart; die state is dan nog niet verwerkt. Het verzoek
   * wacht in de gate; het effect hieronder laat de gate na elke render kijken of
   * het bestand van DIT ontwerp er staat, de prijs klaar is en de quote bij de
   * huidige configuratie hoort, en voegt dan toe met de nieuwste addToCartCore
   * (ref). Niet gelukt: de belofte weigert met de reden en de designer toont hem.
   * Tweede klik tijdens het toevoegen: "bezig". Na 20 s: time-outmelding.
   */
  const addToCartCoreRef = useRef(addToCartCore);
  addToCartCoreRef.current = addToCartCore;
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const productSlugRef = useRef(productSlug);
  productSlugRef.current = productSlug;
  const [pendingDesignerOrderId, setPendingDesignerOrderId] = useState<string | null>(null);
  // Ontwerp-hash van de laatste prijsaanvraag van de designer (onDesignChange); de gate
  // vergelijkt hem met die van het geregistreerde bestand (T6-review M2).
  const [designerDesignHash, setDesignerDesignHash] = useState<string | null>(null);
  const designerOrderGate = useMemo(() => createDesignerOrderGate({
    timeoutMs: DESIGNER_ADD_TO_CART_TIMEOUT_MS,
    messages: () => {
      const m = hostMessages(localeRef.current);
      return { busy: m.busy, failed: m.cartFailed, timeout: m.cartTimeout };
    },
    onChange: setPendingDesignerOrderId,
    // Toevoegen gooide (geen weigering met reden): naar Sentry, de gate zelf blijft zonder imports.
    onRunError: (error) => reportVstDesignerError('add-to-cart', error, { product: productSlugRef.current }),
  }), []);
  const requestDesignerAddToCart = useCallback(
    (result: VinylDesignResult) => designerOrderGate.request(result.uploadId, {
      widthMm: result.widthMm,
      heightMm: result.heightMm,
      materialValue: result.materialValue,
      designHash: result.designHash,
    }),
    [designerOrderGate]
  );
  const currentConfigurationKey = useMemo(() => stableConfigurationKey(configuration), [configuration]);
  const quoteMatchesConfiguration = latestPriceQuoteKey !== null && latestPriceQuoteKey === currentConfigurationKey;
  // Wat de configuratie nu bestelt (vel in mm, kleur); de gate vergelijkt het met het geregistreerde bestand.
  const designerMaterialOptionId = vinylDesigner?.settings.materialOptionId;
  const designerCurrentDesign = useMemo(() => {
    const widthMm = Math.round(Number(configuration.size_width) * 10);
    const heightMm = Math.round(Number(configuration.size_height) * 10);
    if (!designerMaterialOptionId || !(widthMm > 0) || !(heightMm > 0)) return null;
    return {
      widthMm,
      heightMm,
      materialValue: String(configuration[designerMaterialOptionId] ?? ''),
      designHash: designerDesignHash,
    };
  }, [configuration, designerMaterialOptionId, designerDesignHash]);

  useEffect(() => {
    designerOrderGate.step({
      uploadedFileId: uploadedFile?.id,
      isPriceReady,
      isPriceLoading,
      selectionCheckPending,
      configurationError: designerOrderError(configurationError, priceRateLimit, hostMessages(localeRef.current)),
      quoteMatchesConfiguration,
      currentDesign: designerCurrentDesign,
    }, () => addToCartCoreRef.current());
  }, [designerOrderGate, pendingDesignerOrderId, uploadedFile?.id, isPriceReady, isPriceLoading, selectionCheckPending, configurationError, priceRateLimit, quoteMatchesConfiguration, designerCurrentDesign]);

  // Weg van de pagina terwijl een verzoek wacht: de designer niet laten hangen.
  useEffect(() => () => designerOrderGate.cancel(), [designerOrderGate]);

  const productionSheetProfile = useMemo(() => {
    if (providerType !== 'CUSTOM' && providerType !== 'PRINTCOM') return undefined;
    try {
      return verifiedWorkfileProfile(productSlug, configuration) || resolveProductionSheetProfile(localizedSpecifications, configuration) || undefined;
    } catch {
      // An invalid source profile must not enable a guessed production canvas.
      return undefined;
    }
  }, [providerType, localizedSpecifications, configuration, productSlug]);

  const selectedSizeValue = useMemo(() => {
    return resolveEditorSizeValue({ productSlug, options: localizedOptions, specifications: localizedSpecifications, configuration });
  }, [configuration, localizedOptions, localizedSpecifications, productSlug]);

  // Editor off for this product or choice (e.g. a free contour cut); the size
  // above stays, so the upload's file check keeps its target size.
  const editorUnavailable = useMemo(
    () => Boolean(editorUnavailableReason(localizedSpecifications, configuration)),
    [localizedSpecifications, configuration],
  );

  // Vaste formaten waaruit de bestandscheck een kleiner, scherp formaat mag
  // voorstellen. Alleen als het uploadformaat het gekozen formaat zelf is;
  // bij editormaten met afloop of vrije maten past die vergelijking niet.
  const fileCheckSizeOptions = useMemo(() => {
    const sizeOption = localizedOptions.find((option) => option.id === 'size');
    if (!sizeOption || !Array.isArray(sizeOption.values) || isLockedOptionField(localizedOptions, 'size')) return [];
    if (lockedConfiguration && Object.prototype.hasOwnProperty.call(lockedConfiguration, 'size')) return [];
    // Alleen als het uploadformaat het gekozen formaat zelf is (geen editormaat
    // met afloop, werktekening of vrije maat); anders klopt de vergelijking niet.
    const chosen = targetFromSizeValue(String(configuration.size ?? ''));
    const uploadTarget = targetFromSizeValue(selectedSizeValue);
    if (!chosen || !uploadTarget || Math.abs(chosen.widthMm - uploadTarget.widthMm) > 1 || Math.abs(chosen.heightMm - uploadTarget.heightMm) > 1) return [];
    return sizeOption.values
      .filter((entry: any) => String(entry?.value) !== String(configuration.size))
      .filter((entry: any) => !entry?.customSizes && !isConsumerHiddenValue('size', entry) && !isTemporarilyUnavailableValue('size', entry?.value))
      .map((entry: any) => ({ value: String(entry.value), ...(entry.label ? { label: String(entry.label) } : {}) }));
    // De twee filterhelpers worden per render opnieuw gemaakt maar lezen
    // alleen localizedOptions en specificaties; die wisselen samen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localizedOptions, lockedConfiguration, configuration.size, selectedSizeValue]);


  const workfileConfigurationKey = JSON.stringify(configuration);
  const selectedGuideTemplateUrl = useMemo(() => {
    if (printComGuideTemplateUrl?.key === workfileConfigurationKey) return printComGuideTemplateUrl.url;
    for (const option of localizedOptions) {
      const selectedValue = configuration[option.id] ?? option.default;
      if (!selectedValue || !Array.isArray(option.values)) continue;
      const value = option.values.find((item) => String(item.value) === String(selectedValue));
      if (value?.template) {
        const templateUrl = String(value.template);
        if (!isDisplayCardProduct) return templateUrl;
        const separator = templateUrl.includes('?') ? '&' : '?';
        return `${templateUrl}${separator}v=displaycard-guides-20260602`;
      }
    }
    return null;
  }, [configuration, localizedOptions, isDisplayCardProduct, printComGuideTemplateUrl]);

  useEffect(() => {
    setPrintComGuideTemplateUrl(null);
    if (
      !isInitialized
      || currentStep === 'configure'
      || providerType !== 'PRINTCOM'
      || !providerProductId
    ) return;

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch('/api/printcom/workfile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            sku: providerProductId,
            options: getConfigurationWithDefaults(configuration),
            locale,
          }),
        });
        const data = await response.json().catch(() => null);
        const overlay = Array.isArray(data?.overlays) ? data.overlays.find((value: unknown) => typeof value === 'string') : null;
        if (controller.signal.aborted) return;
        if (response.ok && data?.success && typeof overlay === 'string') setPrintComGuideTemplateUrl({ key: workfileConfigurationKey, url: overlay });
      } catch (error) {
        if ((error as { name?: string })?.name !== 'AbortError') {
          console.warn('Print.com-werkbestand kon niet worden geladen:', error);
        }
      }
    }, 180);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [configuration, currentStep, isInitialized, locale, providerProductId, providerType]);

  // Eindformaat in mm voor de grenzen van de hoekradius (max = halve korte zijde).
  function cornerRadiusSizeMm(config: Record<string, string | number>) {
    const dimensions = parseDimensions(resolveEditorSizeValue({ productSlug, options: localizedOptions, specifications: localizedSpecifications, configuration: config }) || '');
    return dimensions && dimensions.width > 0 && dimensions.height > 0 ? { widthMm: dimensions.width, heightMm: dimensions.height } : null;
  }

  const editorFinishingOptions = useMemo(() => {
    const next: Record<string, string | number> = { ...configuration, ...resolveEditorBleedFinishing({ productSlug, specifications: localizedSpecifications, configuration }) };
    const isMultiPhotoFrame = ['multi-fotolijst', 'multi-photo-frame', 'multi-fotorahmen', 'cadre-multi-photos']
      .includes(String(productSlug || '').toLowerCase());

    if (!isMultiPhotoFrame) return next;

    const variantOption = localizedOptions.find((option) => option.id === 'variantSku' || option.name === 'variantSku');
    const selectedVariantValue = String(configuration.variantSku || '');
    const selectedVariant = Array.isArray(variantOption?.values)
      ? variantOption.values.find((item) => String(item.value) === selectedVariantValue)
      : null;

    if (selectedVariant) {
      const metadataKeys = [
        'photoCount',
        'prodigiSku',
        'orientation',
        'mount',
        'widthCm',
        'heightCm',
        'widthMm',
        'heightMm',
        'sourceWidthCm',
        'sourceHeightCm',
        'printWidthPx',
        'printHeightPx',
      ];

      metadataKeys.forEach((key) => {
        const value = (selectedVariant as Record<string, unknown>)[key];
        if ((typeof value === 'string' || typeof value === 'number') && value !== '') {
          next[key] = value;
        }
      });
    }

    next.multiPhotoFrameTemplate = 'prodigi-fixed-collage';
    next.collageWindowSizeMm = next.collageWindowSizeMm || 60;
    next.collageGapMm = next.collageGapMm || 20;

    return next;
  }, [configuration, localizedOptions, localizedSpecifications, productSlug]);

  const selectedDesignPlacements = useMemo(() => {
    if (fileUploadSlots.length > 0) {
      return fileUploadSlots.map((slot: any, index: number) => ({
        key: slot?.key || slot?.role || `side-${index + 1}`,
        label: slot?.label || (index === 0
          ? (locale === 'en' ? 'Front' : locale === 'de' ? 'Vorderseite' : locale === 'fr' ? 'Recto' : 'Voorkant')
          : (locale === 'en' ? 'Back' : locale === 'de' ? 'Rückseite' : locale === 'fr' ? 'Verso' : 'Achterkant')),
        guideTemplateUrl: slot?.guideTemplateUrl || selectedGuideTemplateUrl,
      }));
    }
    if (isDisplayCardDoubleSided) {
      return [
        { key: 'front', label: locale === 'en' ? 'Front' : locale === 'de' ? 'Vorderseite' : locale === 'fr' ? 'Recto' : 'Voorkant', guideTemplateUrl: selectedGuideTemplateUrl },
        { key: 'back', label: locale === 'en' ? 'Back' : locale === 'de' ? 'Rückseite' : locale === 'fr' ? 'Verso' : 'Achterkant', guideTemplateUrl: selectedGuideTemplateUrl },
      ];
    }
    const insidePrint = String(configuration.printtype_back || '').toLowerCase();
    if (insidePrint !== '40_all_over') return [];
    return [
      { key: 'outside', label: locale === 'en' ? 'Outside' : locale === 'de' ? 'Außenseite' : locale === 'fr' ? 'Exterieur' : 'Buitenzijde', guideTemplateUrl: selectedGuideTemplateUrl },
      { key: 'inside', label: locale === 'en' ? 'Inside' : locale === 'de' ? 'Innenseite' : locale === 'fr' ? 'Intérieur' : 'Binnenzijde', guideTemplateUrl: selectedGuideTemplateUrl },
    ];
  }, [configuration.printtype_back, fileUploadSlots, isDisplayCardDoubleSided, locale, selectedGuideTemplateUrl]);

  useEffect(() => {
    if (providerType !== 'PRINTFUL' || !providerProductId || !isInitialized) return;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const size = String(configuration.size || configuration.Maat || configuration.Size || '');
      const color = String(configuration.color || configuration.Kleur || configuration.Color || '');

      fetch(`/api/printful/editor-template?productId=${encodeURIComponent(providerProductId)}&size=${encodeURIComponent(size)}&sizeValue=${encodeURIComponent(selectedSizeValue || '')}&color=${encodeURIComponent(color)}&v=3`, {
        signal: controller.signal,
        cache: 'no-store',
      })
        .then((response) => response.ok ? response.json() : null)
        .then((data) => {
          if (!data?.success) return;
          const firstTemplate = Array.isArray(data.templates) ? data.templates[0] : null;
          const imageUrl =
            data.selectedVariantPreviewUrl ||
            data.selectedVariant?.image ||
            firstTemplate?.backgroundUrl ||
            firstTemplate?.overlayUrl ||
            null;

          window.dispatchEvent(new CustomEvent('printen:product-preview-image', {
            detail: {
              productId,
              imageUrl,
              variantId: data.selectedVariantId || data.selectedVariant?.id || null,
              placement: firstTemplate?.placement || null,
            },
          }));
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            window.dispatchEvent(new CustomEvent('printen:product-preview-image', {
              detail: { productId, imageUrl: null },
            }));
          }
        });
    }, 150);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    providerType,
    providerProductId,
    productId,
    isInitialized,
    selectedSizeValue,
    configuration.size,
    configuration.Maat,
    configuration.Size,
    configuration.color,
    configuration.Kleur,
    configuration.Color,
  ]);

  return (
    // Onderaan ruimte vrijhouden: de sticky mobiele bestelbalk (~104px) legde
    // zich anders over de laatste optietegels en over de prijskaart heen.
    <div className="space-y-6 pb-28 lg:pb-0">
      <AdminProductOverlay productId={productId} />
      {/* Progress Steps — op 390px moeten drie labels plus twee verbindingslijnen
          passen, dus cijfer, tekst en marges schalen mee met het scherm. */}
      <div className="flex items-center justify-between">
        <div className={`flex min-w-0 items-center ${currentStep === 'configure' ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-sm sm:h-8 sm:w-8 sm:text-base ${currentStep === 'configure' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
            {currentStep !== 'configure' ? <Check className="h-4 w-4 sm:h-5 sm:w-5" /> : '1'}
          </div>
          <span className="ml-1.5 whitespace-nowrap text-xs font-medium sm:ml-2 sm:text-sm">{t('stepConfigure')}</span>
        </div>
        {isPrintable && (
          <>
            <div className="mx-2 h-0.5 min-w-2 flex-1 bg-gray-200 sm:mx-4" />
            <div className={`flex min-w-0 items-center ${currentStep === 'upload' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-sm sm:h-8 sm:w-8 sm:text-base ${currentStep === 'upload' ? 'bg-blue-600 text-white' : currentStep === 'checkout' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
                {currentStep === 'checkout' ? <Check className="h-4 w-4 sm:h-5 sm:w-5" /> : '2'}
              </div>
              <span className="ml-1.5 whitespace-nowrap text-xs font-medium sm:ml-2 sm:text-sm">{currentStep === 'checkout' ? ui.stepUpload : flowCopy.stepDesignAndOrder}</span>
            </div>
          </>
        )}
        {/* De aparte bestelstap bestaat alleen nog voor beheer (bestaand
            bestand); klanten bestellen vanuit de ontwerpstap of direct na het
            configureren. */}
        {currentStep === 'checkout' && (
          <>
            <div className="mx-2 h-0.5 min-w-2 flex-1 bg-gray-200 sm:mx-4" />
            <div className="flex min-w-0 items-center text-blue-600">
              <div className="h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-sm sm:h-8 sm:w-8 sm:text-base bg-blue-600 text-white">
                {isPrintable ? '3' : '2'}
              </div>
              <span className="ml-1.5 whitespace-nowrap text-xs font-medium sm:ml-2 sm:text-sm">{t('stepOrder')}</span>
            </div>
          </>
        )}
        {!isPrintable && currentStep !== 'checkout' && <div className="flex-1" />}
      </div>

      {/* Configuration Form */}
      {currentStep === 'configure' && (
        <div ref={configureStepRef} className="scroll-mt-32">
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold">{t('configuration')}</h3>
                {!readOnly && isInitialized && (
                  <button
                    type="button"
                    onClick={resetToDefaultOptions}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                    {ui.resetOptions}
                  </button>
                )}
              </div>

              {(() => {
                // Check if option should be shown based on showIf condition
                const shouldShowOption = (option: ProductOption) => {
                  if (
                    configuration.all_sides_equal_finish !== 'no' &&
                    isBannerSideFinishOption(option.id) &&
                    option.id !== 'finish_left'
                  ) {
                    return false;
                  }
                  return matchesShowIf(option.showIf, configuration);
                };

                // Check if value should be shown based on showIf condition
                const shouldShowValue = (option: ProductOption, value: any) => {
                  if (rejectedSelections.has(optionSelectionKey(configuration, option.id, value.value, countryCode))) return false;
                  // Waarde van een andere leverancier: Print.com-regels gelden niet.
                  if (isForeignSupplierSelection(localizedOptions, option.id, value.value)) return true;
                  const isABordenProduct = String(productSlug || '').toLowerCase() === 'a-borden';
                  const isLetterheadPerforationOption =
                    isPrintedLetterheadsProduct(providerProductId || productSlug) &&
                    option.id === 'perforation';
                  const hiddenPhotoPosterMaterials = new Set([
                    '160gr_ijm814_uncoated',
                    '230gr_premium_matt',
                  ]);
                  if (isABordenProduct && option.id === 'color' && String(value.value) === 'black' && String(configuration.size || 'a1') !== 'a1') return false;
                  if (
                    isPhotoPosterProduct &&
                    option.id === 'material' &&
                    hiddenPhotoPosterMaterials.has(String(value.value))
                  ) return false;
                  const ignoreLiveInvalidValue = option.id === 'material' || option.id === 'cover_material';
                  if (!(isABordenProduct && option.id === 'size') && !matchesShowIf(value.showIf, configuration)) return false;
                  // Tijdelijk niet leverbaar blijft zichtbaar en wordt grijs getoond.
                  if (isTemporarilyUnavailableValue(option.id, value.value)) return true;
                  // Zelfde regel als in filteredOptions hierboven: een uitgesloten
                  // waarde verdwijnt alleen als er geen enkele geldige combinatie
                  // mee bestaat. Bestaat die wel, dan blijft hij kiesbaar en
                  // beweegt de botsende optie mee zodra de klant hem aanklikt.
                  //
                  // Dit is de derde plek waar dezelfde uitsluiting werd toegepast,
                  // en de enige die de oplosser nog niet kende. Op hpl-trespa
                  // overleefde "geen bedrukking" daardoor wel het optiefilter maar
                  // sneuvelde hij alsnog bij het opbouwen van de tegels, terwijl
                  // "geen beschermlaag" destijds de uitsluitregels omzeilde.
                  if (
                    option.id !== 'custom_shape'
                    && isValueExcluded(option.id, String(value.value), configuration)
                    && resolveExcludedSelection({
                      optionId: option.id,
                      value: String(value.value),
                      configuration,
                      options: localizedOptions as any,
                      isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
                    }) === null
                  ) return false;
                  if (!ignoreLiveInvalidValue && !isLetterheadPerforationOption && invalidValues[option.id]?.has(String(value.value))) return false;
                  return true;
                };

                // Filter values based on showIf condition
                const getFilteredValues = (option: ProductOption, values: any[] = []) => {
                  return withStandardCustomShapePresets(option, values).filter((value) => shouldShowValue(option, value));
                };
                const getVisibleOptionValues = (option: ProductOption) => getFilteredValues(option, option.values || []);
                const hasCompleteVisibleValueVisuals = (option: ProductOption) => {
                  const values = getVisibleOptionValues(option);
                  return values.length > 0 && values.every((value: any) => value.image || value.visualSvgContent);
                };
                const usesCompactVisualLabels = (candidateOption: ProductOption) =>
                  candidateOption.visualPresentation === 'compact';
                // Kleuropties krijgen altijd stalen. Voorwaarde: minstens de
                // helft van de zichtbare waarden levert een herkenbare kleur op
                // (of "geen"/transparant), anders wordt het een rij grijze
                // bolletjes voor iets wat geen kleur is (sokkentype, vlaggen).
                const showsColorSwatches = (option: ProductOption) => {
                  // Goedkope naamcontrole eerst: waarden filteren draait de uitsluitingsoplosser.
                  if (!option.values || !isColorOptionKey(option)) return false;
                  const values = uniqueOptionValues(getFilteredValues(option, option.values));
                  return shouldPresentAsColorOption(option, values, hasResolvedColorSwatch);
                };
                // Tot en met 12 kleuren een staalraster; daarboven een
                // doorzoekbare kleurkiezer, want 40 tegels is op mobiel een
                // lange scroll. Afbeeldingen per waarde gaan altijd voor.
                const colorPresentation = (option: ProductOption): 'grid' | 'combobox' | null => {
                  if (!option.values || !isColorOptionKey(option)) return null;
                  if (!['select', 'radio', 'color', 'swatch'].includes(String(option.type))) return null;
                  if (hasCompleteVisibleValueVisuals(option) || !showsColorSwatches(option)) return null;
                  return uniqueOptionValues(getFilteredValues(option, option.values)).length > 12 ? 'combobox' : 'grid';
                };
                const usesColorSwatchGrid = (option: ProductOption) => colorPresentation(option) === 'grid';
                // Zelfde regel als de tegels: uitgesloten maar oplosbaar blijft kiesbaar.
                const isColorValueDisabled = (option: ProductOption, value: any) => {
                  if (isTemporarilyUnavailableValue(option.id, value.value)) return true;
                  if (option.id === 'custom_shape') return false;
                  if (invalidValues[option.id]?.has(value.value)) return true;
                  return isValueExcluded(option.id, value.value, configuration)
                    && resolveExcludedSelection({
                      optionId: option.id,
                      value: String(value.value),
                      configuration,
                      options: localizedOptions as any,
                      isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
                    }) === null;
                };
                // Een verplichte optie met precies een overgebleven waarde kost
                // een volledig tegelraster terwijl er niets te kiezen valt. Alleen
                // compact tonen als die waarde al geselecteerd is, zodat een
                // nog-niet-gekozen waarde aanklikbaar blijft.
                const compactSingleValue = (option: ProductOption) => {
                  if (['number', 'text', 'textarea', 'custom-size'].includes(String(option.type))) return null;
                  if (option.id === 'size' || isDeliverySpeedOption(option) || isProductQuantityOption(option)) return null;
                  const values = uniqueOptionValues(getVisibleOptionValues(option));
                  if (values.length !== 1) return null;
                  const only = values[0];
                  if (String(configuration[option.id] ?? '') !== String(only.value)) return null;
                  return only;
                };
                const hasCompleteVisibleValueIcons = (option: ProductOption) => {
                  const values = getVisibleOptionValues(option);
                  return values.length > 0 && values.every((value: any) => getOptionIcon(option.id, value.value, value.metadata));
                };
                const isProductQuantityOption = (option: ProductOption) =>
                  option.id === 'copies' ||
                  option.name === 'copies' ||
                  (providerType === 'PRINTFUL' && (option.id === 'quantity' || option.name === 'quantity'));

                // Filter out options that have only 1 TOTAL value
                // If an option has 2+ total values but only 1 is visible (showIf), still show it
                const visibleOptionsAfterShowIf = filteredOptions.filter(opt => {
                  if (isDeliverySpeedOption(opt) || isProductQuantityOption(opt) || opt.type === 'specification') {
                    return true; // Always show these
                  }
                  if (opt.type === 'number' || opt.alwaysShow) {
                    return true;
                  }
                  // Always show size option, even if filtered down to 1 value by excludes
                  if (opt.id === 'size') {
                    return true;
                  }
                  if (opt.id === 'usage') {
                    return true;
                  }
                  if (!opt.values) return true;

                  // Use original (unfiltered) total values to decide visibility.
                  // Excludes and showIf reduce the VISIBLE values but the option itself
                  // must stay in the UI so the user can still see and interact with it.
                  const visibleValues = opt.values.filter((value) => isUsableProductOptionValue(opt, value));
                  const onlyNoChoiceVisible =
                    visibleValues.length <= 1 &&
                    visibleValues.every((value) => {
                      const raw = String(value?.value ?? '').toLowerCase();
                      const label = String(value?.label ?? '').toLowerCase();
                      return value?.nullable === true || ['none', 'geen', 'no', 'null'].includes(raw) || label === 'geen';
                    });
                  if (!opt.required && onlyNoChoiceVisible) return false;

                  const originalOption = displayOptions.find(o => o.id === opt.id);
                  const totalValues = originalOption?.values?.filter((value) => isUsableProductOptionValue(originalOption, value)) || [];
                  return totalValues.length > 1;
                });

                // Group options by propertyGroup
                // Skip 'urgency' option as it's handled in the table below
                // Skip 'specification' type options (auto-filled, not user-configurable)
                // Skip 'copies' option as it's handled in the quantity selector (both SELECT and NUMBER)
                // 🚫 Use visibleOptionsAfterShowIf to respect excludes AND showIf
                  const grouped = visibleOptionsAfterShowIf
                  .filter(opt =>
                    !isSupplierBookkeepingOption(opt.id) &&
                    (!opt.hidden ||
                      ((isBannerProduct || isBookmarksProduct || isDisplayCardProduct) && opt.id === 'printtype') ||
                      (isDisplayCubeProduct && opt.id === 'product_size')) &&
                    (
                      !HIDDEN_CONSUMER_OPTION_IDS.has(opt.id) ||
                      (isDisplayCubeProduct && opt.id === 'product_size') ||
                      (isStickerProduct && STICKER_CONTOUR_OPTION_IDS.has(opt.id)) ||
                      (opt.id === CONTOUR_LINES_OPTION_ID && shapeUsesContourLines(getConfigurationWithDefaults(configuration).custom_shape, contourOptionValues, excludes)) ||
                      ((isFlyersProduct || isFoldersProduct) && ['printtype', 'clean_cut'].includes(opt.id)) ||
                      (isPostersProduct && ['printtype'].includes(opt.id)) ||
                      (isBusinessCardsProduct && ['printtype', 'clean_cut'].includes(opt.id)) ||
                      (isBookmarksProduct && ['printtype'].includes(opt.id)) ||
                      (isDisplayCardProduct && ['printtype'].includes(opt.id)) ||
                      (isBannerProduct && ['printtype'].includes(opt.id)) ||
                      (isHdMetalProduct && ['custom_shape'].includes(opt.id)) ||
                      (isPlexiglasProduct && ['white_print', 'printtype', 'custom_shape', 'block-out-foil'].includes(opt.id))
                      || (String(productSlug || '').toLowerCase() === 'stoepbord-met-watertank' && opt.id === 'printing')
                      || (isWayfindingSignProduct && opt.id === 'printing')
                      || (isWoodTextileFrameProduct && opt.id === 'printing')
                    ) &&
                    !opt.id.startsWith('summary_image') &&
                    // Kleur en spiegelen kiest de klant in de Vinyl Lettering Designer; twee kiezers
                    // lopen uit elkaar. Spiegelen: de designer spiegelt bij "binnenkant glas" het
                    // snijbestand zelf (cutScale in de bundel), dus de optie `mirror` blijft
                    // 'normal'; anders zou de leverancier een tweede keer spiegelen.
                    // Spiegelen nooit apart bij een vinyl-ontwerp (Ruling P8), ook niet bij een geregistreerd ontwerp.
                    !(vinylDesigner && opt.id === 'mirror') &&
                    // Ook bij een geregistreerd ontwerp: de kleur hoort bij dat ontwerp, en orders/create
                    // weigert een andere kleur dan geregistreerd (eindreview I4).
                    !((designerOwnsOrder || vinylDesignerMode === 'registered') && vinylDesigner && opt.id === vinylDesigner.settings.materialOptionId) &&
                    !isRedundantTextileColorAlias(opt, filteredOptions) &&
                    !(isCanvasProduct && opt.id === 'material') &&
                    (opt.id === 'custom_shape' || shouldShowOption(opt)) &&
                    !isDeliverySpeedOption(opt) &&
                    // levering=standaard/snel staat als kolommen in de prijsmatrix.
                    opt.id !== deliveryOverrideModel?.optionId &&
                    !isProductQuantityOption(opt) &&
                    (opt.type !== 'specification' || !opt.hidden)
                  )
                  .reduce((acc, option) => {
                    const group = option.propertyGroup || 'general';
                    if (!acc[group]) acc[group] = [];
                    acc[group].push(option);
                    return acc;
                  }, {} as Record<string, typeof options>);

                // Een product kan zijn eigen volgorde meegeven (specifications.optionOrder),
                // bijvoorbeeld vorm voor formaat bij de stempels.
                const productOptionOrder: string[] | null = Array.isArray(specifications?.optionOrder) && specifications.optionOrder.length
                  ? specifications.optionOrder.map(String)
                  : null;
                const optionOrder = productOptionOrder
                  ? productOptionOrder
                  : String(productSlug || '').toLowerCase() === 'buttons'
                  ? [
                      'size',
                      'buttons',
                      'material',
                      'printtype',
                      'personalize',
                      'printingmethod',
                      'urgency',
                    ]
                  : isCarwrapProduct
                  ? [
                      'material',
                      'finish',
                      'custom_shape',
                      'size',
                      'copies',
                      'urgency',
                      'printingmethod',
                      'printtype',
                      'packaging_extras',
                    ]
                  : isPostcardsProduct
                  ? [
                      'size',
                      'fold',
                      'material',
                      'finish',
                      'printtype',
                      'printingmethod',
                      'clean_cut',
                    ]
                  : isFoldersProduct
                  ? [
                      'fold',
                      'size',
                      'material',
                      'printtype',
                      'finish',
                      'spot_finish',
                      'spot_finish_back',
                      'perforation',
                      'die_cut',
                      'loop_stitches',
                      'plano_delivery',
                      'creasing',
                      'folded',
                      'clean_cut',
                      'printingmethod',
                    ]
                  : isHdMetalProduct
                    ? [
                        'material',
                        'custom_shape',
                        'rounded_corners',
                        'frame',
                        'size',
                        'copies',
                        'urgency',
                      ]
                  : isPostersProduct
                    ? [
                        'material',
                        'size',
                        'printtype',
                        'finish',
                        'flexibleprintingmethod',
                        'printingmethod',
                        'customer_packing_option',
                      ]
                  : isPlateMaterialProduct
                    ? [
                        // printtype stond niet in deze lijst en belandde daardoor
                        // achteraan: op hpl-trespa koos de klant eerst het
                        // laminaat en het formaat, en pas onderaan of er
                        // uberhaupt gedrukt moest worden. Dat is precies de
                        // keuze die alles erboven bepaalt, dus die hoort vlak
                        // achter het materiaal.
                        'usage',
                        'material',
                        'printtype',
                        'colour_pet_felt',
                        'finish',
                        'printing_side',
                        'custom_shape',
                        'rounded_corners',
                        'drillholes',
                        'size',
                        'copies',
                        'urgency',
                      ]
                    : isBusinessCardsProduct
                      ? [
                          'size',
                          'material',
                          'printtype',
                          'finish',
                          'spot_finish',
                          'spot_finish_back',
                          'delivery',
                          'fold',
                          'clean_cut',
                          'rounded_corners',
                          'businesscard_creasing',
                          'plano_delivery',
                        ]
                  : isBookmarksProduct
                    ? [
                        'shape',
                        'size',
                        'material',
                        'printtype',
                        'finish',
                        'spot_finish',
                        'personalize',
                      ]
                  : isDisplayCubeProduct
                    ? [
                        'product_size',
                        'copies',
                        'urgency',
                      ]
                  : isDisplayCardProduct
                    ? [
                        'size',
                        'printtype',
                        'copies',
                        'urgency',
                      ]
                  // Standaardvolgorde voor producten zonder eigen lijst hierboven.
                  // printtype en finish stonden er niet in, waardoor ze achteraan
                  // belandden of in een andere groep voorrang kregen: op
                  // hpl-trespa kreeg de klant eerst het laminaat te zien en pas
                  // na het formaat de vraag of er uberhaupt gedrukt moest worden.
                  // De volgorde volgt nu de vraag die de klant zelf stelt: welk
                  // materiaal, wat voor druk, welke afwerking, welk formaat.
                  : ['usage', 'material', 'printtype', 'finish', 'orientation', 'size', 'variantSku', 'frame', 'color', 'accessories_frames', 'frame_systems'];
                Object.values(grouped).forEach((groupOptions: any[]) => {
                  groupOptions.sort((a, b) => {
                    const aIndex = optionOrder.indexOf(a.id);
                    const bIndex = optionOrder.indexOf(b.id);
                    if (aIndex === -1 && bIndex === -1) return 0;
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;
                    return aIndex - bIndex;
                  });
                });

                // Define group order - delivery should be last!
                const groupOrder = String(productSlug || '').toLowerCase() === 'buttons'
                  ? [
                      'dimensions',
                      'general',
                      'finishing',
                      'material',
                      'customization',
                      'extras',
                      'product',
                      'extra',
                      'delivery'
                    ]
                  : isCarwrapProduct
                  ? [
                      'general',
                      'dimensions',
                      'quantity',
                      'delivery'
                    ]
                  : isPostcardsProduct
                  ? [
                      'dimensions',
                      'material',
                      'finishing',
                      'general',
                      'delivery'
                    ]
                  : isPlateMaterialProduct
                  ? [
                      // Volgorde van de vraag die de klant zelf stelt: welk
                      // materiaal, wat voor druk, welke afwerking, welk formaat.
                      // 'general' bevat de bedrukking en stond achter
                      // 'dimensions', waardoor je op hpl-trespa eerst het
                      // laminaat en het formaat koos en pas daarna of er
                      // uberhaupt gedrukt moest worden.
                      'material',
                      'general',
                      'finishing',
                      'dimensions',
                      'quantity',
                      'delivery'
                    ]
                  : isPostersProduct
                  ? [
                      'material',
                      'dimensions',
                      'general',
                      'customization',
                      'extras',
                      'product',
                      'finishing',
                      'extra',
                      'delivery'
                    ]
                  : (isFlyersProduct || isFoldersProduct || isBusinessCardsProduct)
                  ? [
                      'dimensions',     // Formaat
                      'material',       // Materiaal
                      'general',        // Bedrukking, veredeling, schoonsnijden
                      'customization',
                      'extras',
                      'product',
                      'finishing',
                      'extra',
                      'delivery'        // Levertijd - ONDERAAN!
                    ]
                  : [
                      'general',
                      'dimensions',     // Formaat
                      'customization',  // Frame, materiaal
                      'extras',         // Ophangsysteem
                      'product',
                      'material',
                      'finishing',
                      'extra',
                      'delivery'        // Levertijd - ONDERAAN!
                    ];
                const sortedGroups = Object.keys(grouped).sort((a, b) => {
                  const aIndex = groupOrder.indexOf(a);
                  const bIndex = groupOrder.indexOf(b);
                  if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                  if (aIndex === -1) return 1;
                  if (bIndex === -1) return -1;
                  return aIndex - bIndex;
                });

                // Group name translations (Dutch)
                // Sleutels komen rechtstreeks van Print.com. Staat er een niet
                // tussen, dan valt hij terug op de sleutel met een hoofdletter
                // en stond er "Printing" boven een Nederlandse pagina.
                const groupNameTranslations: Record<string, string> = {
                  'dimensions': 'Afmetingen',
                  'customization': 'Personalisatie',
                  'extras': 'Extra\'s',
                  'product': 'Product',
                  'material': 'Materiaal',
                  'printing': 'Bedrukking',
                  'finishing': 'Afwerking',
                  'extra': 'Extra\'s',
                  'delivery': 'Levering'
                };

                return sortedGroups.map((groupName) => {
                  const useDimensionInputGrid =
                    groupName === 'dimensions' &&
                    grouped[groupName].filter((option) => option.type === 'number' && option.id !== 'pages').length >= 2;

                  return (
                  <div key={groupName} className="mb-8 last:mb-0">
                    {groupName !== 'general' && (
                      <h4 className="text-md font-semibold text-gray-700 mb-4 pb-2 border-b">
                        {translatePrintLabel(groupNameTranslations[groupName] || groupName.charAt(0).toUpperCase() + groupName.slice(1), locale)}
                      </h4>
                    )}
                    <div className={useDimensionInputGrid ? 'grid grid-cols-2 gap-3 sm:gap-4' : 'space-y-6'}>
                      {grouped[groupName].map((option) => (
                        <div
                          key={option.id}
                          className={useDimensionInputGrid
                            ? `${option.type === 'number' && option.id !== 'pages' ? '' : 'col-span-2'} min-w-0`
                            : 'mb-6 last:mb-0'}
                        >
                          <Label className="text-sm font-medium mb-3 flex items-center">
                            {getConsumerOptionLabel(option, configuration, locale)}
                            {option.required && <span className="text-red-500 ml-1">*</span>}
                            {optionVisuals[visualMapKey(option.id, String(configuration[option.id] || ''))]?.samplePath && (
                              <a className="ml-auto text-xs font-normal text-blue-600 underline underline-offset-2" target="_blank" rel="noreferrer" href={`/${locale}${optionVisuals[visualMapKey(option.id, String(configuration[option.id] || ''))].samplePath}`}>
                                {locale === 'de' ? 'Details ansehen' : locale === 'fr' ? 'Voir les détails' : locale === 'en' ? 'View details' : 'Bekijk eigenschappen'} ↗
                              </a>
                            )}
                            {(option as any).description && (
                              <OptionInfoButton
                                title={getConsumerOptionLabel(option, configuration, locale)}
                                locale={locale}
                                onOpen={() => setOptionInfoModal({
                                  title: getConsumerOptionLabel(option, configuration, locale),
                                  description: translatePrintLabel(String((option as any).description), locale),
                                })}
                              />
                            )}
                            {option.locked && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {ui.locked}
                                <span className="sr-only">: {formatOptionDisplayLabel(option,
                                  option.values?.find((entry) => String(entry.value) === String(configuration[option.id]))?.label
                                    || String(configuration[option.id] ?? ''), locale)}</span>
                              </Badge>
                            )}
                            {validatingOptions.has(option.id) && (
                              <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {ui.checkingAvailability}
                              </Badge>
                            )}
                          </Label>
                  {isOptionSkeletonVisible ? (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4" aria-hidden="true">
                      {[0, 1, 2, 3].map((placeholder) => (
                        <div key={`option-skeleton-${option.id}-${placeholder}`} className="h-16 animate-pulse rounded-lg bg-gray-100" />
                      ))}
                    </div>
                  ) : (
                  <div inert={option.locked || undefined} aria-disabled={option.locked || undefined} className={option.locked ? 'opacity-70' : undefined}>
                  {compactSingleValue(option) ? (
                    (() => {
                      const only = compactSingleValue(option)!;
                      const swatch = showsColorSwatches(option) ? colorSwatchForValue(only) : null;
                      return (
                        <div className="flex min-h-11 items-center gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                          {(only.image || only.visualSvgContent) ? (
                            <span className="flex h-8 w-8 flex-none items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white">
                              {only.visualSvgContent ? (
                                <span
                                  className="flex h-full w-full items-center justify-center p-1 [&_svg]:h-full [&_svg]:w-full"
                                  dangerouslySetInnerHTML={{ __html: only.visualSvgContent }}
                                />
                              ) : (
                                <img src={only.image} alt="" className="h-full w-full object-cover" />
                              )}
                            </span>
                          ) : swatch ? (
                            <span
                              className="h-6 w-6 flex-none rounded-full border border-gray-300 shadow-inner"
                              style={{ background: swatch }}
                              aria-hidden="true"
                            />
                          ) : null}
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                            {formatOptionDisplayLabel(option, only.label, locale)}
                          </span>
                          <Check className="h-4 w-4 flex-none text-blue-600" aria-hidden="true" />
                        </div>
                      );
                    })()
                  ) : option.id === 'size' && option.values && !forceDropdownRadioOptions ? (
                    <GroupedSizeSelector
                      values={getSizeSelectorValues(option, getFilteredValues(option, option.values), productSlug, providerType, category, selectedCustomSizeLimits(option)).map((value: any) => {
                        const width = Number(value.width);
                        const height = Number(value.height);
                        const shape = `${value.metadata?.shape || ''} ${value.metadata?.printShape || ''}`.toLowerCase();
                        const orientation = shape.includes('hexagon') || shape.includes('circle') || isThreeDimensionalSizeValue(value)
                          ? null
                          : width && height
                          ? width > height ? 'landscape' : height > width ? 'portrait' : 'square'
                          : value.metadata?.orientation || null;
                        return { ...value, priceModifier: getOptionDisplayModifier(option, value), metadata: { ...(value.metadata || {}), orientation } };
                      })}
                      selectedValue={configuration[option.id] as string}
                      onChange={(value) => handleOptionChange(option.id, value)}
                      optionId={option.id}
                      invalidValues={invalidValuesWithTemporarilyUnavailable(option.id)}
                      locale={locale}
                      customSizeLimits={selectedCustomSizeLimits(option)}
                      customWidth={configuration[`${option.id}_width`] as string}
                      customHeight={configuration[`${option.id}_height`] as string}
                      customSizeHint={customSizeHintFor(option)}
                      onCustomSizeChange={(dimension, value) => {
                        // Selecting "custom" alone is not orderable; Print.com needs the
                        // actual width and height alongside it.
                        handleOptionChange(`${option.id}_${dimension}`, value);
                        if (configuration[option.id] !== 'custom') handleOptionChange(option.id, 'custom');
                      }}
                      groupByOrientation={
                        // Vaste productvarianten (bijv. dekbedovertrek en kussensloop) hebben geen liggend/staand.
                        specifications?.sizeOrientationGrouping !== false &&
                        String(productSlug || '').toLowerCase() !== 'all-over-bedrukte-envelop' &&
                        !getSizeSelectorValues(option, getFilteredValues(option, option.values), productSlug, providerType, category, selectedCustomSizeLimits(option)).some(isThreeDimensionalSizeValue)
                      }
                    />
                  ) : ['material', 'cover_material'].includes(option.id) && option.values && getFilteredValues(option, option.values).length > 0 ? (
                    <MaterialFamilySelector
                      values={uniqueOptionValues(getFilteredValues(option, option.values))}
                      selectedValue={configuration[option.id]}
                      onChange={(value) => handleOptionChange(option.id, value)}
                      onInfo={(info) => setOptionInfoModal(info)}
                      locale={locale}
                      isValueUnavailable={(value) => isTemporarilyUnavailableValue(option.id, value)}
                      unavailableLabel={ui.temporarilyUnavailable}
                    />
                  ) : option.type === 'text' ? (
                    <div className="max-w-xl">
                      <Input
                        type="text"
                        value={String(configuration[option.id] ?? option.default ?? '')}
                        onChange={(event) => handleOptionChange(option.id, event.target.value)}
                        placeholder={option.placeholder || optionPlaceholder(option.label)}
                        maxLength={option.maxLength}
                        className="w-full"
                      />
                      {option.maxLength && (
                        <p className="mt-1 text-xs text-gray-500">
                          Maximaal {option.maxLength} tekens
                        </p>
                      )}
                    </div>
                  ) : option.type === 'textarea' ? (
                    <div className="max-w-xl">
                      <Textarea
                        value={String(configuration[option.id] ?? option.default ?? '')}
                        onChange={(event) => handleOptionChange(option.id, event.target.value)}
                        placeholder={option.placeholder || optionPlaceholder(option.label)}
                        maxLength={option.maxLength}
                        rows={(option as any).rows || 3}
                        className="w-full"
                      />
                      {option.maxLength && (
                        <p className="mt-1 text-xs text-gray-500">
                          Maximaal {option.maxLength} tekens
                        </p>
                      )}
                    </div>
                  ) : colorPresentation(option) === 'combobox' ? (
                    <ColorOptionCombobox
                      key={`color-combobox-${option.id}`}
                      ariaLabel={translatePrintLabel(option.label, locale)}
                      placeholder={optionPlaceholder(option.label)}
                      locale={locale}
                      selectedValue={String(configuration[option.id] ?? '')}
                      onSelect={(value) => {
                        setOptionHoverPreview(null);
                        handleOptionChange(option.id, value);
                      }}
                      items={uniqueOptionValues(getFilteredValues(option, option.values)).map((value: any) => {
                        const label = formatOptionDisplayLabel(option, value.label, locale);
                        const displayModifier = getOptionDisplayModifier(option, value);
                        const note = isTemporarilyUnavailableValue(option.id, value.value)
                          ? ui.temporarilyUnavailable
                          : displayModifier !== 0
                            ? formatOptionPriceDelta(displayModifier, locale)
                            : undefined;
                        return {
                          value: String(value.value),
                          label,
                          background: colorSwatchForValue(value),
                          searchText: colorSearchText(value, label),
                          disabled: isColorValueDisabled(option, value),
                          note,
                        };
                      })}
                    />
                  ) : option.type === 'select' && option.values && !hasCompleteVisibleValueVisuals(option) && !usesColorSwatchGrid(option) ? (
                    <div className="flex items-center gap-2">
                    <Select
                      key={`select-${option.id}`}
                      value={String(configuration[option.id] ?? '')}
                      onValueChange={(value) => {
                        setOptionHoverPreview(null);
                        handleOptionChange(option.id, value);
                      }}
                    >
                      <SelectTrigger className={`flex-1 ${
                        option.values?.some((item: any) => item.image || item.visualSvgContent)
                          ? '!h-auto min-h-[68px] rounded-lg border-2 px-4 py-4'
                          : 'min-h-12 py-2'
                      }`}>
                        {showsColorSwatches(option) ? (
                          (() => {
                            const selected = option.values?.find((item: any) => String(item.value) === String(configuration[option.id]));
                            return selected ? (
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-5 w-5 flex-none rounded-full border border-gray-300 shadow-inner"
                                  style={{ background: colorSwatchForValue(selected) }}
                                  aria-hidden="true"
                                />
                                <span className="truncate">{formatOptionDisplayLabel(option, selected?.label || '', locale)}</span>
                              </div>
                            ) : <SelectValue placeholder={optionPlaceholder(option.label)} />;
                          })()
                        ) : option.values.find((item: any) => String(item.value) === String(configuration[option.id]))?.image ||
                            option.values.find((item: any) => String(item.value) === String(configuration[option.id]))?.visualSvgContent ? (
                          (() => {
                            const selected = option.values?.find((item: any) => String(item.value) === String(configuration[option.id]));
                            return (
                              <div className="flex min-w-0 items-center gap-2">
                                {selected?.visualSvgContent ? (
                                  <span
                                    className="h-9 w-9 flex-none overflow-hidden rounded-md border border-gray-200 bg-white"
                                    dangerouslySetInnerHTML={{ __html: selected.visualSvgContent }}
                                  />
                                ) : (
                                  <img src={selected?.image} alt="" className="h-9 w-9 flex-none rounded-md border border-gray-200 object-cover" />
                                )}
                                <span className="truncate">{formatOptionDisplayLabel(option, selected?.label || '', locale)}</span>
                              </div>
                            );
                          })()
                        ) : (
                          <SelectValue placeholder={optionPlaceholder(option.label)} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueOptionValues(getFilteredValues(option, option.values)).map((value, index) => (
                          <SelectItem key={`${option.id}-${value.id}-${value.value}-${index}`} value={String(value.value)} disabled={isTemporarilyUnavailableValue(option.id, value.value)}>
                            {showsColorSwatches(option) ? (
                              <span className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-5 w-5 flex-none rounded-full border border-gray-300 shadow-inner"
                                  style={{ background: colorSwatchForValue(value) }}
                                  aria-hidden="true"
                                />
                                <span className="truncate">{formatOptionDisplayLabel(option, value.label, locale)}</span>
                              </span>
                            ) : value.image || value.visualSvgContent ? (
                              <span
                                className="flex min-w-0 items-center gap-2 pr-2"
                                onMouseEnter={(event) => showOptionHoverPreview(event, value.image)}
                                onMouseMove={(event) => showOptionHoverPreview(event, value.image)}
                                onMouseLeave={() => setOptionHoverPreview(null)}
                              >
                                {value.visualSvgContent ? (
                                  <span
                                    className="h-9 w-9 flex-none overflow-hidden rounded-md border border-gray-200 bg-white"
                                    dangerouslySetInnerHTML={{ __html: value.visualSvgContent }}
                                  />
                                ) : (
                                  <img src={value.image} alt="" className="h-9 w-9 flex-none rounded-md border border-gray-200 object-cover" />
                                )}
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate">{formatOptionDisplayLabel(option, value.label, locale)}</span>
                                  {getOptionDescription(option.id, value, locale) && (
                                    <span className="block truncate text-xs text-gray-500">{getOptionDescription(option.id, value, locale)}</span>
                                  )}
                                </span>
                              </span>
                            ) : (
                              formatOptionDisplayLabel(option, value.label, locale)
                            )}
                            {isTemporarilyUnavailableValue(option.id, value.value) && (
                              <span className="ml-2 text-xs font-semibold text-gray-500">{ui.temporarilyUnavailable}</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const sel = option.values?.find((v: any) => String(v.value) === String(configuration[option.id]));
                      if (sel?.image || sel?.visualSvgContent || sel?.description || (sel && getOptionDescription(option.id, sel, locale))) {
                        return (
                          <button type="button" aria-label="Optie bekijken" onClick={() => setOptionInfoModal({ title: translatePrintLabel(sel.label || option.label, locale), description: getOptionDescription(option.id, sel, locale) || '', image: sel.image, overviewImage: sel.overviewImage, visualSvgContent: sel.visualSvgContent })} className={`inline-flex flex-none items-center justify-center bg-white text-gray-500 shadow-sm ring-1 ring-gray-200 transition hover:bg-blue-50 hover:text-blue-700 ${
                            option.values?.some((item: any) => item.image || item.visualSvgContent)
                              ? 'h-[68px] w-12 rounded-lg'
                              : 'h-10 w-10 rounded-full'
                          }`}>
                            <Info className="h-5 w-5" />
                          </button>
                        );
                      }
                      return null;
                    })()}
                    </div>
                  ) : !forceDropdownRadioOptions && (option.type === 'radio' || option.type === 'custom-size') && option.values && option.id === 'size' ? (
                    <GroupedSizeSelector
                      values={getSizeSelectorValues(option, getFilteredValues(option, option.values), productSlug, providerType, category, selectedCustomSizeLimits(option)).map((value: any) => ({
                        ...value,
                        priceModifier: getOptionDisplayModifier(option, value),
                      }))}
                      selectedValue={configuration[option.id] as string}
                      onChange={(value) => handleOptionChange(option.id, value)}
                      optionId={option.id}
                      invalidValues={invalidValuesWithTemporarilyUnavailable(option.id)}
                      locale={locale}
                      groupByOrientation={specifications?.sizeOrientationGrouping !== false && String(productSlug || '').toLowerCase() !== 'all-over-bedrukte-envelop'}
                    />
                  ) : (option.type === 'radio' || option.type === 'custom-size') && option.values && (
                    forceDropdownRadioOptions ||
                    (option.id !== 'material' && !shouldShowIcons(option.id) && !hasCompleteVisibleValueVisuals(option) && !colorPresentation(option) && getFilteredValues(option, option.values).length > 5)
                  ) ? (
                    /* Dropdown for options with more than 5 values (all types: radio, custom-size) */
                    <Select
                      key={`select-${option.id}`}
                      value={String(configuration[option.id] ?? '')}
                      onValueChange={(value) => {
                        setOptionHoverPreview(null);
                        handleOptionChange(option.id, value);
                      }}
                    >
                      <SelectTrigger className={`w-full ${
                        option.values?.some((item: any) => item.image || item.visualSvgContent)
                          ? '!h-auto min-h-[68px] rounded-lg border-2 px-4 py-4'
                          : 'min-h-12 py-2'
                      }`}>
                        {showsColorSwatches(option) ? (
                          (() => {
                            const selected = option.values?.find((item: any) => String(item.value) === String(configuration[option.id]));
                            return selected ? (
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-5 w-5 flex-none rounded-full border border-gray-300 shadow-inner"
                                  style={{ background: colorSwatchForValue(selected) }}
                                  aria-hidden="true"
                                />
                                <span className="truncate">{formatOptionDisplayLabel(option, selected?.label || '', locale)}</span>
                              </div>
                            ) : <SelectValue placeholder={optionPlaceholder(option.label)} />;
                          })()
                        ) : option.id === 'material' && option.values.find((item: any) => String(item.value) === String(configuration[option.id]))?.image ? (
                          (() => {
                            const selected = option.values?.find((item: any) => String(item.value) === String(configuration[option.id]));
                            return (
                              <div className="flex min-w-0 items-center gap-2">
                                <img src={selected?.image} alt="" className="h-9 w-9 flex-none rounded-md border border-gray-200 object-cover" />
                                <span className="truncate">{formatOptionDisplayLabel(option, selected?.label || '', locale)}</span>
                              </div>
                            );
                          })()
                        ) : (
                          <SelectValue placeholder={optionPlaceholder(option.label)} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueOptionValues(getFilteredValues(option, option.values)).map((value, index) => (
                          <SelectItem key={`${option.id}-${value.id}-${value.value}-${index}`} value={String(value.value)} disabled={isTemporarilyUnavailableValue(option.id, value.value)}>
                            {showsColorSwatches(option) ? (
                              <span className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-5 w-5 flex-none rounded-full border border-gray-300 shadow-inner"
                                  style={{ background: colorSwatchForValue(value) }}
                                  aria-hidden="true"
                                />
                                <span className="truncate">{formatOptionDisplayLabel(option, value.label, locale)}</span>
                              </span>
                            ) : option.id === 'material' && value.image ? (
                              <span
                                className="flex min-w-0 items-center gap-2 pr-2"
                                onMouseEnter={(event) => showOptionHoverPreview(event, value.image)}
                                onMouseMove={(event) => showOptionHoverPreview(event, value.image)}
                                onMouseLeave={() => setOptionHoverPreview(null)}
                              >
                                <img src={value.image} alt="" className="h-9 w-9 flex-none rounded-md border border-gray-200 object-cover" />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate">{formatOptionDisplayLabel(option, value.label, locale)}</span>
                                  {getOptionDescription(option.id, value, locale) && (
                                    <span className="block truncate text-xs text-gray-500">{getOptionDescription(option.id, value, locale)}</span>
                                  )}
                                </span>
                              </span>
                            ) : (
                              formatOptionDisplayLabel(option, value.label, locale)
                            )}
                            {isTemporarilyUnavailableValue(option.id, value.value) && (
                              <span className="ml-2 text-xs font-semibold text-gray-500">{ui.temporarilyUnavailable}</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (option.type === 'color' || option.type === 'swatch' || usesColorSwatchGrid(option)) && option.values ? (
                    <RadioGroup
                      value={(configuration[option.id] as string) || ''}
                      onValueChange={(value) => handleOptionChange(option.id, value)}
                    >
                      <div className={`grid gap-2.5 ${(() => {
                        // Stalen zijn klein, dus dichter dan de tegelrasters, maar
                        // ook hier nooit meer kolommen dan er kleuren zijn.
                        const swatchCount = uniqueOptionValues(getFilteredValues(option, option.values)).length;
                        if (swatchCount <= 2) return 'grid-cols-2 lg:max-w-md';
                        if (swatchCount === 3) return 'grid-cols-2 sm:grid-cols-3 lg:max-w-2xl';
                        if (swatchCount === 4) return 'grid-cols-2 sm:grid-cols-4';
                        return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';
                      })()}`}>
                        {uniqueOptionValues(getFilteredValues(option, option.values)).map((value, index) => {
                          const isSelected = configuration[option.id] === value.value;
                          const isExcluded = option.id === 'custom_shape'
                              ? false
                              // Uitgesloten, maar oplosbaar? Dan blijft de tegel
                              // aanklikbaar en beweegt de botsende optie mee bij
                              // de klik. Alleen wat in geen enkele combinatie kan
                              // blijft uitgeschakeld. Zonder dit werd "geen
                              // bedrukking" wel getoond maar deed klikken niets.
                              : isValueExcluded(option.id, value.value, configuration)
                                && resolveExcludedSelection({
                                  optionId: option.id,
                                  value: String(value.value),
                                  configuration,
                                  options: localizedOptions as any,
                                  isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
                                }) === null;
                          const isTemporarilyUnavailable = isTemporarilyUnavailableValue(option.id, value.value);
                          const isInvalid = isTemporarilyUnavailable || (option.id === 'custom_shape' ? false : invalidValues[option.id]?.has(value.value) || isExcluded);
                          const swatch = colorSwatchForValue(value);
                          const displayModifier = getOptionDisplayModifier(option, value);
                          const description = getOptionDescription(option.id, value, locale);

                          return (
                            <label
                              key={`${option.id}-${value.id}-${value.value}-${index}`}
                              htmlFor={`${option.id}-${value.id}`}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                if (!isInvalid) handleOptionChange(option.id, value.value);
                              }}
                              onClick={(event) => {
                                if (isHiddenRadioBubbleClick(event)) return;
                                event.preventDefault();
                                if (!isInvalid) handleOptionChange(option.id, value.value);
                              }}
                              className={`relative flex min-h-14 cursor-pointer items-center gap-2 rounded-lg border-2 p-2.5 transition-all sm:gap-3 sm:p-3 ${
                                isInvalid
                                  ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-40'
                                  : isSelected
                                  ? 'border-blue-600 bg-blue-50 shadow-md ring-1 ring-blue-600'
                                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                              }`}
                            >
                              <span
                                className="h-6 w-6 flex-none rounded-full border border-gray-300 shadow-inner sm:h-7 sm:w-7"
                                style={{ background: swatch }}
                                aria-hidden="true"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="line-clamp-2 block text-sm font-medium text-gray-900">
                                  {formatOptionDisplayLabel(option, value.label, locale)}
                                </span>
                                {isTemporarilyUnavailable && (
                                  <span className="block text-xs font-semibold text-gray-500">{ui.temporarilyUnavailable}</span>
                                )}
                                {displayModifier !== 0 && (
                                  <span className="mt-0.5 block"><OptionPriceDelta modifier={displayModifier} locale={locale} /></span>
                                )}
                              </span>
                              {description && (
                                <button
                                  type="button"
                                  aria-label="Optie informatie"
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setOptionInfoModal({
                                      title: translatePrintLabel(value.label || option.label, locale),
                                      description,
                                      image: value.image,
                                      overviewImage: value.overviewImage,
                                      visualSvgContent: value.visualSvgContent,
                                    });
                                  }}
                                  className="inline-flex h-7 w-7 flex-none sm:h-8 sm:w-8 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-gray-200 transition hover:bg-blue-50 hover:text-blue-700"
                                >
                                  <Info className="h-4 w-4" />
                                </button>
                              )}
                              {isSelected && (
                                // Als hoekje, zodat het vinkje geen ruimte van het
                                // kleurlabel afsnoept ("Zwa" in plaats van "Zwart").
                                <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 ring-2 ring-white">
                                  <Check className="h-3 w-3 text-white" />
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </RadioGroup>
                  ) : (option.type === 'radio' || option.type === 'select' || option.type === 'checkbox') && option.values && (
                    <RadioGroup
                      value={(configuration[option.id] as string) || ''}
                      onValueChange={(value) => handleOptionChange(option.id, value)}
                    >
                      {/* Use image cards only when every visible value has a visual. Mixed visual/text options stay compact. */}
                      {hasCompleteVisibleValueVisuals(option) && !usesCompactVisualLabels(option) ? (
                        /* Image-based selection */
                        <div className={`grid auto-rows-fr gap-2 sm:gap-3 ${optionGridColumnsClass(uniqueOptionValues(getFilteredValues(option, option.values)).length)}`}>
                          {uniqueOptionValues(getFilteredValues(option, option.values)).map((value, index) => {
                            const isSelected = configuration[option.id] === value.value;
                            const isExcluded = option.id === 'custom_shape'
                              ? false
                              // Uitgesloten, maar oplosbaar? Dan blijft de tegel
                              // aanklikbaar en beweegt de botsende optie mee bij
                              // de klik. Alleen wat in geen enkele combinatie kan
                              // blijft uitgeschakeld. Zonder dit werd "geen
                              // bedrukking" wel getoond maar deed klikken niets.
                              : isValueExcluded(option.id, value.value, configuration)
                                && resolveExcludedSelection({
                                  optionId: option.id,
                                  value: String(value.value),
                                  configuration,
                                  options: localizedOptions as any,
                                  isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
                                }) === null;
                            const isTemporarilyUnavailable = isTemporarilyUnavailableValue(option.id, value.value);
                          const isInvalid = isTemporarilyUnavailable || (option.id === 'custom_shape' ? false : invalidValues[option.id]?.has(value.value) || isExcluded);
                            const isValidating = validatingOptions.has(option.id);
                            const description = getOptionDescription(option.id, value, locale);
                            const displayModifier = getOptionDisplayModifier(option, value);
                            return (
                              <label
                                key={`${option.id}-${value.id}-${value.value}-${index}`}
                                htmlFor={`${option.id}-${value.id}`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  if (!isInvalid) handleOptionChange(option.id, value.value);
                                }}
                                onClick={(event) => {
                                  if (isHiddenRadioBubbleClick(event)) return;
                                  event.preventDefault();
                                  if (!isInvalid) handleOptionChange(option.id, value.value);
                                }}
                                className={`relative block overflow-hidden rounded-lg border-2 transition-all ${
                                  isInvalid
                                    ? 'opacity-40 cursor-not-allowed border-gray-200'
                                    : isSelected
                                    ? 'border-blue-600 bg-blue-50 shadow-md ring-1 ring-blue-600 cursor-pointer'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                                } ${isValidating ? 'animate-pulse' : ''}`}
                              >
                                {/* Image */}
                                {/* Een vaste bandhoogte in plaats van aspect-video. Die groeide mee met de
                                    tegelbreedte, dus bij vier kolommen werd een illustratie van 150 px tot
                                    278 px opgeblazen en boven en onder bijgesneden. Nu blijft het beeld
                                    heel en scherp, ongeacht het aantal kolommen. */}
                                {(value.image || value.visualSvgContent) && (
                                  <div className="relative flex h-24 items-center justify-center overflow-hidden bg-white sm:h-28">
                                    {value.visualSvgContent ? (
                                      <div
                                        className="flex h-full w-full items-center justify-center bg-white p-2 [&_svg]:h-full [&_svg]:w-full"
                                        dangerouslySetInnerHTML={{ __html: value.visualSvgContent }}
                                      />
                                    ) : (
                                      <img
                                        src={value.image}
                                        alt=""
                                        className="h-full w-auto max-w-full object-contain p-1.5"
                                      />
                                    )}
                                    {/* Info icon */}
                                    {description && (
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          setOptionInfoModal({
                                            title: formatOptionDisplayLabel(option, value.label, locale),
                                            description,
                                            image: value.image,
                                            overviewImage: value.overviewImage,
                                            visualSvgContent: value.visualSvgContent,
                                          });
                                        }}
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                        }}
                                        className="absolute top-2 left-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-500 shadow-sm hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                        aria-label="Meer informatie"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    )}
                                    {/* Checkmark on selection */}
                                    {isSelected && (
                                      <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 shadow-md ring-2 ring-white">
                                        <Check className="h-5 w-5 stroke-[3] text-white" />
                                      </div>
                                    )}
                                    {value.eco && (
                                      <div className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-white shadow-md ring-2 ring-white" title={ui.eco}>
                                        <Leaf className="h-4 w-4" />
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Label */}
                                <div className="bg-white px-2 py-2">
                                  <div className="flex items-center justify-center gap-2 text-center text-xs font-medium leading-snug sm:text-sm">
                                    <span>{formatOptionDisplayLabel(option, value.label, locale)}</span>
                                    {value.eco && !optionHasVisual(value) && (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                                        <Leaf className="h-3 w-3" />
                                      </Badge>
                                    )}
                                  </div>
                                  {isTemporarilyUnavailable && (
                                    <p className="mt-1 text-center text-[11px] font-semibold text-gray-500 sm:text-xs">{ui.temporarilyUnavailable}</p>
                                  )}
                                  {(option as any).showValueDescriptions && description && (
                                    <p className="mt-1 line-clamp-3 text-center text-[11px] leading-snug text-gray-500 sm:text-xs">
                                      {description}
                                    </p>
                                  )}
                                  {displayModifier !== 0 && (
                                    <div className="mt-1 text-center"><OptionPriceDelta modifier={displayModifier} locale={locale} /></div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      ) : shouldShowIcons(option.id) && hasCompleteVisibleValueIcons(option) && !usesCompactVisualLabels(option) ? (
                        /* Icon-based selection */
                        <div className={`grid auto-rows-fr gap-2 sm:gap-3 ${optionGridColumnsClass(uniqueOptionValues(getFilteredValues(option, option.values)).length)}`}>
                          {uniqueOptionValues(getFilteredValues(option, option.values)).map((value, index) => {
                            const isSelected = configuration[option.id] === value.value;
                            const isExcluded = option.id === 'custom_shape'
                              ? false
                              // Uitgesloten, maar oplosbaar? Dan blijft de tegel
                              // aanklikbaar en beweegt de botsende optie mee bij
                              // de klik. Alleen wat in geen enkele combinatie kan
                              // blijft uitgeschakeld. Zonder dit werd "geen
                              // bedrukking" wel getoond maar deed klikken niets.
                              : isValueExcluded(option.id, value.value, configuration)
                                && resolveExcludedSelection({
                                  optionId: option.id,
                                  value: String(value.value),
                                  configuration,
                                  options: localizedOptions as any,
                                  isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
                                }) === null;
                            const isTemporarilyUnavailable = isTemporarilyUnavailableValue(option.id, value.value);
                          const isInvalid = isTemporarilyUnavailable || (option.id === 'custom_shape' ? false : invalidValues[option.id]?.has(value.value) || isExcluded);
                            const isValidating = validatingOptions.has(option.id);
                            const iconPath = getOptionIcon(option.id, value.value, value.metadata);
                            const description = getOptionDescription(option.id, value, locale);
                            const displayModifier = getOptionDisplayModifier(option, value);

                            return (
                              <label
                                key={`${option.id}-${value.id}-${value.value}-${index}`}
                                htmlFor={`${option.id}-${value.id}`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  if (!isInvalid) handleOptionChange(option.id, value.value);
                                }}
                                onClick={(event) => {
                                  if (isHiddenRadioBubbleClick(event)) return;
                                  event.preventDefault();
                                  if (!isInvalid) handleOptionChange(option.id, value.value);
                                }}
                                className={`relative overflow-hidden rounded-lg border-2 transition-all cursor-pointer ${
                                  isInvalid
                                    ? 'opacity-40 cursor-not-allowed border-gray-200'
                                    : isSelected
                                    ? 'border-blue-600 bg-blue-50 shadow-md ring-1 ring-blue-600'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                } ${isValidating ? 'animate-pulse' : ''}`}
                              >
                                {description && (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setOptionInfoModal({
                                        title: formatOptionDisplayLabel(option, value.label, locale),
                                        description,
                                        image: iconPath || undefined,
                                        overviewImage: value.overviewImage,
                                      });
                                    }}
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                    className="absolute left-2 top-2 z-10 flex rounded-full bg-white/95 p-1 text-gray-500 shadow-sm hover:text-gray-900"
                                    aria-label="Meer informatie"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                )}

                                <div className="flex flex-col">
                                  {/* Icon. Deze illustraties vulden op volle
                                      tegelbreedte ruim 150px hoogte en oogden grof
                                      naast de materiaalfoto's. Ze dragen weinig
                                      informatie per pixel, dus een vaste, bescheiden
                                      hoogte leest rustiger en houdt het label
                                      leidend. */}
                                  {iconPath && (
                                    <div className="relative flex h-20 w-full items-center justify-center border-b border-gray-200 bg-white px-3 py-2 sm:h-24">
                                      <Image
                                        src={iconPath}
                                        alt=""
                                        width={160}
                                        height={90}
                                        className="h-full w-auto max-w-full object-contain"
                                      />
                                      {value.eco && (
                                        <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white shadow ring-2 ring-white" title={ui.eco}>
                                          <Leaf className="h-3 w-3" />
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {/* Label */}
                                  <div className="min-w-0 px-2 py-2 text-center">
                                    <div className={`text-xs font-medium leading-snug sm:text-sm ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                                      {formatOptionDisplayLabel(option, value.label, locale)}
                                    </div>
                                    {isTemporarilyUnavailable && (
                                      <div className="mt-1 text-[11px] font-semibold text-gray-500 sm:text-xs">{ui.temporarilyUnavailable}</div>
                                    )}
                                  </div>

                                  {/* Badges */}
                                  <div className="flex flex-wrap items-center justify-center gap-2 px-2 pb-2">
                                    {value.eco && !iconPath && (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                                        <Leaf className="h-3 w-3 mr-1" />
                                        ECO
                                      </Badge>
                                    )}
                                    {displayModifier !== 0 && (
                                      <OptionPriceDelta modifier={displayModifier} locale={locale} />
                                    )}
                                  </div>
                                </div>

                                {/* Checkmark on selection */}
                                {isSelected && (
                                  <div className="absolute top-2 right-2">
                                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                      <Check className="h-4 w-4 text-white" />
                                    </div>
                                  </div>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        /* Text-based selection - with card styling like image-based */
                        <div className={`grid auto-rows-fr gap-2 sm:gap-3 ${optionGridColumnsClass(uniqueOptionValues(getFilteredValues(option, option.values)).length)}`}>
                          {uniqueOptionValues(getFilteredValues(option, option.values)).map((value, index) => {
                            const isSelected = configuration[option.id] === value.value;
                            const isExcluded = option.id === 'custom_shape'
                              ? false
                              // Uitgesloten, maar oplosbaar? Dan blijft de tegel
                              // aanklikbaar en beweegt de botsende optie mee bij
                              // de klik. Alleen wat in geen enkele combinatie kan
                              // blijft uitgeschakeld. Zonder dit werd "geen
                              // bedrukking" wel getoond maar deed klikken niets.
                              : isValueExcluded(option.id, value.value, configuration)
                                && resolveExcludedSelection({
                                  optionId: option.id,
                                  value: String(value.value),
                                  configuration,
                                  options: localizedOptions as any,
                                  isExcluded: (id, waarde, config) => isValueExcluded(id, waarde, config as Record<string, string | number>),
                                }) === null;
                            const isTemporarilyUnavailable = isTemporarilyUnavailableValue(option.id, value.value);
                          const isInvalid = isTemporarilyUnavailable || (option.id === 'custom_shape' ? false : invalidValues[option.id]?.has(value.value) || isExcluded);
                            const isValidating = validatingOptions.has(option.id);
                            const description = getOptionDescription(option.id, value, locale);
                            const displayModifier = getOptionDisplayModifier(option, value);
                            return (
                              <label
                                key={`${option.id}-${value.id}-${value.value}-${index}`}
                                htmlFor={`${option.id}-${value.id}`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  if (!isInvalid) handleOptionChange(option.id, value.value);
                                }}
                                onClick={(event) => {
                                  if (isHiddenRadioBubbleClick(event)) return;
                                  event.preventDefault();
                                  if (!isInvalid) handleOptionChange(option.id, value.value);
                                }}
                                className={`relative flex min-h-14 flex-col justify-center rounded-lg border-2 p-3 transition-all ${
                                  isInvalid
                                    ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50'
                                    : isSelected
                                    ? 'border-blue-600 bg-blue-50 shadow-md ring-1 ring-blue-600 cursor-pointer'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                                } ${isValidating ? 'animate-pulse' : ''}`}
                              >
                                <div className={`pr-8 ${usesCompactVisualLabels(option) ? 'flex items-center gap-3' : 'flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3'}`}>
                                  <div className={`min-w-0 flex-1 ${usesCompactVisualLabels(option) ? 'flex items-center gap-3' : 'flex flex-wrap items-center gap-2'}`}>
                                    {usesCompactVisualLabels(option) && (value.image || value.visualSvgContent) && (
                                      <span className="relative flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white">
                                        {value.visualSvgContent ? (
                                          <span
                                            className="flex h-full w-full items-center justify-center p-1.5 [&_svg]:h-full [&_svg]:w-full"
                                            dangerouslySetInnerHTML={{ __html: value.visualSvgContent }}
                                          />
                                        ) : (
                                          <img
                                            src={value.image}
                                            alt=""
                                            className="h-full w-full object-cover"
                                          />
                                        )}
                                        {value.eco && (
                                          <span className="absolute bottom-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-600 text-white shadow ring-1 ring-white" title={ui.eco}>
                                            <Leaf className="h-2.5 w-2.5" />
                                          </span>
                                        )}
                                      </span>
                                    )}
                                    <span className="text-sm font-medium">{formatOptionDisplayLabel(option, value.label, locale)}</span>
                                    {/* Korte, altijd zichtbare uitleg; de volledige staat achter het info-icoon. */}
                                    {sampleOptionValueShortDescription(option.id, value.value, locale) && (
                                      <p className="basis-full text-xs font-normal leading-snug text-gray-500">
                                        {sampleOptionValueShortDescription(option.id, value.value, locale)}
                                      </p>
                                    )}

                                    {/* ECO Badge */}
                                    {value.eco && !optionHasVisual(value) && (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                        <Leaf className="h-3 w-3 mr-1" />
                                        {ui.eco}
                                      </Badge>
                                    )}

                                  </div>
                                  {isInvalid && (
                                    <p className="text-xs font-semibold text-gray-500">
                                      {isTemporarilyUnavailable ? ui.temporarilyUnavailable : ui.unavailable}
                                    </p>
                                  )}

                                  {description && usesCompactVisualLabels(option) && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setOptionInfoModal({
                                          title: formatOptionDisplayLabel(option, value.label, locale),
                                          description,
                                          image: value.image,
                                          overviewImage: value.overviewImage,
                                          visualSvgContent: value.visualSvgContent,
                                        });
                                      }}
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                      }}
                                      className="absolute right-3 top-3 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/95 text-gray-500 shadow-sm hover:text-gray-900"
                                      aria-label="Meer informatie"
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                  )}

                                  <div className={`flex items-center gap-2 ${usesCompactVisualLabels(option) ? 'ml-auto min-w-fit justify-end' : 'min-w-[4.75rem] justify-start sm:justify-end'}`}>
                                    {description && !usesCompactVisualLabels(option) && (
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          setOptionInfoModal({
                                            title: formatOptionDisplayLabel(option, value.label, locale),
                                            description,
                                            image: value.image,
                                            overviewImage: value.overviewImage,
                                            visualSvgContent: value.visualSvgContent,
                                          });
                                        }}
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                        }}
                                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/95 text-gray-500 shadow-sm hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                        aria-label="Meer informatie"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    )}
                                    {displayModifier !== 0 && (
                                      <OptionPriceDelta modifier={displayModifier} locale={locale} />
                                    )}
                                  </div>
                                </div>

                                {/* Checkmark on selection */}
                                {isSelected && (
                                  <div className="absolute top-3 right-3">
                                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                      <Check className="h-4 w-4 text-white" />
                                    </div>
                                  </div>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </RadioGroup>
                  )}

                  {option.type === 'number' && (
                    <div className={useDimensionInputGrid ? 'w-full' : 'max-w-xs'}>
                      {option.id === 'pages' ? (() => {
                        const step = Number((option as any).step || 4);
                        const min = Number(option.min ?? 32);
                        const max = Number(option.max ?? 500);
                        const current = Number(configuration[option.id] ?? option.default ?? min);
                        const normalizePageCount = (raw: number) => {
                          const bounded = Math.min(max, Math.max(min, raw));
                          const stepped = min + Math.round((bounded - min) / step) * step;
                          return Math.min(max, Math.max(min, stepped));
                        };
                        const setPages = (raw: number) => handleOptionChange(option.id, normalizePageCount(raw));
                        return (
                          <div className="flex h-12 items-center overflow-hidden rounded-lg border border-gray-300 bg-white">
                            <button
                              type="button"
                              onClick={() => setPages(current - step)}
                              disabled={current <= min}
                              aria-label="Aantal pagina's verlagen"
                              className="flex h-full w-12 items-center justify-center border-r border-gray-200 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <Input
                              type="number"
                              min={min}
                              max={max}
                              step={step}
                              value={Number.isFinite(current) ? current : min}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                handleOptionChange(option.id, nextValue === '' ? '' : Number(nextValue));
                              }}
                              onBlur={(e) => setPages(Number(e.target.value || min))}
                              className="h-full flex-1 border-0 text-center text-base font-semibold shadow-none focus-visible:ring-0"
                            />
                            <button
                              type="button"
                              onClick={() => setPages(current + step)}
                              disabled={current >= max}
                              aria-label="Aantal pagina's verhogen"
                              className="flex h-full w-12 items-center justify-center border-l border-gray-200 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })() : (
                        <div className="relative">
                          <Input
                            type="number"
                            min={getNumberOptionBounds(option).min}
                            max={getNumberOptionBounds(option).max}
                            step={(option as any).step || 1}
                            value={configuration[option.id] ?? option.default ?? getNumberOptionBounds(option).min ?? 1}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              handleOptionChange(option.id, nextValue === '' ? '' : Number(nextValue));
                            }}
                            className="h-12 w-full pr-12 text-base font-semibold"
                          />
                          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-gray-500">
                            {(option as any).unit || 'cm'}
                          </span>
                        </div>
                      )}
                      {(getNumberOptionBounds(option).min !== undefined || getNumberOptionBounds(option).max !== undefined) && (
                        <p className="mt-1 text-xs text-gray-500">
                          {option.id === 'pages'
                            ? `${option.min !== undefined ? `Min: ${option.min}` : ''}${option.min !== undefined && option.max !== undefined ? ', ' : ''}${option.max !== undefined ? `max: ${option.max}` : ''}${(option as any).step ? `, stap ${(option as any).step}` : ''} pagina's`
                            : (
                              <>
                                {getNumberOptionBounds(option).min !== undefined && getNumberOptionBounds(option).max !== undefined
                                  ? `${getNumberOptionBounds(option).min}–${getNumberOptionBounds(option).max} ${(option as any).unit || 'cm'}`
                                  : getNumberOptionBounds(option).min !== undefined
                                    ? `≥ ${getNumberOptionBounds(option).min} ${(option as any).unit || 'cm'}`
                                    : `≤ ${getNumberOptionBounds(option).max} ${(option as any).unit || 'cm'}`}
                              </>
                            )}
                        </p>
                      )}
                      {!dimensionValidation.valid && ['width_cm', 'height_cm'].includes(option.id) && (
                        <p className="mt-1 text-xs font-medium text-red-600">{dimensionValidation.message}</p>
                      )}
                      {option.id !== 'pages' && ((option as any).unit || 'cm') === 'cm' && (() => {
                        const bounds = getNumberOptionBounds(option);
                        const suggestion = suggestCmFromMm(configuration[option.id], bounds.min, bounds.max);
                        if (suggestion === null) return null;
                        const copy = sizeUnitHintCopy(locale, suggestion);
                        return (
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-amber-900">
                            <span>{copy.question} {copy.hint}</span>
                            <button
                              type="button"
                              onClick={() => handleOptionChange(option.id, suggestion)}
                              className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold hover:bg-amber-100"
                            >
                              {copy.apply}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {shouldRenderSeparateCustomSizePanel({
                    optionId: option.id,
                    optionType: option.type,
                    hasValues: Boolean(option.values),
                    hasCustomSizeValue: Boolean(option.values?.some((value) => value.customSizes)),
                    forceDropdownRadioOptions,
                    hasCompactSingleValue: Boolean(compactSingleValue(option)),
                  }) && option.values && (() => {
                    const customOption = option.values?.find(v => v.customSizes);
                    const isCustomSelected = configuration[option.id] === 'custom' ||
                                            configuration[option.id] === customOption?.value;

                    if (!customOption?.customSizes || !isCustomSelected) return null;
                    const customLimits = selectedCustomSizeLimits(option);

                    return (
                      <div className="mt-4 p-3 sm:p-4 border border-blue-600 bg-blue-50 rounded-lg">
                        <p className="mb-3 text-xs leading-snug text-gray-600">{customSizeHintFor(option)}</p>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">
                              {ui.width} (cm)
                            </Label>
                            <Input
                              type="number"
                              min={customLimits.minWidth}
                              max={customLimits.maxWidth}
                              step="0.1"
                              value={configuration[`${option.id}_width`] || ''}
                              onChange={(e) => handleOptionChange(`${option.id}_width`, parseCustomDimensionInput(e.target.value))}
                              placeholder={`${formatCm(customLimits.minWidth || 0)}-${formatCm(customLimits.maxWidth || 0)}`}
                              className="w-full"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {ui.min}: {formatCm(customLimits.minWidth || 0)}cm, {ui.max}: {formatCm(customLimits.maxWidth || 0)}cm
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">
                              {ui.height} (cm)
                            </Label>
                            <Input
                              type="number"
                              min={customLimits.minHeight}
                              max={customLimits.maxHeight}
                              step="0.1"
                              value={configuration[`${option.id}_height`] || ''}
                              onChange={(e) => handleOptionChange(`${option.id}_height`, parseCustomDimensionInput(e.target.value))}
                              placeholder={`${formatCm(customLimits.minHeight || 0)}-${formatCm(customLimits.maxHeight || 0)}`}
                              className="w-full"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {ui.min}: {formatCm(customLimits.minHeight || 0)}cm, {ui.max}: {formatCm(customLimits.maxHeight || 0)}cm
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {option.type === 'custom-size' && option.id !== 'size' && option.values && option.values.some(v => v.customSizes) && (() => {
                    const customOption = option.values?.find(v => v.customSizes);
                    const isCustomSelected = configuration[option.id] === 'custom' ||
                                            configuration[option.id] === customOption?.value;
                    const hasMany = getFilteredValues(option, option.values).length > 5;

                    // Only show radio buttons if <=5 values
                    // But ALWAYS show custom dimension inputs if custom is selected
                    return hasMany ? isCustomSelected : true;
                  })() && (
                    <div className="space-y-4">
                      {(() => {
                        const customOption = option.values?.find(v => v.customSizes);
                        const isCustomSelected = configuration[option.id] === 'custom' ||
                                                configuration[option.id] === customOption?.value;
                        const hasMany = getFilteredValues(option, option.values).length > 5;

                        return (
                          <>
                            {/* Radio Group with all options including custom - only if <=5 values */}
                            {!hasMany && (
                              <RadioGroup
                                value={(configuration[option.id] as string) || ''}
                                onValueChange={(value) => handleOptionChange(option.id, value)}
                              >
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {/* Standard size options */}
                                {uniqueOptionValues(option.values
                                  .filter(v => !v.customSizes)
                                ).map((value, index) => {
                                    const isSelected = configuration[option.id] === value.value;
                                    return (
                                      <label
                                        key={`${option.id}-${value.id}-${value.value}-${index}`}
                                        htmlFor={`${option.id}-${value.id}`}
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          handleOptionChange(option.id, value.value);
                                        }}
                                        className={`relative rounded-lg border-2 p-4 transition-all cursor-pointer ${
                                          isSelected
                                            ? 'border-blue-600 bg-blue-50 shadow-md'
                                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                        }`}
                                      >
                                        <span className="text-sm font-medium">{formatOptionDisplayLabel(option, value.label, locale)}</span>
                                        {isSelected && (
                                          <div className="absolute top-3 right-3">
                                            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                              <Check className="h-4 w-4 text-white" />
                                            </div>
                                          </div>
                                        )}
                                      </label>
                                    );
                                  })}

                                {/* Custom size option */}
                                {customOption && (() => {
                                  const isSelected = isCustomSelected;
                                  return (
                                    <label
                                      htmlFor={`${option.id}-custom`}
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        handleOptionChange(option.id, customOption.value);
                                      }}
                                      className={`relative rounded-lg border-2 p-4 transition-all cursor-pointer ${
                                        isSelected
                                          ? 'border-blue-600 bg-blue-50 shadow-md'
                                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      <span className="text-sm font-medium">{formatOptionDisplayLabel(option, customOption.label, locale)}</span>
                                      {isSelected && (
                                        <div className="absolute top-3 right-3">
                                          <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                            <Check className="h-4 w-4 text-white" />
                                          </div>
                                        </div>
                                      )}
                                    </label>
                                  );
                                })()}
                              </div>
                              </RadioGroup>
                            )}

                            {/* Custom Dimensions Inputs (shown when custom is selected) */}
                            {customOption?.customSizes && isCustomSelected && (
                              (() => {
                                const customLimits = selectedCustomSizeLimits(option);
                                return (
                                  <div className="p-3 sm:p-4 border border-blue-600 bg-blue-50 rounded-lg">
                                    <p className="mb-3 text-xs leading-snug text-gray-600">{customSizeHintFor(option)}</p>
                                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                      <div>
                                        <Label className="text-xs text-gray-600 mb-1 block">
                                          {ui.width} (cm)
                                        </Label>
                                        <Input
                                          type="number"
                                          min={customLimits.minWidth}
                                          max={customLimits.maxWidth}
                                          step="0.1"
                                          value={configuration[`${option.id}_width`] || ''}
                                          onChange={(e) => handleOptionChange(`${option.id}_width`, parseCustomDimensionInput(e.target.value))}
                                          placeholder={`${formatCm(customLimits.minWidth || 0)}-${formatCm(customLimits.maxWidth || 0)}`}
                                          className="w-full"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                          {ui.min}: {formatCm(customLimits.minWidth || 0)}cm, {ui.max}: {formatCm(customLimits.maxWidth || 0)}cm
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-xs text-gray-600 mb-1 block">
                                          {ui.height} (cm)
                                        </Label>
                                        <Input
                                          type="number"
                                          min={customLimits.minHeight}
                                          max={customLimits.maxHeight}
                                          step="0.1"
                                          value={configuration[`${option.id}_height`] || ''}
                                          onChange={(e) => handleOptionChange(`${option.id}_height`, parseCustomDimensionInput(e.target.value))}
                                          placeholder={`${formatCm(customLimits.minHeight || 0)}-${formatCm(customLimits.maxHeight || 0)}`}
                                          className="w-full"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                          {ui.min}: {formatCm(customLimits.minHeight || 0)}cm, {ui.max}: {formatCm(customLimits.maxHeight || 0)}cm
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  </div>
                  )}
                  {option.id === 'custom_shape' && usesCustomCornerRadius(configuration) && (
                    <CornerRadiusField
                      value={configuration[CORNER_RADIUS_OPTION_ID]}
                      sizeMm={cornerRadiusSizeMm(configuration)}
                      locale={locale}
                      onCommit={(radiusMm) => setConfiguration((previous) => ({ ...previous, [CORNER_RADIUS_OPTION_ID]: radiusMm }))}
                    />
                  )}
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                });
              })()}
            </div>

            {/* Quantity & Urgency Selector.
                Niet bij de Vinyl Lettering Designer: daar kiest de klant het aantal in
                de designer, en een tweede aantalkeuze (de prijstabel) zou een andere
                configuratie prijzen dan de designer toont. */}
            {!designerOwnsOrder && (
            <div className="space-y-6">
              {/* Price and delivery table */}
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="text-sm font-medium">{ui.priceAndDelivery}</Label>
                  {isDeliveryRowsLoading && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" aria-hidden="true" />
                      {ui.pricesLoading}
                    </span>
                  )}
                </div>
                {providerType === 'PRINTCOM' && (
                  <p className="text-xs text-gray-600">
                    {({ nl: 'Verwachte bezorging voor', en: 'Expected delivery for', de: 'Voraussichtliche Lieferung für', fr: 'Livraison prévue pour' } as Record<string, string>)[locale.split('-')[0]] || 'Expected delivery for'} {deliveryDateQuantity} {ui.pieces}.
                    {' '}{({ nl: 'Inclusief één werkdag om je bestelling te verwerken.', en: 'Includes one working day to process your order.', de: 'Einschließlich eines Werktags zur Bearbeitung Ihrer Bestellung.', fr: 'Avec un jour ouvré pour traiter votre commande.' } as Record<string, string>)[locale.split('-')[0]] || 'Includes one working day to process your order.'}
                    {' '}{({ nl: 'Bezorgland', en: 'Delivery country', de: 'Lieferland', fr: 'Pays de livraison' } as Record<string, string>)[locale.split('-')[0]] || 'Delivery country'}: {deliveryCountryName}.
                  </p>
                )}
                {visibleDeliveryColumns.length === 1 ? (
                  // Tijdens herberekenen blijven de vorige prijzen zichtbaar maar
                  // gedimd, zodat verouderde bedragen niet als actueel ogen.
                  <div className={`rounded-lg border border-gray-200 bg-white p-3 transition-opacity ${isDeliveryRowsLoading && !hasVisibleDeliveryPrices && deliveryQuantityRows.length > 0 ? 'pointer-events-none opacity-45' : ''}`}>
                    <div className="mb-2 flex items-center justify-between gap-2 text-xs font-semibold text-gray-600">
                      <span>{ui.quantity}</span>
                      <span className="text-right">{visibleDeliveryColumns[0].businessDays}
                        <span className="block text-[11px] font-normal text-gray-500">
                          {providerType === 'PRINTCOM'
                            ? deliveryTextFor(visibleDeliveryColumns[0].id)
                            : visibleDeliveryColumns[0].estimatedDate}
                        </span>
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4">
                      {deliveryQuantityRows.map((row) => {
                        const column = visibleDeliveryColumns[0];
                        const cell = row.cells[column.id];
                        const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
                        const providerQuantityOption = providerType === 'PRINTFUL'
                          ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
                          : null;
                        const productQuantityOption = providerQuantityOption || copiesOption;
                        const selectedQuantity = Number((productQuantityOption && configuration[productQuantityOption.id]) || quantity || 1);
                        const selected = selectedQuantity === row.quantity && selectedMatrixColumn === column.id;
                        return (
                          <button
                            key={`delivery-quantity-card-${row.quantity}`}
                            type="button"
                            disabled={!cell?.available}
                            onClick={() => {
                              if (!cell?.available) return;
                              selectDeliveryPrice(row.quantity, column.id, cell, productQuantityOption?.id);
                            }}
                            className={`min-h-20 rounded-lg border p-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 ${
                              selected
                                ? 'border-blue-600 bg-blue-50 shadow-sm ring-1 ring-blue-600'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            } ${!cell?.available ? 'cursor-not-allowed opacity-40' : ''}`}
                          >
                            <span className="block text-sm font-bold text-gray-950">{row.quantity}</span>
                            <span className="mt-1 block whitespace-nowrap text-sm font-semibold text-gray-900">
                              {cell?.available ? formatPriceForLocale(cell.totalInclVat, locale) : row.isCustom ? ui.unavailable : '-'}
                            </span>
                            {cell?.available && (
                              <span className="block whitespace-nowrap text-xs text-gray-500">
                                {formatPriceForLocale(cell.unitInclVat, locale)} {ui.unitSuffix}
                              </span>
                            )}
                            {row.isCustom && (
                              <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-blue-700">
                                {ui.custom}
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setCustomMatrixQuantities((current) => current.filter((quantity) => quantity !== row.quantity));
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setCustomMatrixQuantities((current) => current.filter((quantity) => quantity !== row.quantity));
                                    }
                                  }}
                                  className="rounded px-1 text-blue-600 hover:bg-blue-100"
                                  aria-label={uiText('removeQuantity', { quantity: row.quantity })}
                                >
                                  x
                                </span>
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {closedCustomQuantities === null && <form
                      className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const formData = new FormData(event.currentTarget);
                        addMatrixCustomQuantity(String(formData.get('matrixQuantity') || ''));
                      }}
                    >
                      <Input
                        ref={matrixCustomQuantityInputRef}
                        name="matrixQuantity"
                        type="number"
                        min={matrixQuantityBounds.minimum}
                        max={matrixQuantityBounds.maximum}
                        step={matrixQuantityBounds.step}
                        value={matrixCustomQuantity}
                        onChange={(event) => setMatrixCustomQuantity(event.target.value.replace(/[^\d]/g, ''))}
                        placeholder={ui.customQuantity}
                        // Disabled while the localized options are still loading: until then
                        // matrixQuantityBounds is derived from the raw, unmerged initial
                        // options and can report a far lower ceiling than the real one, so a
                        // valid quantity typed here could be rejected on the first try.
                        disabled={isLoadingOptions}
                        className={`h-9 w-32 text-sm ${matrixCustomQuantityError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addMatrixCustomQuantity(matrixCustomQuantityInputRef.current?.value || '');
                        }}
                        disabled={isLoadingOptions}
                        className="h-9"
                      >
                        {ui.add}
                      </Button>
                      <span className={`text-xs ${matrixCustomQuantityError ? 'font-medium text-red-600' : 'text-gray-500'}`}>
                        {isLoadingOptions
                          ? ui.optionsLoadingHint
                          : (matrixCustomQuantityError || (matrixQuantityBounds.maximum >= OPEN_QUANTITY_MAXIMUM
                            ? uiText('minPieces', { min: matrixQuantityBounds.minimum })
                            : uiText('minMaxPieces', { min: matrixQuantityBounds.minimum, max: matrixQuantityBounds.maximum })))}
                      </span>
                    </form>}
                    {renderQuantityNudge()}
                  </div>
                ) : (
                <>

                {/* Zelfde stale-dimming voor de volledige staffel-tabel. De
                    verloop-overlay maakt zichtbaar dat de lijst doorloopt; de
                    harde afkap op 420px sneed de laatste rij dwars door de
                    cijfers en las als een renderfout.
                    De tabel staat bewust ook op mobiel: gestapelde kaarten
                    maakten er een langgerekte, onoverzichtelijke lijst van.
                    Wel een rand-verloop rechts, want op 390px valt ongeveer de
                    helft van de levertijdkolommen buiten beeld achter een
                    scrollbar van 2px, en dat is anders onvindbaar. */}
                <div className="relative">
                <div className={`subtle-scrollbar max-h-[420px] overflow-auto rounded-lg border border-gray-200 bg-white transition-opacity ${isDeliveryRowsLoading && !hasVisibleDeliveryPrices && deliveryQuantityRows.length > 0 ? 'pointer-events-none opacity-45' : ''}`}>
                  <table className="min-w-full border-collapse text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="sticky left-0 top-0 z-20 min-w-20 border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-600">
                          {ui.quantity}
                        </th>
                        {visibleDeliveryColumns.map((column) => (
                          <th key={`delivery-column-${column.id}`} className="sticky top-0 z-10 min-w-28 border-b border-gray-200 bg-gray-50 px-2 py-2 text-center">
                            <span className="flex items-center justify-center gap-0.5 text-xs font-semibold text-gray-900 sm:text-sm">
                              {column.businessDays}
                              {(() => {
                                // Toelichting van de leveringswaarde (bijv. ander
                                // materiaal en kussensloopmaat bij snel), stil in de kop.
                                const override = findDeliveryOverrideColumn(deliveryOverrideModel, column.id);
                                if (!override?.description) return null;
                                const infoLabel = ({ nl: 'Meer informatie', en: 'More information', de: 'Weitere Informationen', fr: 'Plus d’informations' } as Record<string, string>)[locale.split('-')[0]] || 'More information';
                                return (
                                  <button
                                    type="button"
                                    aria-label={`${infoLabel}: ${override.label}`}
                                    aria-haspopup="dialog"
                                    className="relative inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-gray-400 before:absolute before:-inset-2.5 before:content-[''] transition hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setOptionInfoModal({ title: override.label, description: override.description });
                                    }}
                                  >
                                    <Info aria-hidden="true" className="h-3.5 w-3.5" />
                                  </button>
                                );
                              })()}
                            </span>
                            <span className="block text-[11px] font-normal text-gray-500">
                              {providerType === 'PRINTCOM'
                                ? selectedDeliveryRow?.cells[column.id]?.available
                                  ? deliveryTextFor(column.id)
                                  : ui.unavailable
                                : column.estimatedDate}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryQuantityRows.map((row) => (
                        <tr key={`delivery-quantity-${row.quantity}`} className={`border-b border-gray-100 last:border-b-0 ${row.isCustom ? 'bg-blue-50/45' : ''}`}>
                          <th className={`sticky left-0 z-10 border-r border-gray-200 px-2 py-2 text-left font-semibold text-gray-900 ${row.isCustom ? 'bg-blue-50' : 'bg-white'}`}>
                            <span className="block">{row.quantity}</span>
                            {row.isCustom && (
                              <span className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-blue-700">
                                {ui.custom}
                                <button
                                  type="button"
                                  onClick={() => setCustomMatrixQuantities((current) => current.filter((quantity) => quantity !== row.quantity))}
                                  className="rounded px-1 text-blue-600 hover:bg-blue-100"
                                  aria-label={uiText('removeQuantity', { quantity: row.quantity })}
                                >
                                  x
                                </button>
                              </span>
                            )}
                          </th>
                          {visibleDeliveryColumns.map((column) => {
                            const cell = row.cells[column.id];
                            const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
                            const providerQuantityOption = providerType === 'PRINTFUL'
                              ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
                              : null;
                            const productQuantityOption = providerQuantityOption || copiesOption;
                            const selectedQuantity = Number((productQuantityOption && configuration[productQuantityOption.id]) || quantity || 1);
                            const selected = selectedQuantity === row.quantity && selectedMatrixColumn === column.id;
                            return (
                              <td key={`${row.quantity}-${column.id}`} className="p-1 align-middle">
                                <button
                                  type="button"
                                  disabled={!cell?.available}
                                  onClick={() => {
                                    if (!cell?.available) return;
                                    selectDeliveryPrice(row.quantity, column.id, cell, productQuantityOption?.id);
                                  }}
                                  className={`min-h-11 w-full rounded-md border px-2 py-1.5 text-center leading-tight transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 ${
                                    selected
                                      ? 'border-blue-600 bg-blue-50 shadow-sm ring-1 ring-blue-600'
                                      : 'border-transparent hover:border-gray-300 hover:bg-gray-50'
                                  } ${!cell?.available ? 'cursor-not-allowed opacity-40' : ''}`}
                                >
                                  <span className="block whitespace-nowrap text-xs font-semibold text-gray-900 sm:text-sm">
                                    {cell?.available ? formatPriceForLocale(cell.totalInclVat, locale) : row.isCustom ? ui.unavailable : '-'}
                                  </span>
                                  {cell?.available && (
                                    <span className="block whitespace-nowrap text-[11px] text-gray-500">
                                      {formatPriceForLocale(cell.unitInclVat, locale)} {ui.unitSuffix}
                                    </span>
                                  )}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {closedCustomQuantities === null && <tr className="border-t border-gray-200 bg-gray-50/70">
                        <th className="sticky left-0 z-10 border-r border-gray-200 bg-gray-50 px-2 py-2 text-left" colSpan={Math.min(2, Math.max(1, visibleDeliveryColumns.length + 1))}>
                          <form
                            className="flex items-center gap-1.5"
                            onSubmit={(event) => {
                              event.preventDefault();
                              const formData = new FormData(event.currentTarget);
                              addMatrixCustomQuantity(String(formData.get('matrixQuantity') || ''));
                            }}
                          >
                            <Input
                              ref={matrixCustomQuantityInputRef}
                              name="matrixQuantity"
                              type="number"
                              min={matrixQuantityBounds.minimum}
                              max={matrixQuantityBounds.maximum}
                              value={matrixCustomQuantity}
                              onChange={(event) => setMatrixCustomQuantity(event.target.value.replace(/[^\d]/g, ''))}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  return;
                                }
                              }}
                              placeholder={ui.custom}
                              // Disabled while the localized options are still loading: until then
                              // matrixQuantityBounds is derived from the raw, unmerged initial
                              // options and can report a far lower ceiling than the real one, so a
                              // valid quantity typed here could be rejected on the first try.
                              disabled={isLoadingOptions}
                              className={`h-8 w-20 text-xs ${matrixCustomQuantityError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                addMatrixCustomQuantity(matrixCustomQuantityInputRef.current?.value || '');
                              }}
                              disabled={isLoadingOptions}
                              className="h-8 px-2 text-xs"
                            >
                              {ui.add}
                            </Button>
                          </form>
                        </th>
                        <td className="px-2 py-2 text-xs" colSpan={Math.max(1, visibleDeliveryColumns.length - 1)}>
                          {isLoadingOptions ? (
                            <span className="text-gray-500">{ui.optionsLoadingHint}</span>
                          ) : matrixCustomQuantityError ? (
                            <span className="font-medium text-red-600">{matrixCustomQuantityError}</span>
                          ) : pendingMatrixQuantityNumber ? (
                            <span className="text-blue-700">{ui.addQuantityHint}</span>
                          ) : customMatrixQuantities.length > 0 ? (
                            <span className="text-gray-500">{uiText('customRows', { quantities: customMatrixQuantities.join(', ') })}</span>
                          ) : (
                            <span className="text-gray-500">
                              {uiText('enterCustomQuantityHint', { min: matrixQuantityBounds.minimum, max: matrixQuantityBounds.maximum })}
                            </span>
                          )}
                        </td>
                      </tr>}
                      {!deliveryQuantityRows.length && (
                        <tr>
                          <td className="px-3 py-6 text-center" colSpan={Math.max(1, visibleDeliveryColumns.length + 1)}>
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-2">{ui.deliveryPriceLoading}</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pointer-events-none absolute inset-x-px bottom-px h-8 rounded-b-lg bg-gradient-to-t from-white via-white/80 to-transparent" />
                <div className="pointer-events-none absolute inset-y-px right-px w-10 rounded-r-lg bg-gradient-to-l from-white via-white/70 to-transparent sm:hidden" />
                </div>
                {renderQuantityNudge()}
                </>
                )}
              </div>

            </div>
            )}

            {linkedSignPrint && !readOnly && (
              <LinkedSignPrints
                link={linkedSignPrint}
                context={linkedPrintContext}
                boardCount={displayQuantity}
                locale={locale}
                rows={linkedPrintRows}
                onChange={setLinkedPrintRows}
                renderConfigurator={(props) => <ProductConfigurator key={props.draftScope} {...props} />}
              />
            )}
            {!linkedSignPrint && linkedPrintRows.length > 0 && (
              <div role="alert" className="mt-4 rounded-lg border p-4">
                <p>{getLinkedPrintCopy(locale).stale}</p>
                <Button type="button" variant="outline" onClick={() => setLinkedPrintRows([])}>{getLinkedPrintCopy(locale).remove}</Button>
              </div>
            )}
            {/* Product Accessories — linked products from Print.com, max 4 shown */}
            {!accessoriesLoading && accessories.length > 0 && (
              <div className="border-t pt-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-yellow-500" />
                  <h3 className="text-lg font-semibold">{t('recommendedExtras')}</h3>
                </div>
                <div className="space-y-3">
                  {accessories.slice(0, 4).map((accessory) => {
                    const isSelected = selectedAccessories.has(accessory.id);
                    const qty = accessoryQuantities[accessory.id] || 1;
                    const accessoryPrice = accessory.basePrice
                      ? calculateStorefrontPriceWithVAT(accessory.basePrice, vatCountryCode)
                      : 0;
                    const lineTotal = accessoryPrice * qty;

                    return (
                      <div
                        key={accessory.id}
                        className={`flex flex-wrap items-start gap-3 rounded-lg border-2 p-3 transition-all sm:flex-nowrap sm:items-center sm:gap-4 ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {/* Toggle selection */}
                        <button
                          type="button"
                          onClick={() => {
                            const next = new Set(selectedAccessories);
                            if (isSelected) {
                              next.delete(accessory.id);
                              setAccessoryQuantities(p => { const n = {...p}; delete n[accessory.id]; return n; });
                              clearAccessoryUpload(accessory.id);
                            } else {
                              next.add(accessory.id);
                              setAccessoryQuantities(p => ({ ...p, [accessory.id]: 1 }));
                            }
                            setSelectedAccessories(next);
                          }}
                          className={`flex h-8 w-8 flex-none items-center justify-center rounded border-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                            isSelected ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {isSelected && <Check className="h-4 w-4" />}
                        </button>

                        {/* Image */}
                        {accessory.image ? (
                          <img src={accessory.image} alt={accessory.name} className="h-12 w-12 flex-none rounded-lg border border-gray-200 object-cover" />
                        ) : (
                          <div className="h-12 w-12 flex-none rounded-lg bg-gray-100 flex items-center justify-center"><ShoppingCart className="h-5 w-5 text-gray-400" /></div>
                        )}

                        {/* Name + price */}
                        <div className="min-w-0 flex-1 basis-[calc(100%-6.5rem)] sm:basis-0">
                          {accessory.slug && hasIntlContext ? (
                            <Link href={productPath(locale.split('-')[0], accessory.slug)} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate block">{accessory.name}</Link>
                          ) : (
                            <p className="text-sm font-medium text-gray-900 truncate">{accessory.name}</p>
                          )}
                          {accessory.description && <p className="text-xs text-gray-500 line-clamp-1">{accessory.description}</p>}
                          <div className="flex items-center gap-2 mt-0.5">
                            {accessoryPrice > 0 && <span className="text-sm font-bold text-blue-600">{formatPriceForLocale(accessoryPrice, locale)}</span>}
                            <span className="text-xs text-gray-400">{perItemLabel}</span>
                          </div>
                        </div>

                        {/* Quantity controls */}
                        {isSelected && (
                          <div className="flex w-full min-w-0 items-center justify-end gap-1 pl-16 sm:w-auto sm:flex-none sm:pl-0">
                            <button type="button" className="h-9 w-9 rounded border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 sm:h-8 sm:w-8"
                              onClick={() => setAccessoryQuantities(p => ({ ...p, [accessory.id]: Math.max(1, (p[accessory.id] || 1) - 1) }))}
                            >−</button>
                            <span className="w-8 text-center text-sm font-medium">{qty}</span>
                            <button type="button" className="h-9 w-9 rounded border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 sm:h-8 sm:w-8"
                              onClick={() => setAccessoryQuantities(p => ({ ...p, [accessory.id]: (p[accessory.id] || 1) + 1 }))}
                            >+</button>
                          </div>
                        )}

                        {/* Line total */}
                        {isSelected && lineTotal > 0 && (
                          <span className="ml-auto min-w-0 flex-none text-right text-sm font-bold text-blue-700 sm:w-16">{formatPriceForLocale(lineTotal, locale)}</span>
                        )}

                        {/* Info button */}
                        {accessory.image && !isSelected && (
                          <button type="button" aria-label="Bekijk" className="flex-none h-7 w-7 rounded-full bg-white/90 text-gray-400 shadow-sm ring-1 ring-gray-200 hover:bg-blue-50 hover:text-blue-700 flex items-center justify-center"
                            onClick={() => setOptionInfoModal({ title: accessory.name, description: accessory.description || '', image: accessory.image })}>
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {/* File upload for printable accessories */}
                        {isSelected && accessory.isPrintable && (() => {
                          const upload = accessoryUploads[accessory.id];
                          const busy = upload?.status === 'uploading';
                          return (
                            <div className="mt-2 flex w-full min-w-0 flex-wrap items-center gap-2 rounded border border-dashed border-blue-300 bg-blue-50/50 px-3 py-2 sm:ml-10">
                              <Upload className="h-4 w-4 text-blue-600 flex-none" />
                              <span className="text-xs text-blue-700 flex-1">{accessoryCopy.needed}</span>
                              <label className={`rounded bg-blue-600 px-2.5 py-1 text-xs font-medium text-white transition ${busy ? 'cursor-wait opacity-70' : 'cursor-pointer hover:bg-blue-700'}`}>
                                {busy ? accessoryCopy.uploading : upload ? upload.fileName.substring(0, 20) : accessoryCopy.choose}
                                <input
                                  type="file"
                                  accept={ARTWORK_ACCEPT_ATTRIBUTE}
                                  className="hidden"
                                  disabled={busy}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    // Leegmaken, zodat hetzelfde bestand na een fout opnieuw te kiezen is.
                                    e.target.value = '';
                                    if (file) void selectAccessoryFile(accessory.id, file);
                                  }}
                                />
                              </label>
                              {upload && (
                                <button type="button" aria-label={accessoryCopy.remove} className="text-xs text-red-500 hover:text-red-700"
                                  onClick={() => clearAccessoryUpload(accessory.id)}
                                >×</button>
                              )}
                              {upload?.status === 'error' && (
                                <p role="alert" className="w-full text-xs text-red-600">{upload.error}</p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {accessoriesLoading && providerProductId && (
              <div className="border-t pt-6 mt-6">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                  <span className="text-sm text-gray-400">
                    {locale === 'de' ? 'Zubehör wird geladen...' : locale === 'fr' ? 'Chargement des accessoires...' : locale === 'en' ? 'Loading accessories...' : 'Accessoires laden...'}
                  </span>
                </div>
              </div>
            )}

            {/* Delivery Promise - Hidden (handled via urgency option instead) */}
            {/*
              Note: deliveryPromise is a Print.com feature for delivery guarantees.
              We keep it at default (0 = standard) but don't show it to avoid confusion
              with the urgency option which is more user-friendly.
            */}
          </div>
        </Card>
        </div>
      )}

      {/* Upload Step */}
      {isPrintable && currentStep === 'upload' && (
        <div ref={uploadStepRef} className="scroll-mt-32">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">{ui.uploadDesign}</h3>

          {/* Design service promo */}
          {hasIntlContext && (
          <Link href="/ontwerp-service" className="mb-4 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3 transition hover:bg-green-100 hover:border-green-300">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-green-200 text-green-700 flex-none">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-green-900">{ui.needDesignHelp}</p>
              <p className="text-xs text-green-700 truncate">{ui.designersFree}</p>
            </div>
            <span className="text-xs font-bold text-green-700 underline flex-none">{ui.requestDesign}</span>
          </Link>
          )}

          <div className="text-sm text-gray-600 mb-4">
            {fileUploadIntro || ui.uploadIntro}
          </div>
          {templateDownload && (
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
              <p className="mb-2 text-xs leading-snug text-gray-700">{templateDownloadPrompt(locale)}</p>
              <TemplateDownloadButton
                compact
                productSlug={productSlug}
                productName={productName}
                variants={templateDownload.variants}
              />
            </div>
          )}
          <div className="space-y-6">
            {requiredDesignCount > 1 && !isPageUploadProduct && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                {ui.twoDesignsRequired}
              </div>
            )}
            {designSlotCount === 2 && !isPageUploadProduct && (
              <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={useSameDesignBothSides}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setUseSameDesignBothSides(checked);
                    if (checked) {
                      setUploadedFiles((prev) => prev[0] ? [prev[0]] : []);
                    }
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                />
                <span>
                  <span className="block font-medium text-gray-900">Voor- en achterkant hetzelfde ontwerp</span>
                  <span className="block text-gray-600">Vink dit aan als dezelfde print op beide zijdes mag komen.</span>
                </span>
              </label>
            )}
            {Array.from({ length: requiredDesignCount }).map((_, index) => {
              const slotFile = uploadedFiles[index];
              const slotDefinition = fileUploadSlots[index];
              const slotLabel = useSameDesignBothSides && designSlotCount === 2
                ? 'Voor- en achterkant'
                : slotDefinition?.label || (index === 0 ? 'Voorkant' : 'Achterkant');
              const slotHelp = slotDefinition?.description;
              const slotEditorSurface = slotDefinition?.editorSurface || 'single';
              const slotPageCount = slotEditorSurface === 'inside-pages'
                ? Math.max(1, (Number(configuration.pages || 1) || 1) - 4)
                : 1;
              const slotDesignPlacements = fileUploadSlots.length > 0 && slotDefinition
                ? [{
                    key: slotDefinition?.key || slotDefinition?.role || `side-${index + 1}`,
                    label: slotLabel,
                    guideTemplateUrl: slotDefinition?.guideTemplateUrl || selectedGuideTemplateUrl,
                  }]
                : selectedDesignPlacements;

              return (
                <div key={`upload-slot-${index}`} className={designSlotCount > 1 ? 'rounded-lg border border-gray-200 p-4' : ''}>
                  {designSlotCount > 1 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Ontwerp {index + 1}: {slotLabel}
                      </h4>
                      {slotHelp && <p className="mt-1 text-xs text-gray-600">{slotHelp}</p>}
                    </div>
                  )}
                  <FileUploader
                    draftScope={draftScope ? `${draftScope}:slot:${slotDefinition?.key || slotDefinition?.role || index}` : index > 0 ? `product:${productId}:slot:${slotDefinition?.key || slotDefinition?.role || index}` : undefined}
                    key={`${fileUploaderKey}-${index}-${requiredDesignCount}`}
                    productId={productId}
                    providerProductId={providerProductId}
                    productSlug={productSlug}
                    providerType={providerType}
                    // Ongeldige vrije maat (bijv. mm in een cm-veld): geen editor, anders
                    // ontstaat een onbruikbaar bestand van 12 dpi (Sentry PRINTEN-EU-4E/4F).
                    sizeValue={dimensionValidation.valid ? selectedSizeValue : undefined}
                    category={category}
                    productName={productName}
                    uploadMode={isPageUploadProduct ? 'page-pdf' : 'standard'}
                    slotLabel={slotLabel}
                    canvasEdgeType={canvasEdgeType}
                    isCanvasProduct={isCanvasProduct}
                    editorUnavailable={editorUnavailable}
                    aiImageToolsEnabled={aiImageToolsEnabled}
                    onCanvasEdgeTypeChange={setCanvasEdgeType}
                    initialFileUrl={slotFile?.url || (index === 0 ? initialFileUrl : undefined)}
                    initialFileName={slotFile?.fileName || (index === 0 && initialFileUrl ? initialFileUrl.split('/').pop() : undefined)}
                    initialFileSize={slotFile?.fileSize}
                    initialDesignData={slotFile?.designData || (index === 0 ? (accountDesignData || initialDesignData) : undefined)}
                    locale={locale}
                    finishingOptions={editorFinishingOptions}
                    guideTemplateUrl={selectedGuideTemplateUrl}
                    productionSheetProfile={productionSheetProfile}
                    designTemplateCount={designTemplateCount}
                    initialDesignTemplateSlug={index === 0 ? initialDesignTemplateSlug : undefined}
                    editorSurface={slotEditorSurface}
                    pageCount={slotPageCount}
                    designPlacements={slotDesignPlacements}
                    getResellerOptions={designSlotCount === 1 && !isPageUploadProduct ? getResellerCartOptions : undefined}
                    sizeOptions={fileCheckSizeOptions}
                    onSelectSize={fileCheckSizeOptions.length > 0 ? (value) => { void handleOptionChange('size', value); } : undefined}
                    onDesignSaved={scrollToAddToCart}
                    onFileUploaded={(file) => {
                      const fileWithSlot = {
                        ...file,
                        role: slotDefinition?.role ?? (slotFile?.url === file.url ? slotFile?.role : undefined),
                        printPages: slotDefinition?.pages ?? (slotFile?.url === file.url ? slotFile?.printPages : undefined),
                      };
                      setUploadedFiles((prev) => {
                        const next = [...prev];
                        next[index] = fileWithSlot;
                        setUploadedFile(next[0] || null);
                        return next;
                      });
                      if (index === 0) setUploadedFile(fileWithSlot);
                      // Stay on upload step to show preview
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Card>
        </div>
      )}

      {/* Vinyl-ontwerptool, ingebed.
          Staat vlak boven het prijsblok en niet op een aparte pagina, omdat het
          ontwerp de prijs bepaalt: maat, aantal snijcontouren en folie samen.
          Zo ziet de klant het bedrag meebewegen terwijl hij typt. */}
      {vinylDesigner && vinylDesignerMode === 'unavailable' && (
        <div role="alert" className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="vinyl-designer-unavailable">
          <p className="font-semibold">{hostMessages(locale).unavailableTitle}</p>
          {vinylDesignerLoadFailed && <p className="mt-1">{hostMessages(locale).loadFailed}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {vinylDesignerLoadFailed && (
              <Button type="button" size="sm" onClick={() => setVinylDesignerLoadFailed(false)}>
                {hostMessages(locale).retry}
              </Button>
            )}
            <Button asChild type="button" size="sm" variant="outline">
              <a href={localizedPath(locale, '/contact')}>{hostMessages(locale).contactCta}</a>
            </Button>
          </div>
        </div>
      )}
      {vinylDesigner && vinylDesignerMode === 'designer' && (
        <div className="mb-4">
          <VinylDesignerInline
            onLoadError={() => setVinylDesignerLoadFailed(true)}
            productId={productId}
            productSlug={vinylDesigner.productSlug}
            productName={productName}
            locale={locale}
            materials={vinylDesigner.materials}
            defaultMaterial={vinylDesigner.defaultMaterial}
            settings={vinylDesigner.settings}
            allowedCopies={vinylDesigner.allowedCopies}
            contourLimit={vinylDesigner.contourLimit ?? null}
            contourOptionId={vinylDesigner.hasContourOption ? vinylDesigner.settings.contourOptionId : null}
            hasCopiesOption={vinylDesigner.hasCopiesOption ?? true}
            getBaseConfiguration={() => configurationRef.current}
            getCountryCode={() => countryCodeRef.current}
            fetchImpl={sharedPriceFetch}
            onMaterialChange={(value) => {
              // Direct in de configuratie, in dezelfde render als de maat (onDesignChange
              // komt vlak ervoor). Niet via handleOptionChange: die kan asynchroon
              // controleren of andere opties meebewegen, en dan prijst de configurator
              // iets anders dan de designer. De designer toont alleen bestelbare
              // kleuren (Ruling P5).
              setConfiguration((vorige) => withDesignMaterial(vorige, vinylDesigner.settings.materialOptionId, value));
            }}
            onDesignChange={(detail) => {
              // Maat (cm), aantal (configuration.copies) en contouren (alleen met een echte
              // contouroptie); dezelfde samenvoeging die de host prijst (designer-configuration.ts).
              setDesignerDesignHash(detail?.designHash ?? null);
              if (!detail) return;
              setConfiguration((vorige) => withDesignChange(vorige, detail, {
                hasCopiesOption: vinylDesigner.hasCopiesOption ?? true,
                contourOptionId: vinylDesigner.hasContourOption ? vinylDesigner.settings.contourOptionId : null,
              }));
            }}
            onDesignReady={(result) => {
              // Pas hier bestaat er een bestand; de bestelroute eist een drukbestand.
              const bestand = {
                url: result.url,
                fileName: result.fileName,
                fileSize: result.fileSize,
                id: result.uploadId,
                uploadId: result.uploadId,
              };
              setUploadedFile(bestand as any);
              setUploadedFiles([bestand as any]);
              // Ruling P9: de regel zegt "binnenkant glas – gespiegeld snijbestand" (alleen voor dit bestand).
              designerRegisteredRef.current = { uploadId: result.uploadId, glassSide: result.glassSide };
            }}
            onRequestAddToCart={requestDesignerAddToCart}
          />
        </div>
      )}

      {/* Price Summary - Compact */}
      <Card className="p-3 bg-gray-50">
        <div className="space-y-2">
          {isPriceLoading || selectionCheckPending ? (
            <div className="text-center py-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rose-600 mx-auto"></div>
              <p className="text-xs text-gray-600 mt-1">{t('calculatingPrice')}</p>
            </div>
          ) : priceRateLimit && !isValidConfiguration ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center" role="status" data-testid="price-rate-limited">
              <Info className="h-6 w-6 text-amber-600 mx-auto mb-1" />
              <p className="text-xs font-medium text-amber-900">{ui.priceRateLimitedTitle}</p>
              <p className="mt-1 text-xs text-amber-800">
                {priceRateLimit.longWait
                  ? ui.priceRateLimitedLongText
                  : ui.priceRateLimitedText.replace('{seconds}', String(priceRateLimit.seconds))}
              </p>
            </div>
          ) : configurationError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
              <Info className="h-6 w-6 text-red-500 mx-auto mb-1" />
              <p className="text-xs font-medium text-red-800">
                {isTemporaryPricingError ? ui.priceUnavailable : ui.invalidCombination}
              </p>
              <p className="mt-1 text-xs text-red-700">
                {quantityMinimum
                  ? ui.quantityMinimumText.replaceAll('{count}', String(quantityMinimum))
                  : quantityMaximum
                  ? ui.quantityMaximumText.replaceAll('{count}', String(quantityMaximum))
                  : configurationError}
              </p>
              {quantityLimit && copiesOptionForMinimum ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 h-8 text-xs"
                  data-testid="apply-quantity-minimum"
                  onClick={() => setConfiguration((previous) => ({
                    ...previous,
                    [copiesOptionForMinimum.id]: typeof previous[copiesOptionForMinimum.id] === 'number'
                      ? quantityLimit
                      : String(quantityLimit),
                  }))}
                >
                  {(quantityMinimum ? ui.quantityMinimumAction : ui.quantityMaximumAction).replaceAll('{count}', String(quantityLimit))}
                </Button>
              ) : null}
            </div>
          ) : pricing.unitPrice === 0 && vinylDesigner ? (
            /* De ontwerptool staat hierboven. Zolang er geen maat uit komt is er
               geen prijs, en dat is juist: een halve maat mag nooit geprijsd
               worden. "Selecteer alle opties" wees hier naar een veld dat op
               deze pagina niet bestaat. */
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
              <PenTool className="mx-auto mb-1 h-5 w-5 text-rose-600" />
              <p className="text-xs leading-5 text-slate-700">{ui.designerRequiredText}</p>
            </div>
          ) : pricing.unitPrice === 0 && quoteIsOnlyOption ? (
            /* Offerteproduct (bijvoorbeeld LED-neon): er is bewust geen prijs.
               "Selecteer alle opties" klopte hier niet, de klant heeft alles
               ingevuld wat er in te vullen valt. */
            <div className="text-center py-2.5 px-2 rounded-lg bg-amber-50 border border-amber-100">
              <Info className="h-5 w-5 text-amber-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-amber-950">{ui.priceOnRequest}</p>
            </div>
          ) : pricing.unitPrice === 0 ? (
            <div className="text-center py-2.5 px-2 rounded-lg bg-rose-50/70 border border-rose-100">
              <Info className="h-5 w-5 text-rose-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-rose-950">
                {missingRequiredOptions.length > 0
                  ? `${ui.selectAllOptions}: ${missingRequiredOptions.slice(0, 3).join(', ')}${missingRequiredOptions.length > 3 ? '...' : ''}`
                  : ui.selectAllOptions}
              </p>
            </div>
          ) : (
            <>
              {configurationAdjustmentNote && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
                  {configurationAdjustmentNote}
                </div>
              )}
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{t('pricePerUnit')}</span>
                <span className="font-medium">{formatPriceForLocale(pricing.unitPrice, locale)}</span>
              </div>
              {/* De kaart adverteert de goedkoopste uitvoering; als de huidige
                  configuratie duurder is, leg dat verschil hier eerlijk uit. */}
              {typeof fromUnitPriceInclVat === 'number' && fromUnitPriceInclVat > 0 && pricing.unitPrice > fromUnitPriceInclVat * 1.05 && (
                <p className="text-[11px] leading-4 text-gray-500">
                  {ui.fromPriceNote
                    .replace('{price}', formatPriceForLocale(fromUnitPriceInclVat, locale))
                    .replace('{unit}', ui.unitSuffix)}
                </p>
              )}
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{t('quantity')}</span>
                <span className="font-medium">
                  {(() => {
                    // If there's a product-level quantity option, show that value instead of order quantity.
                    const copiesOption = localizedOptions.find(opt => opt.id === 'copies' || opt.name === 'copies');
                    const providerQuantityOption = providerType === 'PRINTFUL'
                      ? localizedOptions.find(opt => opt.id === 'quantity' || opt.name === 'quantity')
                      : null;
                    const productQuantityOption = providerQuantityOption || copiesOption;
                    if (productQuantityOption && configuration[productQuantityOption.id]) {
                      return `${configuration[productQuantityOption.id]}x`;
                    }
                    return `${quantity}x`;
                  })()}
                </span>
              </div>

              {providerType === 'PRINTFUL' && placementSurcharges.map((surcharge) => (
                <div key={surcharge.placement} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">{printfulPlacementLabel(surcharge.placement, locale)}</span>
                  <span className="font-medium">
                    +{formatPriceForLocale(calculatePriceWithVAT(surcharge.unitPrice, vatCountryCode), locale)} {t('perItem')}
                  </span>
                </div>
              ))}

              {pricing.discount > 0 && (
                <div className="flex items-center justify-between text-xs text-green-600">
              <span>{({ nl: 'Korting', en: 'Discount', de: 'Rabatt', fr: 'Remise' } as Record<string, string>)[locale.split('-')[0]] || 'Discount'} ({(pricing.discount * 100).toFixed(0)}%)</span>
              <span className="font-medium">-{formatPriceForLocale(pricing.discountAmount, locale)}</span>
            </div>
              )}

              <div className="border-t pt-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{t('total')}</span>
                  <span className="text-xl font-black text-rose-700">
                    {formatPriceForLocale(pricing.total, locale)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 mt-0.5">
                  <span>{shippingIncluded ? t('inclDeliveryVAT') : exclShippingLabel(t('inclShippingVAT'))}</span>
                  {selectedProductionDays && <div className="flex items-center gap-1 font-medium text-emerald-700">
                    <Truck className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      {productionTimeLabel}: {selectedProductionDays}
                    </span>
                  </div>}
                </div>
              </div>

              {/* Payment method badges */}
              <div className="border-t pt-3 mt-2">
                <div className="flex items-center justify-center gap-2">
                  <img src="/logos/payments/klarna.svg" alt="Klarna" className="h-5 w-auto" />
                  <span className="text-xs text-gray-600">{ui.payInParts}</span>
                </div>
              </div>

              {currentStep === 'configure' && (
                <>
                  {!quoteIsOnlyOption && primaryAction === 'go-to-design' && (
                    <Button
                      data-step-cta
                      className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-md shadow-rose-100 transition"
                      size="lg"
                      disabled={!isConfigurationComplete() || !isPriceReady}
                      onClick={() => changeStep('upload')}
                    >
                      {/* Deze knop opent de ontwerpstap; daar staat daarna direct
                          "Toevoegen aan winkelwagen". Hij mag hier niet zo heten,
                          anders denkt de klant dat het artikel al in de
                          winkelwagen zit. */}
                      {!isValidConfiguration ? ui.adjustSelection : flowCopy.goToDesign}
                    </Button>
                  )}
                  {!quoteIsOnlyOption && primaryAction === 'add-to-cart' && (
                    // Zonder drukbestand valt er niets te ontwerpen: meteen toevoegen,
                    // geen aparte overzichtsstap met dezelfde prijs nog eens.
                    <>
                      <Button
                        data-step-cta
                        data-add-to-cart-cta
                        className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-md shadow-rose-100 transition"
                        size="lg"
                        disabled={!isConfigurationComplete() || !isPriceReady || !linkedPrintsReady || !isDeliverySpeedOrderable}
                        onClick={handleAddToCart}
                      >
                        {!isValidConfiguration ? ui.adjustSelection : onSave ? (
                          <>
                            <Check className="mr-2 h-5 w-5" />
                            {ui.saveChanges}
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="mr-2 h-5 w-5" />
                            {t('addToCart')}
                          </>
                        )}
                      </Button>
                      {!onSave && !readOnly && <PurchaseReassurance locale={locale} countryCode={countryCode} variant="product" />}
                    </>
                  )}
                  {canRequestQuote && !quoteIsOnlyOption && !quoteFormOpen && !quoteRequestSent && (
                    // Een regel tekst naast de bestelknop. Wie gewoon wil
                    // bestellen ziet er nauwelijks iets van; wie een offerte
                    // wil, vindt hem waar hij hem zoekt.
                    <button
                      type="button"
                      className="w-full text-center text-sm text-gray-500 underline underline-offset-2 hover:text-gray-900"
                      onClick={() => setQuoteFormOpen(true)}
                    >
                      {ui.quoteCta}
                    </button>
                  )}
                  {canRequestQuote && (quoteIsOnlyOption || quoteFormOpen || quoteRequestSent) && (
                    <div className={quoteIsOnlyOption ? 'rounded-lg border border-amber-200 bg-amber-50 p-4' : 'rounded-lg border border-gray-200 bg-gray-50 p-4'}>
                      <p className={quoteIsOnlyOption ? 'text-sm font-semibold text-amber-950' : 'text-sm font-semibold text-gray-900'}>
                        {quoteIsOnlyOption ? ui.quoteUnavailableTitle : ui.quoteRequestTitle}
                      </p>
                      <p className={quoteIsOnlyOption ? 'mt-1 text-sm text-amber-800' : 'mt-1 text-sm text-gray-600'}>
                        {quoteIsOnlyOption
                          ? ui.quoteUnavailableDescription
                          : ui.quoteRequestDescription}
                      </p>
                      {quoteRequestSent ? (
                        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800">
                          {quoteNumber
                            ? ui.quoteSentNumber.replace('{number}', quoteNumber)
                            : ui.quoteReceivedManual}
                        </div>
                      ) : (
                        <div className="mt-3 space-y-2">
                          <Input
                            value={quoteRequestForm.name}
                            onChange={(event) => setQuoteRequestForm((current) => ({ ...current, name: event.target.value }))}
                            placeholder={ui.quoteName}
                          />
                          <Input
                            value={quoteRequestForm.email}
                            onChange={(event) => setQuoteRequestForm((current) => ({ ...current, email: event.target.value }))}
                            placeholder={ui.quoteEmail}
                            type="email"
                          />
                          <Input
                            value={quoteRequestForm.phone}
                            onChange={(event) => setQuoteRequestForm((current) => ({ ...current, phone: event.target.value }))}
                            placeholder={ui.quotePhone}
                          />
                          <Input
                            value={quoteRequestForm.company}
                            onChange={(event) => setQuoteRequestForm((current) => ({ ...current, company: event.target.value }))}
                            placeholder={ui.quoteCompany}
                          />
                          <div>
                            <label className="text-xs text-gray-600">{ui.quoteDeliveryDate}</label>
                            <Input
                              type="date"
                              value={quoteRequestForm.deliveryDate}
                              onChange={(event) => setQuoteRequestForm((current) => ({ ...current, deliveryDate: event.target.value }))}
                            />
                          </div>
                          <Textarea
                            value={quoteRequestForm.message}
                            onChange={(event) => setQuoteRequestForm((current) => ({ ...current, message: event.target.value }))}
                            placeholder={ui.quoteMessage}
                            rows={3}
                          />
                          <Button
                            type="button"
                            className="w-full"
                            disabled={quoteRequestSubmitting || !isConfigurationComplete()}
                            onClick={submitProductQuoteRequest}
                          >
                            {quoteRequestSubmitting ? ui.quoteSending : ui.quoteSubmit}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {currentStep === 'upload' && (
            <div className="space-y-2">
              {primaryAction === 'add-to-cart' ? (
                <>
                  {/* Ontwerp klaar: hier meteen toevoegen. Vroeger kwam eerst nog
                      "Doorgaan naar bestellen" en een aparte stap met alleen
                      deze knop. */}
                  {linkedPrintRows.length > 0 && (
                    <div className="rounded-lg border bg-blue-50 p-3 text-sm">
                      <p>{getLinkedPrintCopy(locale).total}: {formatPriceForLocale(linkedPrintRows.reduce((sum, row) => sum + row.item.totalPrice, 0), locale)}</p>
                      <p className="mt-1 font-semibold">{t('total')}: {formatPriceForLocale(pricing.total + linkedPrintRows.reduce((sum, row) => sum + row.item.totalPrice, 0), locale)}</p>
                      {!linkedPrintsReady && <p role="alert" className="mt-2 text-amber-800">{getLinkedPrintCopy(locale).stale}</p>}
                    </div>
                  )}
                  <Button
                    data-step-cta
                    data-add-to-cart-cta
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-md shadow-rose-100 transition"
                    size="lg"
                    onClick={handleAddToCart}
                    disabled={!isConfigurationComplete() || !isPriceReady || !linkedPrintsReady || !isDeliverySpeedOrderable}
                  >
                    {onSave ? (
                      <>
                        <Check className="mr-2 h-5 w-5" />
                        {ui.saveChanges}
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 h-5 w-5" />
                        {t('addToCart')}
                      </>
                    )}
                  </Button>
                  {!onSave && !readOnly && <PurchaseReassurance locale={locale} countryCode={countryCode} variant="product" />}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      debugLog('🔄 Resetting FileUploader...');
                      setUploadedFile(null);
                      setUploadedFiles([]);
                      setFileUploaderKey(prev => prev + 1); // Force FileUploader to remount
                      debugLog('✅ FileUploader reset complete');
                    }}
                  >
                    {ui.uploadAnotherFile}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => changeStep('configure')}
                  >
                    {ui.backToConfiguration}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-center text-xs text-gray-600" data-awaiting-design>{flowCopy.awaitingDesign}</p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => changeStep('configure')}
                  >
                    {ui.backToConfiguration}
                  </Button>
                </>
              )}
            </div>
          )}

          {currentStep === 'checkout' && (
            <div ref={checkoutStepRef} className="space-y-2 scroll-mt-32">
              {linkedPrintRows.length > 0 && (
                <div className="rounded-lg border bg-blue-50 p-3 text-sm">
                  <p>{getLinkedPrintCopy(locale).total}: {formatPriceForLocale(linkedPrintRows.reduce((sum, row) => sum + row.item.totalPrice, 0), locale)}</p>
                  <p className="mt-1 font-semibold">{t('total')}: {formatPriceForLocale(pricing.total + linkedPrintRows.reduce((sum, row) => sum + row.item.totalPrice, 0), locale)}</p>
                  {!linkedPrintsReady && <p role="alert" className="mt-2 text-amber-800">{getLinkedPrintCopy(locale).stale}</p>}
                </div>
              )}
              <Button
                data-step-cta
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-md shadow-rose-100 transition"
                size="lg"
                onClick={handleAddToCart}
                disabled={!isConfigurationComplete() || !isPriceReady || !linkedPrintsReady || !isDeliverySpeedOrderable}
              >
                {onSave ? (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    {ui.saveChanges}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    {t('addToCart')}
                  </>
                )}
              </Button>
              {isPrintable && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => changeStep('upload')}
                >
                  {ui.backToUpload}
                </Button>
              )}
              {!isPrintable && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => changeStep('configure')}
                >
                  {ui.backToConfiguration}
                </Button>
              )}
              {!onSave && !readOnly && <PurchaseReassurance locale={locale} countryCode={countryCode} variant="product" />}
            </div>
          )}
        </div>
      </Card>

      {optionHoverPreview && (
        <div
          className="pointer-events-none fixed z-[1000] hidden h-28 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-2xl sm:block sm:h-32 sm:w-56"
          style={{ left: optionHoverPreview.left, top: optionHoverPreview.top }}
        >
          <img src={optionHoverPreview.image} alt="" className="h-full w-full rounded-md object-cover" />
        </div>
      )}

      <Dialog open={Boolean(optionInfoModal)} onOpenChange={(open) => !open && setOptionInfoModal(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          {optionInfoModal && (
            <>
              <DialogHeader>
                <DialogTitle>{optionInfoModal.title}</DialogTitle>
                <DialogDescription className="whitespace-pre-line font-sans leading-relaxed">{optionInfoModal.description}</DialogDescription>
              </DialogHeader>
              {(optionInfoModal.image || optionInfoModal.overviewImage || optionInfoModal.visualSvgContent) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {(optionInfoModal.image || optionInfoModal.visualSvgContent) && <div className="overflow-hidden rounded-lg border bg-white">
                  {optionInfoModal.visualSvgContent ? (
                    <div
                      className="flex aspect-square w-full items-center justify-center p-6 [&_svg]:h-full [&_svg]:w-full"
                      dangerouslySetInnerHTML={{ __html: optionInfoModal.visualSvgContent }}
                    />
                  ) : (
                    <img
                      src={optionInfoModal.image}
                      alt={optionInfoModal.title}
                      className="max-h-[60vh] w-full object-contain"
                    />
                  )}
                  </div>}
                  {optionInfoModal.overviewImage && <img src={optionInfoModal.overviewImage} alt={`${optionInfoModal.title} overzicht`} className="max-h-[60vh] w-full rounded-lg border object-contain" />}
                </div>
              )}
              {(() => {
                const sample = Object.values(optionVisuals).find((visual) => visual.samplePath && (
                  (optionInfoModal.image && visual.imageUrl === optionInfoModal.image) ||
                  (optionInfoModal.overviewImage && visual.overviewImageUrl === optionInfoModal.overviewImage)
                ));
                return sample?.samplePath ? (
                  <a href={`/${locale}${sample.samplePath}`} target="_blank" rel="noreferrer" className="inline-flex w-fit items-center text-sm font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900">
                    {locale === 'de' ? 'Mehr Informationen über dieses Material' : locale === 'fr' ? 'Plus d’informations sur ce matériau' : locale === 'en' ? 'More information about this material' : 'Meer informatie over dit materiaal'}
                  </a>
                ) : null;
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
